import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import api, { deploySatellite, getUserSatellites, getSatellitesInRadius, performOceanScan } from '../services/api';
import './HackThePlanet.css'; // New professional styling
import './LaserAnimations.css'; // Laser beam animations
import './map-popup-compression.css'; // Compressed popup styles


// Fix default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Exponential backoff retry utility
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries - 1) {
        throw error; // Final attempt failed
      }
      
      // Check if it's a server error worth retrying
      const isRetryableError = error.response?.status >= 500 || 
                              error.response?.status === 503 ||
                              error.code === 'NETWORK_ERROR' ||
                              error.message?.includes('timeout');
      
      if (!isRetryableError) {
        throw error; // Don't retry client errors (4xx)
      }
      
      const delay = baseDelay * Math.pow(2, attempt) + Math.random() * 1000;
      console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${Math.round(delay)}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// Drone Sniffer Cooldown Component
const DroneSnifferCooldown = ({ droneId }) => {
  const [cooldownData, setCooldownData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    const fetchCooldownStatus = async () => {
      try {
        const response = await api.get(`/drone/cooldown-status/${droneId}`);
        
        if (response.status === 200) {
          const data = response.data;
          setCooldownData(data);
          
          if (!data.canUse && data.snifferCooldownExpires) {
            const remaining = new Date(data.snifferCooldownExpires).getTime() - Date.now();
            setTimeRemaining(Math.max(0, Math.ceil(remaining / 1000)));
          }
        }
      } catch (error) {
        console.error('Error fetching cooldown status:', error);
      }
    };

    fetchCooldownStatus();
    const interval = setInterval(fetchCooldownStatus, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [droneId, user.token]);

  useEffect(() => {
    if (timeRemaining > 0) {
      const countdown = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 1;
          if (newTime <= 0) {
            // Refresh cooldown data when timer reaches 0
            setCooldownData(prev => prev ? { ...prev, canUse: true } : null);
          }
          return Math.max(0, newTime);
        });
      }, 1000);

      return () => clearInterval(countdown);
    }
  }, [timeRemaining]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (!cooldownData) {
    return <p style={{ color: '#888', fontSize: '11px' }}>⏳ Checking cooldown...</p>;
  }

  if (cooldownData.canUse) {
    return <p style={{ color: '#00ff41', fontSize: '11px' }}>✅ Sniffer Ready</p>;
  }

  return (
    <div style={{ backgroundColor: '#ff4444', padding: '3px 6px', borderRadius: '3px', fontSize: '11px', marginTop: '5px' }}>
      <p style={{ margin: 0, color: 'white' }}>🚫 Cooldown: {formatTime(timeRemaining)}</p>
    </div>
  );
};

// Function to check if a business is within any active scout drone's sniffer radius
const isBusinessWithinScoutDroneRadius = (business, deployedDrones, droneSniffersActive) => {
  if (!business.location || !business.location.lat || !business.location.lng) return false;
  
  // Check each active scout drone sniffer
  for (const droneId of droneSniffersActive) {
    const drone = deployedDrones.find(d => d.droneId === droneId);
    if (drone && (drone.type === 'scout' || drone.type === 'maritime_scout') && drone.position?.lat && drone.position?.lng && business.location?.lat && business.location?.lng) {
      const distance = calculateDistance(
        drone.position.lat,
        drone.position.lng,
        business.location.lat,
        business.location.lng
      );
      
      // Use proper sniffer radius: 50km for maritime scout, 3km for others
      const snifferRadius = (drone.type === 'maritime_scout') ? 50000 : (drone.specifications?.snifferRadius || 3000);
      if (distance <= snifferRadius) {
        return true;
      }
    }
  }
  
  return false;
};

// Function to calculate direction and create rotated drone icon
const createDirectionalDroneIcon = (drone) => {
  let rotation = 0;
  
  // Calculate direction if drone has previous position and current position
  if (drone.previousPosition && drone.previousPosition.lat && drone.previousPosition.lng && 
      drone.position?.lat && drone.position?.lng) {
    // Calculate movement direction: from previous position to current position
    const deltaLat = drone.position.lat - drone.previousPosition.lat;
    const deltaLng = drone.position.lng - drone.previousPosition.lng;
    
    // Calculate bearing in degrees (direction of movement)
    const bearing = Math.atan2(deltaLng, deltaLat) * 180 / Math.PI;
    
    // Make drone face AWAY from where it came from (add 180° to face opposite direction)
    const awayBearing = bearing + 180;
    
    // Convert bearing to rotation accounting for the plane icon's default 45° angle
    // The plane emoji naturally points at 45° (northeast), so we adjust accordingly
    rotation = awayBearing - 45; // Subtract 45° to account for the icon's default orientation
  }
  
  // Create custom icon with rotation
  const iconSize = [30, 30];
  return L.divIcon({
    html: `<div style="
      font-size: 20px; 
      text-align: center; 
      line-height: ${iconSize[1]}px; 
      cursor: pointer; 
      touch-action: manipulation;
      transform: rotate(${rotation}deg);
      transform-origin: center;
      transition: transform 0.3s ease;
    ">✈️</div>`,
    iconSize: iconSize,
    iconAnchor: [iconSize[0] / 2, iconSize[1] / 2],
    popupAnchor: [0, -iconSize[1] / 2],
    className: 'custom-drone-icon'
  });
};

// Map configuration - use CSS for responsive sizing
const mapStyle = {
  width: '100%',
  height: '100%'
};

// Mobile detection
const isMobile = () => {
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Default center (New York City)
const defaultCenter = [40.7128, -74.0060];

// Create custom icons using divIcon for emoji
const createCustomIcon = (emoji, size = [25, 25]) => {
  // Make icons smaller on mobile to fit better on the map
  const mobileSize = isMobile() ? [Math.max(size[0] * 0.7, 18), Math.max(size[1] * 0.7, 18)] : size;
  
  return L.divIcon({
    html: `<div style="font-size: ${mobileSize[0]-3}px; text-align: center; line-height: ${mobileSize[1]}px; cursor: pointer; touch-action: manipulation;">${emoji}</div>`,
    iconSize: mobileSize,
    iconAnchor: [mobileSize[0] / 2, mobileSize[1]],
    popupAnchor: [0, -mobileSize[1]],
    className: 'custom-emoji-icon'
  });
};

// Helper function to format countdown display
const formatCountdown = (hoursRemaining, minutesRemaining, isExpired) => {
  if (isExpired) {
    return <span style={{ color: '#ff4444', fontWeight: 'bold' }}>⏰ EXPIRED</span>;
  }
  
  if (hoursRemaining === null || minutesRemaining === null) {
    return <span style={{ color: '#888' }}>No expiration</span>;
  }
  
  const color = hoursRemaining < 1 ? '#ff6644' : hoursRemaining < 6 ? '#ffaa44' : '#44ff44';
  
  if (hoursRemaining > 0) {
    return <span style={{ color }}>⏰ {hoursRemaining}h {minutesRemaining}m left</span>;
  } else {
    return <span style={{ color }}>⏰ {minutesRemaining}m left</span>;
  }
};

// Helper function to extract casino ID from hotel ID
const extractCasinoId = (hotelId) => {
  // Hotel IDs are like "HOTEL_CASINO_CASINO_WYNN_LAS_VEGAS_5"
  // Casino IDs are like "CASINO_WYNN_LAS_VEGAS_5"
  // Remove the "HOTEL_CASINO_" prefix to get the actual casino ID
  if (hotelId && hotelId.startsWith('HOTEL_CASINO_')) {
    return hotelId.replace('HOTEL_CASINO_', '');
  }
  return hotelId; // Return as-is if it doesn't have the prefix
};

// Helper function to calculate distance between two points in meters
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distance in meters
};

// Helper function to check if a target is within compound protection radius
const isTargetProtectedByCompound = (targetPosition, userCompound) => {
  if (!userCompound || !userCompound.commandCenter || !userCompound.defenseTowers) {
    return { protected: false, reason: 'No compound defense system', distance: null };
  }

  // Check if command center is active
  if (userCompound.commandCenter.health <= 0) {
    return { protected: false, reason: 'Command center is destroyed', distance: null };
  }

  // Defense towers protect the command center, not the vault directly
  // Protection is provided by active command center within 1000m radius

  // Validate target position
  if (!targetPosition || typeof targetPosition.lat !== 'number' || typeof targetPosition.lng !== 'number') {
    console.error('Invalid target position:', targetPosition);
    return { protected: false, reason: 'Invalid target position', distance: null };
  }

  // Validate command center position
  if (!userCompound.commandCenter.position || 
      typeof userCompound.commandCenter.position.lat !== 'number' || 
      typeof userCompound.commandCenter.position.lng !== 'number') {
    console.error('Invalid command center position:', userCompound.commandCenter.position);
    return { protected: false, reason: 'Invalid command center position', distance: null };
  }

  // Calculate distance from target to command center
  const distanceToCommandCenter = calculateDistance(
    targetPosition.lat,
    targetPosition.lng,
    userCompound.commandCenter.position.lat,
    userCompound.commandCenter.position.lng
  );

  // Validate distance calculation
  if (isNaN(distanceToCommandCenter)) {
    console.error('Distance calculation returned NaN:', {
      targetPosition,
      commandCenterPosition: userCompound.commandCenter.position
    });
    return { protected: false, reason: 'Distance calculation error', distance: null };
  }

  // Protection radius from command center is 1000m (1km)
  const protectionRadius = 1000;
  
  const isProtected = distanceToCommandCenter <= protectionRadius;

  return { 
    protected: isProtected, 
    reason: isProtected ? 'Within command center protection' : 'Outside command center radius',
    distance: Math.round(distanceToCommandCenter),
    protectionRadius: protectionRadius
  };
};

const LeafletTreasureHunt = () => {
  const { user, updateUser, syncData, isInJail, getJailTimeRemaining } = useAuth();
  const { 
    notifyComputerMove, 
    notifyAttackStarted,
    notifyAttackCompleted,
    notifyIncomingAttack,
    removeIncomingAttackNotification,
    notifyBruteForceStarted,
    notifyBruteForceCompleted,
    addNotification
  } = useNotifications();
  
  const mapRef = useRef(null);
  const [treasureHuntData, setTreasureHuntData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedChest, setSelectedChest] = useState(null);
  const [combinationInput, setCombinationInput] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(2);
  const [stats, setStats] = useState(null);
  
  // Bank states
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankAccessCode, setBankAccessCode] = useState('');
  const [accessingBank, setAccessingBank] = useState(false);
  
  // Casino states
  const [casinos, setCasinos] = useState([]);
  const [selectedCasino, setSelectedCasino] = useState(null);
  const [casinoAction, setCasinoAction] = useState(null);
  const [vaultStage, setVaultStage] = useState(null);
  const [casinoHackProgress, setCasinoHackProgress] = useState([]);
  const [activeVaultHacks, setActiveVaultHacks] = useState([]);
  
  // Hotel states
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [checkedInHotel, setCheckedInHotel] = useState(null); // Hotel user is currently staying at
  
  // ATM states
  const [atms, setAtms] = useState([]);
  const [selectedATM, setSelectedATM] = useState(null);
  const [atmAccessCode, setAtmAccessCode] = useState('');
  const [userATMCodes, setUserATMCodes] = useState([]);
  const [searchCoordinates, setSearchCoordinates] = useState({ lat: '', lng: '' });
  
  // Business states
  const [businesses, setBusinesses] = useState([]);
  const [userBusiness, setUserBusiness] = useState(null);
  const [userBusinesses, setUserBusinesses] = useState([]); // Array of all user businesses
  
  // Satellite states
  const [satellites, setSatellites] = useState([]);
  const [userSatellites, setUserSatellites] = useState([]);
  const [isDeployingSatellite, setIsDeployingSatellite] = useState(false);
  const [selectedSatelliteForDeployment, setSelectedSatelliteForDeployment] = useState(null);
  const [showSatellitePanel, setShowSatellitePanel] = useState(false);
  const [satelliteMoving, setSatelliteMoving] = useState(null); // ID of satellite being moved
  
  // Drone states
  const [deployedDrones, setDeployedDrones] = useState([]);
  const [selectedDrone, setSelectedDrone] = useState(null);
  const [isDroneMoving, setIsDroneMoving] = useState(false);
  const [droneTargets, setDroneTargets] = useState([]); // Enemy defense towers
  const [isDeployingDrone, setIsDeployingDrone] = useState(false);
  const [showDronePanel, setShowDronePanel] = useState(false);
  const [selectedDroneForDeployment, setSelectedDroneForDeployment] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isPlacingBusiness, setIsPlacingBusiness] = useState(false);
  const [businessLinkRequests, setBusinessLinkRequests] = useState([]);
  const [selectedBusinessForPlacement, setSelectedBusinessForPlacement] = useState(null);
  const [businessLinkingModal, setBusinessLinkingModal] = useState({
    show: false,
    sourceBusiness: null,
    targetBusinessType: null
  });
  
  // Compound Defense states
  const [nearbyCompounds, setNearbyCompounds] = useState([]);
  const [nearbyDefenseTowers, setNearbyDefenseTowers] = useState([]);
  
  // Career states
  const [careerData, setCareerData] = useState({
    userJob: null,
    offices: []
  });
  const [workSession, setWorkSession] = useState(null);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [fieldOperation, setFieldOperation] = useState(null);
  const [arrestData, setArrestData] = useState(null);
  const [selectedUserForArrest, setSelectedUserForArrest] = useState(null);
  const [arrestPending, setArrestPending] = useState(null);
  const [escapeTimeRemaining, setEscapeTimeRemaining] = useState(0);
  const [isEscapeMode, setIsEscapeMode] = useState(false);
  const [showArrestModal, setShowArrestModal] = useState(false);
  
  // User location states
  const [nearbyBanks, setNearbyBanks] = useState([]);
  const [nearbyCasinos, setNearbyCasinos] = useState([]);
  const [nearbyATMs, setNearbyATMs] = useState([]);
  
  // Computer placement states
  const [computerLocations, setComputerLocations] = useState([]);
  const [selectedComputer, setSelectedComputer] = useState(null);
  const [userComputer, setUserComputer] = useState(null);
  const [isPlacingComputer, setIsPlacingComputer] = useState(false);
  const [isMovingComputer, setIsMovingComputer] = useState(false);
  const [computerStats, setComputerStats] = useState({ 
    movesRemaining: 10, 
    maxMoves: 10, 
    resetPeriod: '6 hours',
    mode: 'survival',
    foodDrinkSupplies: 10
  });
  const [snifferActive, setSnifferActive] = useState(false);
  const [showMyComputerModal, setShowMyComputerModal] = useState(false);
  
  // Drone sniffer states
  const [droneSniffersActive, setDroneSniffersActive] = useState(new Set()); // Set of drone IDs with active sniffers
  const [droneDetectedTargets, setDroneDetectedTargets] = useState({}); // Object mapping drone IDs to their detected targets
  
  // Individual drone-detected target states (simpler approach like computer sniffer)
  const [droneDetectedBanks, setDroneDetectedBanks] = useState({});
  const [droneDetectedCasinos, setDroneDetectedCasinos] = useState({});
  const [droneDetectedATMs, setDroneDetectedATMs] = useState({});
  const [droneDetectedUsers, setDroneDetectedUsers] = useState({});
  const [droneDetectedVaults, setDroneDetectedVaults] = useState({});
  const [droneDetectedCompounds, setDroneDetectedCompounds] = useState({});
  const [droneDetectedComputers, setDroneDetectedComputers] = useState({});
  const [droneDetectedTowers, setDroneDetectedTowers] = useState({});
  const [droneDetectedHouses, setDroneDetectedHouses] = useState({});
  const [droneDetectedBusinesses, setDroneDetectedBusinesses] = useState({});
  const [droneDetectedDrones, setDroneDetectedDrones] = useState({});
  const [networkAttackStatus, setNetworkAttackStatus] = useState({
    activeAttacks: [],
    incomingAttacks: [],
    networkInjectorTools: 0,
    firewallParts: 0
  });
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [computerControlOpen, setComputerControlOpen] = useState(false);
  const [networkAttackOpen, setNetworkAttackOpen] = useState(false);
  
  // ATM Vault states (display only - management moved to System page)
  const [userVaults, setUserVaults] = useState([]); // User's own vaults (always visible)
  const [nearbyVaults, setNearbyVaults] = useState([]); // Other players' vaults (sniffer only)
  const [selectedVault, setSelectedVault] = useState(null);
  const [isPlacingVault, setIsPlacingVault] = useState(false);
  
  // Defense Tower states
  const [defenseTowers, setDefenseTowers] = useState([]);
  const [isPlacingTower, setIsPlacingTower] = useState(false);
  const [towerDeploymentLoading, setTowerDeploymentLoading] = useState(false);
  const [userCompound, setUserCompound] = useState(null);
  const [isPlacingCompound, setIsPlacingCompound] = useState(false);
  const [isMovingCommandCenter, setIsMovingCommandCenter] = useState(false);
  
  
  // House states
  const [houses, setHouses] = useState([]);
  const [unplacedHouses, setUnplacedHouses] = useState([]);
  const [isPlacingHouse, setIsPlacingHouse] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [isSleeping, setIsSleeping] = useState(false);
  const [sleepingInHouse, setSleepingInHouse] = useState(null);
  
  // House countdown timer states
  const [houseCountdown, setHouseCountdown] = useState({ active: false, seconds: 0, action: '' });
  
  // User's active house rentals
  const [activeRentals, setActiveRentals] = useState([]);

  // Brute force states
  const [activeBruteForces, setActiveBruteForces] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [targetType, setTargetType] = useState(null);
  
  // Attack drone states
  const [attackMode, setAttackMode] = useState(false);
  const [selectedAttackDrone, setSelectedAttackDrone] = useState(null);
  const [attackableTargets, setAttackableTargets] = useState([]);
  const [activeLaserBeams, setActiveLaserBeams] = useState([]); // Array of active laser beam animations
  
  // SWAT warning states
  const [slotSwatWarnings, setSlotSwatWarnings] = useState([]);
  const [jailStatus, setJailStatus] = useState({ isJailed: false, jailedUntil: null });
  
  // Casino interaction states
  const [slotHackInProgress, setSlotHackInProgress] = useState(false);
  
  // Users in radius states  
  const [usersInRadius, setUsersInRadius] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  // XP chest countdown popup states
  const [showXPCountdown, setShowXPCountdown] = useState(false);

  // Fullscreen mode states
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Admin coordinate picker states
  const [coordinatePickerMode, setCoordinatePickerMode] = useState(false);
  const [pickedCoordinates, setPickedCoordinates] = useState(null);

  // Vault movement states
  const [isMovingVault, setIsMovingVault] = useState(false);
  const [vaultToMove, setVaultToMove] = useState(null);

  // Vault withdrawal states
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawVault, setWithdrawVault] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  
  // Community task states
  const [communityTasks, setCommunityTasks] = useState([]);
  const [selectedCommunityTask, setSelectedCommunityTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskSolution, setTaskSolution] = useState('');
  const [isPlacingTask, setIsPlacingTask] = useState(false);

  // Filter states with server-side persistence
  const [mapFilters, setMapFilters] = useState({
    showBusinessRadius: true,
    showUsers: true,
    showTasks: true,
    showBanks: true,
    showCasinos: true,
    showATMs: true,
    showVaults: true,
    showComputers: true,
    showTreasureChests: true,
    showHouses: true,
    showHotels: true,
    showBusinesses: true,
    showOffense: true,
    showDrones: true,
    showSatellites: true
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  
  // Load filter preferences from server
  const loadFilterPreferences = useCallback(async () => {
    if (!user?.token) return;
    
    try {
      const response = await api.get('/profile/map-filters');
      
      if (response.status === 200) {
        const data = response.data;
        if (data.mapFilterPreferences) {
          setMapFilters(data.mapFilterPreferences);
        }
      } else {
        // Fallback to localStorage if server fails
        const savedFilters = localStorage.getItem('mapFilters');
        if (savedFilters) {
          setMapFilters(JSON.parse(savedFilters));
        }
      }
    } catch (error) {
      console.error('Error loading map filter preferences:', error);
      // Fallback to localStorage if server fails
      const savedFilters = localStorage.getItem('mapFilters');
      if (savedFilters) {
        setMapFilters(JSON.parse(savedFilters));
      }
    } finally {
      setFiltersLoaded(true);
    }
  }, [user?.token]);
  
  // Save filter preferences to server and localStorage
  const saveFilterPreferences = async () => {
    if (!user?.token) {
      // If not logged in, save to localStorage only
      localStorage.setItem('mapFilters', JSON.stringify(mapFilters));
      addNotification({
        title: '💾 Filter Preferences Saved Locally',
        message: 'Your filter settings have been saved locally.',
        type: 'success'
      });
      return;
    }
    
    try {
      const response = await api.post('/profile/map-filters', { mapFilterPreferences: mapFilters });
      
      if (response.status === 200) {
        // Also save to localStorage as backup
        localStorage.setItem('mapFilters', JSON.stringify(mapFilters));
        addNotification({
          title: '💾 Filter Preferences Saved',
          message: 'Your filter settings have been saved and will persist across devices.',
          type: 'success'
        });
      } else {
        throw new Error('Server save failed');
      }
    } catch (error) {
      console.error('Error saving map filter preferences:', error);
      // Fallback to localStorage if server fails
      localStorage.setItem('mapFilters', JSON.stringify(mapFilters));
      addNotification({
        title: '💾 Filter Preferences Saved Locally',
        message: 'Your filter settings have been saved locally (server unavailable).',
        type: 'warning'
      });
    }
  };
  const [taskToPlace, setTaskToPlace] = useState(null);
  const [userPlacedTasks, setUserPlacedTasks] = useState([]);
  const [unplacedTasks, setUnplacedTasks] = useState([]);
  const [isFieldAgent, setIsFieldAgent] = useState(false);
  const [fieldAgentRemovalReason, setFieldAgentRemovalReason] = useState('');

  // Check URL parameters for vault movement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const vaultMove = urlParams.get('vaultMove');
    
    if (vaultMove === 'true' && userVaults.length > 0) {
      // User has a vault and wants to move it
      setVaultToMove(userVaults[0]); // Get user's vault
      setIsMovingVault(true);
      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [userVaults]);

  // Check URL parameters for task placement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const placeTaskParam = urlParams.get('place-task');
    
    if (placeTaskParam) {
      // Check if task data is available in sessionStorage
      try {
        const taskData = sessionStorage.getItem('taskToPlace');
        if (taskData) {
          const task = JSON.parse(taskData);
          setTaskToPlace(task);
          setIsPlacingTask(true);
          
          // Show notification to user
          addNotification({
            title: 'Task Placement Mode',
            message: `Click anywhere on the map to place "${task.title}" for users to find`
          });
          
          // Clear sessionStorage and URL parameter
          sessionStorage.removeItem('taskToPlace');
        }
      } catch (error) {
        console.error('Error parsing task data from sessionStorage:', error);
      }
      
      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [addNotification]);

  // Check URL parameters for tower placement and compound placement
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    
    if (mode === 'tower-placement') {
      setIsPlacingTower(true);
      
      // Show notification to user
      addNotification({
        title: '🗼 Tower Placement Mode',
        message: 'Click anywhere on the map to deploy a defense tower. Must be within 2km of your command center and 500m from other towers.',
        type: 'info'
      });
      
      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (mode === 'compound-placement') {
      setIsPlacingCompound(true);
      
      // Show notification to user about compound placement
      addNotification({
        title: '🚐 Compound Location Setup',
        message: 'Click anywhere on the map to set your location for compound defense initialization. This will be your command center position.',
        type: 'info'
      });
      
      // Clear URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [addNotification]);

  // Restore sleeping status when both userComputer and houses are loaded
  useEffect(() => {
    if (userComputer && userComputer.isSleeping && userComputer.sleepingHouseId && houses.length > 0 && !sleepingInHouse) {
      console.log('Attempting to restore sleeping house from loaded houses');
      const sleepingHouse = houses.find(house => house._id === userComputer.sleepingHouseId);
      if (sleepingHouse) {
        setSleepingInHouse(sleepingHouse);
        console.log('Sleeping house found and restored:', sleepingHouse);
      } else {
        console.log('Sleeping house not found in loaded houses, fetching by ID');
        fetchHouseById(userComputer.sleepingHouseId);
      }
    }
  }, [userComputer, houses, sleepingInHouse]);

  // Load map filter preferences when user is available
  useEffect(() => {
    if (user?.token && !filtersLoaded) {
      loadFilterPreferences();
    }
  }, [user?.token, filtersLoaded, loadFilterPreferences]);

  const [xpCountdownData, setXPCountdownData] = useState(null);
  const [xpCountdownTimer, setXPCountdownTimer] = useState(5);

  // Computer movement countdown popup states
  const [showMoveCountdown, setShowMoveCountdown] = useState(false);
  const [moveCountdownData, setMoveCountdownData] = useState(null);
  const [moveCountdownTimer, setMoveCountdownTimer] = useState(10);

  // Computer deployment countdown popup states
  const [showDeployCountdown, setShowDeployCountdown] = useState(false);
  const [deployCountdownData, setDeployCountdownData] = useState(null);
  const [deployCountdownTimer, setDeployCountdownTimer] = useState(10);

  // Hotel check-in countdown popup states
  const [showHotelCheckinCountdown, setShowHotelCheckinCountdown] = useState(false);
  const [hotelCheckinCountdownData, setHotelCheckinCountdownData] = useState(null);
  const [hotelCheckinCountdownTimer, setHotelCheckinCountdownTimer] = useState(5);

  // Rental house entry countdown popup states
  const [showRentalHouseCountdown, setShowRentalHouseCountdown] = useState(false);
  const [rentalHouseCountdownData, setRentalHouseCountdownData] = useState(null);
  const [rentalHouseCountdownTimer, setRentalHouseCountdownTimer] = useState(5);

  // ATM travel countdown popup states
  const [showATMTravelCountdown, setShowATMTravelCountdown] = useState(false);
  const [atmTravelCountdownData, setATMTravelCountdownData] = useState(null);
  const [atmTravelCountdownTimer, setATMTravelCountdownTimer] = useState(10);

  // House placement popup states
  const [showHouseDistanceWarning, setShowHouseDistanceWarning] = useState(false);
  const [houseDistanceMessage, setHouseDistanceMessage] = useState('');
  const [showHouseConstruction, setShowHouseConstruction] = useState(false);
  const [houseConstructionTimer, setHouseConstructionTimer] = useState(5);

  // Custom icons
  const treasureIcon = createCustomIcon('💰', [30, 30]);
  const xpChestIcon = createCustomIcon('⭐', [30, 30]);
  const bankIcon = createCustomIcon('🏦', [30, 30]);
  const casinoIcon = createCustomIcon('🎰', [30, 30]);
  const atmIcon = createCustomIcon('🏧', [30, 30]);
  const computerIcon = createCustomIcon('💻', [25, 25]);
  const userIcon = createCustomIcon('👤', [20, 20]);
  const vaultIcon = createCustomIcon('🔐', [25, 25]); // Other players' vaults
  const myVaultIcon = createCustomIcon('🔐', [25, 25]); // User's own vaults
  const houseIcon = createCustomIcon('🏠', [25, 25]); // Houses
  const businessIcon = createCustomIcon('🏢', [25, 25]); // Businesses
  const jobIcon = createCustomIcon('💼', [30, 30]); // Job offices
  const droneIcon = createCustomIcon('✈️', [25, 25]); // Deployed drones
  const towerIcon = createCustomIcon('🗼', [20, 20]); // Defense towers

  // Map click handler component
  const MapClickHandler = () => {
    const map = useMapEvents({
      click: handleMapClick,
      zoomend: (e) => {
        setMapZoom(e.target.getZoom());
      }
    });
    
    // Force map to resize and invalidate size on mount and window resize
    React.useEffect(() => {
      const resizeMap = () => {
        setTimeout(() => {
          if (map && map.getContainer && map.getContainer()) {
            try {
              map.invalidateSize();
            } catch (error) {
              console.warn('Map invalidateSize failed:', error);
            }
          }
        }, 100);
      };
      
      // Initial resize
      resizeMap();
      
      // Listen for window resize
      window.addEventListener('resize', resizeMap);
      
      return () => {
        window.removeEventListener('resize', resizeMap);
      };
    }, [map]);
    
    return null;
  };


  // Load essential data on component mount (optimized for faster loading)
  useEffect(() => {
    if (!user?.token) {
      return;
    }
    
    // Load only essential data immediately for faster page load
    const loadEssentialData = async () => {
      try {
        setInitialLoading(true);
        
        // Load core data first (treasure hunt, user computer, and user vaults)
        await Promise.all([
          loadTreasureHuntData(),
          fetchUserComputer(),
          fetchUserVaults()
        ]);
        
        // Load active attack animations to restore persistent laser beams
        await loadActiveAttackAnimations();
        
        // Core data loaded - page can render
        setInitialLoading(false);
        
        // Load secondary data in background without blocking UI
        setBackgroundLoading(true);
        await Promise.all([
          fetchBanks(),
          fetchCasinos(),
          fetchATMs(),
          fetchHotels(),
          fetchUserBookings(),
          fetchHouses(),
          fetchUnplacedHouses(),
          fetchActiveRentals(),
          fetchBusinesses(),
          fetchUserBusiness(),
          fetchCareerData(),
          fetchCommunityTasks(),
          fetchUserPlacedTasks(),
          fetchUnplacedTasks(),
          checkFieldAgentStatus(),
          loadCompoundData(),
          fetchSatellites(),
          fetchUnplacedSatellites()
        ]);
        
        // Load status data last (non-essential for initial render)
        setTimeout(async () => {
          await Promise.all([
            fetchNetworkAttackStatus(),
            fetchUserATMCodes(),
            fetchSlotSwatWarnings(),
            fetchJailStatus(),
            fetchVaultHackProgress(),
            fetchFieldOperationStatus(),
            fetchBruteForceStatus()
          ]);
          setBackgroundLoading(false);
        }, 1000);
        
      } catch (error) {
        console.error('Error loading treasure hunt data:', error);
        setInitialLoading(false);
        setBackgroundLoading(false);
      }
    };
    
    loadEssentialData();
    
    // Set up intervals for real-time updates (less frequent to reduce load)
    const statusInterval = setInterval(async () => {
      try {
        // Use Promise.allSettled to prevent one failure from stopping others
        const results = await Promise.allSettled([
          fetchNetworkAttackStatus(),
          fetchSlotSwatWarnings(),
          fetchJailStatus(),
          fetchVaultHackProgress(),
          fetchBruteForceStatus(),
          loadCompoundData() // Refresh compound data periodically
        ]);
        
        // Log any failures but don't crash the interval
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.warn(`Status update ${index} failed:`, result.reason);
          }
        });
      } catch (error) {
        console.warn('Status interval error:', error);
      }
    }, 30000); // Reduced to every 30 seconds instead of 10
    
    // Conditional polling for casino hack stage updates  
    const casinoInterval = setInterval(() => {
      // Only poll when user has active casino activities to reduce server load
      fetchVaultHackProgress();
    }, 30000); // Every 30 seconds, reduced from 10s to prevent server overload
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(casinoInterval);
    };
  }, [user?.token, user?._id]); // Only trigger on login/logout, not XP changes

  // Auto-zoom to user's computer on page load (but don't auto-activate sniffer)
  useEffect(() => {
    if (userComputer && userComputer.coordinates) {
      
      // Set map center to user's computer location
      setMapCenter([userComputer.coordinates.lat, userComputer.coordinates.lng]);
      setMapZoom(10); // Zoom in to show computer area clearly
      
      // Don't auto-activate sniffer - user must click their computer or activate button
      // Computer found - zoom to location without activating sniffer
    } else if (userComputer === null && !initialLoading) {
      // User doesn't have a computer yet - show world view for placement (only after loading completes)
      console.log('No computer found after initial loading - showing world view for placement');
      setMapCenter(defaultCenter); // Use default NYC center
      setMapZoom(3); // Show world map for computer placement
    }
  }, [userComputer, initialLoading]); // Include initialLoading to prevent premature "no computer" message

  // Refresh computer locations and animations when sniffer state changes
  useEffect(() => {
    if (user?.token && (userComputer || deployedDrones.length > 0)) {
      fetchComputerLocations();
      // Reload animations with sniffer radius filtering
      loadActiveAttackAnimations();
    }
  }, [snifferActive, droneSniffersActive, userComputer, deployedDrones]);

  // Manage computer locations interval with proper state dependencies
  useEffect(() => {
    if (!user?.token) return;

    // Set up interval that respects current sniffer state
    const computersInterval = setInterval(() => {
      fetchComputerLocations();
    }, 60000); // Reduced to every 60 seconds to minimize server load

    return () => {
      clearInterval(computersInterval);
    };
  }, [user?.token, snifferActive, userComputer]); // Recreate interval when sniffer state changes

  // Real-time updates for casino looting (every second when Stage 3 is active)
  useEffect(() => {
    const hasActiveLootingHack = activeVaultHacks.some(hack => hack.stage === 'looting');
    
    if (!hasActiveLootingHack || !selectedCasino) return;
    
    // Update every second during looting for real-time display
    const lootingInterval = setInterval(() => {
      // Force re-render by updating a dummy state - the display logic will recalculate
      setActiveVaultHacks(prevHacks => [...prevHacks]);
    }, 1000);
    
    return () => {
      clearInterval(lootingInterval);
    };
  }, [activeVaultHacks, selectedCasino]);

  // Refresh businesses and community tasks when sniffer status changes
  useEffect(() => {
    if (user?.token) {
      fetchBusinesses();
      if (userComputer) {
        fetchCommunityTasks();
      }
    }
  }, [snifferActive, user?.token, userComputer]);

  // Work session countdown timer with pause/resume and deadline enforcement
  useEffect(() => {
    if (!workSession || !workSession.active || workSession.paused) return;

    const timer = setInterval(() => {
      const now = Date.now();
      
      // Check if 24-hour deadline has passed
      if (now > workSession.deadline) {
        addNotification({
          title: '⏰ Shift Expired!',
          message: `Work shift deadline exceeded. No payment awarded. Computer returned to ${workSession.office.officeName}.`,
          type: 'error'
        });
        
        // First, call the completion API to update server-side work session status
        api.post('/career/complete-work', {}).then(() => {
          // Refresh computer data after server-side work session is cleared
          setTimeout(() => {
            fetchUserComputer();
            fetchComputerLocations(); // Refresh all computer icons on map
          }, 500); // Small delay to ensure server-side completion is processed
        }).catch(error => {
          console.error('Error completing expired work session:', error);
          // Still refresh computer data even if completion fails
          setTimeout(() => {
            fetchUserComputer();
            fetchComputerLocations();
          }, 500);
        });
        
        // Clear work session and field operation locally
        setWorkSession(null);
        setFieldOperation(null); // Clear field operation state
        
        // Return computer to office location
        setUserComputer({
          userId: user.id,
          username: user.username,
          coordinates: {
            lat: workSession.office.location.lat,
            lng: workSession.office.location.lng
          },
          placedAt: new Date().toISOString(),
          status: 'active'
        });
        
        return;
      }
      
      // Update remaining work time
      const sessionElapsed = now - workSession.startTime;
      const newRemainingTime = workSession.remainingTime - 1000; // Reduce by 1 second
      const newTotalWorkedTime = workSession.totalWorkedTime + 1000;
      
      if (newRemainingTime <= 0) {
        // Work shift completed!
        const totalPay = workSession.hourlyRate * 8;
        
        addNotification({
          title: '🎉 Shift Complete!',
          message: `You earned ${totalPay} WR Coins for your 8-hour shift at ${workSession.office.officeName}!`,
          type: 'success'
        });

        // Call completion API
        api.post('/career/complete-work', {}).then(async (response) => {
          if (response.status === 200) {
            const data = response.data;
            // Update user's balance
            updateUser({
              ...user,
              wrCoins: user.wrCoins + data.data.earnings
            });
            
            // Refresh career data
            fetchCareerData();
            
            // Refresh computer data to show computer icon instead of field agent icon
            fetchUserComputer();
            
            // Refresh computer locations to update all icons on map
            fetchComputerLocations();
          }
        }).catch(error => {
          console.error('Error completing work shift:', error);
        });

        // Return computer to office location
        setUserComputer({
          userId: user.id,
          username: user.username,
          coordinates: {
            lat: workSession.office.location.lat,
            lng: workSession.office.location.lng
          },
          placedAt: new Date().toISOString(),
          status: 'active'
        });

        // Clear work session and field operation
        setWorkSession(null);
        setFieldOperation(null); // Clear field operation state to show computer icon again
      } else {
        // Update work session progress
        setWorkSession(prev => ({
          ...prev,
          remainingTime: newRemainingTime,
          totalWorkedTime: newTotalWorkedTime
        }));
      }
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, [workSession, user, addNotification, updateUser]);

  // Handle fullscreen change events (e.g., when user presses Escape)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // XP chest countdown timer effect
  useEffect(() => {
    if (!showXPCountdown || xpCountdownTimer <= 0) return;

    const countdown = setInterval(() => {
      setXPCountdownTimer(prev => {
        if (prev <= 1) {
          // Timer reached 0, close the popup
          setShowXPCountdown(false);
          setXPCountdownData(null);
          return 5; // Reset for next time
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showXPCountdown, xpCountdownTimer]);

  // Computer deployment countdown timer effect
  useEffect(() => {
    if (!showDeployCountdown || deployCountdownTimer <= 0) return;

    const countdown = setInterval(() => {
      setDeployCountdownTimer(prev => {
        if (prev <= 1) {
          // Timer reached 0, close the popup
          setShowDeployCountdown(false);
          setDeployCountdownData(null);
          return 10; // Reset for next time
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showDeployCountdown, deployCountdownTimer]);

  // Hotel check-in countdown timer effect
  useEffect(() => {
    if (!showHotelCheckinCountdown || hotelCheckinCountdownTimer <= 0) return;

    const countdown = setInterval(() => {
      setHotelCheckinCountdownTimer(prev => {
        if (prev <= 1) {
          // Timer reached 0, close the popup
          setShowHotelCheckinCountdown(false);
          setHotelCheckinCountdownData(null);
          return 5; // Reset for next time
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showHotelCheckinCountdown, hotelCheckinCountdownTimer]);

  // Rental house entry countdown timer effect
  useEffect(() => {
    if (!showRentalHouseCountdown || rentalHouseCountdownTimer <= 0) return;

    const countdown = setInterval(() => {
      setRentalHouseCountdownTimer(prev => {
        if (prev <= 1) {
          // Timer reached 0, close the popup
          setShowRentalHouseCountdown(false);
          setRentalHouseCountdownData(null);
          return 5; // Reset for next time
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showRentalHouseCountdown, rentalHouseCountdownTimer]);

  // ATM travel countdown timer effect
  useEffect(() => {
    if (!showATMTravelCountdown || atmTravelCountdownTimer <= 0) return;

    const countdown = setInterval(() => {
      setATMTravelCountdownTimer(prev => {
        if (prev <= 1) {
          // Timer reached 0, close the popup
          setShowATMTravelCountdown(false);
          setATMTravelCountdownData(null);
          return 10; // Reset for next time
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [showATMTravelCountdown, atmTravelCountdownTimer]);

  // Check arrest status when user data changes
  useEffect(() => {
    checkArrestStatus();
  }, [user]);

  // Refresh compound data periodically to catch updates from other components
  useEffect(() => {
    // Refresh compound data every 30 seconds to catch any updates
    const compoundRefreshInterval = setInterval(async () => {
      try {
        await loadCompoundData();
      } catch (error) {
        console.warn('Compound data refresh failed:', error);
      }
    }, 30000); // 30 seconds

    return () => {
      clearInterval(compoundRefreshInterval);
    };
  }, []);

  // Real-time updates for towers under attack (faster refresh during battles)
  useEffect(() => {
    // Check if any towers are under attack
    const towersUnderAttack = deployedDrones.some(drone => 
      drone.status?.inBattle && drone.battle?.targetTowerId
    );
    
    if (!towersUnderAttack) return;
    
    // Refresh compound data every 5 seconds during active battles
    const battleRefreshInterval = setInterval(() => {
      loadCompoundData();
      fetchDeployedDrones(); // Also refresh drone data to get latest battle status
    }, 5000); // 5 seconds for real-time battle updates

    return () => {
      clearInterval(battleRefreshInterval);
    };
  }, [deployedDrones]);


  // Arrest pending countdown timer
  useEffect(() => {
    if (!arrestPending) return;

    let timeoutProcessed = false;
    const countdown = setInterval(() => {
      const now = new Date();
      const escapeDeadline = new Date(arrestPending.escapeDeadline);
      const timeRemaining = Math.max(0, escapeDeadline - now);
      
      if (timeRemaining > 0) {
        setEscapeTimeRemaining(timeRemaining);
      } else if (!timeoutProcessed) {
        // Time expired - process arrest and jail user (only once)
        timeoutProcessed = true;
        clearInterval(countdown);
        processArrestTimeout();
      }
    }, 100); // Update every 100ms for smooth countdown

    return () => clearInterval(countdown);
  }, [arrestPending]);

  // Load treasure hunt data
  const loadTreasureHuntData = async () => {
    try {
      const response = await api.get('/treasure-hunt/current');
      
      if (response.status === 200) {
        const data = response.data;
        setTreasureHuntData(data.data);
        
        // Also load deployed drones
        await fetchDeployedDrones();
        
        // Load satellites
        await fetchSatellites();
        
        // Load stats
        const statsResponse = await api.get('/treasure-hunt/stats');
        
        if (statsResponse.status === 200) {
          const statsData = statsResponse.data;
          setStats(statsData.data);
        }
      } else {
        const errorData = response.data;
        setError(errorData.message || 'Failed to load treasure hunt data');
      }
    } catch (error) {
      console.error('Error loading treasure hunt data:', error);
      setError('Failed to connect to server');
    }
  };

  // Load compound and defense tower data (cache-bust: v2)
  const loadCompoundData = async () => {
    if (!user?.token) {
      setUserCompound(null);
      setDefenseTowers([]);
      return;
    }
    
    // Check if user has defense systems before making API call
    const hasDefenseSystem = user?.partsInventory?.some(part => 
      part.name === 'MPU5 Fortress Core' || 
      part.name === 'MUP5 Fortress Core' || // Handle typo in part name
      part.category === 'defense_systems'
    );
    
    if (!hasDefenseSystem) {
      // User doesn't have defense systems, no need to check for compound
      setUserCompound(null);
      setDefenseTowers([]);
      return;
    }
    
    try {
      const response = await api.get('/compound-defense/status');
      const data = response.data;
        
      if (data.compound) {
        setUserCompound(data.compound);
        
        // Extract defense towers if compound exists
        if (data.compound.defenseTowers) {
          setDefenseTowers(data.compound.defenseTowers);
        } else {
          setDefenseTowers([]);
        }
      } else {
        setUserCompound(null);
        setDefenseTowers([]);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // No compound exists yet
        setUserCompound(null);
        setDefenseTowers([]);
      } else {
        console.error('Exception loading compound data:', error);
        setUserCompound(null);
        setDefenseTowers([]);
      }
    }
  };

  // Manual compound refresh function - can be called when compound is initialized
  const refreshCompoundData = async () => {
    await loadCompoundData();
    
    // Also refresh nearby compounds for sniffer detection
    if (userComputer && snifferActive) {
      const radius = calculateSnifferRadius();
      try {
        const response = await api.get(`/compound-defense/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${radius}`);
        const data = response.data;
        if (data?.success) {
          setNearbyCompounds(data.compounds || []);
          setNearbyDefenseTowers(data.defenseTowers || []);
        }
      } catch (error) {
        console.error('Error refreshing nearby compounds:', error);
      }
    }
  };

  // Fetch banks (only within sniffer radius)
  const fetchBanks = async () => {
    try {
      // Only show banks if user has a computer and sniffer is active
      if (!userComputer || !snifferActive) {
        setBanks([]);
        return;
      }
      
      const radius = calculateSnifferRadius();
      const response = await api.get(`/treasure-hunt/banks/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${radius}`);
      
      if (response.status === 200) {
        const data = response.data;
        setBanks(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  // Fetch casinos (only within sniffer radius)
  const fetchCasinos = async () => {
    try {
      // Only show casinos if user has a computer and sniffer is active
      if (!userComputer || !snifferActive) {
        setCasinos([]);
        return;
      }
      
      // Fetching casinos within sniffer radius
      const radius = calculateSnifferRadius();
      const response = await api.get(`/treasure-hunt/casinos/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${radius}`);
      
      if (response.status === 200 || response.status === 304) {
        // Handle both successful responses and 304 Not Modified (cached data)
        if (response.status === 304) {
          // Using cached casino data
          return; // Keep existing casino data
        }
        const data = response.data;
        // Casinos fetched successfully within radius
        setCasinos(data.data || []);
      } else {
        console.error('Failed to fetch casinos:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching casinos:', error);
    }
  };

  // Fetch ATMs (only within sniffer radius)
  const fetchATMs = async () => {
    try {
      // Only show ATMs if user has a computer and sniffer is active
      if (!userComputer || !snifferActive) {
        setAtms([]);
        return;
      }
      
      const radius = calculateSnifferRadius();
      const response = await api.get(`/treasure-hunt/atms/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${radius}`);
      
      if (response.status === 200) {
        const data = response.data;
        setAtms(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching ATMs:', error);
    }
  };
  
  // Fetch community tasks within sniffer radius
  const fetchCommunityTasks = async () => {
    if (!userComputer) return;
    
    try {
      const radius = calculateSnifferRadius();
      const response = await api.get(`/community-tasks/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${radius}`);
      
      if (response.status === 200) {
        const data = response.data;
        setCommunityTasks((data && data.tasks) ? data.tasks : []);
      }
    } catch (error) {
      console.error('Error fetching community tasks:', error);
    }
  };
  
  // Fetch user's placed tasks
  const fetchUserPlacedTasks = async () => {
    try {
      const response = await api.get('/community-tasks/user/placed');
      
      if (response.status === 200) {
        const data = response.data;
        setUserPlacedTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching user placed tasks:', error);
    }
  };
  
  // Fetch user's unplaced tasks
  const fetchUnplacedTasks = async () => {
    try {
      const response = await api.get('/community-tasks/user/created');
      
      if (response.status === 200) {
        const data = response.data;
        // Filter for tasks that are not location-based (not placed yet)
        const unplaced = (Array.isArray(data) ? data : []).filter(task => !task.isLocationBased);
        setUnplacedTasks(unplaced);
      }
    } catch (error) {
      console.error('Error fetching unplaced tasks:', error);
    }
  };

  // Handle task rating (thumbs up/down)
  const handleTaskRating = async (taskId, rating) => {
    try {
      const response = await api.post(`/community-tasks/rate/${taskId}`, { rating });

      const data = response.data;

      if (response.status === 200) {
        addNotification({
          title: '✅ Rating Submitted',
          message: `You ${rating === 'up' ? 'liked' : 'disliked'} this task`,
          type: 'success'
        });
      } else {
        addNotification({
          title: '❌ Rating Failed',
          message: data.message || 'Could not submit rating',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
      addNotification({
        title: '❌ Connection Error',
        message: 'Failed to submit rating',
        type: 'error'
      });
    }
  };

  // Handle task reporting
  const handleTaskReport = async (taskId, taskTitle) => {
    try {
      const reason = prompt(`Why are you reporting "${taskTitle}"?\n\nCommon reasons:\n- Inappropriate content\n- Spam or misleading\n- Offensive language\n- Copyright violation\n- Other abuse`);
      
      if (!reason || reason.trim() === '') {
        return; // User cancelled or didn't provide a reason
      }

      const response = await api.post(`/community-tasks/report/${taskId}`, { reason: reason.trim() });

      const data = response.data;

      if (response.status === 200) {
        addNotification({
          title: '🚨 Report Submitted',
          message: 'Thank you for reporting this task. Admins will review it.',
          type: 'success'
        });
      } else {
        addNotification({
          title: '❌ Report Failed',
          message: data.message || 'Could not submit report',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      addNotification({
        title: '❌ Connection Error',
        message: 'Failed to submit report',
        type: 'error'
      });
    }
  };
  
  // Handle community task interaction (when envelope is clicked)
  const handleTaskInteraction = async (task) => {
    try {
      const response = await api.get(`/community-tasks/interaction/${task._id}`);
      
      if (response.status === 200) {
        const data = response.data;
        setSelectedCommunityTask(data.task);
        setShowTaskModal(true);
      } else {
        const errorData = response.data;
        addNotification({
          title: 'Task Error',
          message: errorData.message || 'Could not load task details'
        });
      }
    } catch (error) {
      console.error('Error loading task details:', error);
      addNotification({
        title: 'Connection Error',
        message: 'Failed to load task details'
      });
    }
  };
  
  // Rate community task (thumbs up/down)
  const rateTask = async (taskId, ratingType) => {
    try {
      const response = await api.post(`/community-tasks/rate/${taskId}`, {
        rating: ratingType === 'upvote' ? 'up' : 'down' // Convert to backend format
      });

      const data = response.data;
      
      if (response.status === 200) {
        // Update the task in the state with new rating
        setSelectedCommunityTask(prev => ({
          ...prev,
          upvotes: data.upvotes,
          downvotes: data.downvotes,
          userRating: data.userRating
        }));
        
        // Also update in the community tasks list
        setCommunityTasks(prev => prev.map(task => 
          task._id === taskId 
            ? { ...task, upvotes: data.upvotes, downvotes: data.downvotes, userRating: data.userRating }
            : task
        ));
        
        addNotification({
          title: 'Rating Submitted',
          message: `You ${ratingType === 'upvote' ? 'liked' : 'disliked'} this task`
        });
      } else {
        addNotification({
          title: 'Rating Error',
          message: data.message || 'Failed to submit rating'
        });
      }
    } catch (error) {
      console.error('Error rating task:', error);
      addNotification({
        title: 'Connection Error',
        message: 'Failed to submit rating'
      });
    }
  };
  
  // Submit solution for community task
  const submitTaskSolution = async () => {
    if (!selectedCommunityTask || !taskSolution.trim()) {
      addNotification({
        title: 'Invalid Input',
        message: 'Please enter a solution'
      });
      return;
    }
    
    try {
      const response = await api.post('/community-tasks/submit-location', {
          taskId: selectedCommunityTask._id,
          solution: taskSolution
        });
      
      const data = response.data;
      
      if (response.status === 200) {
        addNotification({
          title: 'Success!',
          message: `Task completed! Earned ${data.xpAwarded} XP${data.creatorPaid > 0 ? ` (Paid creator ${data.creatorPaid} WR Coins)` : ''}`
        });
        
        // Update user XP and coins
        if (user) {
          updateUser({
            ...user,
            xp: (user.xp || 0) + data.xpAwarded,
            wrCoins: (user.wrCoins || 0) - (data.creatorPaid > 0 ? 1 : 0)
          });
        }
        
        // Close modal and refresh tasks
        setShowTaskModal(false);
        setTaskSolution('');
        setSelectedCommunityTask(null);
        fetchCommunityTasks();
      } else {
        addNotification({
          title: 'Task Failed',
          message: data.message || 'Incorrect solution'
        });
      }
    } catch (error) {
      console.error('Error submitting task solution:', error);
      addNotification({
        title: 'Connection Error',
        message: 'Failed to submit solution'
      });
    }
  };
  
  // Report community task
  const reportCommunityTask = async (reason, description = '') => {
    if (!selectedCommunityTask) return;
    
    try {
      const response = await api.post('/community-task/report-location', {
          taskId: selectedCommunityTask._id,
          reason,
          description
        });
      
      const data = response.data;
      
      if (response.status === 200) {
        addNotification({
          title: 'Task Reported',
          message: 'Thank you for reporting this task. It will be reviewed.'
        });
        
        setShowTaskModal(false);
        setSelectedCommunityTask(null);
        fetchCommunityTasks(); // Refresh tasks in case it was auto-removed
      } else {
        addNotification({
          title: 'Report Failed',
          message: data.message || 'Failed to report task'
        });
      }
    } catch (error) {
      console.error('Error reporting task:', error);
      addNotification({
        title: 'Connection Error',
        message: 'Failed to report task'
      });
    }
  };
  
  // Check if user is an active field agent
  const checkFieldAgentStatus = async () => {
    try {
      const response = await api.get('/treasure-hunt/field-agent-status');
      
      if (response.status === 200) {
        const data = response.data;
        setIsFieldAgent(data.isFieldAgent || false);
      }
    } catch (error) {
      console.error('Error checking field agent status:', error);
      setIsFieldAgent(false);
    }
  };
  
  // Field agent remove community task
  const fieldAgentRemoveTask = async () => {
    if (!selectedCommunityTask || !fieldAgentRemovalReason.trim()) {
      addNotification({
        title: 'Invalid Input',
        message: 'Please provide a reason for removal'
      });
      return;
    }
    
    if (!window.confirm(`Are you sure you want to permanently remove this community task?\n\nTask: "${selectedCommunityTask.title}"\nCreator: ${selectedCommunityTask.createdBy.username}\nReason: ${fieldAgentRemovalReason}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await api.post('/community-task/field-agent-remove', {
          taskId: selectedCommunityTask._id,
          reason: fieldAgentRemovalReason
        });
      
      const data = response.data;
      
      if (response.status === 200) {
        addNotification({
          title: 'Task Removed',
          message: `Community task "${selectedCommunityTask.title}" has been removed from the map`
        });
        
        // Close modal and refresh tasks
        setShowTaskModal(false);
        setSelectedCommunityTask(null);
        setFieldAgentRemovalReason('');
        fetchCommunityTasks();
      } else {
        addNotification({
          title: 'Removal Failed',
          message: data.message || 'Failed to remove task'
        });
      }
    } catch (error) {
      console.error('Error removing task:', error);
      addNotification({
        title: 'Connection Error',
        message: 'Failed to remove task'
      });
    }
  };

  // Fetch hotels (legacy function - hotels now handled by unified entities endpoint)
  const fetchHotels = async () => {
    try {
      // Hotels are now handled by the unified entities endpoint in refreshSnifferRadiusFeatures
      // This function is disabled to prevent conflicts with the unified system
      return;
    } catch (error) {
      console.error('Error in fetchHotels:', error);
    }
  };

  // Fetch businesses (user's own businesses always shown, others only within sniffer radius)
  const fetchBusinesses = async () => {
    try {
      let allBusinesses = [];
      
      // Always fetch ALL user's own businesses (not just the first one)
      try {
        const userBusinessResponse = await api.get('/business/my-business');
        
        if (userBusinessResponse.status === 200) {
          const userData = userBusinessResponse.data;
          // Get ALL user businesses from the businesses array
          if (userData.businesses && userData.businesses.length > 0) {
            userData.businesses.forEach(business => {
              // Mark as user's own business for visibility logic
              // Fishing businesses get special priority for visibility
              allBusinesses.push({
                ...business,
                visibilityReason: 'own_business',
                alwaysVisible: business.businessType === 'fishing' // Special flag for fishing businesses
              });
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user businesses:', error);
      }
      
      // If sniffer is active AND user has a computer, fetch nearby businesses
      if (snifferActive && userComputer) {
        const radius = calculateSnifferRadius();
        const response = await api.get(`/business/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${radius}`);
        
        if (response.status === 200) {
          const data = response.data;
          const nearbyBusinesses = (data && data.businesses) ? data.businesses : [];
          
          // Add nearby businesses that aren't already in the list (to avoid duplicates)
          nearbyBusinesses.forEach(business => {
            const alreadyExists = allBusinesses.some(existing => 
              existing._id === business._id || existing.id === business.id
            );
            if (!alreadyExists) {
              allBusinesses.push(business);
            }
          });
        }
      }
      
      setBusinesses(allBusinesses);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  // Fetch user's business for placement functionality
  const fetchUserBusiness = async () => {
    try {
      const response = await api.get('/business/my-business');
      
      if (response.status === 200) {
        const data = response.data;
        setUserBusiness(data.business); // Keep for backward compatibility
        setUserBusinesses(data.businesses || []); // Set all user businesses
        
        // Find first unplaced business for auto-selection
        const unplacedBusiness = data.businesses && data.businesses.find(business => 
          !business.location || !business.location.lat || !business.location.lng || 
          (business.location.lat === 0 && business.location.lng === 0)
        );
        
        if (unplacedBusiness && !selectedBusinessForPlacement) {
          setSelectedBusinessForPlacement(unplacedBusiness);
        } else if (data.business) {
        }
      }
    } catch (error) {
      console.error('Error fetching user business:', error);
    }
  };

  // Fetch career data
  const fetchCareerData = async () => {
    try {
      const response = await api.get('/career/overview');
      
      if (response.status === 200) {
        const data = response.data;
        setCareerData(data.data || { userJob: null, offices: [] });
        
        // Initialize work session state if there's an active work session
        if (data.data && data.data.workSession) {
          const activeSession = data.data.workSession;
          const now = Date.now();
          const startTime = new Date(activeSession.startTime).getTime();
          const duration = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
          const elapsedTime = now - startTime;
          const remainingTime = Math.max(0, duration - elapsedTime);
          
          setWorkSession({
            startTime: now,
            originalStartTime: startTime,
            deadline: startTime + (24 * 60 * 60 * 1000), // 24 hours from original start
            duration: duration,
            remainingTime: remainingTime,
            totalWorkedTime: elapsedTime,
            hourlyRate: activeSession.hourlyRate,
            office: data.data.offices.find(office => office._id === activeSession.officeId),
            active: true,
            paused: false
          });
        }
      }
    } catch (error) {
      console.error('Error fetching career data:', error);
    }
  };

  // Fetch user hotel bookings
  const fetchUserBookings = async () => {
    try {
      const response = await api.get('/hotel/bookings/my-bookings');
      
      if (response.status === 200) {
        const data = response.data;
        const bookings = data.bookings || [];
        setUserBookings(bookings);
        
        // Find currently checked-in hotel
        const checkedIn = bookings.find(booking => booking.status === 'checked_in');
        setCheckedInHotel(checkedIn || null);
      }
    } catch (error) {
      console.error('Error fetching user bookings:', error);
    }
  };

  // Fetch computer locations
  const fetchComputerLocations = async (customLat = null, customLng = null) => {
    try {
      // Always build query parameters with sniffer state to avoid initialization timing issues
      const params = new URLSearchParams({
        snifferActive: snifferActive.toString()
      });
      
      // Add position data if sniffer is active - use custom coordinates if provided, otherwise use userComputer
      if (snifferActive) {
        const lat = customLat || userComputer?.coordinates?.lat;
        const lng = customLng || userComputer?.coordinates?.lng;
        
        if (lat && lng) {
          params.append('userLat', lat.toString());
          params.append('userLng', lng.toString());
        }
      }
      
      const queryParams = '?' + params.toString();
      
      const response = await api.get(`/treasure-hunt/computers/locations${queryParams}`);
      
      if (response.status === 200) {
        const data = response.data;
        setComputerLocations((data && data.data) ? data.data : []);
      }
    } catch (error) {
      console.error('Error fetching computer locations:', error);
    }
  };

  // Fetch house by ID for sleeping status restoration
  const fetchHouseById = async (houseId) => {
    try {
      const response = await api.get(`/house/details/${houseId}`);
      
      if (response.status === 200) {
        const data = response.data;
        if (data.success && data.house) {
          setSleepingInHouse(data.house);
          console.log('Sleeping house fetched and restored:', data.house);
        }
      }
    } catch (error) {
      console.error('Error fetching house by ID:', error);
    }
  };

  // Fetch user computer
  const fetchUserComputer = async () => {
    try {
      const response = await api.get('/treasure-hunt/computers/locations');
      
      if (response.status === 200) {
        const data = response.data;
        const computerData = (data && data.data) ? data.data : [];
        const myComputer = computerData.find(comp => comp.userId === user?.id || comp.userId === user?._id);
        setUserComputer(myComputer || null);
        
        // Store computer data for later sleeping status restoration
        if (myComputer && myComputer.isSleeping && myComputer.sleepingHouseId) {
          console.log('User is sleeping in house, will restore sleeping status after houses load');
          setIsSleeping(true);
          // House will be set when houses are loaded
        } else if (!myComputer || !myComputer.isSleeping) {
          // Ensure sleeping status is cleared if not sleeping
          setIsSleeping(false);
          setSleepingInHouse(null);
        }
        
        // NEW SIMPLIFIED SYSTEM: Update computer stats based on supplies = moves
        
        // Check if user is locked out (12 hours after exhausting emergency supplies)
        const isLockoutActive = user?.survivalMoves?.lockoutUntil && 
          new Date() < new Date(user.survivalMoves.lockoutUntil);
        
        if (isLockoutActive) {
          // Computer is locked - exhausted all supplies and emergency supplies
          const lockoutTime = new Date(user.survivalMoves.lockoutUntil);
          const hoursRemaining = Math.ceil((lockoutTime - new Date()) / (1000 * 60 * 60));
          setComputerStats({
            movesRemaining: 0,
            maxMoves: 10,
            resetPeriod: `${hoursRemaining}h lockout remaining`,
            mode: 'locked',
            foodDrinkSupplies: 0 // No supplies when locked
          });
        } else {
          // Check current supplies
          const currentSupplies = user?.foodDrink?.currentSupplies || 0;
          
          if (currentSupplies > 0) {
            // Has regular supplies: each supply = 1 move
            setComputerStats({
              movesRemaining: currentSupplies,
              maxMoves: 10,
              resetPeriod: 'Food/Drink supplies',
              mode: 'supplies',
              foodDrinkSupplies: currentSupplies
            });
          } else {
            // No regular supplies - check emergency supplies
            const emergencyMovesUsed = user?.survivalMoves?.emergencyMoves || 0;
            const remainingEmergency = Math.max(0, 3 - emergencyMovesUsed);
            
            setComputerStats({
              movesRemaining: remainingEmergency,
              maxMoves: 3,
              resetPeriod: '3 emergency supplies',
              mode: 'emergency',
              foodDrinkSupplies: remainingEmergency
            });
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user computer:', error);
    }
  };

  // Get time until next reset for display
  const getTimeUntilReset = (mode, lastResetTime) => {
    if (!lastResetTime) return 'Unknown';
    
    const now = new Date();
    const lastReset = new Date(lastResetTime);
    const resetPeriodHours = mode === 'emergency' ? 6 : 6;
    const hoursUntilReset = resetPeriodHours - ((now - lastReset) / (1000 * 60 * 60));
    
    if (hoursUntilReset <= 0) return 'Ready to reset';
    
    const hours = Math.floor(hoursUntilReset);
    const minutes = Math.floor((hoursUntilReset % 1) * 60);
    
    return `${hours}h ${minutes}m`;
  };

  // Fetch all houses for map display
  const fetchHouses = async () => {
    try {
      const response = await api.get('/house/all');
      
      if (response.status === 200) {
        const data = response.data;
        setHouses(data.houses || []);
      }
    } catch (error) {
      console.error('Error fetching houses:', error);
    }
  };

  // Fetch user's unplaced houses
  const fetchUnplacedHouses = async () => {
    try {
      const response = await api.get('/house/my-houses');
      
      if (response.status === 200) {
        const data = response.data;
        // Filter for unplaced houses (location 0,0)
        const unplaced = (data.houses || []).filter(house => 
          house.location.lat === 0 && house.location.lng === 0
        );
        setUnplacedHouses(unplaced);
      }
    } catch (error) {
      console.error('Error fetching unplaced houses:', error);
    }
  };

  // Fetch user's active house rentals
  const fetchActiveRentals = async () => {
    try {
      const response = await api.get('/house/my-rentals');
      
      if (response.status === 200) {
        const data = response.data;
        setActiveRentals(data.activeRentals || []);
      }
    } catch (error) {
      console.error('Error fetching active rentals:', error);
    }
  };

  // Place house at clicked location
  const placeHouse = async (lat, lng) => {
    try {
      const response = await api.post('/house/place', { lat, lng });
      
      const data = response.data;
      
      if (data.success) {
        // Show construction popup
        setHouseConstructionTimer(5);
        setShowHouseConstruction(true);
        
        // Start countdown timer
        const countdownInterval = setInterval(() => {
          setHouseConstructionTimer(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              setShowHouseConstruction(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Refresh house data
        await Promise.all([
          fetchHouses(),
          fetchUnplacedHouses()
        ]);
        
        // Exit placement mode
        setIsPlacingHouse(false);
      } else {
        // Check if it's a distance-related error
        if (data.message && (
          data.message.includes('too close') || 
          data.message.includes('distance') || 
          data.message.includes('km') || 
          data.message.includes('meters')
        )) {
          // Show distance warning popup
          setHouseDistanceMessage(data.message);
          setShowHouseDistanceWarning(true);
        } else {
          // Show regular error notification for other errors
          addNotification({
            title: '❌ House Placement Failed',
            message: data.message,
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error placing house:', error);
      addNotification({
        title: '❌ House Placement Error',
        message: 'Failed to place house. Please try again.',
        type: 'error'
      });
    }
  };

  // Handle clicking on an unplaced task to place it
  const handlePlaceUnplacedTask = (task) => {
    setTaskToPlace(task);
    setIsPlacingTask(true);
    addNotification({
      title: '📍 Task Placement Mode',
      message: `Click anywhere on the map to place "${task.title}"`,
      type: 'info'
    });
  };

  // Place community task at clicked location
  const placeTask = async (lat, lng) => {
    if (!taskToPlace) {
      addNotification({
        title: 'Error',
        message: 'No task selected for placement'
      });
      setIsPlacingTask(false);
      return;
    }
    
    try {
      // First validate the placement location
      const validationResponse = await api.get(`/community-tasks/validate-placement?lat=${lat}&lng=${lng}`);
      
      const validationData = validationResponse.data;
      
      if (!validationData.success || !validationData.validation.valid) {
        addNotification({
          title: 'Invalid Location',
          message: validationData.validation?.reason || 'Cannot place task at this location'
        });
        return;
      }
      
      // Place the task
      const response = await api.post('/community-tasks/place-on-map', {
          taskId: taskToPlace._id,
          lat,
          lng
        });
      
      const data = response.data;
      
      if (data.success) {
        addNotification({
          title: 'Task Placed!',
          message: `"${taskToPlace.title}" has been placed on the map. Users with active sniffers can now find it!`
        });
        
        // Exit placement mode
        setIsPlacingTask(false);
        setTaskToPlace(null);
        
        // Refresh community tasks to show the newly placed task
        await fetchCommunityTasks();
        // Refresh unplaced tasks list
        await fetchUnplacedTasks();
        
      } else {
        addNotification({
          title: 'Placement Failed',
          message: data.message || 'Failed to place task on map'
        });
      }
    } catch (error) {
      console.error('Error placing task:', error);
      addNotification({
        title: 'Connection Error',
        message: 'Failed to place task due to network error'
      });
    }
  };

  // Place vault at clicked location
  const placeVault = async (lat, lng) => {
    try {
      console.log('🆕 Creating new vault at coordinates:', lat, lng);
      console.log('🆕 User already has vaults:', userVaults.length);
      
      if (userVaults.length > 0) {
        console.warn('⚠️ WARNING: User already has vault(s), but creating new one!', userVaults);
      }
      
      const response = await api.post('/atm-vaults/create', { lat, lng });
      
      const data = response.data;
      console.log('🆕 Vault creation response:', data);
      
      if (data.success) {
        addNotification({
          title: '✅ Vault Created Successfully',
          message: `Your ATM vault has been placed at coordinates ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
          type: 'success'
        });
        
        // Exit placement mode
        setIsPlacingVault(false);
        
        // Refresh vault data
        await fetchUserVaults();
        
      } else {
        addNotification({
          title: '❌ Vault Placement Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error placing vault:', error);
      addNotification({
        title: '❌ Vault Placement Error',
        message: 'Failed to place vault. Please try again.',
        type: 'error'
      });
    }
  };

  // Move vault to clicked location
  const moveVault = async (lat, lng) => {
    try {
      console.log('🔄 Moving vault:', vaultToMove?.vaultId, 'to coordinates:', lat, lng);
      
      if (!vaultToMove || !vaultToMove.vaultId) {
        addNotification({
          title: '❌ Move Error',
          message: 'No vault selected for movement',
          type: 'error'
        });
        setIsMovingVault(false);
        return;
      }
      
      const response = await api.put(`/atm-vaults/${vaultToMove.vaultId}/move`, {
        lat: lat,
        lng: lng
      });

      const data = response.data;
      console.log('🔄 Vault move response:', data);
      
      if (response.status === 200 && data.success) {
        addNotification({
          title: '✅ Vault Moved Successfully',
          message: `Your vault has been moved to coordinates ${lat.toFixed(4)}, ${lng.toFixed(4)}. Cost: ${data.cost || 100} WR Coins`,
          type: 'success'
        });

        setIsMovingVault(false);
        setVaultToMove(null);
        
        // Update user balance if provided
        if (data.userBalance !== undefined && updateUser) {
          updateUser({ wrCoins: data.userBalance });
        }

        // Refresh vault data
        await fetchUserVaults();
        
      } else {
        addNotification({
          title: '❌ Vault Movement Failed',
          message: data.message,
          type: 'error'
        });
        setIsMovingVault(false);
        setVaultToMove(null);
      }
    } catch (error) {
      console.error('Error moving vault:', error);
      addNotification({
        title: '❌ Vault Movement Error',
        message: 'Failed to move vault. Please try again.',
        type: 'error'
      });
      setIsMovingVault(false);
      setVaultToMove(null);
    }
  };

  // Check if vault is within withdrawal range (1000m)
  const isVaultInRange = (vault) => {
    if (!userComputer || !userComputer.coordinates || userComputer.coordinates.lat === null || userComputer.coordinates.lng === null) return false;
    if (!vault.coordinates || vault.coordinates.lat === null || vault.coordinates.lng === null) return false;
    
    const computerLocation = { lat: userComputer.coordinates.lat, lng: userComputer.coordinates.lng };
    const vaultLocation = vault.coordinates;
    
    // Calculate distance using Haversine formula
    const R = 6371000; // Earth's radius in meters
    const dLat = (vaultLocation.lat - computerLocation.lat) * Math.PI / 180;
    const dLng = (vaultLocation.lng - computerLocation.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(computerLocation.lat * Math.PI / 180) * Math.cos(vaultLocation.lat * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    return distance <= 1000; // 1000m withdrawal radius
  };

  // Handle withdraw button click
  const handleWithdrawClick = (vault) => {
    setWithdrawVault(vault);
    setWithdrawAmount('');
    setShowWithdrawModal(true);
  };

  // Handle withdraw form submission
  const handleWithdrawSubmit = async () => {
    if (!withdrawVault || !withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      addNotification({
        title: '❌ Invalid Amount',
        message: 'Please enter a valid withdrawal amount',
        type: 'error'
      });
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (amount > withdrawVault.storedBalance) {
      addNotification({
        title: '❌ Insufficient Balance',
        message: 'Vault does not have enough balance',
        type: 'error'
      });
      return;
    }

    await withdrawFromVault(withdrawVault.vaultId, amount);
    setShowWithdrawModal(false);
    setWithdrawVault(null);
    setWithdrawAmount('');
  };

  // Withdraw from vault
  const withdrawFromVault = async (vaultId, amount) => {
    try {
      const response = await api.post(`/atm-vaults/${vaultId}/withdraw`, { amount });

      const data = response.data;
      
      if (response.status === 200 && data.success) {
        addNotification({
          title: '💰 Withdrawal Successful',
          message: `Successfully withdrew ${amount} WR Coins from vault!`,
          type: 'success'
        });
        
        // Update user balance if provided
        if (data.userBalance !== undefined) {
          updateUser({ wrCoins: data.userBalance });
        }
        
        // Refresh vault data
        await fetchUserVaults();
      } else {
        addNotification({
          title: '❌ Withdrawal Failed',
          message: data.message || 'Failed to withdraw from vault',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error withdrawing from vault:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to withdraw due to network error',
        type: 'error'
      });
    }
  };

  // Place business at clicked location
  const placeBusiness = async (lat, lng) => {
    if (!selectedBusinessForPlacement) {
      addNotification({
        title: '❌ No Business Selected',
        message: 'Please select a business to place first.',
        type: 'error'
      });
      return;
    }

    if (selectedBusinessForPlacement.location && selectedBusinessForPlacement.location.lat && selectedBusinessForPlacement.location.lng && selectedBusinessForPlacement.location.lat !== 0 && selectedBusinessForPlacement.location.lng !== 0) {
      addNotification({
        title: '❌ Business Already Placed',
        message: 'This business is already placed on the map.',
        type: 'error'
      });
      return;
    }

    try {
      
      const requestBody = { 
        lat, 
        lng,
        businessId: selectedBusinessForPlacement._id 
      };
      
      
      const response = await api.post('/business/place', requestBody);
      
      const data = response.data;
      
      
      if (data.success) {
        addNotification({
          title: '🏗️ Business Placed!',
          message: `${selectedBusinessForPlacement.businessName} has been placed on the map successfully!`,
          type: 'success'
        });
        
        // Refresh business data
        await Promise.all([
          fetchBusinesses(),
          fetchUserBusiness()
        ]);
        
        // Exit placement mode and clear selection
        setIsPlacingBusiness(false);
        setSelectedBusinessForPlacement(null);
      } else {
        addNotification({
          title: '❌ Business Placement Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error placing business:', error);
      addNotification({
        title: '❌ Business Placement Error',
        message: 'Failed to place business. Please try again.',
        type: 'error'
      });
    }
  };

  // Place defense tower at clicked location
  const placeTower = async (lat, lng) => {
    // Prevent duplicate calls
    if (towerDeploymentLoading) {
      console.log('Tower deployment already in progress, ignoring click');
      return;
    }
    
    try {
      setTowerDeploymentLoading(true);
      
      // Calculate distance from command center for debugging
      if (userCompound && userCompound.commandCenter && userCompound.commandCenter.position?.lat && userCompound.commandCenter.position?.lng) {
        const centerLat = userCompound.commandCenter.position.lat;
        const centerLng = userCompound.commandCenter.position.lng;
        const R = 6371000; // Earth's radius in meters
        const dLat = (lat - centerLat) * Math.PI / 180;
        const dLng = (lng - centerLng) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                  Math.cos(centerLat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) *
                  Math.sin(dLng/2) * Math.sin(dLng/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;
      }

      // Check distance from other players' businesses and ATM vaults (prevent tower placement near them)
      const minDistanceFromOthers = 500; // 500m minimum distance from other players' assets
      
      // Check distance from other players' businesses
      if (businesses && businesses.length > 0) {
        for (const business of businesses) {
          if (business.ownerUsername !== user?.username && business.location && business.location.lat && business.location.lng) {
            const businessDistance = calculateDistance(lat, lng, business.location.lat, business.location.lng);
            if (businessDistance < minDistanceFromOthers) {
              addNotification({
                title: '❌ Too Close to Business',
                message: `Defense towers cannot be placed within ${minDistanceFromOthers}m of other players' businesses. Distance: ${Math.round(businessDistance)}m`,
                type: 'error'
              });
              return;
            }
          }
        }
      }
      
      // Check distance from other players' ATM vaults
      if (nearbyVaults && nearbyVaults.length > 0) {
        for (const vault of nearbyVaults) {
          if (vault.ownerUsername !== user?.username && vault.coordinates && vault.coordinates.lat && vault.coordinates.lng) {
            const vaultDistance = calculateDistance(lat, lng, vault.coordinates.lat, vault.coordinates.lng);
            if (vaultDistance < minDistanceFromOthers) {
              addNotification({
                title: '❌ Too Close to ATM Vault',
                message: `Defense towers cannot be placed within ${minDistanceFromOthers}m of other players' ATM vaults. Distance: ${Math.round(vaultDistance)}m`,
                type: 'error'
              });
              return;
            }
          }
        }
      }
      
      const response = await api.post('/compound-defense/deploy-tower', {
        position: { lat, lng },
        towerId: `tower_${Date.now()}`
      });
      
      const data = response.data;
      
      console.log('Tower deployment response:', { status: response.status, data });
      
      if (data.success || response.status === 200 || response.status === 201) {
        addNotification({
          title: '✅ Defense Tower Deployed',
          message: `Tower deployed successfully! (${data.totalTowers || 'New'}/8 towers)`,
          type: 'success'
        });
        
        // Exit tower placement mode
        setIsPlacingTower(false);
        
        // Add the new tower to the state immediately for instant visibility
        if (data.tower) {
          setDefenseTowers(prevTowers => [...prevTowers, {
            ...data.tower,
            position: { lat, lng },
            _id: data.tower._id || `tower_${Date.now()}`,
            health: data.tower.health || 100,
            ownerUsername: user.username
          }]);
        }
        
        // Refresh map data to show new tower
        await loadTreasureHuntData();
        await loadCompoundData();
        
        // Update user balance
        if (data.newBalance) {
          updateUser({ wrCoins: data.newBalance });
        }
      } else {
        addNotification({
          title: '❌ Tower Deployment Failed',
          message: data.message || 'Failed to deploy defense tower. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error placing tower:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to place defense tower. Please try again.';
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid tower placement request';
      } else if (error.response?.status === 409) {
        errorMessage = 'Position already occupied or tower limit reached';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      addNotification({
        title: '❌ Tower Placement Error',
        message: errorMessage,
        type: 'error'
      });
    } finally {
      // Always reset loading state
      setTowerDeploymentLoading(false);
    }
  };

  // Move command center to new location
  const moveCommandCenter = async (lat, lng) => {
    try {
      // Check if user has destroyed defense towers that need to be deleted
      const destroyedTowers = defenseTowers.filter(tower => tower.health <= 0 || tower.destroyedAt);
      if (destroyedTowers.length > 0) {
        addNotification({
          title: '❌ Delete Destroyed Towers First',
          message: `You must delete ${destroyedTowers.length} destroyed defense tower(s) before moving your command center. Go to System → Compound Defense → Defenses tab.`,
          type: 'error'
        });
        setIsMovingCommandCenter(false);
        return;
      }

      const response = await api.post('/compound-defense/move-command-center', {
          position: { lat, lng }
        });
      
      const data = response.data;
      
      if (data.success || response.status === 200) {
        addNotification({
          title: '✅ Command Center Moved',
          message: `Command center relocated successfully! Moves remaining: ${data.movesRemaining}/3`,
          type: 'success'
        });
        
        // Exit movement mode
        setIsMovingCommandCenter(false);
        
        // Refresh map data to show new position
        await loadTreasureHuntData();
        await loadCompoundData();
      } else {
        addNotification({
          title: '❌ Movement Failed',
          message: data.message || 'Failed to move command center. Please try again.',
          type: 'error'
        });
        setIsMovingCommandCenter(false);
      }
    } catch (error) {
      console.error('Error moving command center:', error);
      addNotification({
        title: '❌ Movement Error',
        message: 'Failed to move command center. Please try again.',
        type: 'error'
      });
      setIsMovingCommandCenter(false);
    }
  };

  // Deploy drone at clicked location
  const deployDrone = async (lat, lng, clickEvent = null) => {
    try {
      // Deploy selected drone at coordinates
      
      // Check if user has drones in inventory
      if (!user?.droneInventory || user.droneInventory.length === 0) {
        addNotification({
          title: '❌ No Drones Available',
          message: 'You need to purchase drones from the Computer Parts store first.',
          type: 'error'
        });
        setIsDeployingDrone(false);
        setSelectedDroneForDeployment(null);
        return;
      }

      // Check if a drone is selected
      if (!selectedDroneForDeployment) {
        addNotification({
          title: '❌ No Drone Selected',
          message: 'Please select a drone to deploy first.',
          type: 'error'
        });
        setIsDeployingDrone(false);
        return;
      }

      // Check if this is a maritime scout drone and validate water placement
      const isMaritimeScout = selectedDroneForDeployment.name?.toLowerCase().includes('maritime') || 
                              selectedDroneForDeployment.droneType === 'maritime_scout';
      
      if (isMaritimeScout && clickEvent) {
        // Maritime scout drones can only be deployed on water
        const isWater = await isWaterArea(clickEvent);
        
        if (!isWater) {
          addNotification({
            title: '🌊 Water Required',
            message: 'Maritime Scout Drones can only be deployed in water. Please select a location in sea/ocean areas.',
            type: 'error'
          });
          return; // Don't exit deployment mode, allow user to try again
        }
      }

      // Find the drone index in inventory
      const droneIndex = user.droneInventory.indexOf(selectedDroneForDeployment);
      
      // Check if drone is still in inventory (prevent duplicate deployment)
      if (droneIndex === -1) {
        addNotification({
          title: '❌ Drone Already Deployed',
          message: 'This drone has already been deployed. Please refresh the page to see updated inventory.',
          type: 'error'
        });
        setIsDeployingDrone(false);
        setSelectedDroneForDeployment(null);
        setShowDronePanel(false);
        return;
      }

      const response = await api.post('/drone/deploy', {
          position: { lat, lng },
          droneId: droneIndex,
          droneType: selectedDroneForDeployment.droneType || selectedDroneForDeployment.name
        });

      const data = response.data;

      // Drone deployment successful

      if (data.success || response.status === 200) {
        addNotification({
          title: '✅ Drone Deployed',
          message: `${selectedDroneForDeployment.name} deployed successfully! Click on the map to activate it.`,
          type: 'success'
        });

        // Exit drone deployment mode
        setIsDeployingDrone(false);
        setSelectedDroneForDeployment(null);
        setShowDronePanel(false);
        
        // Update local user state to remove deployed drone from inventory
        const updatedInventory = [...user.droneInventory];
        updatedInventory.splice(droneIndex, 1);
        updateUser({
          ...user,
          droneInventory: updatedInventory
        });

        // IMMEDIATE UPDATE: Use actual drone data from server response
        if (data.drone) {
          // Add the deployed drone immediately to map using real server data
          setDeployedDrones(prevDrones => [...prevDrones, data.drone]);
          console.log('✅ Drone immediately added to map:', data.drone.droneId);
        }
      } else {
        addNotification({
          title: '❌ Deployment Failed',
          message: data.message || 'Failed to deploy drone. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deploying drone:', error);
      
      // Handle specific error cases
      let errorMessage = 'An error occurred while deploying the drone.';
      
      if (error.response?.status === 400) {
        // Bad request - likely drone already deployed or invalid
        errorMessage = error.response.data?.message || 'Invalid deployment request. The drone may already be deployed.';
      } else if (error.response?.status === 503) {
        errorMessage = 'Server is temporarily unavailable. Please try again in a moment.';
      }
      
      addNotification({
        title: '❌ Deployment Error',
        message: errorMessage,
        type: 'error'
      });
      setIsDeployingDrone(false);
      setSelectedDroneForDeployment(null);
      setShowDronePanel(false);
    }
  };

  // Deploy satellite at clicked location
  const deploySatellite = async (lat, lng, clickEvent = null) => {
    try {
      // Check if user has satellites in inventory (stored in satelliteInventory after purchase)
      const unplacedSatellites = user?.satelliteInventory?.filter(satellite => 
        satellite.readyForDeployment && !satellite.deployedSatelliteId
      ) || [];
      
      if (unplacedSatellites.length === 0) {
        addNotification({
          title: '❌ No Satellites Available',
          message: 'You need to purchase Ocean Surveillance Satellites from the Defense Systems store first.',
          type: 'error'
        });
        setIsDeployingSatellite(false);
        setSelectedSatelliteForDeployment(null);
        return;
      }

      // Use the first available satellite for deployment (satellites are identical)
      const satelliteToUse = unplacedSatellites[0];

      // Satellites can only be deployed in water
      if (clickEvent) {
        const isWater = await isWaterArea(clickEvent);
        
        if (!isWater) {
          addNotification({
            title: '🌊 Ocean Required',
            message: 'Ocean Surveillance Satellites can only be deployed in ocean areas. Please select a location in sea/ocean.',
            type: 'error'
          });
          return; // Don't exit deployment mode, allow user to try again
        }
      }

      const response = await api.post('/satellite/deploy', {
          lat: lat,
          lng: lng,
          partId: satelliteToUse.partId || satelliteToUse._id
        });

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          title: '🛰️ Satellite Deployed',
          message: 'Ocean Surveillance Satellite deployed successfully! It will begin scanning for naval assets within 1500km radius.',
          type: 'success'
        });

        setIsDeployingSatellite(false);
        setSelectedSatelliteForDeployment(null);
        setShowSatellitePanel(false);
        
        // Refresh satellite and map data to show deployed satellite
        await fetchSatellites();
        await fetchUnplacedSatellites();
        await loadTreasureHuntData();
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Deployment Failed',
          message: errorData.message || 'Failed to deploy satellite. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deploying satellite:', error);
      addNotification({
        title: '❌ Deployment Error',
        message: 'An error occurred while deploying the satellite. Please try again.',
        type: 'error'
      });
      setIsDeployingSatellite(false);
      setSelectedSatelliteForDeployment(null);
      setShowSatellitePanel(false);
    }
  };

  // Handle satellite deletion
  const handleDeleteSatellite = async (satelliteId) => {
    try {
      const { deleteSatellite } = await import('../services/api');
      await deleteSatellite(satelliteId);
      
      addNotification({
        title: '🛰️ Satellite Removed',
        message: 'Satellite has been successfully removed',
        type: 'success'
      });
      
      // Refresh satellite data
      await fetchSatellites();
      await fetchUnplacedSatellites();
    } catch (error) {
      addNotification({
        title: '❌ Delete Failed',
        message: error.response?.data?.message || 'Failed to remove satellite',
        type: 'error'
      });
    }
  };

  // Handle satellite movement
  const handleSatelliteMove = async (lat, lng) => {
    if (!satelliteMoving) return;
    
    try {
      const { moveSatellite } = await import('../services/api');
      await moveSatellite(satelliteMoving, lat, lng);
      
      addNotification({
        title: '🛰️ Satellite Moved',
        message: 'Satellite has been successfully moved to new location',
        type: 'success'
      });
      
      setSatelliteMoving(null);
      // Refresh satellite data
      await fetchSatellites();
    } catch (error) {
      addNotification({
        title: '❌ Move Failed',
        message: error.response?.data?.message || 'Failed to move satellite',
        type: 'error'
      });
      setSatelliteMoving(null);
    }
  };

  // Fetch deployed drones
  const fetchDeployedDrones = async () => {
    try {
      const response = await api.get('/drone/user-drones');

      if (response.status === 200) {
        const data = response.data;
        const drones = data.drones || [];
        setDeployedDrones(drones);
        
        // Initialize laser beams for drones already in battle
        initializeBattleLaserBeams(drones);
      } else {
        console.error('Failed to fetch deployed drones');
        setDeployedDrones([]);
      }
    } catch (error) {
      console.error('Error fetching deployed drones:', error);
      setDeployedDrones([]);
    }
  };

  // Fetch satellites
  const fetchSatellites = async () => {
    try {
      // Get satellites in radius around user's current location
      const userLat = userComputer?.position?.lat || user?.gameLocation?.lat || 0;
      const userLng = userComputer?.position?.lng || user?.gameLocation?.lng || 0;
      
      const response = await api.get(`/satellite/in-radius?lat=${userLat}&lng=${userLng}&radius=50000000`);

      if (response.status === 200) {
        const data = response.data;
        const radiusSatellites = (data && data.satellites) ? data.satellites : [];
        
        // Also get user's own satellites to ensure they're included and properly marked
        const userSatResponse = await api.get('/satellite/user');
        
        if (userSatResponse.status === 200) {
          const userSatData = userSatResponse.data;
          const userSatellites = (userSatData && userSatData.satellites) ? userSatData.satellites : [];
          setUserSatellites(userSatellites);
          
          // Merge satellites, ensuring user satellites are included and marked properly
          const allSatellites = [...radiusSatellites];
          
          // Add user satellites that might not be in the radius
          userSatellites.forEach(userSat => {
            const existingIndex = allSatellites.findIndex(sat => 
              sat.id === userSat.id || sat.satelliteId === userSat.id
            );
            
            if (existingIndex >= 0) {
              // Update existing with user satellite data
              allSatellites[existingIndex] = {
                ...allSatellites[existingIndex],
                ...userSat,
                isOwnSatellite: true,
                username: user.username
              };
            } else {
              // Add user satellite not in radius
              allSatellites.push({
                ...userSat,
                isOwnSatellite: true,
                username: user.username
              });
            }
          });
          
          setSatellites(allSatellites);
        } else {
          setSatellites(radiusSatellites);
        }
      } else {
        console.error('Failed to fetch satellites');
        setSatellites([]);
        setUserSatellites([]);
      }
    } catch (error) {
      console.error('Error fetching satellites:', error);
      setSatellites([]);
      setUserSatellites([]);
    }
  };

  // Fetch unplaced satellites (satellites in user inventory)
  const fetchUnplacedSatellites = async () => {
    try {
      // Check if user has any unplaced satellites in their defense systems inventory
      const hasUnplacedSatellites = user?.partsInventory?.some(part => 
        part.name === 'Ocean Surveillance Satellite' && part.category === 'defense_systems'
      );
      
      if (hasUnplacedSatellites) {
        setShowSatellitePanel(true);
      } else {
        setShowSatellitePanel(false);
      }
    } catch (error) {
      console.error('Error checking unplaced satellites:', error);
      setShowSatellitePanel(false);
    }
  };

  // Load active attack animations from database on page load
  const loadActiveAttackAnimations = async () => {
    try {
      const response = await api.get('/drone/animation/active');

      if (response.status === 200) {
        const data = response.data;
        const animations = data.animations || [];
        // Animations loaded successfully
        
        // Filter animations by sniffer radius if any sniffer is active (computer or drones)
        let filteredAnimations = animations;
        const hasActiveSniffers = snifferActive || droneSniffersActive.size > 0;
        
        if (hasActiveSniffers && (userComputer || deployedDrones.length > 0)) {
          // Collect all sniffer positions with their specific radii
          const snifferPositions = [];
          
          // Add computer position if computer sniffer is active
          if (snifferActive && userComputer) {
            const computerRadius = calculateSnifferRadius();
            snifferPositions.push({
              lat: userComputer.coordinates.lat,
              lng: userComputer.coordinates.lng,
              radius: computerRadius,
              type: 'computer'
            });
          }
          
          // Add positions of drones with active sniffers (3000m radius)
          deployedDrones.forEach(drone => {
            if (drone.position && droneSniffersActive.has(drone.droneId)) {
              snifferPositions.push({
                lat: drone.position.lat,
                lng: drone.position.lng,
                radius: (drone.type === 'maritime_scout') ? 50000 : 3000, // 50km for maritime scout, 3km for others
                type: 'drone'
              });
            }
          });
          
          filteredAnimations = animations.filter(animation => {
            // Check if animation is within radius of any sniffer position
            const isVisible = snifferPositions.some(snifferPos => {
              const droneDist = calculateDistance(snifferPos.lat, snifferPos.lng, animation.dronePosition.lat, animation.dronePosition.lng);
              const targetDist = calculateDistance(snifferPos.lat, snifferPos.lng, animation.targetPosition.lat, animation.targetPosition.lng);
              
              // Show animation if either drone or target is within radius of this sniffer
              return droneDist <= snifferPos.radius || targetDist <= snifferPos.radius;
            });
            
            return isVisible;
          });
        } else if (!hasActiveSniffers) {
          // If no sniffers are active, don't show any animations
          filteredAnimations = [];
        }
        
        const restoredBeams = filteredAnimations.map(animation => ({
          id: animation.id,
          dronePosition: [animation.dronePosition.lat, animation.dronePosition.lng],
          towerPosition: [animation.targetPosition.lat, animation.targetPosition.lng],
          droneId: animation.droneId,
          towerId: animation.targetId,
          targetType: animation.targetType,
          droneType: animation.droneType,
          startTime: new Date(animation.startTime).getTime(),
          endTime: new Date(animation.endTime).getTime(),
          isActive: true
        }));

        // Clear existing beams and add filtered ones
        setActiveLaserBeams(restoredBeams);
        
        // Start burst firing for each restored animation
        restoredBeams.forEach(beam => {
          startBurstFiring(beam);
          
          // Schedule cleanup when animation ends
          const remainingTime = beam.endTime - Date.now();
          if (remainingTime > 0) {
            setTimeout(async () => {
              // Remove from state
              setActiveLaserBeams(prev => prev.filter(b => b.id !== beam.id));
              
              // Remove from database with retry and circuit breaker pattern
              let cleanupSuccess = false;
              try {
                await retryWithBackoff(
                  () => api.delete(`/drone/animation/remove/${beam.id}`),
                  3, // Max 3 retries
                  2000 // Start with 2 second delay
                );
                console.log(`✅ Restored animation ${beam.id} completed and cleaned up`);
                cleanupSuccess = true;
              } catch (error) {
                console.error('❌ Error cleaning up restored animation after retries:', error);
                // Don't refresh map data if cleanup failed to prevent cascade
                return;
              }
              
              // Only refresh map data if cleanup was successful
              if (cleanupSuccess) {
                try {
                  await loadCompoundData();
                  await fetchDeployedDrones();
                } catch (refreshError) {
                  console.warn('Map refresh failed after animation cleanup:', refreshError);
                }
              }
            }, remainingTime);
          }
        });
        
        
      } else if (response.status !== 504) {
        // Only log non-timeout errors to reduce console noise
        console.warn('Failed to load active attack animations:', response.status);
      }
    } catch (error) {
      // Only log non-timeout errors to reduce console noise
      if (!error.message.includes('timeout') && !error.message.includes('504')) {
        console.warn('Error loading active attack animations:', error.message);
      }
    }
  };

  // Initialize laser beams for drones that are already in battle
  const initializeBattleLaserBeams = (drones) => {
    // Initializing battle laser beams
    
    const newLaserBeams = [];
    
    drones.forEach(drone => {
      if (drone.status?.inBattle && drone.battle && drone.battle.targetTowerId && drone.position?.lat && drone.position?.lng) {
        // Processing battle drone
        
        // Find the target tower from drone's discovery data
        const targetTower = drone.discoveryData?.find(d => 
          d.type === 'defense_tower' && d.towerId === drone.battle.targetTowerId
        );
        
        if (targetTower && targetTower.position?.lat && targetTower.position?.lng) {
          const beamId = `${drone.droneId}-${drone.battle.targetTowerId}-existing`;
          const battleEndTime = drone.battle.battleEndsAt ? new Date(drone.battle.battleEndsAt).getTime() : 0;
          const remainingTime = Math.max(0, battleEndTime - Date.now());
          
          // Battle timing calculated
          
          if (remainingTime > 1000) { // At least 1 second remaining
            const laserBeam = {
              id: beamId,
              dronePosition: [drone.position.lat, drone.position.lng],
              towerPosition: [targetTower.position.lat, targetTower.position.lng],
              droneId: drone.droneId,
              towerId: drone.battle.targetTowerId,
              droneType: drone.type || 'assault',
              startTime: Date.now() - (30 * 60 * 1000 - remainingTime),
              duration: remainingTime,
              isActive: true,
              intervalId: null // Initialize interval tracking
            };
            
            newLaserBeams.push(laserBeam);
            
            // Laser beam created
            
            // Schedule removal when battle ends
            setTimeout(() => {
              // Battle ended, stopping animations and removing consumed drone
              setActiveLaserBeams(prev => {
                const updatedBeams = prev.filter(beam => beam.id !== beamId);
                // Clear interval if it exists
                const endingBeam = prev.find(beam => beam.id === beamId);
                if (endingBeam?.intervalId) {
                  clearInterval(endingBeam.intervalId);
                }
                return updatedBeams;
              });
              loadCompoundData(); // Refresh to show destroyed tower
              fetchDeployedDrones(); // Refresh to remove consumed drone from map
            }, remainingTime + 1000); // Add buffer for cleanup
          } else {
            // Battle ending soon, skipping animation
          }
        } else {
          // Target tower not found in discovery data
        }
      }
    });
    
    if (newLaserBeams.length > 0) {
      // Adding laser beams to state
      setActiveLaserBeams(prev => {
        // Remove any existing beams with same IDs to prevent duplicates
        const filteredPrev = prev.filter(existingBeam => 
          !newLaserBeams.some(newBeam => newBeam.id === existingBeam.id)
        );
        return [...filteredPrev, ...newLaserBeams];
      });
      
      // Start burst firing with progressive delays to prevent overwhelming
      newLaserBeams.forEach((beam, index) => {
        setTimeout(() => {
          // Starting burst firing for resumed attack
          startBurstFiring(beam);
        }, 1500 + (index * 200)); // Stagger start times by 200ms each
      });
    } else {
      // No ongoing battles found
    }
  };

  // Move selected drone to new location
  const moveDrone = async (lat, lng, clickEvent = null) => {
    if (!selectedDrone) return;

    try {
      // Check if this is a maritime scout drone and validate water movement
      const isMaritimeScout = selectedDrone.type === 'maritime_scout' || 
                              selectedDrone.name?.toLowerCase().includes('maritime');
      
      if (isMaritimeScout && clickEvent) {
        // Maritime scout drones can only move on water
        const isWater = await isWaterArea(clickEvent);
        
        if (!isWater) {
          addNotification({
            title: '🌊 Water Required',
            message: 'Maritime Scout Drones can only move on water. Please select a location in sea/ocean areas.',
            type: 'error'
          });
          return; // Don't exit movement mode, allow user to try again
        }
      }

      // Moving drone to new position
      
      const response = await api.post('/drone/move', {
          droneId: selectedDrone.droneId || selectedDrone._id,
          newPosition: { lat, lng }
        });

      if (response.status === 200) {
        const result = response.data;
        
        // Auto-disable drone sniffer after movement
        const droneId = selectedDrone.droneId || selectedDrone._id;
        if (droneSniffersActive.has(droneId)) {
          setDroneSniffersActive(prev => {
            const newSet = new Set(prev);
            newSet.delete(droneId);
            return newSet;
          });
          addNotification({
            type: 'info',
            title: '📡 Drone Sniffer Disabled',
            message: 'Drone movement has disabled the sniffer. Click your drone to reactivate.',
            icon: '🔄'
          });
        }
        
        addNotification({
          title: '✈️ Drone Moved',
          message: `Drone successfully moved to new location.`,
          type: 'success'
        });
        
        // Refresh map data and reset movement state
        await loadTreasureHuntData();
        
        // Refresh sniffer data at new drone position
        await refreshSnifferRadiusFeatures(lat, lng);
        
        // Refresh drone sniffer detection if any sniffers are active
        await fetchDeployedDrones(); // This will update drone discovery data
        
        setIsDroneMoving(false);
        setSelectedDrone(null);
        
        console.log('Drone moved and sniffer data refreshed at new position');
      } else {
        const error = response.data;
        addNotification({
          title: '❌ Move Failed',
          message: error.message || 'Failed to move drone.',
          type: 'error'
        });
      }
    } catch (err) {
      console.error('Error moving drone:', err);
      addNotification({
        title: '❌ Move Failed',
        message: 'Network error while moving drone.',
        type: 'error'
      });
    }
  };

  // Attack defense tower with selected drone
  const attackTower = async (tower) => {
    if (!selectedDrone) {
      addNotification({
        title: '❌ No Drone Selected',
        message: 'Please select a drone first before attacking.',
        type: 'error'
      });
      return;
    }

    try {
      // Calculate distance between drone and tower
      const dronePos = selectedDrone.position;
      const towerPos = tower.position;
      const distance = Math.sqrt(
        Math.pow(dronePos.lat - towerPos.lat, 2) + 
        Math.pow(dronePos.lng - towerPos.lng, 2)
      ) * 111000; // Convert to meters

      if (distance > 500) {
        addNotification({
          title: '❌ Out of Range',
          message: `Tower is ${Math.round(distance)}m away. Drone must be within 500m to attack.`,
          type: 'error'
        });
        return;
      }

      console.log('Attacking tower:', tower.towerId);
      
      const response = await api.post('/drone/attack', {
          droneId: selectedDrone.droneId || selectedDrone._id,
          towerId: tower.towerId || tower._id
        });

      if (response.status === 200) {
        const result = response.data;
        addNotification({
          title: '⚔️ Attack Initiated',
          message: `Drone is attacking the defense tower. Battle will last 1 hour.`,
          type: 'success'
        });
        
        // Refresh map data and reset selection
        await loadTreasureHuntData();
        setSelectedDrone(null);
      } else {
        const error = response.data;
        addNotification({
          title: '❌ Attack Failed',
          message: error.message || 'Failed to attack tower.',
          type: 'error'
        });
      }
    } catch (err) {
      console.error('Error attacking tower:', err);
      addNotification({
        title: '❌ Attack Failed',
        message: 'Network error while attacking tower.',
        type: 'error'
      });
    }
  };

  // Start attack mode - enable tower selection
  const startAttackMode = (drone) => {
    // Starting attack mode
    
    if (!drone || drone.status.inBattle) {
      // Drone unavailable for attack
      addNotification({
        title: '❌ Cannot Attack',
        message: 'This drone is already in battle or unavailable.',
        type: 'error'
      });
      return;
    }

    if (drone.type === 'scout') {
      console.log('❌ Scout drone cannot attack');
      addNotification({
        title: '❌ Cannot Attack',
        message: 'Scout drones cannot attack. Use assault or nuke drones for attacks.',
        type: 'error'
      });
      return;
    }

    // Setting up attack mode for drone
    setSelectedAttackDrone(drone);
    setAttackMode(true);
    
    // Close the drone popup so user can select targets
    setSelectedDrone(null);
    
    // Find nearby attackable targets from drone's discovery data
    let attackableTargets = [];
    if (drone.discoveryData) {
      if (drone.type === 'nuke') {
        // Nuke drones can attack command centers
        attackableTargets = drone.discoveryData.filter(d => d.type === 'command_center');
        // Found command centers for nuke attack
      } else if (drone.type === 'assault') {
        // Assault drones can attack both defense towers and command centers
        attackableTargets = drone.discoveryData.filter(d => 
          d.type === 'defense_tower' || d.type === 'command_center'
        );
        // Found attackable targets for assault drone
      } else {
        // Scout drones cannot attack
        attackableTargets = [];
      }
    }
    
    setAttackableTargets(attackableTargets);
    
    const targetType = drone.type === 'nuke' ? 'command center' : 'defense tower';
    addNotification({
      title: '⚔️ Attack Mode Active',
      message: `${drone.name} ready to attack. Click on a ${targetType} to target it.`,
      type: 'success'
    });
  };

  // Cancel attack mode
  const cancelAttackMode = () => {
    setAttackMode(false);
    setSelectedAttackDrone(null);
    setAttackableTargets([]);
    
    addNotification({
      title: '🔄 Attack Mode Cancelled',
      message: 'Tower selection cancelled.',
      type: 'info'
    });
  };

  // Attack selected target with attack drone (tower or command center)
  const attackTowerWithDrone = async (target) => {
    // Starting tower attack
    
    if (!selectedAttackDrone || !attackMode) {
      // No attack drone selected
      addNotification({
        title: '❌ No Attack Drone Selected',
        message: 'Please select an attack drone first.',
        type: 'error'
      });
      return;
    }

    try {
      // Calculate distance between drone and target
      const dronePos = selectedAttackDrone.position;
      const targetPos = target.position;
      const distance = Math.sqrt(
        Math.pow(dronePos.lat - targetPos.lat, 2) + 
        Math.pow(dronePos.lng - targetPos.lng, 2)
      ) * 111000; // Rough conversion to meters

      // Check if target is within range
      const maxRange = selectedAttackDrone.type === 'nuke' ? 5000 : 500; // Nuke drones have 5km range for command centers
      const targetType = target.type === 'command_center' ? 'command center' : 'tower';
      
      if (distance > maxRange) {
        addNotification({
          title: '❌ Target Out of Range',
          message: `${targetType} is ${Math.round(distance)}m away. ${selectedAttackDrone.type} drone range is ${maxRange}m.`,
          type: 'error'
        });
        return;
      }

      // Use different API endpoints based on target type
      let apiEndpoint, requestBody;
      
      if (target.type === 'command_center') {
        console.log('🚀 Making API call to attack command center...');
        apiEndpoint = '/drone/attack-command-center';
        requestBody = {
          droneId: selectedAttackDrone.droneId || selectedAttackDrone._id,
          targetOwnerId: target.ownerId
        };
      } else {
        // Making API call to start attack
        apiEndpoint = '/drone/attack-tower';
        requestBody = {
          droneId: selectedAttackDrone.droneId || selectedAttackDrone._id,
          towerId: target.towerId,
          towerPosition: target.position,
          ownerUsername: target.ownerUsername
        };
      }

      const response = await api.post(apiEndpoint, requestBody);

      // API response received

      if (response.status === 200) {
        const data = response.data;
        // Attack started successfully
        
        // Start laser beam animation BEFORE canceling attack mode (so selectedAttackDrone is still available)
        startLaserBeamAnimation(selectedAttackDrone, target);
        
        // Cancel attack mode after animation is started
        cancelAttackMode();
        
        // Refresh drone data to show attack progress
        await fetchDeployedDrones();
        console.log('🔄 Drone data refreshed');
        
        const attackDuration = target.type === 'command_center' ? '20 minutes' : '30 minutes';
        const targetName = target.type === 'command_center' ? 'command center' : 'defense tower';
        
        addNotification({
          title: '⚔️ Attack Started',
          message: `${selectedAttackDrone.name} is attacking ${target.ownerUsername}'s ${targetName}. Attack will complete in ${attackDuration}.`,
          type: 'success'
        });
      } else {
        const error = response.data;
        console.log('❌ Attack API failed:', response.status, error);
        addNotification({
          title: '❌ Attack Failed',
          message: error.message || 'Failed to start attack.',
          type: 'error'
        });
      }
    } catch (err) {
      console.error('Error starting attack:', err);
      addNotification({
        title: '❌ Attack Failed',
        message: 'Network error while starting attack.',
        type: 'error'
      });
    }
  };

  // Start burst fire laser animation from drone to tower
  const startLaserBeamAnimation = async (drone, target) => {
    // Starting burst fire animation
    
    if (!drone || !target || !drone.position?.lat || !drone.position?.lng || !target.position?.lat || !target.position?.lng) {
      console.log('❌ Missing drone, target, or position data for burst fire');
      return;
    }
    
    const attackId = `${drone.droneId}-${target.towerId || target.ownerId || 'target'}-${Date.now()}`;
    // Creating burst fire attack
    
    // Determine attack duration based on drone type and target type
    const attackDuration = (drone.type === 'nuke' && target.type === 'command_center') ? 20 * 60 * 1000 : 
                          (drone.type === 'nuke') ? 30 * 60 * 1000 : 15 * 60 * 1000; // milliseconds
    
    // Create burst fire system
    const burstFire = {
      id: attackId,
      dronePosition: [drone.position.lat, drone.position.lng],
      towerPosition: [target.position.lat, target.position.lng],
      droneId: drone.droneId,
      towerId: target.towerId || target.ownerId,
      targetType: target.type || 'tower',
      droneType: drone.type || 'assault',
      startTime: Date.now(),
      endTime: Date.now() + attackDuration,
      isActive: true
    };

    // Animation is automatically created by server when attack starts

    // Burst fire system created
    setActiveLaserBeams(prev => {
      // Adding burst fire to active attacks
      return [...prev, burstFire];
    });

    // Start firing bursts immediately
    startBurstFiring(burstFire);

    // Remove burst fire when attack completes and clean up database
    setTimeout(async () => {
      // Attack completed! Stopping burst fire and refreshing map
      
      // Stop burst firing
      setActiveLaserBeams(prev => prev.filter(beam => beam.id !== attackId));
      
      // Remove from database
      try {
        await api.delete(`/drone/animation/remove/${attackId}`);
        console.log('✅ Attack animation removed from database');
      } catch (error) {
        console.error('❌ Error removing attack animation from database:', error);
      }
      
      // Refresh compound data to show tower destruction
      await loadCompoundData();
      
      // Refresh deployed drones to remove consumed drone from map
      await fetchDeployedDrones();
      
      console.log(`🏗️ Map refreshed - drone ${drone.droneId} should be removed from map after ${drone.type} attack`);
    }, attackDuration);
  };

  // Start continuous burst firing
  const startBurstFiring = (burstFire) => {
    const { droneType, id } = burstFire;
    
    // Prevent duplicate intervals for the same burst fire
    if (burstFire.intervalId) {
      // Burst fire already has an active interval, skipping
      return;
    }
    
    // Different firing rates based on drone type - faster for stream effect
    const firingRates = {
      assault: 150, // Fire every 150ms - rapid fire stream
      nuke: 200,    // Fire every 200ms - heavy plasma stream
      scout: 120    // Fire every 120ms - precise rapid stream
    };
    
    const firingRate = firingRates[droneType] || firingRates.assault;
    // Starting burst firing
    
    // Validate beam still exists before starting
    setActiveLaserBeams(prev => {
      const existingBeam = prev.find(beam => beam.id === id);
      if (!existingBeam) {
        // Beam not found in active beams
        return prev;
      }
      
      const firingInterval = setInterval(() => {
        // Check if this burst fire is still active using React state
        setActiveLaserBeams(currentBeams => {
          const activeBeam = currentBeams.find(beam => beam.id === id && beam.isActive);
          if (!activeBeam) {
            // Stopping burst fire - beam no longer active
            clearInterval(firingInterval);
            return currentBeams;
          }
          
          // Fire a projectile with current beam state
          fireProjectile(activeBeam);
          return currentBeams;
        });
      }, firingRate);
      
      // Update the beam with interval ID
      const updatedBeams = prev.map(beam => 
        beam.id === id ? { ...beam, intervalId: firingInterval } : beam
      );
      
      // Burst fire interval started
      return updatedBeams;
    });
  };

  // Fire a single projectile from drone to tower
  const fireProjectile = (burstFire) => {
    // Validate input data
    if (!burstFire || !burstFire.dronePosition || !burstFire.towerPosition) {
      // Invalid burst fire data, skipping projectile
      return;
    }
    
    const projectileId = `projectile-${Date.now()}-${Math.random()}`;
    
    // Calculate positions on screen
    const mapContainer = document.querySelector('.leaflet-container');
    if (!mapContainer) {
      // No map container found, skipping projectile
      return;
    }
    
    // Convert lat/lng to screen pixels
    const map = mapRef.current || window.leafletMapInstance;
    if (!map) {
      // No map instance available for projectile positioning
      return;
    }
    
    try {
      const dronePoint = map.latLngToContainerPoint(burstFire.dronePosition);
      const towerPoint = map.latLngToContainerPoint(burstFire.towerPosition);
      
      // Validate screen coordinates
      if (!dronePoint || !towerPoint || 
          isNaN(dronePoint.x) || isNaN(dronePoint.y) || 
          isNaN(towerPoint.x) || isNaN(towerPoint.y)) {
        // Invalid screen coordinates, skipping projectile
        return;
      }
      
      // Create projectile element
      const projectile = document.createElement('div');
      projectile.id = projectileId;
      projectile.className = `laser-burst-projectile laser-burst-${burstFire.droneType || 'assault'}`;
      
      // Set starting position relative to map container
      projectile.style.position = 'absolute';
      projectile.style.left = `${dronePoint.x}px`;
      projectile.style.top = `${dronePoint.y}px`;
      projectile.style.pointerEvents = 'none';
      projectile.style.zIndex = '1000';
      
      // Calculate movement distance and angle
      const deltaX = towerPoint.x - dronePoint.x;
      const deltaY = towerPoint.y - dronePoint.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
      
      // Skip if target is too close (might be same position)
      if (distance < 5) {
        return;
      }
      
      // Rotate projectile to face target
      projectile.style.transform = `rotate(${angle + 90}deg)`;
      
      // Create custom keyframe for smooth bullet travel
      const travelAnimation = `
        @keyframes projectileTravel-${projectileId} {
          0% {
            transform: translate(0, 0) rotate(${angle + 90}deg) scale(1);
            opacity: 1;
          }
          90% {
            transform: translate(${deltaX}px, ${deltaY}px) rotate(${angle + 90}deg) scale(1);
            opacity: 1;
          }
          95% {
            transform: translate(${deltaX}px, ${deltaY}px) rotate(${angle + 90}deg) scale(1.3);
            opacity: 0.8;
          }
          100% {
            transform: translate(${deltaX}px, ${deltaY}px) rotate(${angle + 90}deg) scale(0.3);
            opacity: 0;
          }
        }
      `;
      
      // Add animation style
      const style = document.createElement('style');
      style.textContent = travelAnimation;
      document.head.appendChild(style);
      
      // Fast, consistent bullet speed for game-like feel
      const duration = Math.max(0.2, Math.min(0.6, distance / 800)); // Faster bullets
      projectile.style.animation = `projectileTravel-${projectileId} ${duration}s ease-out, bulletPulse 0.1s infinite ease-in-out`;
      
      // Add to map
      mapContainer.appendChild(projectile);
      
      // Create impact effect at target location
      setTimeout(() => {
        try {
          // Create impact flash
          const impact = document.createElement('div');
          impact.style.position = 'absolute';
          impact.style.left = `${towerPoint.x - 5}px`;
          impact.style.top = `${towerPoint.y - 5}px`;
          impact.style.width = '10px';
          impact.style.height = '10px';
          impact.style.borderRadius = '50%';
          impact.style.background = `radial-gradient(circle, ${burstFire.droneType === 'assault' ? '#ff3333' : burstFire.droneType === 'nuke' ? '#ff8800' : '#00ff44'}, transparent)`;
          impact.style.boxShadow = `0 0 15px ${burstFire.droneType === 'assault' ? '#ff3333' : burstFire.droneType === 'nuke' ? '#ff8800' : '#00ff44'}`;
          impact.style.pointerEvents = 'none';
          impact.style.zIndex = '999';
          impact.style.animation = 'impactFlash 0.2s ease-out';
          
          // Add impact flash animation
          const impactStyle = document.createElement('style');
          impactStyle.textContent = `
            @keyframes impactFlash {
              0% { transform: scale(0.5); opacity: 1; }
              50% { transform: scale(2); opacity: 0.8; }
              100% { transform: scale(3); opacity: 0; }
            }
          `;
          document.head.appendChild(impactStyle);
          mapContainer.appendChild(impact);
          
          // Remove impact effect
          setTimeout(() => {
            if (impact.parentNode) impact.parentNode.removeChild(impact);
            if (impactStyle.parentNode) impactStyle.parentNode.removeChild(impactStyle);
          }, 200);
          
        } catch (impactError) {
          console.log('🔫 Error creating impact effect:', impactError);
        }
      }, duration * 1000 * 0.9); // Create impact just before bullet disappears
      
      // Remove projectile after animation
      setTimeout(() => {
        try {
          if (projectile.parentNode) {
            projectile.parentNode.removeChild(projectile);
          }
          if (style.parentNode) {
            style.parentNode.removeChild(style);
          }
        } catch (cleanupError) {
          console.log('🔫 Error during projectile cleanup:', cleanupError);
        }
      }, duration * 1000 + 100);
      
      // Projectile fired successfully
      
    } catch (error) {
      console.log('🔫 Error creating projectile:', error);
    }
  };

  // Stop laser beam animation
  const stopLaserBeamAnimation = (droneId, towerId) => {
    setActiveLaserBeams(prev => 
      prev.filter(beam => !(beam.droneId === droneId && beam.towerId === towerId))
    );
  };

  // Complete attack immediately (for testing - can be removed later)
  const completeAttackNow = async (droneId, towerId) => {
    try {
      console.log('🚀 Completing attack immediately for testing...');
      
      // Stop laser beam
      setActiveLaserBeams(prev => prev.filter(beam => beam.droneId === droneId || beam.towerId === towerId));
      
      // Refresh data to show destroyed tower
      await loadCompoundData();
      await fetchDeployedDrones();
      
      addNotification({
        title: '💥 Tower Destroyed!',
        message: 'Defense tower has been destroyed and removed from the map.',
        type: 'success'
      });
      
      console.log('🏗️ Attack completed - tower should be removed from map');
    } catch (error) {
      console.error('Error completing attack:', error);
    }
  };

  // Get laser beam style based on drone type with enhanced animations
  const getLaserBeamStyle = (drone) => {
    const baseStyle = {
      weight: 4,
      opacity: 1.0,
      dashArray: '6, 4, 2, 4', // Complex dash pattern for energy movement
      lineCap: 'round',
      lineJoin: 'round'
    };

    // Different colors and styles for different drone types
    switch (drone?.type || 'assault') {
      case 'assault':
        return { 
          ...baseStyle, 
          color: '#ff4444',
          className: 'laser-beam-animation laser-beam-assault',
          weight: 5,
          dashArray: '8, 3, 2, 3', // Fast moving energy pattern
          opacity: 0.95
        };
      case 'nuke':
        return { 
          ...baseStyle, 
          color: '#ff8800',
          className: 'laser-beam-animation laser-beam-nuke laser-beam-critical',
          weight: 8,
          dashArray: '12, 2, 4, 2, 2, 2', // Intense irregular pattern
          opacity: 1.0
        };
      case 'scout':
        return { 
          ...baseStyle, 
          color: '#00ff41',
          className: 'laser-beam-animation laser-beam-scout',
          weight: 3,
          dashArray: '4, 6, 1, 6', // Lighter, more subtle pattern
          opacity: 0.85
        };
      default:
        return { 
          ...baseStyle, 
          color: '#ff4444',
          className: 'laser-beam-animation laser-beam-assault',
          dashArray: '8, 3, 2, 3'
        };
    }
  };

  // Set user location for compound placement
  const setCompoundLocation = async (lat, lng) => {
    try {
      console.log('Setting compound location at coordinates:', lat, lng);
      
      // Update user's game location in the backend
      const response = await api.put('/profile/game-location', {
          lat: lat,
          lng: lng,
          isPublic: user.gameLocation?.isPublic || true // Preserve existing privacy setting or default to public
        });
      
      if (response.status === 200) {
        const data = response.data;
        
        // Update local user context
        updateUser({ 
          gameLocation: { 
            lat: lat, 
            lng: lng, 
            isPublic: data.gameLocation?.isPublic || true 
          } 
        });
        
        addNotification({
          title: '📍 Location Set Successfully',
          message: 'Your compound location has been set! You can now return to Compound Defense to initialize your fortress.',
          type: 'success'
        });
        
        // Exit compound placement mode
        setIsPlacingCompound(false);
        
        // Refresh compound data to check if user has initialized their compound
        setTimeout(() => {
          refreshCompoundData();
        }, 1000);
        
        // Show a follow-up notification with action button
        setTimeout(() => {
          addNotification({
            title: '🚐 Ready for Compound Initialization',
            message: 'Go to System → Compound Defense to initialize your fortress at this location.',
            type: 'info'
          });
        }, 2000);
        
      } else {
        const error = response.data;
        addNotification({
          title: '❌ Location Setting Failed',
          message: error.message || 'Failed to set location. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error setting compound location:', error);
      addNotification({
        title: '❌ Location Setting Error',
        message: 'Failed to set compound location. Please try again.',
        type: 'error'
      });
    }
  };

  // Delete unplaced house with full refund
  const deleteUnplacedHouse = async (houseId, totalCost) => {
    try {
      const confirmed = window.confirm(
        `Are you sure you want to delete this house?\n\n` +
        `You will receive a full refund of ${totalCost} WR Coins.\n\n` +
        `This action cannot be undone.`
      );
      
      if (!confirmed) {
        return;
      }

      const response = await api.delete(`/house/${houseId}`);
      
      const data = response.data;
      
      if (data.success) {
        // Show success notification
        addNotification({
          title: '✅ House Deleted Successfully',
          message: data.message,
          type: 'success'
        });
        
        // Update user balance in context
        updateUser({ 
          wrCoins: data.newBalance 
        });
        
        // Refresh unplaced houses list
        await fetchUnplacedHouses();
      } else {
        addNotification({
          title: '❌ House Deletion Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting house:', error);
      addNotification({
        title: '❌ House Deletion Error',
        message: 'Failed to delete house. Please try again.',
        type: 'error'
      });
    }
  };

  // Check if user is within house radius (500m for any house, or 5km with sniffer for own houses)
  const isWithinHouseRadius = (house) => {
    if (!userComputer || !userComputer.coordinates) return false;
    
    const distance = calculateDistance(
      userComputer.coordinates.lat,
      userComputer.coordinates.lng,
      house.location.lat,
      house.location.lng
    );
    
    // For user's own houses: allow sleep within 500m OR within 5km if sniffer is active
    if (house.ownerUsername === user?.username) {
      return distance <= 500 || (snifferActive && distance <= calculateSnifferRadius());
    }
    
    // For other users' houses: only within 500m
    return distance <= 500;
  };

  // House countdown function
  const startHouseCountdown = (action, callback) => {
    setHouseCountdown({ active: true, seconds: 5, action });
    
    let timeLeft = 5;
    const timer = setInterval(() => {
      timeLeft -= 1;
      setHouseCountdown(prev => ({ ...prev, seconds: timeLeft }));
      
      if (timeLeft <= 0) {
        clearInterval(timer);
        setHouseCountdown({ active: false, seconds: 0, action: '' });
        callback();
      }
    }, 1000);
  };

  // Execute sleep action (after countdown)
  // Regenerate house image
  const regenerateHouseImage = async (houseId) => {
    try {
      const response = await api.post(`/house/${houseId}/regenerate-image`, {});

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          title: '🖼️ Image Regenerated!',
          message: 'New house image generated successfully'
        });
        
        // Refresh houses to show new image
        fetchHouses();
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Regeneration Failed',
          message: errorData.message || 'Failed to regenerate house image'
        });
      }
    } catch (error) {
      console.error('Error regenerating house image:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to regenerate house image'
      });
    }
  };

  // Stop house from being sold/rented
  const stopHouseListing = async (houseId, currentStatus) => {
    try {
      const response = await api.post(`/house/${houseId}/stop-listing`, {});

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          title: '🏠 Listing Stopped',
          message: `House is no longer ${currentStatus === 'for_sale' ? 'for sale' : 'for rent'}`
        });
        
        // Refresh houses to show updated status
        fetchHouses();
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Failed to Stop Listing',
          message: errorData.message || 'Failed to stop house listing'
        });
      }
    } catch (error) {
      console.error('Error stopping house listing:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to stop house listing'
      });
    }
  };

  const executeSleepInHouse = async (house) => {
    try {
      // Call the server-side sleep API
      const response = await api.post('/house/sleep', { houseId: house._id });

      if (response.status === 200) {
        const data = response.data;
        
        // Set house entry status FIRST to ensure the house remains visible
        setIsSleeping(true);
        setSleepingInHouse(house);
        
        addNotification({
          title: '😴 Sleeping in House',
          message: data.message,
          type: 'success'
        });
        
        // Keep computer data but mark as sleeping - don't set to null
        if (userComputer) {
          setUserComputer(prev => ({
            ...prev,
            isSleeping: true,
            sleepingHouseId: house._id
          }));
        }
        
        // Disable sniffer tool when entering house
        if (snifferActive) {
          setSnifferActive(false);
          // Clear sniffer data
          setNearbyBanks([]);
          setNearbyCasinos([]);
          setNearbyATMs([]);
          setUsersInRadius([]);
          setNearbyVaults([]);
          
          addNotification({
            title: '📡 Sniffer Disabled',
            message: 'Sniffer tool automatically disabled while in house',
            type: 'info'
          });
        }
        
        // Immediate refresh to hide computer when entering house
        fetchComputerLocations();
        
        // Comprehensive map refresh when entering house - with longer delay to ensure state is set
        setTimeout(() => {
          fetchComputerLocations();
          loadTreasureHuntData(); // Refresh treasure hunt data
          fetchHouses(); // Refresh houses - this should now keep the sleeping house visible
          fetchUserComputer(); // Refresh user computer status
          
          // Add visual feedback for map refresh
          addNotification({
            title: '🔄 Map Refreshed',
            message: 'Map data updated after entering house',
            type: 'info'
          });
        }, 1000); // Increased delay to ensure React state has been updated

      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Sleep Error',
          message: errorData.message || 'Failed to sleep in house',
          type: 'error'
        });
      }

    } catch (error) {
      console.error('Error sleeping in house:', error);
      addNotification({
        title: '❌ Sleep Error',
        message: 'Failed to sleep in house. Please try again.',
        type: 'error'
      });
    }
  };

  // Sleep in house (with countdown)
  const sleepInHouse = async (house) => {
    if (!userComputer || !userComputer.coordinates) {
      addNotification({
        title: '🏠 Computer Required',
        message: 'You need to deploy your computer to sleep in houses.',
        type: 'error'
      });
      return;
    }

    const distance = calculateDistance(
      userComputer.coordinates.lat,
      userComputer.coordinates.lng,
      house.location.lat,
      house.location.lng
    );

    // Check if within range
    const isOwnHouse = house.ownerUsername === user?.username;
    const withinComputerRange = distance <= 500;
    const withinSnifferRange = snifferActive && distance <= calculateSnifferRadius();
    
    if (!withinComputerRange && !(isOwnHouse && withinSnifferRange)) {
      const rangeMessage = isOwnHouse 
        ? `You must be within 500m of your house, or within 5km with the sniffer tool active. Current distance: ${Math.round(distance)}m.`
        : `You must be within 500m of the house to sleep. Current distance: ${Math.round(distance)}m.`;
        
      addNotification({
        title: '🏠 Too Far Away',
        message: rangeMessage,
        type: 'error'
      });
      return;
    }

    // Start countdown then execute
    startHouseCountdown('Entering House', () => executeSleepInHouse(house));
  };

  // Execute leave house action (after countdown)
  const executeLeaveHouse = async () => {
    try {
      // Call the server-side leave house API
      const response = await api.post('/house/leave', {});

      if (response.status === 200) {
        const data = response.data;
        
        setIsSleeping(false);
        setSleepingInHouse(null);
        
        // Restore computer state when leaving house - do this immediately
        if (userComputer) {
          setUserComputer(prev => ({
            ...prev,
            isSleeping: false,
            sleepingHouseId: null
          }));
        }
        
        // Immediate refresh to show computer when leaving house
        fetchComputerLocations();
        
        // Comprehensive map refresh when leaving house
        setTimeout(() => {
          fetchComputerLocations();
          fetchUserComputer(); // Refresh user computer data to show it again
          loadTreasureHuntData(); // Refresh treasure hunt data
          fetchHouses(); // Refresh houses
          
          // Add visual feedback for map refresh
          addNotification({
            title: '🔄 Map Refreshed',
            message: 'Map data updated after leaving house',
            type: 'info'
          });
        }, 500); // 500ms delay to ensure server processing
        
        addNotification({
          title: '🌅 Left House',
          message: data.message,
          type: 'info'
        });

      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Leave Error',
          message: errorData.message || 'Failed to leave house',
          type: 'error'
        });
      }

    } catch (error) {
      console.error('Error leaving house:', error);
      addNotification({
        title: '❌ Leave Error',
        message: 'Failed to leave house. Please try again.',
        type: 'error'
      });
    }
  };

  // Leave house (with countdown)
  const leaveHouse = () => {
    startHouseCountdown('Leaving House', executeLeaveHouse);
  };

  // Handle house rental
  const handleRentHouse = async (house) => {
    try {
      // Fixed 12-hour rental period
      const totalCost = house.rentPrice; // Cost for 12-hour period
      
      // Confirm rental
      const confirmed = window.confirm(`Rent ${house.ownerUsername}'s house for 12 hours (1 night stay)?\n\nCost: ${totalCost} WR Coins\nDuration: 12 hours\nYou can enter/exit multiple times during this period.`);
      
      if (!confirmed) {
        return;
      }

      const response = await api.post('/house/rent', { 
          houseId: house._id
        });

      if (response.status === 200) {
        const data = response.data;
        
        // Refresh houses to show updated status
        fetchHouses();
        
        addNotification({
          title: '🏠 House Rented Successfully',
          message: `You've rented ${house.ownerUsername}'s house for 12 hours. Total cost: ${totalCost} WR Coins. You can enter/exit multiple times during this period.`,
          type: 'success'
        });

        // Refresh rental data to show new rental
        await fetchActiveRentals();

      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Rental Failed',
          message: errorData.message || 'Failed to rent house',
          type: 'error'
        });
      }

    } catch (error) {
      console.error('Error renting house:', error);
      addNotification({
        title: '❌ Rental Error',
        message: 'Failed to rent house. Please try again.',
        type: 'error'
      });
    }
  };

  // Enter a rented house (hide computer)
  const enterRentedHouse = async (house) => {
    // Show countdown popup
    setRentalHouseCountdownData({
      house: house,
      houseName: `${house.bedrooms}BR House`
    });
    setShowRentalHouseCountdown(true);
    setRentalHouseCountdownTimer(5);

    // Wait 0.5 seconds then make the API call (while countdown is still showing)
    setTimeout(async () => {
      try {
        const response = await api.post('/house/enter', { houseId: house._id });

        const data = response.data;

        if (data.success) {
        // Set house entry status FIRST to ensure the house remains visible
        setIsSleeping(true);
        setSleepingInHouse(house);
        
        addNotification({
          title: '🏠 Entered Rented House',
          message: data.message,
          type: 'success'
        });
        
        // Keep computer data but mark as sleeping - don't set to null
        if (userComputer) {
          setUserComputer(prev => ({
            ...prev,
            isSleeping: true,
            sleepingHouseId: house._id
          }));
        }
        
        // Disable sniffer tool when entering house
        if (snifferActive) {
          setSnifferActive(false);
          // Clear sniffer data
          setNearbyBanks([]);
          setNearbyCasinos([]);
          setNearbyATMs([]);
          setUsersInRadius([]);
          setNearbyVaults([]);
          
          addNotification({
            title: '📡 Sniffer Disabled',
            message: 'Sniffer tool automatically disabled while in house',
            type: 'info'
          });
        }
        
        // Comprehensive map refresh when entering house - with longer delay to ensure state is set
        setTimeout(() => {
          fetchComputerLocations();
          loadTreasureHuntData(); // Refresh treasure hunt data
          fetchHouses(); // Refresh houses - this should now keep the sleeping house visible
          fetchUserComputer(); // Refresh user computer status
          fetchActiveRentals(); // Refresh rental data
          
          // Add visual feedback for map refresh
          addNotification({
            title: '🔄 Map Refreshed',
            message: 'Map data updated after entering house',
            type: 'info'
          });
        }, 1000); // Increased delay to ensure React state has been updated

        } else {
          addNotification({
            title: '❌ Cannot Enter House',
            message: data.message || 'Failed to enter rented house',
            type: 'error'
          });
        }

      } catch (error) {
        console.error('Error entering rented house:', error);
        addNotification({
          title: '❌ House Entry Error',
          message: 'Failed to enter rented house. Please try again.',
          type: 'error'
        });
      }
    }, 500); // 0.5 second delay
  };

  // Fullscreen toggle functions
  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        // Enter fullscreen mode
        const element = document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        // Exit fullscreen mode
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Fullscreen operation failed:', error);
      // Fallback to CSS-only fullscreen for browsers that don't support it
      setIsFullscreen(!isFullscreen);
    }
  };

  // Calculate sniffer radius based on user's sniffer parts
  const calculateSnifferRadius = () => {
    // Base radius is 2000m
    const baseRadius = 2000;
    
    // Calculate total bonus from sniffer parts (each part gives +600m)
    let totalBonus = 0;
    if (user?.snifferInventory && user.snifferInventory.length > 0) {
      // Since we can't access the Part model here, we'll use the backend calculation
      // For display purposes, we'll estimate based on parts owned
      const snifferParts = user.snifferInventory.length;
      
      // Each part gives +600m bonus (5 parts total)
      const bonusPerPart = 600;
      totalBonus = Math.min(snifferParts, 5) * bonusPerPart;
    }
    
    const totalRadius = baseRadius + totalBonus;
    return totalRadius;
  };

  // Sniffer toggle function for fullscreen mode
  const toggleSnifferInFullscreen = async () => {
    if (!snifferActive && userComputer) {
      // Activate sniffer - fetch nearby objects
      setSnifferActive(true);
      
      const computer = userComputer;
      const radius = calculateSnifferRadius(); // Calculate radius based on user's sniffer parts
      
      try {
        // Use unified entities endpoint for comprehensive detection
        const entitiesResponse = await api.get(`/scanner/entities-in-radius?lat=${computer.coordinates.lat}&lng=${computer.coordinates.lng}&radius=${radius}`);
        
        if (entitiesResponse.data?.success) {
          const entities = entitiesResponse.data.entities || [];
          
          // Separate entities by type and set them in their respective state variables
          const banks = entities.filter(e => e.type === 'bank');
          const casinos = entities.filter(e => e.type === 'casino');
          const atms = entities.filter(e => e.type === 'atm');
          const businesses = entities.filter(e => e.type === 'business');
          const houses = entities.filter(e => e.type === 'house');
          const drones = entities.filter(e => e.type === 'drone');
          const vaults = entities.filter(e => e.type === 'atm_vault');
          const defenseTowers = entities.filter(e => e.type === 'defense_tower');
          const commandCenters = entities.filter(e => e.type === 'command_center');
          const hotels = entities.filter(e => e.type === 'hotel');
          const users = entities.filter(e => e.type === 'computer');
          
          
          // Process vaults to match expected format for rendering
          const processedVaults = vaults.map(vault => ({
            ...vault,
            coordinates: vault.position, // Map position to coordinates for rendering
            storedBalance: vault.balance || 0,
            atmCodeCount: vault.atmCodeCount || 0,
            vaultId: vault.vaultId || vault._id
          }));
          
          // Set all entity types
          setNearbyBanks(banks);
          setNearbyCasinos(casinos);
          setNearbyATMs(atms);
          // Don't overwrite houses from other sources - they're managed separately  
          setUsersInRadius(users);
          setNearbyVaults(processedVaults.filter(vault => vault.ownerUsername !== user?.username));
          setNearbyDefenseTowers(defenseTowers.filter(tower => tower.ownerUsername !== user?.username));
          
          
          // Process businesses to match expected format with complete data
          const processedBusinesses = businesses.map(business => ({
            _id: business.businessId,
            businessName: business.name || business.businessName,
            businessType: business.businessType,
            description: business.description,
            businessImageUrl: business.businessImageUrl,
            location: business.location || business.position,
            ownerUsername: business.ownerUsername,
            supplyLevel: business.supplyLevel,
            maxCapacity: business.maxCapacity,
            operationalStatus: business.operationalStatus,
            pricing: business.pricing,
            supplyChain: business.supplyChain,
            visibilityReason: 'sniffer_detected',
            distance: Math.round(business.distance)
          }));
          setBusinesses(processedBusinesses);
          
          // Process compounds from defense towers and command centers
          const compounds = [];
          if (commandCenters.length > 0) {
            commandCenters.forEach(cc => {
              const towers = defenseTowers.filter(t => t.ownerUsername === cc.ownerUsername);
              compounds.push({
                userId: { username: cc.ownerUsername },
                commandCenter: cc,
                defenseTowers: towers
              });
            });
          }
          setNearbyCompounds(compounds);
        }
        
        // Also get additional data using existing calls for compatibility
        await Promise.all([
          // Get users in radius
          api.get(`/treasure-hunt/users-in-radius?lat=${computer.coordinates.lat}&lng=${computer.coordinates.lng}&radius=${radius}`)
            .then(response => {
              if (response.data) {
                const userData = response.data.users || response.data || [];
                setUsersInRadius(Array.isArray(userData) ? userData : []);
              }
            }),
          
          // Get community tasks in radius
          fetchCommunityTasks(),
          
          // Get hotels in radius
          fetchHotels(),
          
          // Get businesses in radius (legacy call for additional data)
          fetchBusinesses()
        ]);
        
        addNotification({
          title: '🔍 Sniffer Activated',
          message: 'Scanning complete! Found nearby banks, casinos, ATMs, hotels, businesses, users, and vaults.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error fetching nearby features:', error);
        addNotification({
          title: '⚠️ Sniffer Error',
          message: 'Failed to fetch some nearby features. Check connection.',
          type: 'error'
        });
      }
    } else {
      // Deactivate sniffer - clear nearby objects but keep user's own businesses
      setSnifferActive(false);
      setNearbyBanks([]);
      setNearbyCasinos([]);
      setNearbyATMs([]);
      setHotels([]);
      fetchBusinesses(); // This will now only show user's own businesses
      setUsersInRadius([]);
      setNearbyVaults([]);
      addNotification({
        title: '🔍 Sniffer Deactivated',
        message: 'Stopped scanning for nearby objects.',
        type: 'info'
      });
    }
  };

  // Move computer function for fullscreen mode
  const moveComputerInFullscreen = () => {
    if (!userComputer || !userComputer.isActive) {
      addNotification({
        title: '❌ No Computer',
        message: 'You need to deploy a computer first.',
        type: 'error'
      });
      return;
    }

    if (jailStatus.isJailed) {
      addNotification({
        title: '🚔 Movement Blocked',
        message: 'Cannot move computer while in jail.',
        type: 'error'
      });
      return;
    }

    // Check computer status and moves
    if (computerStats.dailyMoves >= 10) {
      addNotification({
        title: '🔒 Computer Locked',
        message: 'Computer is locked for 24 hours after 10 moves.',
        type: 'error'
      });
      return;
    }

    setIsMovingComputer(true);
    addNotification({
      title: '📍 Click Map',
      message: 'Click anywhere on the map to move your computer.',
      type: 'info'
    });
  };

  // Fetch network attack status
  const fetchNetworkAttackStatus = async () => {
    try {
      const response = await api.get('/treasure-hunt/network-attacks/status');
      
      if (response.status === 200) {
        const data = response.data;
        setNetworkAttackStatus(data.data);
      }
    } catch (error) {
      console.error('Error fetching network attack status:', error);
    }
  };

  // Fetch user ATM codes
  const fetchUserATMCodes = async () => {
    try {
      const response = await api.get('/treasure-hunt/atms/codes');
      
      if (response.status === 200) {
        const data = response.data;
        // Filter out used ATM codes (codes that have been added to vaults)
        const atmCodes = (data && data.data && data.data.atmCodes) ? data.data.atmCodes : [];
        setUserATMCodes(atmCodes.filter(code => !code.used));
      }
    } catch (error) {
      console.error('Error fetching ATM codes:', error);
    }
  };

  // Delete ATM code and remove ATM from database
  const deleteATMCode = async (atmId) => {
    if (!window.confirm('Are you sure you want to delete this ATM code? This will permanently remove the ATM machine from the map.')) {
      return;
    }

    try {
      const response = await api.delete(`/treasure-hunt/atms/codes/${atmId}`);

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          type: 'success',
          title: '✅ ATM Code Deleted',
          message: data.message
        });
        
        // Refresh ATM codes list
        fetchUserATMCodes();
        
        // Refresh ATMs on map to remove the deleted one
        fetchATMs();
      } else {
        const errorData = response.data;
        addNotification({
          type: 'error',
          title: '❌ ATM Code Deletion Failed',
          message: errorData.message || 'Failed to delete ATM code'
        });
      }
    } catch (error) {
      console.error('Error deleting ATM code:', error);
      addNotification({
        type: 'error',
        title: '❌ Network Error',
        message: 'Network error occurred while deleting ATM code'
      });
    }
  };

  // Check if user has available moves (updated to use supply-based system)
  // NEW SIMPLIFIED SYSTEM: Check if user has available supplies for movement
  const hasAvailableMoves = () => {
    if (!user) return false;
    
    // Check if user is locked out (12 hours after exhausting emergency supplies)
    if (user?.survivalMoves?.lockoutUntil && new Date() < new Date(user.survivalMoves.lockoutUntil)) {
      return false; // Computer is locked - no supplies available
    }
    
    // Check regular supplies first
    const currentSupplies = user?.foodDrink?.currentSupplies || 0;
    if (currentSupplies > 0) {
      return true; // Has regular supplies - can move
    }
    
    // No regular supplies - check emergency supplies
    const emergencyMovesUsed = user?.survivalMoves?.emergencyMoves || 0;
    return emergencyMovesUsed < 3; // Can move if still has emergency supplies
  };

  const getRemainingSupplies = () => {
    const currentSupplies = user?.foodDrink?.currentSupplies || 0;
    return Math.max(0, currentSupplies);
  };

  // NEW SIMPLIFIED SYSTEM: Calculate remaining supplies (supplies = moves)
  const getEffectiveRemainingMoves = () => {
    // Check if user is locked out
    if (user?.survivalMoves?.lockoutUntil && new Date() < new Date(user.survivalMoves.lockoutUntil)) {
      return 0; // Locked out - no supplies available
    }
    
    // Primary supplies: supplies in foodDrink.currentSupplies
    const currentSupplies = user?.foodDrink?.currentSupplies || 0;
    if (currentSupplies > 0) {
      return currentSupplies; // Show remaining supplies (each supply = 1 move)
    }
    
    // Emergency supplies: 3 available when supplies reach 0
    const emergencyMovesUsed = user?.survivalMoves?.emergencyMoves || 0;
    return Math.max(0, 3 - emergencyMovesUsed);
  };

  const getRemainingEmergencyMoves = () => {
    const now = new Date();
    const lastReset = user?.survivalMoves?.lastEmergencyReset ? new Date(user.survivalMoves.lastEmergencyReset) : now;
    const hoursSinceReset = (now - lastReset) / (1000 * 60 * 60);
    
    // If 6+ hours passed, emergency moves should be reset to 0
    const effectiveEmergencyMoves = hoursSinceReset >= 6 ? 0 : (user?.survivalMoves?.emergencyMoves || 0);
    
    return Math.max(0, 3 - effectiveEmergencyMoves);
  };

  // Move computer near ATM location
  const moveComputerNearATM = async (atmId, atmCode, fromVault = false, vaultId = null) => {
    // Validate input parameters first
    if (!atmId) {
      addNotification({
        type: 'error',
        title: '❌ Invalid Request',
        message: 'ATM ID is missing. Please try again.'
      });
      return;
    }

    if (!atmCode || !atmCode.coordinates || !atmCode.coordinates.lat || !atmCode.coordinates.lng) {
      addNotification({
        type: 'error',
        title: '❌ Invalid ATM Data',
        message: 'ATM location coordinates are missing or invalid. Please refresh the page and try again.'
      });
      return;
    }

    // Force user data sync to ensure we have latest lockout status
    try {
      await syncData();
    } catch (error) {
      console.log('User data sync failed, continuing with cached data:', error);
    }

    // Debug logging for movement validation
    console.log('Movement Validation Debug:', {
      hasSupplies: (user?.foodDrink?.currentSupplies || 0),
      lockoutUntil: user?.dailyComputerMoves?.lockoutUntil,
      emergencyMoves: user?.survivalMoves?.emergencyMoves,
      isLocked: user?.dailyComputerMoves?.lockoutUntil ? new Date() < new Date(user.dailyComputerMoves.lockoutUntil) : false
    });

    // Validation checks first
    if (!hasAvailableMoves()) {
      // Check if computer is locked first
      if (user?.dailyComputerMoves?.lockoutUntil) {
        const lockoutTime = new Date(user.dailyComputerMoves.lockoutUntil);
        const now = new Date();
        if (now < lockoutTime) {
          const hoursRemaining = Math.ceil((lockoutTime - now) / (1000 * 60 * 60));
          addNotification({
            type: 'error',
            title: '🔒 Computer Locked',
            message: `Computer is locked after 3rd move. ${hoursRemaining} hours remaining until unlock.`
          });
          return;
        }
      }
      
      // Check if daily moves are exhausted
      const movesUsedToday = user?.dailyComputerMoves?.moves || 0;
      const remainingDailyMoves = 10 - movesUsedToday;
      
      if (remainingDailyMoves <= 0) {
        // Daily moves exhausted, check supplies
        const hasSupplies = (user?.foodDrink?.currentSupplies || 0) > 0;
        const emergencyMovesUsed = user?.survivalMoves?.emergencyMoves || 0;
        
        if (!hasSupplies && emergencyMovesUsed >= 3) {
          addNotification({
            type: 'error',
            title: '❌ No Movement Available',
            message: 'You have used all 10 daily moves and all 3 emergency moves. Purchase Food/Drink supplies from shops to restore movement.'
          });
        } else {
          addNotification({
            type: 'error',
            title: '❌ Daily Moves Exhausted',
            message: 'You have used all 10 daily moves. Purchase Food/Drink supplies from shops for additional movement.'
          });
        }
      } else {
        // This shouldn't happen if hasAvailableMoves() is working correctly
        addNotification({
          type: 'error',
          title: '❌ Movement Error',
          message: 'Unable to move computer. Please try again.'
        });
      }
      return;
    }

    if (!userComputer) {
      addNotification({
        type: 'error',
        title: '❌ No Computer Deployed',
        message: 'You must have an active computer deployed to move near ATM locations.'
      });
      return;
    }

    const hasSupplies = (user?.foodDrink?.currentSupplies || 0) > 0;
    const movementType = hasSupplies ? 'supplies' : 'emergency';
    const remainingSupplies = user?.foodDrink?.currentSupplies || 0;
    const emergencyMovesUsed = user?.survivalMoves?.emergencyMoves || 0;
    
    // Show countdown popup instead of confirmation dialog
    setATMTravelCountdownData({
      atmId,
      atmCode,
      fromVault,
      vaultId,
      movementType,
      costText: movementType === 'supplies' ? `1 supply (${remainingSupplies} remaining)` : `1 emergency move (${3 - emergencyMovesUsed} remaining)`
    });
    setShowATMTravelCountdown(true);
    setATMTravelCountdownTimer(10);

    // Wait 0.5 seconds then make the API call (while countdown is still showing)
    setTimeout(async () => {
      try {
        // Validate atmCode coordinates before making the request
        if (!atmCode || !atmCode.coordinates || !atmCode.coordinates.lat || !atmCode.coordinates.lng) {
          addNotification({
            type: 'error',
            title: '❌ Invalid ATM Data',
            message: 'ATM location coordinates are missing or invalid. Please try again.'
          });
          setShowATMTravelCountdown(false);
          return;
        }

        // Debug logging
        console.log('[ATM MOVE DEBUG] Request data:', {
          atmId,
          atmLocation: {
            lat: atmCode.coordinates.lat,
            lng: atmCode.coordinates.lng
          }
        });

      const response = await api.post('/treasure-hunt/move-computer-near-atm', {
          atmId,
          atmLocation: {
            lat: atmCode.coordinates.lat,
            lng: atmCode.coordinates.lng
          }
        });

      if (response.status === 200) {
        const data = response.data;
        const newMovementType = data.movement?.type || movementType;
        const suppliesUsed = newMovementType === 'supplies';
        
        addNotification({
          type: 'success',
          title: '✅ Computer Moved Successfully',
          message: `Computer moved near ATM location! Used 1 ${suppliesUsed ? `supply. Supplies remaining: ${data.foodDrink?.currentSupplies || 0}/10` : `emergency move. Emergency moves remaining: ${data.movement?.movesRemaining || 0}/3`}. Your computer is now within range of the ATM.`
        });
        
        // Update user context with new balance and move count
        updateUser({ 
          wrCoins: data.userBalance,
          dailyComputerMoves: data.dailyComputerMoves,
          foodDrink: data.foodDrink,
          survivalMoves: data.survivalMoves,
          computerLocation: data.newComputerLocation
        });

        // If ATM was spawned, immediately add it to the ATMs state for instant visibility
        if (data.spawnedATM) {
          console.log('Adding spawned ATM to map immediately:', data.spawnedATM);
          setAtms(prevAtms => {
            // Remove any existing ATM with same ID and add the new one
            const filteredAtms = prevAtms.filter(atm => atm._id !== data.spawnedATM.atmId);
            return [...filteredAtms, {
              _id: data.spawnedATM.atmId,
              name: data.spawnedATM.name,
              coordinates: data.spawnedATM.coordinates,
              accessCode: data.spawnedATM.accessCode,
              isActive: true,
              atmType: 'treasure'
            }];
          });
        }

        // Add a small delay to ensure database operations are complete before refresh
        setTimeout(async () => {
          // Force complete map refresh to show updated data
          await fetchUserComputer(); // First update computer location
          await loadTreasureHuntData(); // Refresh treasure hunt data and all markers
          
          // Refresh all sniffer radius features at the new location
          const newLat = data.newComputerLocation?.lat;
          const newLng = data.newComputerLocation?.lng;
          if (newLat && newLng) {
            await refreshSnifferRadiusFeatures(newLat, newLng);
          } else {
            await refreshSnifferRadiusFeatures(); // Fallback to current position
          }
          
          fetchUserATMCodes();
        }, 1000); // 1 second delay to ensure database consistency
      } else {
        const errorData = response.data;
        addNotification({
          type: 'error',
          title: '❌ Computer Move Failed',
          message: errorData.message || 'Failed to move computer near ATM'
        });
      }
      } catch (error) {
        console.error('Error moving computer near ATM:', error);
        console.error('Error response data:', error.response?.data);
        console.error('Error status:', error.response?.status);
        
        // Check if this is an authentication error
        if (error.isAuthError || error.message?.includes('Authentication expired')) {
          addNotification({
            type: 'error',
            title: '❌ Authentication Expired',
            message: 'Your session has expired. Please refresh the page and try again.'
          });
        } else if (error.response?.status === 400) {
          // Handle 400 Bad Request errors with specific message
          const errorMessage = error.response?.data?.message || 'Invalid request parameters';
          console.error('400 Error Message:', errorMessage);
          addNotification({
            type: 'error',
            title: '❌ ATM Movement Failed',
            message: errorMessage
          });
        } else {
          addNotification({
            type: 'error',
            title: '❌ Network Error',
            message: 'Network error occurred while moving computer near ATM'
          });
        }
      }
    }, 500); // 0.5 second delay
  };

  // Handle ATM brute force attack
  const handleATMBruteForce = async (atm) => {
    try {
      if (!userComputer) {
        addNotification({
          type: 'error',
          title: '❌ No Computer Deployed',
          message: 'You must have an active computer deployed to start brute force attacks.'
        });
        return;
      }

      // ATM brute force has fixed 20-minute duration (no parts reduction)
      const confirmMessage = `Start brute force attack on ATM ${atm.atmId}?\n\n` +
        `📍 Location: ${atm.locationDescription}\n` +
        `⏱️ Attack Duration: 20 minutes (fixed)\n` +
        `🔧 Parts do not reduce ATM attack time\n` +
        `📅 Weekly Limit: One ATM attack per week\n\n` +
        `Success will give you the ATM access code to withdraw 500 WR Coins.`;

      if (!window.confirm(confirmMessage)) {
        return;
      }

      const response = await api.post('/treasure-hunt/brute-force/start', {
          targetId: atm.atmId,
          targetType: 'atm',
          targetLocation: {
            lat: atm.coordinates.lat,
            lng: atm.coordinates.lng
          }
        });

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          type: 'success',
          title: '✅ ATM Brute Force Started',
          message: `ATM brute force attack initiated! Duration: 20 minutes. Monitor progress in your Tools panel.`
        });
        
        // Refresh brute force status to show new attack
        if (typeof fetchBruteForceStatus === 'function') {
          fetchBruteForceStatus();
        }
      } else {
        const errorData = response.data;
        addNotification({
          type: 'error',
          title: '❌ Brute Force Attack Failed',
          message: errorData.message || 'Failed to start ATM brute force attack'
        });
      }
    } catch (error) {
      console.error('Error starting ATM brute force:', error);
      addNotification({
        type: 'error',
        title: '❌ Network Error',
        message: 'Network error occurred while starting brute force attack'
      });
    }
  };

  // Fetch slot SWAT warnings
  const fetchSlotSwatWarnings = async () => {
    try {
      const response = await api.get('/treasure-hunt/casinos/slot-swat-warnings');
      
      if (response.status === 200) {
        const data = response.data;
        const activeWarnings = (data && data.data && data.data.activeWarnings) ? data.data.activeWarnings : [];
        setSlotSwatWarnings(activeWarnings);
      }
    } catch (error) {
      console.error('Error fetching slot SWAT warnings:', error);
    }
  };

  // Fetch jail status
  const fetchJailStatus = async () => {
    try {
      const response = await api.get('/profile');
      
      if (response.status === 200) {
        const data = response.data;
        const now = new Date();
        const jailedUntil = data.user?.jailedUntil ? new Date(data.user.jailedUntil) : null;
        
        setJailStatus({
          isJailed: jailedUntil && jailedUntil > now,
          jailedUntil: jailedUntil
        });
      }
    } catch (error) {
      console.error('Error fetching jail status:', error);
    }
  };

  // Fetch user's own vaults (always visible on map)
  const fetchUserVaults = async () => {
    try {
      const response = await api.get('/atm-vaults/my-vaults');
      
      if (response.status === 200) {
        const data = response.data;
        setUserVaults(data.vaults || []);
        // Loaded ${data.vaults?.length || 0} user vaults
      }
    } catch (error) {
      console.error('Error fetching user vaults:', error);
    }
  };

  // Track opened chests to prevent duplicate processing
  const [openedChests, setOpenedChests] = useState(new Set());
  const [processingChests, setProcessingChests] = useState(new Set());

  // Handle opening treasure chest
  const handleOpenChest = async (chest) => {
    if (isInJail()) {
      alert(`🔒 You cannot open treasure chests while in jail!\n\nJail time remaining: ${getJailTimeRemaining()} minutes`);
      return;
    }
    
    // Prevent duplicate processing of the same chest
    if (openedChests.has(chest.chestId) || processingChests.has(chest.chestId)) {
      return;
    }
    
    if (chest.type === 'xp' || chest.type === 'movement_xp') {
      // Mark chest as processing immediately to prevent duplicate clicks
      setProcessingChests(prev => new Set([...prev, chest.chestId]));
      setOpenedChests(prev => new Set([...prev, chest.chestId]));
      
      // Show countdown popup immediately to prevent double-clicks
      const initialMessage = chest.type === 'movement_xp' 
        ? `🚀 Movement XP Crate opened! You gained ${chest.xpAmount || 1} XP!`
        : `🎉 XP Chest opened! You gained ${chest.xpAmount || 1} XP!`;
      
      setXPCountdownData({
        message: initialMessage,
        xpGained: chest.xpAmount || 1,
        chestType: chest.type
      });
      setXPCountdownTimer(5);
      setShowXPCountdown(true);

      // Immediately remove the chest from the map to prevent double-clicks
      if (treasureHuntData && treasureHuntData.treasureChests) {
        const updatedChests = treasureHuntData.treasureChests.filter(c => c.chestId !== chest.chestId);
        setTreasureHuntData({
          ...treasureHuntData,
          treasureChests: updatedChests
        });
      }

      // XP chests and movement XP crates open immediately
      try {
        const response = await api.post('/treasure-hunt/open-chest', {
          chestId: chest.chestId,
          coordinates: { lat: chest.coordinates.lat, lng: chest.coordinates.lng }
        });

        if (response.status === 200) {
          const data = response.data;
          
          // Update the popup with actual server data if different
          const serverMessage = data.message || initialMessage;
          const actualXP = data.xpGained || chest.xpAmount || 1;
          
          // Add notification for successful XP chest opening
          addNotification({
            type: 'xp_reward',
            category: 'treasure',
            title: chest.type === 'movement_xp' ? '🚀 Movement XP Crate!' : '⭐ XP Chest Opened!',
            message: `You gained ${actualXP} XP!`,
            details: chest.type === 'movement_xp' ? 'Movement reward bonus' : 'Treasure hunt discovery',
            icon: chest.type === 'movement_xp' ? '🚀' : '⭐',
            color: '#9C27B0'
          });
          
          // Update countdown popup with server data if needed
          if (showXPCountdown) {
            setXPCountdownData({
              message: serverMessage,
              xpGained: actualXP,
              chestType: chest.type
            });
          }
          
          // Update user XP in context without triggering full data refresh
          if (data.xpGained && updateUser) {
            updateUser({ xp: (user?.xp || 0) + data.xpGained }, true); // skipSync = true
          }
          
          // Defer expensive operations to avoid blocking the UI
          setTimeout(() => {
            fetchATMs();
            fetchUserATMCodes();
          }, 100);
        } else {
          const errorData = response.data;
          console.error(`Error opening XP chest: ${errorData.message}`);
          
          // Close countdown popup on error and show error message
          setShowXPCountdown(false);
          setXPCountdownData(null);
          
          // Show error notification with refresh option
          if (addNotification) {
            addNotification({
              id: Date.now(),
              type: 'error',
              title: '❌ XP Crate Error',
              message: (errorData.message || 'Failed to open XP chest') + '\n\nPlease refresh the page to try again.',
              duration: 5000
            });
          }
        }
      } catch (error) {
        console.error('Error opening XP chest:', error);
        
        // Close countdown popup on network error
        setShowXPCountdown(false);
        setXPCountdownData(null);
        
        // Show error notification with refresh option
        if (addNotification) {
          addNotification({
            id: Date.now(),
            type: 'error',
            title: '❌ Network Error',
            message: (error.response?.data?.message || 'Network error: Failed to open XP chest') + '\n\nPlease refresh the page to try again.',
            duration: 5000
          });
        }
      } finally {
        // Remove from processing set when done
        setProcessingChests(prev => {
          const newSet = new Set(prev);
          newSet.delete(chest.chestId);
          return newSet;
        });
      }
    } else {
      // Treasure chests require combination
      setSelectedChest(chest);
      setCombinationInput('');
    }
  };

  // Handle combination submission
  const handleCombinationSubmit = async () => {
    if (!selectedChest || !combinationInput.trim()) {
      console.log('Please enter a combination');
      return;
    }

    try {
      const response = await api.post('/treasure-hunt/open-chest', {
        chestId: selectedChest.chestId,
        combination: combinationInput.trim(),
        coordinates: { lat: selectedChest.coordinates.lat, lng: selectedChest.coordinates.lng }
      });

      if (response.status === 200) {
        const data = response.data;
        // Show notification for successful treasure chest opening
        addNotification({
          type: 'treasure_chest_opened',
          category: 'treasure',
          title: '🎉 Treasure Chest Opened!',
          message: `Successfully unlocked treasure chest #${selectedChest.chestId}`,
          details: `Found: ${data.coinsGained} WR Coins | Combination: ${combinationInput.trim()}`,
          icon: '💰',
          color: '#FFD700'
        });
        setSelectedChest(null);
        setCombinationInput('');
        // Refresh data in background to preserve map view
        setTimeout(() => {
          loadTreasureHuntData();
          // Also refresh ATMs and ATM codes in case any were generated
          fetchATMs();
          fetchUserATMCodes();
        }, 500);
      } else {
        const errorData = response.data;
        // Show notification for failed chest opening
        addNotification({
          type: 'treasure_chest_failed',
          category: 'treasure',
          title: '❌ Wrong Combination',
          message: 'Failed to unlock treasure chest',
          details: errorData.message || 'Invalid combination entered',
          icon: '🔒',
          color: '#F44336'
        });
      }
    } catch (error) {
      console.error('Error opening chest:', error);
      // Show notification for network error
      addNotification({
        type: 'treasure_chest_error',
        category: 'error',
        title: '🌐 Network Error',
        message: 'Failed to connect to treasure system',
        details: error.response?.data?.message || 'Please check your connection and try again',
        icon: '📡',
        color: '#FF9800'
      });
    }
  };

  // Clear personal XP chests for current user
  const clearPersonalChests = async () => {
    try {
      const response = await api.post('/treasure-hunt/clear-my-personal-chests', {});

      if (response.status === 200) {
        const data = response.data;
        console.log('✅ Personal XP chests cleared:', data.message);
        
        // Show notification
        if (addNotification) {
          addNotification({
            id: Date.now(),
            type: 'success',
            message: data.message,
            duration: 5000
          });
        }
        
        // Refresh treasure hunt data to update the display
        loadTreasureHuntData();
        
      } else {
        const errorData = response.data;
        console.error('Error clearing personal XP chests:', errorData.message);
        
        // Show error notification
        if (addNotification) {
          addNotification({
            id: Date.now(),
            type: 'error',
            message: errorData.message || 'Failed to clear personal XP chests',
            duration: 5000
          });
        }
      }
    } catch (error) {
      console.error('Error clearing personal XP chests:', error);
      
      // Show error notification
      if (addNotification) {
        addNotification({
          id: Date.now(),
          type: 'error',
          message: 'Failed to clear personal XP chests. Please try again.',
          duration: 5000
        });
      }
    }
  };

  // Calculate distance between two points
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI/180;
    const φ2 = lat2 * Math.PI/180;
    const Δφ = (lat2-lat1) * Math.PI/180;
    const Δλ = (lng2-lng1) * Math.PI/180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  };

  // Toggle sniffer tool
  const toggleSniffer = async () => {
    if (!userComputer) {
      // Try to refetch user computer before failing
      try {
        const response = await api.get('/treasure-hunt/computers/locations');
        
        if (response.status === 200) {
          const data = response.data;
          const computerData = (data && data.data) ? data.data : [];
        const myComputer = computerData.find(comp => comp.userId === user?.id || comp.userId === user?._id);
          if (!myComputer) {
            addNotification({
              title: '🎯 Deploy Computer First',
              message: 'You need to deploy your computer on the map before using the sniffer. Click anywhere on the map to place it!',
              type: 'info'
            });
            return;
          }
          setUserComputer(myComputer);
        } else {
          addNotification({
            title: '🎯 Deploy Computer First',
            message: 'You need to deploy your computer on the map before using the sniffer. Click anywhere on the map to place it!',
            type: 'info'
          });
          return;
        }
      } catch (error) {
        console.error('Error checking for user computer:', error);
        addNotification({
          title: '🎯 Deploy Computer First',
          message: 'You need to deploy your computer on the map before using the sniffer. Click anywhere on the map to place it!',
          type: 'info'
        });
        return;
      }
    }

    const newSnifferState = !snifferActive;
    setSnifferActive(newSnifferState);
    
    if (newSnifferState) {
      // Activate sniffer - use unified entities endpoint for comprehensive detection
      const computer = userComputer;
      const radius = calculateSnifferRadius(); // Calculate radius based on user's sniffer parts
      
      try {
        // Use unified entities endpoint that detects everything
        const entitiesResponse = await api.get(`/scanner/entities-in-radius?lat=${computer.coordinates.lat}&lng=${computer.coordinates.lng}&radius=${radius}`);
        
        if (entitiesResponse.data?.success) {
          const entities = entitiesResponse.data.entities || [];
          
          // Separate entities by type and set them in their respective state variables
          const banks = entities.filter(e => e.type === 'bank');
          const casinos = entities.filter(e => e.type === 'casino');
          const atms = entities.filter(e => e.type === 'atm');
          const businesses = entities.filter(e => e.type === 'business');
          const houses = entities.filter(e => e.type === 'house');
          const drones = entities.filter(e => e.type === 'drone');
          const vaults = entities.filter(e => e.type === 'atm_vault');
          const defenseTowers = entities.filter(e => e.type === 'defense_tower');
          const commandCenters = entities.filter(e => e.type === 'command_center');
          const hotels = entities.filter(e => e.type === 'hotel');
          const users = entities.filter(e => e.type === 'computer');
          
          
          // Process vaults to match expected format for rendering
          const processedVaults = vaults.map(vault => ({
            ...vault,
            coordinates: vault.position, // Map position to coordinates for rendering
            storedBalance: vault.balance || 0,
            atmCodeCount: vault.atmCodeCount || 0,
            vaultId: vault.vaultId || vault._id
          }));
          
          // Process hotels to match expected format for rendering
          const processedHotels = hotels.map(hotel => ({
            ...hotel,
            coordinates: hotel.position // Map position to coordinates for rendering
          }));
          
          // Set all entity types
          setNearbyBanks(banks);
          setNearbyCasinos(casinos);
          setNearbyATMs(atms);
          setHotels(processedHotels);
          // Don't overwrite houses from other sources - they're managed separately
          setUsersInRadius(users);
          setNearbyVaults(processedVaults.filter(vault => vault.ownerUsername !== user?.username));
          setNearbyDefenseTowers(defenseTowers.filter(tower => tower.ownerUsername !== user?.username));
          
          
          // Process businesses to match expected format with complete data
          const processedBusinesses = businesses.map(business => ({
            _id: business.businessId,
            businessName: business.name || business.businessName,
            businessType: business.businessType,
            description: business.description,
            businessImageUrl: business.businessImageUrl,
            location: business.location || business.position,
            ownerUsername: business.ownerUsername,
            supplyLevel: business.supplyLevel,
            maxCapacity: business.maxCapacity,
            operationalStatus: business.operationalStatus,
            pricing: business.pricing,
            supplyChain: business.supplyChain,
            visibilityReason: 'sniffer_detected',
            distance: Math.round(business.distance)
          }));
          
          // Also get user's own businesses to include them
          try {
            const userBusinessResponse = await api.get('/business/my-business');
            if (userBusinessResponse.status === 200) {
              const userData = userBusinessResponse.data;
              if (userData.businesses && userData.businesses.length > 0) {
                userData.businesses.forEach(business => {
                  // Only add if not already in processed businesses
                  const alreadyExists = processedBusinesses.some(existing => 
                    existing._id === business._id || existing._id === business.businessId
                  );
                  if (!alreadyExists) {
                    processedBusinesses.push({
                      ...business,
                      visibilityReason: 'own_business'
                    });
                  }
                });
              }
            }
          } catch (error) {
            console.error('Error fetching user businesses:', error);
          }
          
          setBusinesses(processedBusinesses);
          
          // Process compounds from defense towers and command centers
          const compounds = [];
          if (commandCenters.length > 0) {
            commandCenters.forEach(cc => {
              const towers = defenseTowers.filter(t => t.ownerUsername === cc.ownerUsername);
              compounds.push({
                userId: { username: cc.ownerUsername },
                commandCenter: cc,
                defenseTowers: towers
              });
            });
          }
          setNearbyCompounds(compounds);
        }
        
        // Also get hotels and community tasks using existing calls for compatibility
        await Promise.all([
          // Get hotels in radius
          fetchHotels(),
          
          // Get community tasks in radius
          fetchCommunityTasks()
        ]);
      } catch (error) {
        console.error('Error fetching nearby features:', error);
        
        // Fallback to client-side filtering if API fails
        const nearbyBanks = banks.filter(bank => {
          const distance = calculateDistance(
            computer.coordinates.lat, computer.coordinates.lng,
            bank.coordinates.lat, bank.coordinates.lng
          );
          return distance <= radius;
        });
        setNearbyBanks(nearbyBanks);
        
        const nearbyCasinos = casinos.filter(casino => {
          if (!casino.coordinates || !casino.coordinates.lat || !casino.coordinates.lng) {
            return false;
          }
          const distance = calculateDistance(
            computer.coordinates.lat, computer.coordinates.lng,
            casino.coordinates.lat, casino.coordinates.lng
          );
          return distance <= radius;
        });
        setNearbyCasinos(nearbyCasinos);
        
        const nearbyATMs = atms.filter(atm => {
          const distance = calculateDistance(
            computer.coordinates.lat, computer.coordinates.lng,
            atm.coordinates.lat, atm.coordinates.lng
          );
          return distance <= radius;
        });
        setNearbyATMs(nearbyATMs);
      }
      
      // Find users in radius
      try {
        const response = await api.get(`/treasure-hunt/users-in-radius?lat=${computer.coordinates.lat}&lng=${computer.coordinates.lng}&radius=${radius}`);
        
        if (response.status === 200) {
          const data = response.data;
          if (data && data.success) {
            const users = (data.data && data.data.users) ? data.data.users : [];
            setUsersInRadius(users);
          }
        }
      } catch (error) {
        console.error('Error fetching users in radius:', error);
      }
    } else {
      // Deactivate sniffer
      setNearbyBanks([]);
      setNearbyCasinos([]);
      setNearbyATMs([]);
      setHotels([]);
      setNearbyVaults([]);
      setUsersInRadius([]);
      setNearbyCompounds([]);
      setBusinesses([]);
    }
    
    // Debug: Log final state
  };

  // Handle slot machine SWAT escape
  const handleSlotSwatEscape = async (casinoId) => {
    try {
      const response = await api.post('/treasure-hunt/casinos/slot-escape', { casinoId });

      if (response.status === 200) {
        const data = response.data;
        console.log('SWAT escape result:', data.message);
        fetchSlotSwatWarnings(); // Refresh warnings
      } else {
        const errorData = response.data;
        console.error(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error escaping SWAT:', error);
    }
  };

  // Client-side pixel color detection - most accurate method
  const isWaterArea = async (clickEvent) => {
    return new Promise((resolve) => {
      try {
        console.log('[PIXEL CHECK] Starting pixel color analysis...');
        
        // Get the map container and click position
        const mapContainer = clickEvent.target._container;
        if (!mapContainer) {
          console.log('[PIXEL CHECK] No map container found, allowing movement');
          resolve(false);
          return;
        }

        // Batch DOM reads to prevent forced reflow
        requestAnimationFrame(() => {
          const rect = mapContainer.getBoundingClientRect();
          const x = clickEvent.originalEvent.clientX - rect.left;
          const y = clickEvent.originalEvent.clientY - rect.top;
          const containerWidth = mapContainer.offsetWidth || 800;
          const containerHeight = mapContainer.offsetHeight || 600;
          
          console.log(`[PIXEL CHECK] Click position: (${x}, ${y})`);

          // Use html2canvas to capture the map area
          import('html2canvas').then(html2canvas => {
            html2canvas(mapContainer, {
              useCORS: true,
              allowTaint: true,
              scale: 1,
              logging: false,
              width: containerWidth,
              height: containerHeight
          }).then(canvas => {
            try {
              // Get pixel data at click position
              const ctx = canvas.getContext('2d');
              const pixelData = ctx.getImageData(x, y, 1, 1).data;
              const [r, g, b, a] = pixelData;
              
              console.log(`[PIXEL CHECK] Pixel color at (${x}, ${y}): R=${r}, G=${g}, B=${b}, A=${a}`);
              
              // Analyze if pixel is blue-ish (water)
              // Water detection criteria:
              // 1. Blue component is higher than red and green
              // 2. Blue component is above a threshold (indicates blue water)
              // 3. Not too dark (avoid black areas) and not too light (avoid white areas)
              
              const isBlue = b > r && b > g && b > 80 && b < 240;
              const isWaterBlue = isBlue && (b - Math.max(r, g)) > 20; // Blue dominance
              
              if (isWaterBlue) {
                console.log('[PIXEL CHECK] 🌊 WATER detected - blue pixel');
                resolve(true);
              } else {
                console.log('[PIXEL CHECK] 🏞️ LAND detected - non-blue pixel');
                resolve(false);
              }
              
            } catch (pixelError) {
              console.log('[PIXEL CHECK] Pixel analysis error, allowing movement:', pixelError);
              resolve(false);
            }
          }).catch(canvasError => {
            console.log('[PIXEL CHECK] Canvas capture error, allowing movement:', canvasError);
            resolve(false);
          });
        }).catch(importError => {
          console.log('[PIXEL CHECK] html2canvas import error, bypassing client check (server will validate):', importError);
          resolve(true); // Allow deployment attempt - server will validate water location
        });
        }); // Close requestAnimationFrame callback

      } catch (error) {
        console.log('[PIXEL CHECK] General error, allowing movement:', error);
        resolve(false);
      }
    });
  };

  // Handle map click for computer placement/movement/vault placement/business placement/drone deployment
  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;

    if (coordinatePickerMode) {
      await copyCoordinatesToClipboard(lat, lng);
    } else if (isEscapeMode) {
      await escapeArrest(lat, lng);
    } else if (isPlacingComputer) {
      await placeComputer(lat, lng);
    } else if (isMovingComputer) {
      await moveComputer(lat, lng);
    } else if (isPlacingVault) {
      await placeVault(lat, lng);
    } else if (isMovingVault) {
      await moveVault(lat, lng);
    } else if (isPlacingHouse) {
      await placeHouse(lat, lng);
    } else if (isPlacingBusiness) {
      await placeBusiness(lat, lng);
    } else if (isPlacingTask) {
      await placeTask(lat, lng);
    } else if (isPlacingTower) {
      await placeTower(lat, lng);
    } else if (isDeployingDrone) {
      await deployDrone(lat, lng, e);
    } else if (isDeployingSatellite) {
      await deploySatellite(lat, lng, e);
    } else if (satelliteMoving) {
      await handleSatelliteMove(lat, lng);
    } else if (isDroneMoving) {
      await moveDrone(lat, lng, e);
    } else if (isPlacingCompound) {
      await setCompoundLocation(lat, lng);
    } else if (isMovingCommandCenter) {
      await moveCommandCenter(lat, lng);
    }
  };

  // Copy coordinates to clipboard for admin feature creation
  const copyCoordinatesToClipboard = async (lat, lng) => {
    const coordString = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(coordString);
      setPickedCoordinates({ lat, lng });
      
      addNotification({
        title: '📋 Coordinates Copied!',
        message: `Coordinates ${coordString} copied to clipboard. Go to Admin Panel to paste them into your feature.`
      });
      
      // Auto-turn off coordinate picker mode after successful copy
      setTimeout(() => {
        setCoordinatePickerMode(false);
        setPickedCoordinates(null);
      }, 3000);
    } catch (error) {
      addNotification({
        title: '❌ Copy Failed',
        message: 'Failed to copy coordinates to clipboard. Please copy manually: ' + coordString
      });
    }
  };

  // Toggle drone sniffer for a specific drone (simplified approach like computer sniffer)
  const toggleDroneSniffer = async (droneId, dronePosition) => {
    // Drone sniffer toggle called for ${droneId}
    const isCurrentlyActive = droneSniffersActive.has(droneId);
    
    // Find the drone object to get its discovery data
    const drone = deployedDrones.find(d => d.droneId === droneId);
    
    if (isCurrentlyActive) {
      // Deactivate drone sniffer
      const newActiveSniffers = new Set(droneSniffersActive);
      newActiveSniffers.delete(droneId);
      setDroneSniffersActive(newActiveSniffers);
      
      // Clear detected targets for this drone
      setDroneDetectedBanks(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedCasinos(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedATMs(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedUsers(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedVaults(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedCompounds(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedComputers(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedTowers(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedHouses(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      setDroneDetectedBusinesses(prev => {
        const newState = { ...prev };
        delete newState[droneId];
        return newState;
      });
      
      addNotification({
        title: '✈️ Drone Sniffer Deactivated',
        message: 'Drone stopped scanning for nearby objects.',
        type: 'info'
      });
    } else {
      // Check cooldown for maritime scout drones before activation
      if (drone && drone.type === 'maritime_scout') {
        try {
          const cooldownResponse = await api.get(`/drone/cooldown-status/${droneId}`);
          
          if (cooldownResponse.status === 200) {
            const cooldownData = cooldownResponse.data;
            if (!cooldownData.canUse) {
              addNotification({
                title: '🛥️ Maritime Scout Cooldown',
                message: cooldownData.message || `Sniffer on cooldown. ${cooldownData.remainingMinutes} minutes remaining.`,
                type: 'warning'
              });
              return;
            }
          }
        } catch (error) {
          console.error('Error checking maritime scout cooldown:', error);
        }
        
        // Check if maritime scout drone is over water
        try {
          const waterCheckResponse = await api.post('/utils/validate-water', {
            lat: dronePosition.lat,
            lng: dronePosition.lng
          });
          
          if (waterCheckResponse.status === 200) {
            const waterValidation = waterCheckResponse.data;
            if (waterValidation.isLand) {
              addNotification({
                title: '🏝️ Maritime Drone Over Land',
                message: 'Maritime scout drones can only activate their sniffer when deployed over water (ocean/sea).',
                type: 'error'
              });
              return;
            }
          }
        } catch (error) {
          console.error('Error checking water validation:', error);
          // Continue anyway if water check fails
        }
      }
      
      // Activate drone sniffer - use drone's discovery data
      try {
        if (!drone || !drone.discoveryData) {
          // No drone or discovery data found for drone ID: ${droneId}
          addNotification({
            title: '⚠️ Sniffer Data Missing',
            message: 'No discovery data available for this drone. Try moving the drone to update its sniffer data.',
            type: 'warning'
          });
          return;
        }
        
        // Process the drone's discovery data by type
        const computers = drone.discoveryData.filter(d => d.type === 'computer');
        const banks = drone.discoveryData.filter(d => d.type === 'bank');
        const casinos = drone.discoveryData.filter(d => d.type === 'casino');
        const houses = drone.discoveryData.filter(d => d.type === 'house');
        const hotels = drone.discoveryData.filter(d => d.type === 'hotel');
        const businesses = drone.discoveryData.filter(d => d.type === 'business');
        const vaults = drone.discoveryData.filter(d => d.type === 'atm_vault');
        const towers = drone.discoveryData.filter(d => d.type === 'defense_tower');
        const commandCenters = drone.discoveryData.filter(d => 
          d.type === 'command_center' && 
          d.owner?.username !== user?.username && 
          d.ownerUsername !== user?.username
        );
        const userTasks = drone.discoveryData.filter(d => d.type === 'user_task');
        const otherDrones = drone.discoveryData.filter(d => d.type === 'drone');
        
        // Update all discovery states
        setDroneDetectedComputers(prev => ({ ...prev, [droneId]: computers }));
        setDroneDetectedBanks(prev => ({ ...prev, [droneId]: banks }));
        setDroneDetectedCasinos(prev => ({ ...prev, [droneId]: casinos }));
        setDroneDetectedATMs(prev => ({ ...prev, [droneId]: [] })); // ATMs handled separately
        setDroneDetectedUsers(prev => ({ ...prev, [droneId]: [] })); // Users with gameLocation (separate from computers)
        setDroneDetectedVaults(prev => ({ ...prev, [droneId]: vaults }));
        setDroneDetectedTowers(prev => ({ ...prev, [droneId]: towers }));
        setDroneDetectedHouses(prev => ({ ...prev, [droneId]: houses }));
        setDroneDetectedBusinesses(prev => ({ ...prev, [droneId]: businesses }));
        setDroneDetectedDrones(prev => ({ ...prev, [droneId]: otherDrones }));
        setDroneDetectedCompounds(prev => ({ ...prev, [droneId]: commandCenters })); // Use compounds for command centers
        
        // Drone sniffer activated with discovery data: ${drone.discoveryData.length} total entities
        
        // Debug: Log computer details
        if (computers.length > 0) {
          // Computer details from discovery data: ${computers.length} computers
          // Debug: Computer details logged
        } else {
          // No computers found in drone discovery data
        }
        
        // Activate sniffer
        const newActiveSniffers = new Set(droneSniffersActive);
        newActiveSniffers.add(droneId);
        setDroneSniffersActive(newActiveSniffers);

        // Calculate total targets found from discovery data
        const totalTargets = drone.discoveryData.length;

        addNotification({
          title: '✈️ Drone Sniffer Activated',
          message: `Drone detected ${totalTargets} targets within ${(drone.type === 'maritime_scout') ? '50km' : '3km'} radius.`,
          type: 'success'
        });

      } catch (error) {
        console.error('Error activating drone sniffer:', error);
        addNotification({
          title: '❌ Drone Sniffer Error',
          message: 'Failed to activate drone sniffer.',
          type: 'error'
        });
      }
    }
  };

  // Place computer at clicked location
  const placeComputer = async (lat, lng) => {
    if (isInJail()) {
      alert(`🔒 You cannot place your computer while in jail!\n\nJail time remaining: ${getJailTimeRemaining()} minutes`);
      return;
    }
    
    // Show countdown popup for first-time deployment
    setDeployCountdownData({ lat, lng });
    setDeployCountdownTimer(10);
    setShowDeployCountdown(true);
    
    // Execute computer deployment after a short delay (while popup is visible)
    setTimeout(async () => {
      await executeComputerDeployment(lat, lng);
    }, 500);
    
    // Start countdown timer
    const countdownInterval = setInterval(() => {
      setDeployCountdownTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setShowDeployCountdown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const executeComputerDeployment = async (lat, lng) => {
    try {
      const response = await api.post('/treasure-hunt/computers/place', { lat, lng });

      if (response.status === 200) {
        const data = response.data;
        console.log('Computer deployed successfully:', data.message);
        setIsPlacingComputer(false);
        
        // Show success notification
        addNotification({
          title: '🖥️ Computer Deployed!',
          message: `Computer successfully deployed at ${lat.toFixed(4)}, ${lng.toFixed(4)}`
        });
        
        // Force complete map refresh to show new computer location
        await loadTreasureHuntData();
        fetchUserComputer();
        fetchComputerLocations();
      } else {
        const errorData = response.data;
        console.error('Error deploying computer:', errorData.message);
        
        // Cancel countdown popup if water placement was attempted
        if (errorData.message && errorData.message.includes('water')) {
          setShowDeployCountdown(false);
          setDeployCountdownData(null);
          setDeployCountdownTimer(10); // Reset timer
        }
        
        addNotification({
          title: '❌ Deployment Failed',
          message: errorData.message || 'Failed to deploy computer'
        });
      }
    } catch (error) {
      console.error('Error deploying computer:', error);
      addNotification({
        title: '❌ Deployment Error',
        message: 'Network error occurred while deploying computer'
      });
    }
  };

  // Move computer to clicked location with countdown popup
  const moveComputer = async (lat, lng) => {
    if (isInJail()) {
      alert(`🔒 You cannot move your computer while in jail!\n\nJail time remaining: ${getJailTimeRemaining()} minutes`);
      return;
    }
    
    // Check if user has available moves before starting countdown
    if (!hasAvailableMoves()) {
      // Check if daily moves are exhausted
      const movesUsedToday = user?.dailyComputerMoves?.moves || 0;
      const remainingDailyMoves = 10 - movesUsedToday;
      
      if (remainingDailyMoves <= 0) {
        const hasSupplies = (user?.foodDrink?.currentSupplies || 0) > 0;
        const emergencyMovesUsed = user?.survivalMoves?.emergencyMoves || 0;
        
        if (!hasSupplies && emergencyMovesUsed >= 3) {
          addNotification({
            type: 'error',
            category: 'computer',
            title: '❌ No Movement Available',
            message: 'You have used all 10 daily moves and all 3 emergency moves. Purchase Food/Drink supplies from shops to restore movement.',
            icon: '🚫',
            color: '#f44336'
          });
        } else {
          addNotification({
            type: 'error',
            category: 'computer', 
            title: '❌ Daily Moves Exhausted',
            message: 'You have used all 10 daily moves. Purchase Food/Drink supplies from shops for additional movement.',
            icon: '🛒',
            color: '#f44336'
          });
        }
      } else {
        // This shouldn't happen if hasAvailableMoves() is working correctly
        addNotification({
          type: 'error',
          category: 'computer',
          title: '❌ Movement Error',
          message: 'Unable to move computer. Please try again.',
          icon: '⚠️',
          color: '#f44336'
        });
      }
      return;
    }
    
    // Show countdown popup only if payment validation passes
    setMoveCountdownData({ lat, lng });
    setMoveCountdownTimer(10);
    setShowMoveCountdown(true);
    
    // Execute computer move after a short delay (while popup is visible)
    setTimeout(async () => {
      await executeComputerMove(lat, lng);
    }, 500); // Move after 0.5 seconds of popup being visible
    
    // Start countdown timer
    const countdownInterval = setInterval(() => {
      setMoveCountdownTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setShowMoveCountdown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Execute the actual computer move
  const executeComputerMove = async (lat, lng) => {
    try {
      const response = await api.post('/treasure-hunt/computers/move', { lat, lng });

      if (response.status === 200) {
        const data = response.data;
        console.log('Computer moved successfully:', data.message);
        setIsMovingComputer(false);
        
        // Auto-disable sniffer after computer movement
        if (snifferActive) {
          setSnifferActive(false);
          addNotification({
            type: 'info',
            title: '📡 Sniffer Disabled',
            message: 'Computer movement has disabled the sniffer. Click your computer to reactivate.',
            icon: '🔄'
          });
        }
        
        // First, refresh user data to get updated supply counts from server
        await updateUser();
        
        // Then update computer location data with fresh user context
        await fetchUserComputer();
        await fetchComputerLocations();
        
        // Add a small delay to ensure XP crates are fully spawned on server
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Then load basic treasure hunt data with forced refresh
        setTreasureHuntData(null); // Force clear state
        await loadTreasureHuntData();
        
        // Additional delay to ensure state propagation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Finally, refresh ALL sniffer radius features with the new computer position
        // Pass the coordinates directly to avoid any state timing issues
        await refreshSnifferRadiusFeatures(lat, lng);
        
        // Also ensure we refresh any other location-dependent data
        if (snifferActive) {
          await refreshSnifferRadiusElements();
        }
        
        // Send notification about successful move with movement info
        const movementType = data.data?.movement?.type || 'unknown';
        const movesRemaining = data.data?.movement?.movesRemaining || 0;
        const suppliesUsed = movementType === 'supplies';
        
        addNotification({
          type: 'success',
          category: 'computer',
          title: '🚚 Computer Moved Successfully',
          message: data.message || 'Your computer has been relocated to the new position',
          details: `Used 1 ${suppliesUsed ? `supply. Supplies remaining: ${movesRemaining}/10` : `emergency move. Emergency moves remaining: ${movesRemaining}/3`}`,
          icon: suppliesUsed ? '🍔' : '🚨',
          color: suppliesUsed ? '#4CAF50' : '#FF9800'
        });
        
        // Refresh businesses to ensure user's own businesses remain visible after movement
        await fetchBusinesses();
        
      } else {
        const errorData = response.data;
        console.error('Error moving computer:', errorData.message);
        
        // Cancel countdown popup if water placement was attempted
        if (errorData.message && errorData.message.includes('water')) {
          setShowMoveCountdown(false);
          setMoveCountdownData(null);
          setMoveCountdownTimer(10); // Reset timer
        }
        
        alert(errorData.message || 'Failed to move computer. Please try again.');
      }
    } catch (error) {
      console.error('Error moving computer:', error.response?.data?.message || error.message);
      const errorMessage = error.response?.data?.message || 'Failed to move computer. Please check your connection and try again.';
      alert(errorMessage);
    }
  };

  // Handle arrest escape by moving computer
  const escapeArrest = async (lat, lng) => {
    try {
      setIsEscapeMode(false); // Exit escape mode immediately
      
      // Show escape attempt message
      addNotification({
        title: '🏃 ESCAPE ATTEMPT!',
        message: 'Attempting to move computer to escape arrest...',
        type: 'info'
      });

      // Use the existing computer move endpoint
      const response = await api.post('/treasure-hunt/computers/move', { lat, lng });

      const data = response.data;

      if (response.status === 200) {
        // Success - escape worked
        setArrestPending(null);
        setEscapeTimeRemaining(0);
        setShowArrestModal(false);
        
        addNotification({
          title: '✅ ESCAPE SUCCESSFUL!',
          message: 'You successfully moved your computer and escaped arrest!',
          type: 'success'
        });

        // Refresh user data to reflect updated move supply counts
        await updateUser();
        
        // Add delay to ensure XP crates are spawned on server
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh treasure hunt data to load newly spawned XP crates
        setTreasureHuntData(null); // Force clear state
        await loadTreasureHuntData();
        
        // Refresh businesses to ensure user's own businesses remain visible after escape
        await fetchBusinesses();
        
        // Refresh sniffer radius features after escape
        await refreshSnifferRadiusFeatures();
      } else {
        // Failed to move
        addNotification({
          title: '❌ ESCAPE FAILED!',
          message: data.message || 'Failed to move computer. You are still under arrest!',
          type: 'error'
        });
        setIsEscapeMode(true); // Re-enable escape mode
      }
    } catch (error) {
      console.error('Error escaping arrest:', error);
      addNotification({
        title: '❌ ESCAPE ERROR!',
        message: 'Error trying to escape. Please try again!',
        type: 'error'
      });
      setIsEscapeMode(true); // Re-enable escape mode
    }
  };

  // Move computer to job location
  const moveComputerToJob = async () => {
    if (!careerData.userJob) {
      addNotification({
        title: '❌ No Job Found',
        message: 'You must apply for a job first before moving to a job location.',
        type: 'error'
      });
      return;
    }

    if (isInJail()) {
      addNotification({
        title: '🔒 Cannot Move',
        message: 'You cannot move your computer while in jail!',
        type: 'error'
      });
      return;
    }

    if (!hasAvailableMoves()) {
      addNotification({
        title: '🔒 No Moves Available',
        message: 'You have no computer moves remaining. Wait for reset or use supplies.',
        type: 'error'
      });
      return;
    }

    // Find the office location
    const office = careerData.offices?.find(o => o._id === careerData.userJob.officeId);
    if (!office) {
      addNotification({
        title: '❌ Office Not Found',
        message: 'Could not find your job office location.',
        type: 'error'
      });
      return;
    }

    const confirmation = window.confirm(
      `Move your computer to ${office.officeName} in ${office.city}, ${office.country}?\n\nThis will use 1 computer move and position you near your workplace so you can start your work shift.`
    );

    if (!confirmation) {
      return;
    }

    try {
      setIsMovingComputer(true);
      
      const response = await api.post('/treasure-hunt/computers/move', {
          lat: office.location.lat,
          lng: office.location.lng
        });

      if (response.status === 200) {
        const data = response.data;
        
        // Update user state
        updateUser({ 
          wrCoins: data.data?.userBalance || user.wrCoins,
          dailyComputerMoves: data.data?.dailyComputerMoves || user.dailyComputerMoves,
          foodDrink: data.data?.foodDrink || user.foodDrink,
          survivalMoves: data.data?.survivalMoves || user.survivalMoves,
          computerLocation: data.data?.newComputerLocation || user.computerLocation
        });

        // Update local computer state
        setUserComputer(prev => ({
          ...prev,
          coordinates: {
            lat: office.location.lat,
            lng: office.location.lng
          }
        }));

        // Update map view
        setMapCenter([office.location.lat, office.location.lng]);
        setMapZoom(12);

        // Add delay to ensure XP crates are spawned on server
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Refresh computer locations and status with forced treasure hunt refresh
        setTreasureHuntData(null); // Force clear state
        await Promise.all([
          fetchComputerLocations(),
          loadTreasureHuntData()
        ]);

        // Refresh businesses to ensure user's own businesses remain visible after job move
        await fetchBusinesses();

        // Refresh sniffer radius features at new job location
        await refreshSnifferRadiusFeatures(office.location.lat, office.location.lng);

        addNotification({
          type: 'success',
          title: '💼 Moved to Job Location!',
          message: `Computer successfully moved to ${office.officeName}. You can now start your work shift!`,
          icon: '🏢'
        });

      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Move Failed',
          message: errorData.message || 'Failed to move computer to job location.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error moving computer to job:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to move computer. Please check your connection.',
        type: 'error'
      });
    } finally {
      setIsMovingComputer(false);
    }
  };

  // Helper function to check if today is a work day (Monday-Thursday)
  const isWorkDay = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
    return dayOfWeek >= 1 && dayOfWeek <= 4; // Monday (1) through Thursday (4)
  };

  // Start Field Operations (Field Agents only)
  const startFieldOperations = async () => {
    if (!userComputer?.coordinates) {
      addNotification({
        title: '❌ Computer Required',
        message: 'You must deploy your computer first.',
        type: 'error'
      });
      return;
    }

    // Check work day before making API call
    if (!isWorkDay()) {
      addNotification({
        title: '❌ Work Day Restriction',
        message: 'Field operations can only be conducted Monday through Thursday.',
        type: 'error'
      });
      return;
    }

    try {
      const response = await api.post('/career/start-field-ops', {
          lat: userComputer.coordinates.lat,
          lng: userComputer.coordinates.lng
        });

      if (response.status === 200) {
        const data = response.data;
        setFieldOperation(data.data);
        
        // Refresh computer locations to show black suit icon
        fetchComputerLocations();
        
        addNotification({
          title: '🤵 Field Operations Started',
          message: 'You are now on active field duty with special agent abilities!',
          type: 'success'
        });
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Cannot Start Operations',
          message: errorData.message || 'Failed to start field operations.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error starting field operations:', error);
      
      // Check if it's a 400 error with a specific message from server
      const errorMessage = error.response?.data?.message || 'Failed to start field operations. Please try again.';
      
      addNotification({
        title: '❌ Field Operations Error',
        message: errorMessage,
        type: 'error'
      });
    }
  };

  // Complete Field Operations
  const completeFieldOperations = async () => {
    try {
      const response = await api.post('/career/complete-field-ops', {});

      if (response.status === 200) {
        const data = response.data;
        setFieldOperation(null);
        
        // Refresh computer locations to remove black suit icon
        fetchComputerLocations();
        
        addNotification({
          title: '💼 Field Operations Complete',
          message: data.message || 'Field operations completed successfully!',
          type: 'success'
        });
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Error',
          message: errorData.message || 'Failed to complete field operations.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error completing field operations:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to complete field operations. Please try again.',
        type: 'error'
      });
    }
  };

  // Arrest User (Field Agent special ability)
  const arrestUser = async (targetUser) => {
    if (!fieldOperation) {
      addNotification({
        title: '❌ Not On Duty',
        message: 'You must be on active field operations to make arrests.',
        type: 'error'
      });
      return;
    }

    try {
      const response = await api.post('/career/arrest-user', {
          targetUserId: targetUser.userId,
          lat: userComputer.coordinates.lat,
          lng: userComputer.coordinates.lng
        });

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          title: '🚨 Arrest Made',
          message: data.message || `Successfully arrested ${targetUser.username}`,
          type: 'success'
        });
        
        // Refresh map data
        fetchComputerLocations();
        setSelectedUserForArrest(null);
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Arrest Failed',
          message: errorData.message || 'Failed to arrest user.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error arresting user:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to arrest user. Please try again.',
        type: 'error'
      });
    }
  };

  // Get User Active Hacks (Field Agent special ability)
  const getUserActiveHacks = async (targetUserId) => {
    if (!fieldOperation) {
      addNotification({
        title: '❌ Not On Duty',
        message: 'You must be on active field operations to view user data.',
        type: 'error'
      });
      return null;
    }

    try {
      const response = await api.get(`/career/get-user-hacks/${targetUserId}`);

      if (response.status === 200) {
        const data = response.data;
        return data.data;
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Access Denied',
          message: errorData.message || 'Failed to access user data.',
          type: 'error'
        });
        return null;
      }
    } catch (error) {
      console.error('Error getting user active hacks:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to get user data. Please try again.',
        type: 'error'
      });
      return null;
    }
  };

  // Process arrest timeout and jail the user
  const processArrestTimeout = async () => {
    try {
      // Check if arrest is still pending and time has actually expired
      if (!arrestPending) {
        console.log('No arrest pending, skipping timeout processing');
        return;
      }

      const now = new Date();
      const escapeDeadline = new Date(arrestPending.escapeDeadline);
      if (now < escapeDeadline) {
        console.log('Arrest time has not expired yet, skipping timeout processing');
        return;
      }

      // Prevent multiple calls
      setArrestPending(null);
      setEscapeTimeRemaining(0);
      setIsEscapeMode(false);
      setShowArrestModal(false);

      const response = await api.post('/career/process-arrest-timeout', {});

      const data = response.data;

      if (response.status === 200) {
        // Successfully jailed
        addNotification({
          title: '🚔 JAILED!',
          message: data.message || 'You have been jailed for failing to escape arrest.',
          type: 'error'
        });

        // Refresh user data to update jail status
        await loadTreasureHuntData();
      } else {
        // Error processing arrest
        addNotification({
          title: '❌ Arrest Processing Error',
          message: data.message || 'Failed to process arrest timeout.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error processing arrest timeout:', error);
      addNotification({
        title: '❌ System Error',
        message: 'Failed to process arrest. Please refresh the page.',
        type: 'error'
      });
    }
  };

  // Check for arrest pending status
  const checkArrestStatus = () => {
    if (user && user.arrestPending && user.arrestPending.isArrestPending) {
      const now = new Date();
      const escapeDeadline = new Date(user.arrestPending.escapeDeadline);
      const timeRemaining = Math.max(0, escapeDeadline - now);
      
      if (timeRemaining > 0) {
        setArrestPending(user.arrestPending);
        setEscapeTimeRemaining(timeRemaining);
        setShowArrestModal(true);
        
        // Show immediate notification if this is a new arrest
        if (!arrestPending) {
          addNotification({
            title: '🚨 ARREST WARRANT ISSUED!',
            message: `Field Agent ${user.arrestPending.arrestedBy} has issued an arrest warrant! You have 1 MINUTE to escape by moving your computer at least 50 MILES away or you will be jailed for 8 hours!`,
            type: 'error'
          });
        }
      } else {
        // Time expired, arrest should be processed
        setArrestPending(null);
        setEscapeTimeRemaining(0);
        setShowArrestModal(false);
      }
    } else {
      setArrestPending(null);
      setEscapeTimeRemaining(0);
      setShowArrestModal(false);
    }
  };

  // Fetch field operation status
  const fetchFieldOperationStatus = async () => {
    try {
      const response = await api.get('/career/field-ops-status');

      if (response.status === 200) {
        const data = response.data;
        const hasActiveOperation = (data && data.data && data.data.hasActiveOperation);
        const operation = (data && data.data && data.data.operation) ? data.data.operation : null;
        setFieldOperation(hasActiveOperation ? operation : null);
      }
    } catch (error) {
      console.error('Error fetching field operation status:', error);
    }
  };

  // Handle purchasing supplies from farm
  const handlePurchaseFromFarm = async (farmId, quantity, farmName) => {
    // Check actual supplies for purchasing
    const currentSupplies = user?.foodDrink?.currentSupplies || 0;
    const maxSupplies = 10;
    
    if (currentSupplies >= maxSupplies) {
      addNotification({
        title: '❌ Max Supplies Reached',
        message: `You already have the maximum ${maxSupplies} supplies. Use some supplies before purchasing more.`,
        type: 'error'
      });
      return;
    }
    
    // Check if purchase would exceed maximum supplies
    if (currentSupplies + quantity > maxSupplies) {
      const availableSpace = maxSupplies - currentSupplies;
      addNotification({
        title: '❌ Purchase Would Exceed Limit',
        message: `You can only purchase ${availableSpace} more supplies (currently have ${currentSupplies}/${maxSupplies} supplies).`,
        type: 'error'
      });
      return;
    }
    
    try {
      const response = await api.post('/business/purchase-from-farm', { 
          farmId: farmId, 
          suppliesAmount: quantity 
        });

      const data = response.data;

      if (response.status === 200) {
        addNotification({
          title: '✅ Farm Supplies Purchased',
          message: `Purchased ${quantity} supplies from ${farmName} for ${data.data.totalCost} WR coins`,
          type: 'success'
        });

        // Update user data to reflect new supplies and balance
        await updateUser({
          ...user,
          wrCoins: data.data.userBalance,
          foodDrink: {
            ...user.foodDrink,
            currentSupplies: data.data.currentSupplies,
            lastPurchase: new Date(),
            totalPurchased: (user.foodDrink?.totalPurchased || 0) + data.data.suppliesPurchased
          }
        });

        // Refresh treasure hunt data to update map display
        await loadTreasureHuntData();

        // Refresh businesses to update UI
        fetchBusinesses();
      } else {
        console.error('Farm purchase failed with status:', response.status);
        console.error('Error response:', data);
        addNotification({
          title: '❌ Farm Purchase Failed',
          message: data.message || 'Unknown error occurred',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error purchasing from farm:', error);
      addNotification({
        title: '❌ Farm Purchase Error',
        message: 'Failed to purchase supplies from farm. Please try again.',
        type: 'error'
      });
    }
  };

  // Get target business type for linking (supply chain hierarchy)
  const getTargetBusinessType = (businessType) => {
    switch(businessType) {
      case 'farm': return 'factory';
      case 'factory': return 'warehouse';
      case 'warehouse': return 'shop';
      default: return null;
    }
  };

  // Handle business linking for user's own businesses
  const handleBusinessLinking = async (sourceBusiness, targetBusiness) => {
    try {
      const response = await api.post('/business/create-link-request', {
          sourceBusinessId: sourceBusiness._id,
          targetBusinessId: targetBusiness._id,
          proposedPrice: 25, // Default price for now
          linkType: 'supplier', // Source business wants to be a supplier to target business
          message: `Link request from ${sourceBusiness.businessName} to ${targetBusiness.businessName}`
        });

      if (response.status === 200) {
        const data = response.data;
        addNotification({
          title: '🔗 Link Request Sent',
          message: `Link request sent from ${sourceBusiness.businessName} to ${targetBusiness.businessName}`,
          type: 'success'
        });
        
        // Close modal
        setBusinessLinkingModal({ show: false, sourceBusiness: null, targetBusinessType: null });
        
        // Refresh business data
        fetchBusinesses();
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Link Failed',
          message: errorData.message || 'Failed to create business link',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error linking businesses:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to link businesses. Please try again.',
        type: 'error'
      });
    }
  };

  // Check if user is actively working (computer should be invisible)
  const isActivelyWorking = () => {
    return workSession && workSession.active && !workSession.paused;
  };

  // Pause work session
  const pauseWorkSession = () => {
    if (!workSession || !workSession.active) return;
    
    const now = Date.now();
    const sessionElapsed = now - workSession.startTime;
    
    setWorkSession(prev => ({
      ...prev,
      paused: true,
      totalWorkedTime: prev.totalWorkedTime + sessionElapsed,
      pausedAt: now
    }));
    
    // Return computer to office location when leaving work
    setUserComputer({
      userId: user.id,
      username: user.username,
      coordinates: {
        lat: workSession.office.location.lat,
        lng: workSession.office.location.lng
      },
      placedAt: new Date().toISOString(),
      status: 'active'
    });
    
    fetchUserComputer(); // Refresh computer data
    
    addNotification({
      title: '⏸️ Work Paused',
      message: `Left ${workSession.office.officeName}. Progress saved. Computer returned to office.`,
      type: 'info'
    });
  };

  // Resume work session
  const resumeWorkSession = async () => {
    if (!workSession || workSession.active || !workSession.paused) return;
    
    const now = Date.now();
    
    // Check if deadline has passed while paused
    if (now > workSession.deadline) {
      addNotification({
        title: '⏰ Shift Expired!',
        message: `Work shift deadline exceeded while paused. No payment awarded.`,
        type: 'error'
      });
      
      setWorkSession(null);
      return;
    }
    
    // Check work day before making API call
    if (!isWorkDay()) {
      addNotification({
        title: '❌ Work Day Restriction',
        message: 'You can only resume work Monday through Thursday.',
        type: 'error'
      });
      return;
    }
    
    try {
      // Restart work session via API
      const response = await api.post('/career/start-work', {
          lat: userComputer.coordinates.lat,
          lng: userComputer.coordinates.lng
        });
      
      if (response.status === 200) {
        setWorkSession(prev => ({
          ...prev,
          active: true,
          paused: false,
          startTime: now // Reset start time for current session
        }));
        
        // Computer will be hidden by filtering logic during active work
        
        addNotification({
          title: '▶️ Work Resumed',
          message: `Resumed work at ${workSession.office.officeName}. Timer continues from where you left off.`,
          type: 'success'
        });
      } else {
        const errorData = response.data;
        addNotification({
          title: '❌ Cannot Resume Work',
          message: errorData.message || 'Failed to resume work session.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error resuming work:', error);
      
      // Check if it's a 400 error with a specific message from server
      const errorMessage = error.response?.data?.message || 'Failed to resume work. Please check your connection.';
      
      addNotification({
        title: '❌ Resume Work Error',
        message: errorMessage,
        type: 'error'
      });
    }
  };

  // Refresh sniffer radius elements (banks, casinos, ATMs, vaults)
  // Refresh sniffer radius features after computer movement (always update, regardless of sniffer status)
  const refreshSnifferRadiusFeatures = async (customLat = null, customLng = null) => {
    if (!userComputer && !customLat && !customLng) {
      return;
    }
    
    try {
      const radius = calculateSnifferRadius(); // Calculate radius based on user's sniffer parts
      const lat = customLat || userComputer?.coordinates?.lat;
      const lng = customLng || userComputer?.coordinates?.lng;
      
      if (!lat || !lng) {
        console.warn('No valid coordinates for refreshing sniffer radius features');
        return;
      }
      
      // Use unified entities endpoint for comprehensive detection (includes drones and admin-created features)
      const entitiesResponse = await api.get(`/scanner/entities-in-radius?lat=${lat}&lng=${lng}&radius=${radius}`);
      
      if (entitiesResponse.data?.success) {
        const entities = entitiesResponse.data.entities || [];
        
        // Separate entities by type and set them in their respective state variables
        const banks = entities.filter(e => e.type === 'bank');
        const casinos = entities.filter(e => e.type === 'casino');
        const atms = entities.filter(e => e.type === 'atm');
        const businesses = entities.filter(e => e.type === 'business');
        const houses = entities.filter(e => e.type === 'house');
        const drones = entities.filter(e => e.type === 'drone');
        const vaults = entities.filter(e => e.type === 'atm_vault');
        const defenseTowers = entities.filter(e => e.type === 'defense_tower');
        const commandCenters = entities.filter(e => e.type === 'command_center');
        const hotels = entities.filter(e => e.type === 'hotel');
        const users = entities.filter(e => e.type === 'computer');
        const communityTasks = entities.filter(e => e.type === 'user_task');
        const satellites = entities.filter(e => e.type === 'satellite');
        
        // Process vaults to match expected format for rendering
        const processedVaults = vaults.map(vault => ({
          ...vault,
          coordinates: vault.position, // Map position to coordinates for rendering
          storedBalance: vault.balance || 0,
          atmCodeCount: vault.atmCodeCount || 0,
          vaultId: vault.vaultId || vault._id
        }));
        
        // Process hotels to match expected format for rendering
        const processedHotels = hotels.map(hotel => ({
          ...hotel,
          coordinates: hotel.position // Map position to coordinates for rendering
        }));
        
        // Set all entity types
        setBanks(banks);
        setCasinos(casinos);
        setAtms(atms);
        setBusinesses(businesses);
        setHouses(houses);
        setDeployedDrones(drones);
        setHotels(processedHotels);
        setUsersInRadius(users);
        setCommunityTasks(communityTasks);
        setSatellites(satellites);
        setNearbyDefenseTowers(defenseTowers);
        setNearbyCompounds(commandCenters.map(cc => ({ commandCenter: cc })));
        
        // Update sniffer-specific state if sniffer is active
        if (snifferActive) {
          setNearbyCasinos(casinos);
          setNearbyBanks(banks);
          setNearbyATMs(atms);
          setNearbyVaults(processedVaults);
        } else {
          // Only update vault state if sniffer is not active
          setNearbyVaults(processedVaults);
        }
        
      }
      
      // Also refresh user computer locations with new coordinates
      await fetchComputerLocations(lat, lng);
      
      // Sniffer radius features refreshed after computer movement
    } catch (error) {
      console.error('Error refreshing sniffer radius features:', error);
    }
  };

  const refreshSnifferRadiusElements = async () => {
    if (!userComputer || !snifferActive) return;
    
    try {
      const radius = calculateSnifferRadius(); // Calculate radius based on user's sniffer parts
      const { lat, lng } = userComputer.coordinates;
      
      // Refresh all sniffer-dependent elements in parallel
      await Promise.all([
        // Refresh banks in radius
        api.get(`/treasure-hunt/banks/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`).then(res => res.status === 200 ? res.data : [])
          .then(data => setNearbyBanks(data || [])),
        
        // Refresh casinos in radius
        api.get(`/treasure-hunt/casinos/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`).then(res => res.status === 200 ? res.data : [])
          .then(data => setNearbyCasinos(data || [])),
        
        // Refresh ATMs in radius
        api.get(`/treasure-hunt/atms/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`).then(res => res.status === 200 ? res.data : [])
          .then(data => setNearbyATMs(data || [])),
        
        // Vaults are handled by the unified entities endpoint - no separate call needed
        
        // Refresh users in radius
        api.get(`/treasure-hunt/users-in-radius?lat=${lat}&lng=${lng}&radius=${radius}`).then(res => res.status === 200 ? res.data : null)
          .then(data => data?.success ? setUsersInRadius(data.users || []) : null),
        
        // Refresh hotels in radius
        fetchHotels(),
        
        // Refresh community tasks in radius
        fetchCommunityTasks(),
        
        // Refresh compounds and defense towers in radius
        api.get(`/compound-defense/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`)
          .then(response => {
            const data = response.data;
            if (data?.success) {
              setNearbyCompounds(data.compounds || []);
              setNearbyDefenseTowers(data.defenseTowers || []);
            }
          }),
        
        // Refresh houses in radius
        api.get(`/house/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`).then(res => res.status === 200 ? res.data : null)
          .then(data => setHouses(data?.houses || [])),
        
        // Refresh businesses in radius
        api.get(`/business/in-radius?lat=${lat}&lng=${lng}&radius=${radius}`).then(res => res.status === 200 ? res.data : null)
          .then(data => setBusinesses(data?.businesses || [])),
        
        // Refresh deployed drones
        fetchDeployedDrones(),
        
        // Refresh user computer locations
        fetchComputerLocations(),
        
        // Refresh active animations within sniffer radius
        loadActiveAttackAnimations()
      ]);
      
      console.log('Sniffer radius elements refreshed after computer movement - including houses, businesses, drones, and computers');
    } catch (error) {
      console.error('Error refreshing sniffer radius elements:', error);
    }
  };

  // Refresh all global map features (not limited to sniffer radius)
  const refreshAllGlobalFeatures = async () => {
    try {
      console.log('Refreshing all global map features after movement...');
      
      // Refresh all global features in parallel
      await Promise.all([
        // Always refresh basic map data
        loadTreasureHuntData(),
        
        // Refresh user's own data
        fetchUserVaults(),
        fetchBusinesses(), // User's own businesses
        loadCompoundData(),
        
        // Refresh career/office data
        fetchCareerData(),
        
        // Refresh houses (user's own + rented)
        fetchHouses(),
        
        // If sniffer is active, also refresh radius-specific data
        snifferActive ? refreshSnifferRadiusElements() : Promise.resolve(),
        
        // Refresh user protection status
        userCompound ? api.get('/compound-defense/protection-status')
          .then(response => {
            const data = response.data;
            if (data?.success) {
              console.log('Updated protection status:', data.protection);
            }
          })
          .catch(error => {
            console.error('Error updating protection status:', error);
          }) : Promise.resolve()
      ]);
      
      // All global map features refreshed successfully
    } catch (error) {
      console.error('Error refreshing global map features:', error);
    }
  };

  // Handle computer hacking
  const hackComputer = async (targetComputer) => {
    if (isInJail()) {
      alert(`🔒 You cannot hack computers while in jail!\n\nJail time remaining: ${getJailTimeRemaining()} minutes`);
      return;
    }
    
    try {
      const response = await api.post('/treasure-hunt/computers/hack', { 
          targetUserId: targetComputer.userId,
          targetUsername: targetComputer.username 
        });

      if (response.status === 200) {
        const data = response.data;
        
        // Show success popup notification
        addNotification({
          title: "🚀 Network Injection Started",
          message: `Network injection attack started on ${targetComputer.username}`,
          type: "success"
        });
        
        fetchNetworkAttackStatus();
        setSelectedComputer(null); // Close the modal
      } else {
        const errorData = response.data;
        
        // Check if user was jailed for attacking a Field Agent
        if (errorData.jailed) {
          addNotification({
            title: "🚨 ARRESTED!",
            message: errorData.message || "You have been arrested for attempting to hack a Field Agent computer!",
            type: "error"
          });
          
          // Refresh user data to show jail status
          if (user && user.updateUser) {
            user.updateUser();
          }
          
          // Refresh map to hide user's computer (jailed users can't see their computer)
          fetchComputerLocations();
          
        } else if (errorData.message && errorData.message.includes("already have an active attack")) {
          addNotification({
            title: "⚠️ Attack In Progress",
            message: "Attack in progress, check your tools",
            type: "warning"
          });
        } else {
          addNotification({
            title: "❌ Attack Failed",
            message: errorData.message || "Failed to start network attack",
            type: "error"
          });
        }
      }
    } catch (error) {
      console.error('Error hacking computer:', error);
      addNotification({
        title: "❌ Network Error",
        message: "Failed to hack computer. Please try again.",
        type: "error"
      });
    }
  };

  // Casino slot machine hacking
  const handleSlotHack = async (casino) => {
    return new Promise(async (resolve, reject) => {
      try {
        setSlotHackInProgress(true);
        console.log(`Starting slot machine hack at ${casino.name}...`);
        
        const response = await api.post('/treasure-hunt/casinos/slot-hack', { casinoId: casino.casinoId });

        if (response.status === 200) {
          const data = response.data;
          const hackData = data.data;
          
          console.log('Slot hack successful:', hackData);
          
          // Update user data in context immediately for responsive UI
          if (updateUser) {
            updateUser({
              wrCoins: hackData.remainingCoins,
              xp: hackData.totalXP
            }, true); // Skip sync to prevent conflicts
          }
          
          // Create detailed notification for slot machine hack result
          addNotification({
            type: 'slot_machine_hack',
            category: 'casino',
            title: hackData.success ? '🎰 Slot Machine Jackpot!' : '🎰 Slot Machine Attempt',
            message: hackData.success 
              ? `Won ${hackData.payout} WR Coins at ${casino.name}!`
              : `No win this time at ${casino.name}`,
            details: hackData.success 
              ? `Net payout: ${hackData.netPayout} WR (after 20% Occult Tax) | +${hackData.xpEarned} XP | Daily hacks: ${hackData.dailyHacksUsed}/10`
              : `+${hackData.xpEarned} XP earned | Daily hacks: ${hackData.dailyHacksUsed}/10`,
            icon: '🎰',
            color: hackData.success ? '#4CAF50' : '#FF9800'
          });
          
          // Add SWAT warning notification if triggered
          if (hackData.swatTriggered && hackData.swatWarning) {
            addNotification({
              type: 'swat_warning',
              category: 'security',
              title: '🚨 SWAT ALERT!',
              message: `SWAT detected your activity at ${casino.name}`,
              details: `Move 50+ miles within ${Math.floor(hackData.swatWarning.timeToEscape/60)}m ${hackData.swatWarning.timeToEscape%60}s to escape!`,
              icon: '🚔',
              color: '#F44336'
            });
          }
          
          // Update SWAT warnings in background without affecting UI
          try {
            await fetchSlotSwatWarnings();
          } catch (swatError) {
            console.warn('Could not update SWAT warnings:', swatError);
          }
          
          console.log(`Slot hack completed: ${data.message}`);
          setSlotHackInProgress(false);
          resolve(hackData);
          
        } else {
          const errorData = response.data;
          console.error(`Slot hack failed: ${errorData.message}`);
          
          // Add error notification
          addNotification({
            type: 'slot_machine_error',
            category: 'casino',
            title: '🎰 Slot Machine Error',
            message: `Failed to hack ${casino.name}`,
            details: errorData.message,
            icon: '❌',
            color: '#F44336'
          });
          
          setSlotHackInProgress(false);
          reject(new Error(errorData.message));
        }
      } catch (error) {
        console.error('Error hacking slot machines:', error);
        
        // Add network error notification
        addNotification({
          type: 'slot_machine_error',
          category: 'casino',
          title: '🎰 Connection Error',
          message: `Unable to connect to ${casino.name}`,
          details: 'Network error occurred. Please try again.',
          icon: '🔌',
          color: '#F44336'
        });
        
        setSlotHackInProgress(false);
        reject(error);
      }
    });
  };

  // Casino vault hacking - Start security injection
  const handleVaultHackStart = async (casino) => {
    try {
      // Show hack initialized notification immediately
      addNotification({
        type: 'vault_hack_initialized',
        category: 'casino',
        title: '🔓 Vault Hack Initialized',
        message: `Security injection started at ${casino.name}`,
        details: 'Network injection tool is compromising security systems...',
        icon: '⚡',
        color: '#FF9800'
      });

      const response = await api.post('/treasure-hunt/casinos/vault/start', { casinoId: casino.casinoId });

      if (response.status === 200) {
        const data = response.data;
        console.log(data.message);
        fetchVaultHackProgress();
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
        
        // Show user notification for specific errors
        if (errorData.message && errorData.message.includes('Network Injection Tool')) {
          addNotification({
            title: '🔧 Missing Required Tools',
            message: 'You need Network Injection Tools to hack casino vaults. Go to System → Parts → Network Parts to purchase them.',
            type: 'error'
          });
        } else {
          addNotification({
            title: '❌ Vault Hack Failed',
            message: errorData.message || 'Unable to start vault hacking. Please try again.',
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error starting vault hack:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to start vault hack. Please check your connection and try again.',
        type: 'error'
      });
    }
  };

  // Casino vault hacking - Start malware stage
  const handleMalwareStage = async (casinoId) => {
    try {
      const response = await api.post('/treasure-hunt/casinos/vault/malware', { casinoId });

      if (response.status === 200) {
        const data = response.data;
        console.log(data.message);
        fetchVaultHackProgress();
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
        
        // Show user notification for malware stage errors
        if (errorData.message && errorData.message.includes('Malware Tool')) {
          addNotification({
            title: '🦠 Missing Malware Tools',
            message: 'You need Malware Tools to proceed with casino vault hacking. Go to System → Parts → Malware Parts to purchase them.',
            type: 'error'
          });
        } else {
          addNotification({
            title: '❌ Malware Stage Failed',
            message: errorData.message || 'Unable to start malware stage. Please try again.',
            type: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error starting malware stage:', error);
      addNotification({
        title: '❌ Network Error',
        message: 'Failed to start malware stage. Please check your connection and try again.',
        type: 'error'
      });
    }
  };

  // Casino vault hacking - Start timer looting
  const handleTimerLooting = async (casinoId) => {
    try {
      const response = await api.post('/malware/stage3/start', { casinoId });

      if (response.status === 200) {
        const data = response.data;
        console.log(data.message);
        fetchVaultHackProgress();
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error starting timer looting:', error);
      console.log('Failed to start timer looting. Please try again.');
    }
  };

  // Stop casino attack
  const handleStopAttack = async (casinoId) => {
    try {
      const response = await api.post('/treasure-hunt/casinos/vault/stop-attack', { casinoId });

      if (response.status === 200) {
        const data = response.data;
        console.log(data.message);
        fetchVaultHackProgress();
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error stopping attack:', error);
      console.log('Failed to stop attack. Please try again.');
    }
  };

  // Handle movement supplies purchase
  const handlePurchaseMovementSupplies = async (shopBusinessId, supplyAmount, shopName) => {
    try {
      const response = await api.post('/business/purchase-movement-supplies', { 
          shopBusinessId: shopBusinessId, 
          supplyAmount: supplyAmount 
        });

      const data = response.data;

      if (response.status === 200) {
        addNotification({
          title: '✅ Supplies Purchased',
          message: `Purchased ${data.purchase.supplies} movement supplies from ${shopName} for ${data.purchase.cost} WR coins`,
          type: 'success'
        });

        // Update user data to reflect new supplies and balance
        updateUser({
          wrCoins: user.wrCoins - data.purchase.cost,
          movementSupplies: {
            ...user.movementSupplies,
            currentSupplies: data.purchase.newSupplyCount,
            lastPurchase: new Date(),
            totalPurchased: (user.movementSupplies?.totalPurchased || 0) + data.purchase.supplies
          }
        });

        // Refresh businesses to update UI
        fetchBusinesses();
      } else {
        addNotification({
          title: '❌ Purchase Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error purchasing movement supplies:', error);
      addNotification({
        title: '❌ Purchase Error',
        message: 'Failed to purchase supplies. Please try again.',
        type: 'error'
      });
    }
  };

  // New user-to-user shop purchase function
  const handlePurchaseFromUserShop = async (shopBusinessId, quantity, shopName) => {
    // Check actual supplies for purchasing
    const currentSupplies = user?.foodDrink?.currentSupplies || 0;
    const maxSupplies = 10;
    
    if (currentSupplies >= maxSupplies) {
      addNotification({
        title: '❌ Max Supplies Reached',
        message: `You already have the maximum ${maxSupplies} supplies. Use some supplies before purchasing more.`,
        type: 'error'
      });
      return;
    }
    
    // Check if purchase would exceed maximum supplies
    if (currentSupplies + quantity > maxSupplies) {
      const availableSpace = maxSupplies - currentSupplies;
      addNotification({
        title: '❌ Purchase Would Exceed Limit',
        message: `You can only purchase ${availableSpace} more supplies (currently have ${currentSupplies}/${maxSupplies} supplies).`,
        type: 'error'
      });
      return;
    }
    
    try {
      const response = await api.post('/business/purchase-from-user-shop', { 
        shopBusinessId: shopBusinessId, 
        supplyAmount: quantity 
      });

      const data = response.data;

      if (response.status === 200) {
        addNotification({
          title: '✅ Supplies Purchased',
          message: `Purchased ${quantity} supplies from ${shopName} for ${data.totalCost} WR coins`,
          type: 'success'
        });

        // Update user data to reflect new supplies and balance
        await updateUser({
          ...user,
          wrCoins: data.userBalance,
          foodDrink: {
            ...user.foodDrink,
            currentSupplies: data.currentSupplies,
            lastPurchase: new Date(),
            totalPurchased: (user.foodDrink?.totalPurchased || 0) + data.suppliesPurchased
          }
        });

        // Add a small delay to ensure database changes are committed
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Refresh treasure hunt data to update map display
        await loadTreasureHuntData();

        // Refresh businesses to update UI
        fetchBusinesses();
      } else {
        console.error('Purchase failed with status:', response.status);
        console.error('Error response:', data);
        addNotification({
          title: '❌ Purchase Failed',
          message: data.message || 'Unknown error occurred',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error purchasing from user shop:', error);
      const errorMessage = error.response?.data?.message || 'Failed to purchase supplies. Please try again.';
      addNotification({
        title: '❌ Purchase Error',
        message: errorMessage,
        type: 'error'
      });
    }
  };

  // Handle business linking
  const handleBusinessLink = async (supplierBusinessId, supplierBusinessName) => {
    try {
      const response = await api.post('/business/link', { 
          supplierBusinessId: supplierBusinessId
        });

      const data = response.data;

      if (response.status === 200) {
        addNotification({
          title: '✅ Business Linked',
          message: `Successfully linked with ${supplierBusinessName}`,
          type: 'success'
        });

        // Refresh businesses to update UI
        fetchBusinesses();
      } else {
        addNotification({
          title: '❌ Link Failed',
          message: data.message,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error linking business:', error);
      addNotification({
        title: '❌ Link Error',
        message: 'Failed to link business. Please try again.',
        type: 'error'
      });
    }
  };

  // Fetch vault hack progress
  const fetchVaultHackProgress = async () => {
    // Only poll if user potentially has active casino activities
    if (!user?.casinoHack?.isActive && !user?.activeVaultHacks?.length) {
      return; // Skip polling if no active casino activities
    }
    
    try {
      const response = await api.get('/treasure-hunt/casinos/vault/status');

      if (response.status === 200) {
        const data = response.data;
        const activeVaultHacks = (data && data.data && data.data.activeVaultHacks) ? data.data.activeVaultHacks : [];
        let vaultHacks = activeVaultHacks;
        
        // Use fresh casinoHack data from server instead of stale user context
        const freshCasinoHack = (data && data.data && data.data.casinoHack) ? data.data.casinoHack : null;
        if (freshCasinoHack && freshCasinoHack.isActive) {
          const casinoHack = freshCasinoHack;
          
          // Update user context with fresh casinoHack data
          updateUser({
            casinoHack: freshCasinoHack
          }, true); // Skip sync to prevent conflicts
          
          // Convert new casinoHack format to old activeVaultHacks format for UI compatibility
          const convertedHack = {
            casinoId: casinoHack.casinoName ? casinoHack.casinoName.replace(/^casino/, '') : 'unknown',
            stage: casinoHack.currentStage === 1 ? 'network_injection' : 
                   casinoHack.currentStage === 2 ? 'malware' : 
                   casinoHack.currentStage === 3 ? 'looting' : 'security',
            startTime: casinoHack.startTime,
            estimatedCompletion: casinoHack.completionTime,
            stage1Complete: casinoHack.currentStage >= 2,
            stage2Complete: casinoHack.currentStage >= 3,
            casinoEmployeeEmail: casinoHack.employeeEmail,
            swatChance: casinoHack.swatChance || 0,
            lastCoinTime: casinoHack.lastCoinTime
          };
          
          // Check if this casino hack already exists in activeVaultHacks to avoid duplicates
          const existingHackIndex = vaultHacks.findIndex(hack => hack.casinoId === convertedHack.casinoId);
          if (existingHackIndex !== -1) {
            // Update existing hack with newer data
            vaultHacks[existingHackIndex] = convertedHack;
          } else {
            // Add new hack from casinoHack field
            vaultHacks.push(convertedHack);
          }
        }
        
        setActiveVaultHacks(vaultHacks);
      }
    } catch (error) {
      // Handle server overload gracefully
      if (error.response?.status >= 500 || error.response?.status === 502) {
        console.warn('Server temporarily unavailable for vault status, will retry later');
        return; // Skip this poll cycle, let it retry on next interval
      }
      console.error('Error fetching vault hack progress:', error);
    }
  };

  // Bank access functionality
  const handleBankAccess = async (bank, accessCode) => {
    try {
      const response = await api.post('/treasure-hunt/banks/access', { 
          bankId: bank.bankId,
          accessCode: accessCode
        });

      if (response.status === 200) {
        const data = response.data;
        console.log(data.message);
        setBankAccessCode('');
        setSelectedBank(null);
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error accessing bank:', error);
      console.log('Failed to access bank. Please try again.');
    }
  };

  // Brute force attack functionality
  const handleBruteForce = async (target, targetType) => {
    try {
      const response = await api.post('/treasure-hunt/brute-force/start', { 
          targetId: targetType === 'bank' ? target.bankId : target.chestId,
          targetType: targetType
        });

      if (response.status === 200) {
        const data = response.data;
        console.log(data.message);
        fetchBruteForceStatus();
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Error starting brute force:', error);
      console.log('Failed to start brute force attack. Please try again.');
    }
  };

  // Fetch brute force status
  const fetchBruteForceStatus = async () => {
    try {
      const response = await api.get('/treasure-hunt/brute-force/status');

      if (response.status === 200) {
        const data = response.data;
        const activeBruteForces = (data && data.data && data.data.activeBruteForces) ? data.data.activeBruteForces : [];
        setActiveBruteForces(activeBruteForces);
      }
    } catch (error) {
      console.error('Error fetching brute force status:', error);
    }
  };

  // ATM access functionality
  const handleATMAccess = async (atm, accessCode) => {
    try {
      const response = await api.post('/treasure-hunt/atms/access', { 
          atmId: atm.atmId,
          accessCode: accessCode
        });

      if (response.status === 200) {
        const data = response.data;
        
        // Show success notification
        if (data.data && data.data.notification) {
          addNotification({
            type: 'success',
            title: data.data.notification.title,
            message: data.data.notification.message,
            details: `Location: ${data.data.notification.details.location}`,
            duration: 8000,
            icon: '🏧'
          });
        }
        
        // Update user data with new rewards
        if (updateUser) {
          updateUser({
            wrCoins: user.wrCoins + data.data.cashAmount,
            xp: user.xp + data.data.xpEarned
          });
        }
        
        // Clear form and close modal
        setAtmAccessCode('');
        setSelectedATM(null);
        
        // Force complete map refresh to remove ATM and update all data
        await loadTreasureHuntData(); // Refresh treasure hunt data and all markers (including removing used ATM)
        fetchUserATMCodes(); // Refresh ATM codes
        fetchUserVaults(); // Refresh vault data to show updated ATM codes
        
        console.log(data.message);
      } else {
        const errorData = response.data;
        console.log(`Error: ${errorData.message}`);
        addNotification({
          type: 'error',
          title: '🏧 ATM Access Failed',
          message: errorData.message,
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error accessing ATM:', error);
      console.log('Failed to access ATM. Please try again.');
      addNotification({
        type: 'error',
        title: '🏧 ATM Access Failed',
        message: 'Failed to access ATM. Please try again.',
        duration: 5000
      });
    }
  };

  // Handle hotel check-in
  const handleHotelCheckIn = async (hotel) => {
    try {
      // First check if user has an active booking for this hotel
      const bookingsResponse = await api.get('/hotel/bookings/my-bookings');
      
      if (bookingsResponse.status !== 200) {
        addNotification({
          type: 'error',
          title: '🏨 Check-in Failed',
          message: 'Failed to check bookings. Please try again.',
          duration: 5000
        });
        return;
      }
      
      const bookingsData = bookingsResponse.data;
      const activeBooking = bookingsData.bookings?.find(booking => 
        booking.hotelId === hotel.hotelId && 
        booking.status === 'booked' &&
        new Date() >= new Date(booking.checkInDate) &&
        new Date() <= new Date(booking.checkOutDate)
      );
      
      if (!activeBooking) {
        addNotification({
          type: 'error',
          title: '🏨 No Booking Found',
          message: `You don't have an active booking for ${hotel.name}. Please book a room first.`,
          duration: 5000
        });
        return;
      }
      
      // Check if computer is within 3000m radius
      if (!userComputer || !userComputer.coordinates) {
        addNotification({
          type: 'error',
          title: '🏨 Computer Required',
          message: 'You need to deploy your computer first.',
          duration: 5000
        });
        return;
      }
      
      const distance = calculateDistance(
        userComputer.coordinates.lat,
        userComputer.coordinates.lng,
        hotel.coordinates.lat,
        hotel.coordinates.lng
      );
      
      if (distance > 3000) {
        addNotification({
          type: 'error',
          title: '🏨 Too Far Away',
          message: `Computer must be within 3000m of hotel. Current distance: ${Math.round(distance)}m`,
          duration: 5000
        });
        return;
      }
      
      // Show countdown popup before check-in
      setHotelCheckinCountdownData({
        hotel: hotel,
        booking: activeBooking,
        action: 'Checking In'
      });
      setHotelCheckinCountdownTimer(5);
      setShowHotelCheckinCountdown(true);
      
      // Execute check-in after a short delay (while popup is visible)
      setTimeout(async () => {
        await executeHotelCheckIn(activeBooking, hotel);
      }, 500);
      
      // Start countdown timer
      const countdownInterval = setInterval(() => {
        setHotelCheckinCountdownTimer(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowHotelCheckinCountdown(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Error checking into hotel:', error);
      addNotification({
        type: 'error',
        title: '🏨 Check-in Error',
        message: 'Failed to check into hotel. Please try again.',
        duration: 5000
      });
    }
  };

  // Execute hotel check-in (called after countdown)
  const executeHotelCheckIn = async (activeBooking, hotel) => {
    try {
      // Attempt check-in
      const checkInResponse = await api.post('/hotel/bookings/checkin', {
          bookingId: activeBooking.bookingId
        });
      
      if (checkInResponse.status === 200) {
        const data = checkInResponse.data;
        addNotification({
          type: 'success',
          title: '🏨 Checked In!',
          message: data.message,
          duration: 8000
        });
        
        // Set hotel sleep state (similar to house sleep)
        setIsSleeping(true);
        
        // Keep computer data but mark as sleeping - don't set to null
        if (userComputer) {
          setUserComputer(prev => ({
            ...prev,
            isSleeping: true,
            sleepingHotelId: activeBooking.hotelId
          }));
        }
        
        // Refresh user data to update safe zone status
        updateUser();
        
        // Refresh bookings to update checked-in hotel status
        fetchUserBookings();
        
        // Immediate refresh to hide computer when checking into hotel
        fetchComputerLocations();
        
        // Close hotel popup
        setSelectedHotel(null);
      } else {
        const errorData = checkInResponse.data;
        addNotification({
          type: 'error',
          title: '🏨 Check-in Failed',
          message: errorData.message,
          duration: 5000
        });
      }
      
    } catch (error) {
      console.error('Error checking into hotel:', error);
      addNotification({
        type: 'error',
        title: '🏨 Check-in Error',
        message: 'Failed to check into hotel. Please try again.',
        duration: 5000
      });
    }
  };

  // Handle hotel check-out
  const handleHotelCheckOut = async (booking) => {
    try {
      const checkOutResponse = await api.post('/hotel/bookings/checkout', {
          bookingId: booking.bookingId
        });
      
      if (checkOutResponse.status === 200) {
        const data = checkOutResponse.data;
        addNotification({
          type: 'success',
          title: '🚪 Checked Out',
          message: data.message,
          duration: 8000
        });
        
        // Clear hotel sleep state (similar to leaving house)
        setIsSleeping(false);
        
        // Restore computer state when checking out of hotel
        if (userComputer) {
          setUserComputer(prev => ({
            ...prev,
            isSleeping: false,
            sleepingHotelId: null
          }));
        }
        
        // Refresh user data, bookings, and treasure hunt data to show computer in new location
        updateUser();
        fetchUserBookings();
        loadTreasureHuntData();
        
        // Immediate refresh to show computer when checking out of hotel
        fetchComputerLocations();
        
        // Close hotel popup
        setSelectedHotel(null);
      } else {
        const errorData = checkOutResponse.data;
        addNotification({
          type: 'error',
          title: '🚪 Check-out Failed',
          message: errorData.message,
          duration: 5000
        });
      }
      
    } catch (error) {
      console.error('Error checking out of hotel:', error);
      addNotification({
        type: 'error',
        title: '🚪 Check-out Error',
        message: 'Failed to check out of hotel. Please try again.',
        duration: 5000
      });
    }
  };

  // Handle vault hacking
  const handleVaultHack = async (vault) => {
    try {
      const response = await api.post(`/atm-vaults/${vault.vaultId}/hack`, {});

      if (response.status === 200) {
        const data = response.data;
        if (data.success) {
          if (data.result.success) {
            // Show notification for successful vault hack
            addNotification({
              type: 'vault_hack_success',
              category: 'hacking',
              title: '🎉 ATM Vault Hacked!',
              message: `Successfully breached ${vault.ownerUsername}'s vault`,
              details: `Stolen: ${data.result.coinsStolen} WR Coins, ${data.result.codesStolen} ATM codes | +50 XP`,
              icon: '💰',
              color: '#4CAF50'
            });
            
            // Update user data
            if (data.userBalance !== undefined) updateUser({ wrCoins: data.userBalance });
            if (data.userXP !== undefined) updateUser({ xp: data.userXP });
            
            // Refresh both user vaults and nearby vaults after hacking
            await fetchUserVaults(); // Refresh user's own vaults
            
            if (snifferActive && userComputer) {
              try {
                const vaultResponse = await api.get(`/atm-vaults/find/in-radius?lat=${userComputer.coordinates.lat}&lng=${userComputer.coordinates.lng}&radius=${calculateSnifferRadius()}`);
                
                if (vaultResponse.status === 200) {
                  const vaultData = vaultResponse.data;
                  if (vaultData.success) {
                    // Filter out user's own vaults from nearby vaults (they're shown separately)
                    const otherVaults = (vaultData.vaults || []).filter(vault => vault.ownerUsername !== user?.username);
                    setNearbyVaults(otherVaults);
                  }
                }
              } catch (error) {
                console.error('Error refreshing vaults:', error);
              }
            }
          } else {
            // Show notification for failed vault hack
            addNotification({
              type: 'vault_hack_failed',
              category: 'hacking',
              title: '❌ Vault Hack Failed',
              message: `Failed to breach ${vault.ownerUsername}'s vault`,
              details: `Success rate was ${data.result.successRate}% - security system blocked the attack`,
              icon: '🛡️',
              color: '#F44336'
            });
          }
          
          if (data.result.swatCalled) {
            // Show notification for SWAT response
            addNotification({
              type: 'swat_called',
              category: 'security',
              title: '🚨 SWAT Response!',
              message: 'SWAT team has been dispatched to your location',
              details: 'You may be jailed for 2 hours - move your computer to escape!',
              icon: '🚨',
              color: '#FF5722'
            });
          }
        }
        
        setSelectedVault(null);
      } else {
        const errorData = response.data;
        // Show notification for API error
        addNotification({
          type: 'vault_hack_error',
          category: 'error',
          title: '❌ Vault Hack Error',
          message: 'Failed to initiate vault hack',
          details: errorData.message || 'Unknown error occurred',
          icon: '⚠️',
          color: '#FF9800'
        });
      }
    } catch (error) {
      console.error('Error hacking vault:', error);
      // Show notification for network error
      addNotification({
        type: 'vault_hack_network_error',
        category: 'error',
        title: '🌐 Network Error',
        message: 'Failed to connect to vault system',
        details: 'Please check your connection and try again',
        icon: '📡',
        color: '#F44336'
      });
    }
  };

  // Only show blocking loading screen on very first load
  if (initialLoading) {
    return (
      <div className="hack-container">
        <div className="hack-loading">
          Initializing Hack The Planet...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hack-container">
        <div className="hack-error">
          <h2>🚨 Connection Error</h2>
          <p>{error}</p>
          <button onClick={loadTreasureHuntData} className="hack-action-button primary">
            🔄 Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hack-container">
      {/* Subtle background loading indicator */}
      {backgroundLoading && (
        <div style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          background: 'rgba(0, 255, 65, 0.9)',
          color: '#000',
          padding: '5px 10px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 10000,
          border: '1px solid #00ff41'
        }}>
          ⚡ Syncing...
        </div>
      )}

      {/* Global House Countdown Overlay */}
      {houseCountdown.active && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10000,
          background: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '30px',
          borderRadius: '15px',
          border: '3px solid #ffaa00',
          textAlign: 'center',
          boxShadow: '0 0 30px rgba(255, 170, 0, 0.5)',
          minWidth: '200px'
        }}>
          <div>
            <p style={{ 
              margin: '0 0 15px 0', 
              color: '#ffaa00', 
              fontWeight: 'bold', 
              fontSize: '18px'
            }}>
              ⏱️ {houseCountdown.action}...
            </p>
            <p style={{ 
              margin: '0', 
              color: '#ff6b35', 
              fontWeight: 'bold', 
              fontSize: '36px'
            }}>
              {houseCountdown.seconds}
            </p>
          </div>
        </div>
      )}
      
      <div className="hack-header">
        <h1 className="hack-title">🌍 Hack The Planet</h1>
        <p className="hack-subtitle">Global Network Infiltration System</p>
      </div>

      {/* Stats Bar */}
      {treasureHuntData && stats && (
        <div className="hack-stats-bar">
          <div className="hack-stat-card">
            <span className="hack-stat-label">Duration</span>
            <span className="hack-stat-value">Ongoing</span>
          </div>
          <div className="hack-stat-card">
            <span className="hack-stat-label">Targets Found</span>
            <span className="hack-stat-value">{stats.chestsOpened}</span>
          </div>
        </div>
      )}

      {/* SWAT Warning Display */}
      {slotSwatWarnings.length > 0 && (
        <div className="swat-warnings-container">
          {slotSwatWarnings.map(warning => {
            const timeUntilSwat = new Date(warning.swatArrivalTime) - new Date();
            const minutes = Math.floor(timeUntilSwat / 60000);
            const seconds = Math.floor((timeUntilSwat % 60000) / 1000);
            
            if (timeUntilSwat <= 0) return null;
            
            return (
              <div key={warning.casinoId} className="swat-warning-alert">
                <h4>🚨 SWAT ALERT - {warning.casinoName}!</h4>
                <p>⏰ Time until arrival: {minutes}m {seconds}s</p>
                <button 
                  onClick={() => handleSlotSwatEscape(warning.casinoId)}
                  className="escape-button"
                >
                  ✈️ ESCAPE SWAT (Move 50+ miles)
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Jail Status Display */}
      {jailStatus.isJailed && (
        <div className="jail-status-container">
          <div className="jail-alert">
            <h4>🔒 YOU ARE IN JAIL</h4>
            <p>Release time: {new Date(jailStatus.jailedUntil).toLocaleString()}</p>
            <p>Most game actions are blocked while in jail.</p>
          </div>
        </div>
      )}

      {/* Mobile Fullscreen Button - Only show on mobile when not in fullscreen */}
      {isMobile() && !isFullscreen && (
        <div style={{
          width: '100%',
          background: 'rgba(0, 0, 0, 0.9)',
          borderBottom: '2px solid #00ff41',
          padding: '10px 20px',
          textAlign: 'center'
        }}>
          <button 
            onClick={toggleFullscreen}
            style={{
              background: 'linear-gradient(135deg, #00ff41 0%, #00cc33 100%)',
              color: '#000',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              fontFamily: 'VT323, monospace',
              width: '100%',
              maxWidth: '300px',
              touchAction: 'manipulation',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            🔍 Enter Fullscreen Mode
          </button>
          <p style={{
            margin: '8px 0 0 0',
            fontSize: '12px',
            color: '#888',
            fontFamily: 'VT323, monospace'
          }}>
            Tap for better mobile experience
          </p>
        </div>
      )}

      {/* Main Content Layout */}
      <div className="hack-content">
        {/* Sidebar */}
        <div className="hack-sidebar">
          <h3 className="hack-sidebar-title">Control Panel</h3>
          
          {/* System Status */}
          <div className="hack-control-panel">
            <h4 className="hack-control-title">System Status</h4>
            <div className="hack-map-status">
              <div className="hack-status-indicator"></div>
              <span>Network Active</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="hack-control-panel">
            <h4 className="hack-control-title">Quick Actions</h4>
            <div className="hack-control-grid">
              <button 
                onClick={() => window.location.reload()}
                className="hack-control-button"
              >
                🔄 Move Refresh
              </button>
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`hack-control-button ${showFilterPanel ? 'active' : ''}`}
              >
                🎛️ Filters
              </button>
              <button
                onClick={() => setShowDronePanel(!showDronePanel)}
                className={`hack-control-button ${showDronePanel ? 'active' : ''}`}
                disabled={!user?.droneInventory || user.droneInventory.length === 0}
              >
                ✈️ Drones ({user?.droneInventory?.length || 0})
              </button>
              <button
                onClick={() => setShowSatellitePanel(!showSatellitePanel)}
                className={`hack-control-button ${showSatellitePanel ? 'active' : ''}`}
                disabled={!user?.satelliteInventory?.some(satellite => satellite.readyForDeployment)}
              >
                🛰️ Satellites ({user?.satelliteInventory?.filter(satellite => satellite.readyForDeployment && !satellite.deployedSatelliteId).length || 0})
              </button>
            </div>
          </div>

          {/* Map Filters */}
          {showFilterPanel && (
            <div className="hack-control-panel">
              <h4 className="hack-control-title">Map Filters</h4>
              <div className="hack-action-buttons">
                <button
                  onClick={() => setMapFilters(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: true }), {}))}
                  className="hack-action-button primary"
                >
                  Show All
                </button>
                <button
                  onClick={() => setMapFilters(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}))}
                  className="hack-action-button danger"
                >
                  Hide All
                </button>
                <button
                  onClick={saveFilterPreferences}
                  className="hack-action-button success"
                  style={{ marginTop: '5px' }}
                >
                  💾 Save Preferences
                </button>
              </div>
              
              <div className="hack-control-grid">
                {Object.entries(mapFilters).map(([key, value]) => (
                  <label key={key} className="hack-filter-toggle">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setMapFilters(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="hack-filter-checkbox"
                    />
                    <span className="hack-filter-label">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace('show', '').trim()}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Drone Panel */}
          {showDronePanel && (
            <div className="hack-control-panel">
              <h4 className="hack-control-title">Drone Deployment</h4>
              
              {(!user?.droneInventory || user.droneInventory.length === 0) ? (
                <div className="hack-drone-empty">
                  <p>No drones available. Purchase drones from the Computer Parts store first.</p>
                  <button 
                    onClick={() => window.location.href = '/dashboard'}
                    className="hack-action-button primary"
                  >
                    🛒 Go to Parts Store
                  </button>
                </div>
              ) : (
                <>
                  <div className="hack-action-buttons">
                    {selectedDroneForDeployment ? (
                      <button
                        onClick={() => {
                          setIsDeployingDrone(true);
                          addNotification({
                            title: '🎯 Deployment Mode Active',
                            message: `Click anywhere on the map to deploy your ${selectedDroneForDeployment.name}`,
                            type: 'info'
                          });
                        }}
                        className={`hack-action-button success ${isDeployingDrone ? 'active' : ''}`}
                      >
                        🎯 Deploy {selectedDroneForDeployment.name}
                      </button>
                    ) : (
                      <p style={{ marginBottom: '10px', color: '#00ff41' }}>Select a drone to deploy:</p>
                    )}
                    
                    {isDeployingDrone && (
                      <button
                        onClick={() => {
                          setIsDeployingDrone(false);
                          setSelectedDroneForDeployment(null);
                          addNotification({
                            title: '❌ Deployment Cancelled',
                            message: 'Drone deployment cancelled',
                            type: 'info'
                          });
                        }}
                        className="hack-action-button danger"
                      >
                        ❌ Cancel Deployment
                      </button>
                    )}
                  </div>
                  
                  <div className="hack-drone-list">
                    {user.droneInventory.map((drone, index) => (
                      <div 
                        key={drone.partId || drone._id || index}
                        className={`hack-drone-item ${(selectedDroneForDeployment?.partId && selectedDroneForDeployment.partId === drone.partId) || (selectedDroneForDeployment?._id === drone._id) ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedDroneForDeployment(drone);
                          setIsDeployingDrone(false);
                        }}
                      >
                        <div className="hack-drone-icon">
                          {drone.icon || (drone.name?.includes('Scout') ? '🔍' : drone.name?.includes('Assault') ? '✈️' : '☢️')}
                        </div>
                        <div className="hack-drone-info">
                          <div className="hack-drone-name">{drone.name}</div>
                          <div className="hack-drone-specs">
                            {drone.name?.includes('Scout') && '20 moves • 3000m range • 6hr recharge'}
                            {drone.name?.includes('Assault') && '2 moves • Tower destroyer • 1hr duration'}
                            {drone.name?.includes('Nuke') && '3 moves • Command center destroyer • 30min duration'}
                          </div>
                        </div>
                        {((selectedDroneForDeployment?.partId && selectedDroneForDeployment.partId === drone.partId) || (selectedDroneForDeployment?._id === drone._id)) && (
                          <div className="hack-drone-selected">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Satellite Panel */}
          {showSatellitePanel && (
            <div className="hack-control-panel">
              <h4 className="hack-control-title">Satellite Deployment</h4>
              
              {(!user?.satelliteInventory?.some(satellite => satellite.readyForDeployment)) ? (
                <div className="hack-drone-empty">
                  <p>No satellites available. Purchase Ocean Surveillance Satellites from the Defense Systems store first.</p>
                  <button 
                    onClick={() => window.location.href = '/shopping'}
                    className="hack-action-button primary"
                  >
                    🛒 Go to Defense Systems Store
                  </button>
                </div>
              ) : (
                <>
                  <div className="hack-action-buttons">
                    <button
                      onClick={() => {
                        setIsDeployingSatellite(true);
                        addNotification({
                          title: '🛰️ Deployment Mode Active',
                          message: 'Click anywhere in ocean/sea areas to deploy your Ocean Surveillance Satellite',
                          type: 'info'
                        });
                      }}
                      className={`hack-action-button success ${isDeployingSatellite ? 'active' : ''}`}
                    >
                      🛰️ Deploy Satellite
                    </button>
                    
                    {isDeployingSatellite && (
                      <button
                        onClick={() => {
                          setIsDeployingSatellite(false);
                          addNotification({
                            title: '❌ Deployment Cancelled',
                            message: 'Satellite deployment cancelled',
                            type: 'info'
                          });
                        }}
                        className="hack-action-button danger"
                      >
                        ❌ Cancel Deployment
                      </button>
                    )}
                  </div>
                  
                  <div className="hack-satellite-info">
                    <p><strong>Available Satellites:</strong> {user?.satelliteInventory?.filter(satellite => satellite.readyForDeployment && !satellite.deployedSatelliteId).length || 0}</p>
                    <p><strong>Scan Radius:</strong> 1500km</p>
                    <p><strong>Scan Frequency:</strong> Daily</p>
                    <p><strong>Detection:</strong> All naval assets in range</p>
                    <p style={{ color: '#ff6b35' }}><strong>⚠️ Ocean Only:</strong> Can only be deployed in water</p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Map Container */}
        <div className="hack-map-container">
          <div className="hack-map-header">
            <h3 className="hack-map-title">Global Network View</h3>
            <div className="hack-map-status">
              <div className="hack-status-indicator"></div>
              <span>Systems Online</span>
            </div>
          </div>
          
          <div className="hack-map-wrapper">
            <MapContainer
            ref={mapRef}
            center={mapCenter}
            zoom={mapZoom}
            style={mapStyle}
            scrollWheelZoom={!isMobile()}
            dragging={true}
            touchZoom={true}
            tap={true}
            doubleClickZoom={!isMobile()}
            zoomControl={true}
            attributionControl={false}
            keyboard={false}
            boxZoom={false}
            preferCanvas={true}
            updateWhenIdle={true}
            zoomSnap={0.5}
            zoomDelta={0.5}
            maxZoom={18}
            minZoom={2}
            worldCopyJump={false}
            maxBounds={[[-85, -180], [85, 180]]}
            maxBoundsViscosity={1.0}
            whenCreated={(mapInstance) => {
              window.leafletMapInstance = mapInstance;
              mapRef.current = mapInstance;
            }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              noWrap={true}
              bounds={[[-85, -180], [85, 180]]}
            />
            <MapClickHandler />


            {/* Treasure Chests - Only show when sniffer is active and within range */}
            {snifferActive && userComputer && treasureHuntData?.treasureChests?.filter(chest => {
              if (!chest.coordinates) return false;
              const distance = calculateDistance(
                userComputer.coordinates.lat, userComputer.coordinates.lng,
                chest.coordinates.lat, chest.coordinates.lng
              );
              return distance <= calculateSnifferRadius() && mapFilters.showTreasureChests;
            }).slice(0, 50).map((chest) => {
              // Keep chest icons consistent - no dynamic changes
              let chestIconEmoji = (chest.type === 'xp' || chest.type === 'movement_xp') ? '⭐' : '💰';
              
              const dynamicChestIcon = createCustomIcon(chestIconEmoji, [30, 30]);
              
              return (
                <Marker
                  key={`${chest.chestId}-${chest.type}-${chest.spawnedAt || 'static'}`}
                  position={[chest.coordinates.lat, chest.coordinates.lng]}
                  icon={dynamicChestIcon}
                  eventHandlers={{
                    click: (e) => {
                      e.originalEvent?.stopPropagation();
                      // Auto-open all chest types on direct marker click (restored original behavior)
                      handleOpenChest(chest);
                    }
                  }}
                >
                  <Popup>
                    <div className="chest-popup" style={{ minWidth: '200px' }}>
                      {(chest.type === 'xp' || chest.type === 'movement_xp') ? (
                        <>
                          <h4 style={{ margin: '0 0 10px 0' }}>
                            {chestIconEmoji} {chest.type === 'movement_xp' ? 'Movement XP Crate' : 'XP Chest'}
                            {chest.xpAmount > 100 && <span style={{ color: '#ffd700' }}> (Rare!)</span>}
                          </h4>
                          <p>Gain {chest.xpAmount} XP instantly!</p>
                          {chest.type === 'movement_xp' && (
                            <p style={{ fontSize: '12px', color: '#ff6b35', margin: '5px 0' }}>
                              🚀 Spawned from computer movement
                            </p>
                          )}
                          {chest.coordinates.nearCity && (
                            <p style={{ fontSize: '12px', color: '#888', margin: '5px 0' }}>
                              📍 Near {chest.coordinates.nearCity}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <h4 style={{ margin: '0 0 10px 0' }}>
                            {chestIconEmoji} Treasure Chest
                            {chest.rarity && chest.rarity !== 'common' && (
                              <span style={{ 
                                color: chest.rarity === 'legendary' ? '#ffd700' : 
                                       chest.rarity === 'epic' ? '#9d4edd' : 
                                       chest.rarity === 'rare' ? '#0084ff' : '#ffffff',
                                marginLeft: '5px',
                                fontSize: '12px'
                              }}>
                                ({chest.rarity.toUpperCase()})
                              </span>
                            )}
                          </h4>
                          <p>Requires 6-digit combination</p>
                          {chest.prizeAmount && (
                            <p style={{ color: '#00ff41', margin: '5px 0' }}>
                              💰 Contains: {chest.prizeAmount} WR Coins
                            </p>
                          )}
                          {chest.coordinates.nearCity && (
                            <p style={{ fontSize: '12px', color: '#888', margin: '5px 0' }}>
                              📍 Near {chest.coordinates.nearCity}
                            </p>
                          )}
                        </>
                      )}
                      <button 
                        onClick={() => handleOpenChest(chest)}
                        disabled={processingChests.has(chest.chestId) || openedChests.has(chest.chestId)}
                        style={{
                          background: processingChests.has(chest.chestId) ? '#666' : 
                                    (chest.type === 'xp' || chest.type === 'movement_xp') ? '#4a90e2' : '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: processingChests.has(chest.chestId) ? 'not-allowed' : 'pointer',
                          width: '100%',
                          opacity: processingChests.has(chest.chestId) ? 0.6 : 1,
                          marginBottom: (chest.type === 'xp' || chest.type === 'movement_xp') ? '5px' : '0'
                        }}
                      >
                        {processingChests.has(chest.chestId) ? 'Processing...' :
                         openedChests.has(chest.chestId) ? 'Opened' :
                         (chest.type === 'xp' || chest.type === 'movement_xp') ? 'Open Chest' : 'Enter Combination'}
                      </button>
                      {(chest.type === 'xp' || chest.type === 'movement_xp') && (
                        <button 
                          onClick={() => {
                            addNotification({
                              type: 'info',
                              title: '🔄 Refreshing Page',
                              message: 'Refreshing page to reload XP crates...',
                              duration: 1000
                            });
                            // Actually refresh the page after short delay
                            setTimeout(() => {
                              window.location.reload();
                            }, 1000);
                          }}
                          style={{
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            width: '100%',
                            fontSize: '12px'
                          }}
                        >
                          🔄 Refresh Page
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Deployed Drones */}
            {deployedDrones.filter(drone => mapFilters.showDrones !== false && drone.position?.lat && drone.position?.lng).map((drone, index) => (
              <React.Fragment key={drone._id}>
                <Marker
                  position={[drone.position.lat, drone.position.lng]}
                  icon={droneIcon}
                  eventHandlers={{
                    click: (e) => {
                      e.originalEvent?.stopPropagation();
                      setSelectedDrone(drone);
                      
                      // Auto-activate drone sniffer when clicking on own drone
                      if (!droneSniffersActive.has(drone.droneId)) {
                        toggleDroneSniffer(drone.droneId, drone.position);
                      }
                    }
                  }}
                >
                <Popup>
                  <div className="drone-popup" style={{ minWidth: '200px' }}>
                    <div style={{ marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, color: '#00ff41' }}>
                        ✈️ Drone #{index + 1} - {drone.name}
                      </h4>
                    </div>
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      <p><strong>Type:</strong> {drone.type}</p>
                      <p><strong>Status:</strong> {drone.status?.active ? 'Active' : 'Inactive'}</p>
                      <p><strong>Moves Used:</strong> {drone.status?.movesUsed || 0}/{drone.specifications?.moves || 'Unlimited'}</p>
                      {drone.specifications.health && (
                        <p><strong>Health:</strong> {drone.specifications.health}/{drone.specifications.maxHealth || 100}</p>
                      )}
                      <p><strong>Sniffer Range:</strong> {drone.type === 'maritime_scout' ? '50km' : '3000m'}</p>
                      {drone.type !== 'scout' && drone.specifications.attackPower && (
                        <p><strong>Attack Power:</strong> {drone.specifications.attackPower}</p>
                      )}
                      <p><strong>Deployed:</strong> {drone.deployment?.deployedAt ? new Date(drone.deployment.deployedAt).toLocaleTimeString() : 'Unknown'}</p>
                      
                      {/* Battle Progress Information */}
                      {drone.status?.inBattle && drone.battle && (
                        <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(255, 68, 68, 0.1)', borderRadius: '4px', border: '1px solid #ff4444' }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#ff4444' }}>
                            🔥 BATTLE IN PROGRESS
                          </p>
                          <p style={{ fontSize: '12px', margin: '2px 0' }}>
                            <strong>Target:</strong> Defense Tower
                          </p>
                          <p style={{ fontSize: '12px', margin: '2px 0' }}>
                            <strong>Owner:</strong> {drone.battle?.targetOwnerUsername || 'Unknown'}
                          </p>
                          <p style={{ fontSize: '12px', margin: '2px 0' }}>
                            <strong>Started:</strong> {drone.battle?.battleStartedAt ? new Date(drone.battle.battleStartedAt).toLocaleTimeString() : 'Unknown'}
                          </p>
                          {drone.battle?.battleEndsAt && (
                            <p style={{ fontSize: '12px', margin: '2px 0', color: '#ffaa44' }}>
                              <strong>Completes:</strong> {new Date(drone.battle.battleEndsAt).toLocaleTimeString()}
                            </p>
                          )}
                          {(() => {
                            if (!drone.battle?.battleEndsAt) return null;
                            const now = new Date();
                            const endTime = new Date(drone.battle.battleEndsAt);
                            const timeRemaining = Math.max(0, endTime.getTime() - now.getTime());
                            const minutesRemaining = Math.floor(timeRemaining / 60000);
                            const secondsRemaining = Math.floor((timeRemaining % 60000) / 1000);
                            
                            if (timeRemaining <= 0) {
                              return (
                                <p style={{ fontSize: '12px', margin: '2px 0', color: '#44ff44', fontWeight: 'bold' }}>
                                  ✅ ATTACK COMPLETE
                                </p>
                              );
                            }
                            
                            return (
                              <p style={{ fontSize: '12px', margin: '2px 0', color: '#ffaa44', fontWeight: 'bold' }}>
                                ⏰ {minutesRemaining}m {secondsRemaining}s remaining
                              </p>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Sniffer Discovery Data */}
                      {drone.discoveryData && drone.discoveryData.length > 0 && (
                        <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(0, 255, 65, 0.1)', borderRadius: '4px' }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#00ff41' }}>
                            📡 Sniffer Discoveries ({drone.discoveryData.length})
                          </p>
                          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {drone.discoveryData.map((discovery, index) => (
                              <div key={index} style={{ fontSize: '11px', margin: '2px 0', padding: '2px 4px', background: 'rgba(0, 0, 0, 0.2)', borderRadius: '2px' }}>
                                <strong>{discovery.type === 'computer' ? '💻' : discovery.type === 'house' ? '🏠' : discovery.type === 'hotel' ? '🏨' : discovery.type === 'casino' ? '🎰' : discovery.type === 'bank' ? '🏦' : discovery.type === 'business' ? '🏢' : discovery.type === 'atm_vault' ? '🏧' : discovery.type === 'defense_tower' ? '🗼' : discovery.type === 'command_center' ? '🚐' : discovery.type === 'user_task' ? '📋' : discovery.type === 'drone' ? '✈️' : '❓'} {discovery.type === 'drone' ? `${discovery.droneType || 'unknown'} drone` : discovery.type}:</strong> {discovery.ownerUsername || 'Unknown'} ({discovery.distance}m)
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Show message if sniffer has no discoveries */}
                      {(!drone.discoveryData || drone.discoveryData.length === 0) && (
                        <div style={{ marginTop: '10px', padding: '6px', background: 'rgba(255, 255, 0, 0.1)', borderRadius: '4px' }}>
                          <p style={{ margin: '0', fontSize: '11px', color: '#ffaa00' }}>
                            📡 No entities detected within {drone.type === 'maritime_scout' ? '50km' : '3km'} radius
                          </p>
                        </div>
                      )}
                    </div>
                    {drone.status.active && (
                      <div style={{ marginTop: '10px' }}>
                        <button 
                          onClick={() => setIsDroneMoving(true)}
                          style={{
                            background: '#4a90e2',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '5px',
                            fontSize: '12px'
                          }}
                        >
                          📍 Move Drone
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDroneSniffer(drone.droneId, drone.position);
                          }}
                          style={{
                            background: droneSniffersActive.has(drone.droneId) ? '#ff8800' : '#00ff41',
                            color: droneSniffersActive.has(drone.droneId) ? 'white' : 'black',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '5px',
                            fontSize: '12px'
                          }}
                        >
                          {droneSniffersActive.has(drone.droneId) ? '📡 Disable Sniffer' : '📡 Activate Sniffer'}
                        </button>
                        {drone.type !== 'scout' && drone.type !== 'maritime_scout' && !drone.status.inBattle && (
                          <button 
                            onClick={() => startAttackMode(drone)}
                            style={{
                              background: attackMode && selectedAttackDrone?.droneId === drone.droneId ? '#ff8800' : '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            {attackMode && selectedAttackDrone?.droneId === drone.droneId ? '🎯 Select Target' : '⚔️ Attack'}
                          </button>
                        )}
                        
                        {attackMode && selectedAttackDrone?.droneId === drone.droneId && (
                          <button 
                            onClick={cancelAttackMode}
                            style={{
                              background: '#6c757d',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginLeft: '5px'
                            }}
                          >
                            Cancel Attack
                          </button>
                        )}
                        
                        {drone.status.inBattle && (
                          <div style={{ 
                            color: '#ff4444', 
                            fontSize: '12px', 
                            fontWeight: 'bold', 
                            marginTop: '5px',
                            border: '1px solid #ff4444',
                            padding: '8px',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(255, 68, 68, 0.1)'
                          }}>
                            <div>🔥 ATTACKING DEFENSE TOWER</div>
                            {drone.battle && drone.battle.targetTowerId && (
                              <div style={{ marginTop: '5px', fontSize: '11px' }}>
                                <div>🎯 Target: Tower {drone.battle.targetTowerId}</div>
                                <div>👤 Owner: {drone.battle.targetOwnerUsername}</div>
                                {drone.battle.timeRemaining && (
                                  <div style={{ color: '#ff6644', fontWeight: 'bold' }}>
                                    ⏱️ {Math.floor(drone.battle.timeRemaining / 60)}m {drone.battle.timeRemaining % 60}s remaining
                                  </div>
                                )}
                                {drone.battle.startedAt && (
                                  <div style={{ color: '#999', fontSize: '10px' }}>
                                    Started: {new Date(drone.battle.startedAt).toLocaleTimeString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Popup>
                </Marker>
                
                {/* Drone Sniffer Radius Circle (when sniffer is active) */}
                {droneSniffersActive.has(drone.droneId) && (
                  <Circle
                    center={[drone.position.lat, drone.position.lng]}
                    radius={(drone.type === 'maritime_scout') ? 50000 : 3000} // 50km for maritime scout, 3km for others
                    pathOptions={{
                      color: '#00ff41',
                      weight: 3,
                      opacity: 0.8,
                      fillColor: '#00ff41',
                      fillOpacity: 0.1,
                      dashArray: '10, 10' // Dashed line for better visibility
                    }}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Deployed Satellites */}
            {satellites.filter(satellite => mapFilters.showSatellites !== false && satellite.location?.lat && satellite.location?.lng).map((satellite) => (
              <React.Fragment key={satellite.id || satellite.satelliteId}>
                <Marker
                  position={[satellite.location.lat, satellite.location.lng]}
                  icon={createCustomIcon('🛰️', [32, 32])}
                  eventHandlers={{
                    click: () => {
                      // TODO: Handle satellite click for scan options
                    }
                  }}
                >
                  <Popup>
                    <div className="satellite-popup">
                      <h4>🛰️ Ocean Surveillance Satellite</h4>
                      <p>👑 Owner: {satellite.username || satellite.ownerUsername}</p>
                      <p>📡 Scan Radius: 1500km</p>
                      <p>❤️ Health: {satellite.health || 1000}/1000</p>
                      <p>🌊 Ocean-based surveillance system</p>
                      
                      
                      {(satellite.isOwnSatellite || satellite.username === user?.username || satellite.ownerUsername === user?.username) && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginBottom: '10px' }}>
                            <button 
                              onClick={async () => {
                                try {
                                  const { performOceanScan } = await import('../services/api');
                                  const scanData = await performOceanScan(satellite.id || satellite.satelliteId);
                                  addNotification({
                                    title: '🛰️ Ocean Scan Complete',
                                    message: `Detected ${scanData.scanResults?.assetsFound || 0} naval assets within 1500km radius. Check MDSS tab for details.`,
                                    type: 'success'
                                  });
                                  // Refresh satellite data
                                  fetchSatellites();
                                } catch (error) {
                                  addNotification({
                                    title: '❌ Scan Failed',
                                    message: error.response?.data?.message || 'Failed to initiate satellite scan',
                                    type: 'error'
                                  });
                                }
                              }}
                              disabled={!satellite.canScan}
                              className="brute-force-button"
                              style={{ 
                                fontSize: '11px', 
                                padding: '5px 8px',
                                backgroundColor: satellite.canScan ? '#28a745' : '#6c757d'
                              }}
                            >
                              {satellite.canScan ? '📡 Scan Ocean' : '⏳ Scan Cooldown'}
                            </button>
                            
                            <button 
                              onClick={() => {
                                if (window.confirm('Click on the ocean where you want to move your satellite. Move cooldown: 24 hours.')) {
                                  setSatelliteMoving(satellite.id || satellite.satelliteId);
                                  addNotification({
                                    title: '🛰️ Move Mode',
                                    message: 'Click on the ocean to move your satellite',
                                    type: 'info'
                                  });
                                }
                              }}
                              disabled={!satellite.canMove}
                              className="brute-force-button"
                              style={{ 
                                fontSize: '11px', 
                                padding: '5px 8px',
                                backgroundColor: satellite.canMove ? '#17a2b8' : '#6c757d'
                              }}
                            >
                              {satellite.canMove ? '🚀 Move' : '⏳ Move Cooldown'}
                            </button>
                          </div>
                          
                          <button 
                            onClick={() => {
                              if (window.confirm('Are you sure you want to remove this satellite? This action cannot be undone and you will need to purchase a new one.')) {
                                handleDeleteSatellite(satellite.id || satellite.satelliteId);
                              }
                            }}
                            className="brute-force-button"
                            style={{ 
                              fontSize: '11px', 
                              padding: '5px 8px',
                              backgroundColor: '#dc3545',
                              width: '100%'
                            }}
                          >
                            🗑️ Remove Satellite
                          </button>
                          
                          {(!satellite.canScan || !satellite.canMove) && (
                            <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
                              {!satellite.canScan && <div>Next scan: {satellite.nextScanAt ? new Date(satellite.nextScanAt).toLocaleString() : 'Ready'}</div>}
                              {!satellite.canMove && <div>Next move: {satellite.nextMoveAt ? new Date(satellite.nextMoveAt).toLocaleString() : 'Ready'}</div>}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>

                {/* Satellite Scan Radius Circle (animated radar effect) */}
                <Circle
                  center={[satellite.location.lat, satellite.location.lng]}
                  radius={1500000} // 1500km radius
                  pathOptions={{
                    color: '#00ff41',
                    weight: 2,
                    opacity: 0.6,
                    fillColor: '#00ff41',
                    fillOpacity: 0.05,
                    dashArray: '15, 15' // Animated dashed line for radar effect
                  }}
                />
              </React.Fragment>
            ))}

            {/* Banks (only visible when sniffer is active) */}
            {snifferActive && nearbyBanks.filter(bank => mapFilters.showBanks && bank.coordinates && bank.coordinates.lat && bank.coordinates.lng).map((bank) => {
              // Check for active brute force on this bank
              const activeBruteForce = activeBruteForces.find(bf => bf.targetId === bank.bankId && bf.targetType === 'bank');
              
              return (
                <Marker
                  key={bank.bankId}
                  position={[bank.coordinates.lat, bank.coordinates.lng]}
                  icon={bankIcon}
                  eventHandlers={{
                    click: () => setSelectedBank(bank)
                  }}
                >
                  <Popup>
                    <div className="bank-popup">
                      <h4>🏦 {bank.name}</h4>
                      <p>👑 Owner: {bank.ownedBy === 'occult_bank' ? 'Occult Bank' : 'Private'}</p>
                      <p>Type: {bank.type}</p>
                      <p>Security: Level {bank.securityLevel}</p>
                      <p>💰 Reward: {bank.reward ? `${bank.reward.toLocaleString()} WR Coins` : '50,000-100,000 WR Coins'}</p>
                      
                      {activeBruteForce && (
                        <div style={{ marginTop: '10px', padding: '5px', backgroundColor: '#ff8800', borderRadius: '3px' }}>
                          <p><strong>🔨 Brute Force Active</strong></p>
                          <p>Time remaining: {activeBruteForce.timeRemaining || 'Calculating...'}</p>
                        </div>
                      )}
                      
                      <div style={{ marginTop: '10px' }}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBank(bank);
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            width: '100%'
                          }}
                        >
                          🏦 Access Bank
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Casinos (only visible when sniffer is active) */}
            {snifferActive && nearbyCasinos.filter(casino => {
              // Basic filters
              if (!mapFilters.showCasinos || !casino.coordinates || !casino.coordinates.lat || !casino.coordinates.lng) {
                return false;
              }
              
              // Check if there's a casino hotel at the same location
              const hasCasinoHotelAtSameLocation = hotels.some(hotel => 
                hotel.isCasino && 
                hotel.coordinates && 
                hotel.coordinates.lat === casino.coordinates.lat && 
                hotel.coordinates.lng === casino.coordinates.lng
              );
              
              // Only show standalone casinos that don't have a casino hotel at the same location
              return !hasCasinoHotelAtSameLocation;
            }).map((casino) => {
              // Check if this casino has an active vault hack
              const activeHack = activeVaultHacks.find(hack => hack.casinoId === casino.casinoId);
              
              return (
                <Marker
                  key={casino.casinoId}
                  position={[casino.coordinates.lat, casino.coordinates.lng]}
                  icon={casinoIcon}
                  eventHandlers={{
                    click: () => setSelectedCasino(casino)
                  }}
                >
                  <Popup>
                    <div className="casino-popup">
                      <h4>🎰 {casino.name}</h4>
                      <p>📍 Location: {casino.location}</p>
                      <p>👑 Owner: {casino.ownedBy === 'occult_bank' ? 'Occult Bank' : 'Private'}</p>
                      {activeHack && (
                        <div style={{ marginTop: '10px', padding: '5px', backgroundColor: '#ff4444', borderRadius: '3px' }}>
                          <p><strong>🚨 Active Vault Hack</strong></p>
                          <p>Stage: {activeHack.stage}</p>
                        </div>
                      )}
                      <div style={{ marginTop: '10px' }}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCasino(casino);
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            width: '100%'
                          }}
                        >
                          🎰 Access Casino
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* ATMs (only visible when sniffer is active) */}
            {snifferActive && nearbyATMs.filter(atm => mapFilters.showATMs && atm.coordinates && atm.coordinates.lat && atm.coordinates.lng).map((atm) => {
              // Check if user has the code for this ATM (for system ATMs) or if it's user-owned
              const hasCode = userATMCodes.find(code => code.atmId === atm.atmId);
              const isUserOwned = atm.isUserOwned || atm.ownedBy === 'user';
              
              // Use different icon for user-owned ATMs
              const atmMarkerIcon = isUserOwned ? createCustomIcon('🏧', [35, 35]) : atmIcon;
              
              return (
                <Marker
                  key={atm.atmId}
                  position={[atm.coordinates.lat, atm.coordinates.lng]}
                  icon={atmMarkerIcon}
                  eventHandlers={{
                    click: () => setSelectedATM(atm)
                  }}
                >
                  <Popup>
                    <div className="atm-popup">
                      <h4>🏧 {isUserOwned ? 'Your ATM Machine' : 'ATM'}</h4>
                      <p>🏦 Bank: {atm.bankName}</p>
                      <p>👑 Owner: {
                        isUserOwned ? 'You' :
                        atm.ownedBy === 'occult_bank' ? 'Occult Bank' : 'Private'
                      }</p>
                      <p>📍 Location: {atm.locationDescription}</p>
                      <p>💰 Contains: 500 WR Coins</p>
                      
                      {isUserOwned && atm.accessCode && (
                        <div style={{ marginTop: '10px', padding: '5px', backgroundColor: '#28a745', borderRadius: '3px' }}>
                          <p><strong>🔑 Your ATM Code:</strong></p>
                          <p style={{ fontSize: '16px', fontWeight: 'bold' }}>{atm.accessCode}</p>
                          {atm.foundAt && (
                            <p style={{ fontSize: '11px', color: '#ddd' }}>
                              Found: {new Date(atm.foundAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {hasCode && !isUserOwned && (
                        <div style={{ marginTop: '10px', padding: '5px', backgroundColor: '#28a745', borderRadius: '3px' }}>
                          <p><strong>🔑 You have the code!</strong></p>
                          <p>Code: {hasCode.code}</p>
                        </div>
                      )}
                      
                      <div style={{ marginTop: '10px' }}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedATM(atm);
                          }}
                          style={{
                            background: isUserOwned ? '#28a745' : '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            marginBottom: '5px'
                          }}
                        >
                          {isUserOwned ? '💰 Access Your ATM' : '🏧 Access ATM'}
                        </button>
                        
                        {/* Brute Force button for non-user-owned ATMs */}
                        {!isUserOwned && !hasCode && (() => {
                          // Check if this ATM was detected by a drone
                          const isDroneDetected = Object.values(droneDetectedATMs).some(atms => 
                            Array.isArray(atms) && atms.some(detectedAtm => detectedAtm.atmId === atm.atmId)
                          );
                          
                          if (isDroneDetected) {
                            return (
                              <div style={{ 
                                padding: '10px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '5px', 
                                border: '2px solid #ff6b35',
                                marginTop: '10px'
                              }}>
                                <p style={{ margin: '0 0 5px 0', color: '#ff6b35', fontSize: '12px', fontWeight: 'bold' }}>
                                  ✈️ Drone Reconnaissance
                                </p>
                                <p style={{ margin: '0', color: '#dc3545', fontSize: '11px' }}>
                                  🚫 Drones cannot perform ATM attacks
                                </p>
                              </div>
                            );
                          }
                          
                          return (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleATMBruteForce(atm);
                              }}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                width: '100%',
                                fontSize: '14px',
                                fontWeight: 'bold'
                              }}
                            >
                              🔓 Brute Force ATM
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Defense Towers (user's compound - always visible if exists) */}
            {userCompound && defenseTowers.filter(tower => mapFilters.showDefenseTowers !== false && tower.position?.lat && tower.position?.lng).map((tower) => (
              <Marker
                key={tower.id}
                position={[tower.position.lat, tower.position.lng]}
                icon={createCustomIcon('🗼', [30, 30])}
              >
                <Popup>
                  <div className="tower-popup">
                    <h4>🗼 Defense Tower</h4>
                    <p>📍 Location: {tower.position?.lat?.toFixed(4) || 'N/A'}, {tower.position?.lng?.toFixed(4) || 'N/A'}</p>
                    <p>💪 Health: {tower.health}/{tower.maxHealth}</p>
                    <p>⚡ Status: {tower.active ? '🟢 Online' : '🔴 Offline'}</p>
                    <p>🛡️ Shield Radius: {tower.shieldRadius}m</p>
                    <p>⚙️ Type: {tower.type}</p>
                    
                    {/* Check if tower is under attack */}
                    {(() => {
                      const attackingDrone = deployedDrones.find(drone => 
                        drone.status?.inBattle && 
                        drone.battle?.targetTowerId === tower.id
                      );
                      
                      if (attackingDrone && attackingDrone.battle) {
                        const battleEndTime = new Date(attackingDrone.battle.battleEndsAt).getTime();
                        const now = Date.now();
                        const remainingTime = Math.max(0, battleEndTime - now);
                        const totalTime = 30 * 60 * 1000; // 30 minutes
                        const progress = Math.max(0, Math.min(100, ((totalTime - remainingTime) / totalTime) * 100));
                        const minutesLeft = Math.floor(remainingTime / (60 * 1000));
                        const secondsLeft = Math.floor((remainingTime % (60 * 1000)) / 1000);
                        
                        return (
                          <div style={{ 
                            marginTop: '10px', 
                            padding: '8px', 
                            background: 'rgba(255, 0, 0, 0.1)', 
                            border: '1px solid #ff4444',
                            borderRadius: '4px'
                          }}>
                            <p style={{ color: '#ff4444', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                              🚨 UNDER ATTACK!
                            </p>
                            <p style={{ margin: '0 0 5px 0', fontSize: '12px' }}>
                              Attacker: {attackingDrone.type === 'assault' ? '✈️' : '☢️'} {attackingDrone.name}
                            </p>
                            <p style={{ margin: '0 0 5px 0', fontSize: '12px' }}>
                              Time Remaining: {minutesLeft}m {secondsLeft}s
                            </p>
                            <div style={{ 
                              background: '#333', 
                              borderRadius: '3px', 
                              overflow: 'hidden', 
                              height: '8px',
                              marginBottom: '5px'
                            }}>
                              <div style={{ 
                                background: '#ff4444', 
                                height: '100%', 
                                width: `${progress}%`,
                                transition: 'width 1s ease'
                              }} />
                            </div>
                            <p style={{ margin: '0', fontSize: '11px', color: '#ff6666' }}>
                              Attack Progress: {progress.toFixed(1)}%
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {tower.health <= 0 && tower.destroyedAt && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        background: 'rgba(128, 128, 128, 0.2)', 
                        border: '1px solid #888',
                        borderRadius: '4px'
                      }}>
                        <p style={{ color: '#888', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                          💀 DESTROYED
                        </p>
                        <p style={{ margin: '0', fontSize: '12px', color: '#aaa' }}>
                          Tower will be removed in {(() => {
                            const destroyedTime = new Date(tower.destroyedAt).getTime();
                            const removeTime = destroyedTime + (12 * 60 * 60 * 1000);
                            const timeLeft = Math.max(0, removeTime - Date.now());
                            const hoursLeft = Math.floor(timeLeft / (60 * 60 * 1000));
                            const minutesLeft = Math.floor((timeLeft % (60 * 60 * 1000)) / (60 * 1000));
                            return `${hoursLeft}h ${minutesLeft}m`;
                          })()}
                        </p>
                      </div>
                    )}
                    
                    {tower.health < tower.maxHealth && tower.health > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ color: '#ff8800' }}>🔧 Repair needed</p>
                        <p style={{ fontSize: '12px' }}>Cost: {(tower.maxHealth - tower.health) * 2} WR Coins</p>
                      </div>
                    )}
                    
                    <div style={{ marginTop: '10px' }}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate to compound management
                          window.location.href = '/system?tab=compound';
                        }}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          width: '100%',
                          fontSize: '12px'
                        }}
                      >
                        🚐 Manage Compound
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Individual Defense Tower Protection Radii (1000m each) */}
            {userCompound && defenseTowers.filter(tower => tower.active && tower.health > 0 && !tower.destroyedAt && mapFilters.showDefenseTowers !== false).map((tower) => (
              <Circle
                key={`tower-radius-${tower.id}`}
                center={[tower.position.lat, tower.position.lng]}
                radius={tower.shieldRadius || 1000}
                pathOptions={{
                  fillColor: '#ff6b35',
                  fillOpacity: 0.12,
                  color: '#ff6b35',
                  weight: 2,
                  opacity: 0.6,
                  dashArray: '8, 8'
                }}
              />
            ))}

            {/* Deployed Drones (when offense filter is enabled) */}
            {mapFilters.showOffense && deployedDrones.map((drone) => (
              <Marker
                key={drone.droneId || drone._id}
                position={[drone.position.lat, drone.position.lng]}
                icon={createDirectionalDroneIcon(drone)}
                eventHandlers={{
                  click: (e) => {
                    e.originalEvent?.stopPropagation();
                    setSelectedDrone(drone);
                    
                    // Auto-activate drone sniffer when clicking on own drone
                    if (!droneSniffersActive.has(drone.droneId)) {
                      toggleDroneSniffer(drone.droneId, drone.position);
                    }
                  }
                }}
              >
                <Popup>
                  <div className="drone-popup">
                    <h4>✈️ {drone.type === 'maritime_scout' ? 'Maritime Scout Drone' : 'Deployed Drone'}</h4>
                    <p>📍 Location: {drone.position?.lat?.toFixed(4) || 'N/A'}, {drone.position?.lng?.toFixed(4) || 'N/A'}</p>
                    <p>⚡ Status: {drone.active ? '🟢 Active' : '🔴 Inactive'}</p>
                    <p>🔍 Type: {drone.type.replace('_', ' ').toUpperCase()}</p>
                    {drone.type === 'maritime_scout' ? (
                      <>
                        <p>🌊 Sniffer Range: 50km</p>
                        <p>⏱️ Cooldown: 20 minutes</p>
                        <DroneSnifferCooldown droneId={drone.droneId} />
                      </>
                    ) : (
                      <>
                        <p>🎯 Attack Range: {drone.attackRange || 500}m</p>
                        <p>📡 Sniffer Range: {drone.type === 'maritime_scout' ? '50km' : '3000m'}</p>
                      </>
                    )}
                    <p>⏰ Expires: {new Date(drone.expiresAt).toLocaleString()}</p>
                    <p>👤 Owner: {drone.ownerUsername || 'You'}</p>
                    <div style={{ marginTop: '10px' }}>
                      {selectedDrone?.droneId === drone.droneId ? (
                        <>
                          <button 
                            onClick={() => setSelectedDrone(null)}
                            style={{
                              background: '#666',
                              color: 'white',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              marginRight: '5px'
                            }}
                          >
                            ❌ Deselect
                          </button>
                          <button 
                            onClick={() => setIsDroneMoving(true)}
                            style={{
                              background: '#ff6b35',
                              color: 'white',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            🎯 Move
                          </button>
                        </>
                      ) : (
                        <button 
                          onClick={() => {
                            setSelectedDrone(drone);
                            // Auto-activate drone sniffer when selecting drone from popup
                            if (!droneSniffersActive.has(drone.droneId)) {
                              toggleDroneSniffer(drone.droneId, drone.position);
                            }
                          }}
                          style={{
                            background: '#007bff',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          🎯 Select Drone
                        </button>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Enemy Defense Towers (when offense filter is enabled) */}
            {mapFilters.showOffense && droneTargets.map((tower) => (
              <Marker
                key={tower.towerId || tower._id}
                position={[tower.position.lat, tower.position.lng]}
                icon={createCustomIcon('🗼', [25, 25])}
              >
                <Popup>
                  <div className="enemy-tower-popup">
                    <h4>🗼 Enemy Defense Tower</h4>
                    <p>📍 Location: {tower.position?.lat?.toFixed(4) || 'N/A'}, {tower.position?.lng?.toFixed(4) || 'N/A'}</p>
                    <p>💪 Health: {tower.health}/{tower.maxHealth}</p>
                    <p>🛡️ Shield Radius: {tower.shieldRadius}m</p>
                    <p>👤 Owner: {tower.ownerUsername}</p>
                    <div style={{ marginTop: '10px' }}>
                      {selectedDrone && selectedDrone.type !== 'scout' && selectedDrone.type !== 'maritime_scout' && (
                        <button 
                          onClick={() => attackTower(tower)}
                          style={{
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          ⚔️ Attack Tower
                        </button>
                      )}
                      {selectedDrone && (selectedDrone.type === 'scout' || selectedDrone.type === 'maritime_scout') && (
                        <p style={{ color: '#888', fontSize: '12px', textAlign: 'center', margin: '5px 0' }}>
                          🛡️ Scout drones are for reconnaissance only
                        </p>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Command Center (if compound exists) */}
            {userCompound && userCompound.commandCenter && (
              <>
                <Marker
                  position={[userCompound.commandCenter.position.lat, userCompound.commandCenter.position.lng]}
                  icon={createCustomIcon(
                    userCompound.commandCenter.isOnWater ? '🛥️' : '🚐', 
                    [40, 40]
                  )}
                >
                  <Popup>
                    <div className="command-center-popup">
                      <h4>{userCompound.commandCenter.isOnWater ? '🛥️ Naval Command Center' : '🚐 Command Center'}</h4>
                      <p>📍 Fortress Headquarters</p>
                      <p>💪 Health: {userCompound.commandCenter.health.toLocaleString()}/{userCompound.commandCenter.maxHealth.toLocaleString()}</p>
                      <p>⚡ Power: {userCompound.commandCenter.powerLevel}%</p>
                      <p>🛡️ Shields: {userCompound.commandCenter.shieldsActive ? '🟢 Active' : '🔴 Offline'}</p>
                      <p>🗼 Defense Towers: {defenseTowers.filter(t => t.active).length}/{defenseTowers.length}</p>
                    
                    <div style={{ marginTop: '10px' }}>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = '/system?tab=compound';
                        }}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          width: '100%',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        🚐 Command & Control
                      </button>
                      
                      {/* Show Move button when command center health is 0 */}
                      {userCompound.commandCenter.health === 0 && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsMovingCommandCenter(true);
                          }}
                          style={{
                            background: '#ff6b35',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            marginTop: '8px'
                          }}
                        >
                          🚚 Move Command Center
                        </button>
                      )}
                    </div>
                  </div>
                </Popup>
                </Marker>
                
                {/* Tower Placement Perimeter (1000m radius) - Only show when placing towers */}
                {isPlacingTower && (
                  <Circle
                    center={[userCompound.commandCenter.position.lat, userCompound.commandCenter.position.lng]}
                    radius={1000}
                    pathOptions={{
                      fillColor: '#00ff41',
                      fillOpacity: 0.2,
                      color: '#00ff41',
                      weight: 3,
                      dashArray: '10, 5'
                    }}
                  />
                )}

                {/* Compound Protection Zone (1000m radius) - Show when defense is adequate */}
                {userCompound.defenseTowers && userCompound.defenseTowers.filter(t => t.active && t.health > 0).length >= 4 && (
                  <Circle
                    center={[userCompound.commandCenter.position.lat, userCompound.commandCenter.position.lng]}
                    radius={1000}
                    pathOptions={{
                      fillColor: '#00ff88',
                      fillOpacity: 0.25,
                      color: '#00ff88',
                      weight: 5,
                      opacity: 1.0,
                      dashArray: '20, 15'
                    }}
                  />
                )}
                
                {/* Compound Protection Zone Warning (1000m radius) - Show when defense is inadequate */}
                {userCompound.defenseTowers && userCompound.defenseTowers.filter(t => t.active && t.health > 0).length < 4 && userCompound.defenseTowers.length > 0 && (
                  <Circle
                    center={[userCompound.commandCenter.position.lat, userCompound.commandCenter.position.lng]}
                    radius={1000}
                    pathOptions={{
                      fillColor: '#ff6b35',
                      fillOpacity: 0.15,
                      color: '#ff6b35',
                      weight: 3,
                      opacity: 0.7,
                      dashArray: '10, 10'
                    }}
                  />
                )}
              </>
            )}

            {/* User's Own Vaults (always visible) */}
            {mapFilters.showVaults && userVaults.map((vault) => {
              const vaultEmoji = vault.isOnWater ? '⚓' : '🔐';
              const dynamicVaultIcon = createCustomIcon(vaultEmoji, [25, 25]);
              
              return (
                <Marker
                  key={`my-vault-${vault.vaultId}`}
                  position={[vault.coordinates.lat, vault.coordinates.lng]}
                  icon={dynamicVaultIcon}
                >
                  <Popup>
                    <div className="vault-popup">
                      <h4>{vault.isOnWater ? '⚓ Maritime Vault' : '🔐 My Vault'}</h4>
                    <p>📍 Location: {vault.coordinates?.lat?.toFixed(4) || 'N/A'}, {vault.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
                    <p>💰 Balance: {vault.storedBalance} WR Coins</p>
                    <p>🔑 ATM Codes: {vault.atmCodes ? vault.atmCodes.length : (vault.atmCodeCount || 0)}/4</p>
                    
                    {/* Compound Protection Status for Vault */}
                    {userCompound && userCompound.commandCenter && userCompound.defenseTowers && (() => {
                      const activeTowers = userCompound.defenseTowers.filter(t => t.active && t.health > 0);
                      
                      // Try different ways to access vault coordinates
                      let vaultPosition = null;
                      if (vault.coordinates && vault.coordinates.lat && vault.coordinates.lng) {
                        vaultPosition = { lat: vault.coordinates.lat, lng: vault.coordinates.lng };
                      } else if (vault.position && vault.position.lat && vault.position.lng) {
                        vaultPosition = { lat: vault.position.lat, lng: vault.position.lng };
                      } else if (vault.lat && vault.lng) {
                        vaultPosition = { lat: vault.lat, lng: vault.lng };
                      } else {
                        console.error('Could not find vault coordinates in:', vault);
                        vaultPosition = { lat: null, lng: null };
                      }
                      
                      const protectionStatus = isTargetProtectedByCompound(vaultPosition, userCompound);
                      
                      return (
                        <div style={{ 
                          margin: '8px 0', 
                          padding: '8px', 
                          borderRadius: '6px',
                          backgroundColor: protectionStatus.protected ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 53, 0.2)',
                          border: `2px solid ${protectionStatus.protected ? '#00ff88' : '#ff6b35'}`
                        }}>
                          <p style={{ 
                            margin: 0, 
                            fontSize: '12px', 
                            fontWeight: 'bold',
                            color: protectionStatus.protected ? '#00ff88' : '#ff6b35'
                          }}>
                            {protectionStatus.protected ? 
                              '🛡️ VAULT PROTECTED - Immune to hacking' : 
                              protectionStatus.reason.includes('Command center is destroyed') ?
                                '⚠️ VAULT VULNERABLE - Command center destroyed' :
                                `⚠️ OUT OF RANGE - ${protectionStatus.distance}m from command center`
                            }
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#666' }}>
                            Defense: {activeTowers.length}/8 towers active
                          </p>
                          {protectionStatus.distance !== null && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: protectionStatus.protected ? '#00ff88' : '#ff6b35' }}>
                              📏 Distance: {protectionStatus.distance}m (Protection radius: {protectionStatus.protectionRadius}m)
                            </p>
                          )}
                          {activeTowers.length >= 4 && !protectionStatus.protected && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#ff6b35' }}>
                              🔄 Move vault within 1000m of command center for protection
                            </p>
                          )}
                          {protectionStatus.protected && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#00ff88' }}>
                              🔐 Hackers must destroy compound first
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    
                    <p>📅 Weekly Move: {vault.weeklyMovementUsed && vault.lastWeeklyMoveAt && new Date() < new Date(new Date(vault.lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000)) ? 'Used' : 'Available'}</p>
                    {vault.isLocked && vault.lockedUntil && new Date() < new Date(vault.lockedUntil) && (
                      <p style={{ color: '#ff6666' }}>🚨 Locked until: {new Date(vault.lockedUntil).toLocaleDateString()}</p>
                    )}
                    {(!vault.weeklyMovementUsed || !vault.lastWeeklyMoveAt || new Date() >= new Date(new Date(vault.lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000))) && (
                      <p style={{ color: '#00ff41' }}>💸 Move cost: 100 WR Coins</p>
                    )}
                    {vault.weeklyMovementUsed && vault.lastWeeklyMoveAt && new Date() < new Date(new Date(vault.lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000)) && (
                      <p style={{ color: '#ffaa00' }}>⏰ Next move in: {Math.ceil((new Date(new Date(vault.lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000)) - new Date()) / (1000 * 60 * 60 * 24))} day(s)</p>
                    )}
                    
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {vault.storedBalance > 0 && (
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWithdrawClick(vault);
                          }}
                          disabled={!isVaultInRange(vault)}
                          style={{
                            background: isVaultInRange(vault) ? '#28a745' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: isVaultInRange(vault) ? 'pointer' : 'not-allowed',
                            width: '100%',
                            fontSize: '12px',
                            opacity: isVaultInRange(vault) ? 1 : 0.6
                          }}
                          title={!isVaultInRange(vault) ? 'Must be within 1000m of vault to withdraw' : 'Withdraw WR Coins from vault'}
                        >
                          {isVaultInRange(vault) ? '💰 Withdraw' : '📏 Out of Range'}
                        </button>
                      )}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          // Navigate to System page ATM Vault tab
                          window.location.href = '/system?tab=vaults';
                        }}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          width: '100%',
                          fontSize: '12px'
                        }}
                      >
                        🔐 Manage Vault
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
              );
            })}

            {/* Other Players' Vaults (only visible when sniffer is active) */}
            {(snifferActive ? nearbyVaults.filter(vault => mapFilters.showVaults && vault.coordinates?.lat && vault.coordinates?.lng) : []).map((vault) => {
              const vaultEmoji = vault.isOnWater ? '⚓' : '🔐';
              const dynamicVaultIcon = createCustomIcon(vaultEmoji, [25, 25]);
              
              return (
                <Marker
                  key={vault.vaultId}
                  position={[vault.coordinates.lat, vault.coordinates.lng]}
                  icon={dynamicVaultIcon}
                  eventHandlers={{
                    click: () => setSelectedVault(vault)
                  }}
                >
                  <Popup>
                    <div className="vault-popup">
                      <h4>{vault.isOnWater ? '⚓ Maritime Vault' : '🔐 ATM Vault'}</h4>
                    <p>👤 Owner: {vault.ownerUsername}</p>
                    <p>📍 Location: {vault.coordinates?.lat?.toFixed(4) || 'N/A'}, {vault.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
                    <p>💰 Balance: {vault.storedBalance} WR Coins</p>
                    <p>🔑 ATM Codes: {vault.atmCodeCount}/4</p>
                    <p>📦 Moves: {vault.movementCount}/3</p>
                    {vault.isLocked && vault.lockedUntil && new Date() < new Date(vault.lockedUntil) && (
                      <p style={{ color: '#ff6666' }}>🚨 Locked until: {new Date(vault.lockedUntil).toLocaleDateString()}</p>
                    )}
                    
                    {/* Show protection status */}
                    {vault.isProtected && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: 'rgba(40, 167, 69, 0.2)', 
                        border: '1px solid #28a745',
                        borderRadius: '4px'
                      }}>
                        <p style={{ margin: '0', color: '#28a745', fontWeight: 'bold' }}>
                          🛡️ PROTECTED BY COMMAND CENTER
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                          Must destroy command center first
                        </p>
                      </div>
                    )}
                    
                    {!vault.isProtected && vault.commandCenterActive === false && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: 'rgba(220, 53, 69, 0.2)', 
                        border: '1px solid #dc3545',
                        borderRadius: '4px'
                      }}>
                        <p style={{ margin: '0', color: '#dc3545', fontWeight: 'bold' }}>
                          ⚠️ VULNERABLE - NO PROTECTION
                        </p>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                          Command center destroyed
                        </p>
                      </div>
                    )}
                    
                    <div style={{ marginTop: '10px' }}>
                      {(() => {
                        // Check if this vault was detected by a drone
                        const isDroneDetected = Object.values(droneDetectedVaults).some(vaults => 
                          Array.isArray(vaults) && vaults.some(detectedVault => detectedVault.vaultId === vault.vaultId)
                        );
                        
                        if (isDroneDetected) {
                          return (
                            <div style={{ 
                              padding: '8px', 
                              backgroundColor: '#f8f9fa', 
                              borderRadius: '4px', 
                              border: '1px solid #ff6b35',
                              textAlign: 'center'
                            }}>
                              <p style={{ margin: '0', color: '#ff6b35', fontSize: '10px', fontWeight: 'bold' }}>
                                ✈️ Drone Reconnaissance Only
                              </p>
                            </div>
                          );
                        }
                        
                        return (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVault(vault);
                            }}
                            disabled={vault.isProtected}
                            style={{
                              background: vault.isProtected ? '#6c757d' : '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '3px',
                              cursor: vault.isProtected ? 'not-allowed' : 'pointer',
                              width: '100%',
                              opacity: vault.isProtected ? 0.6 : 1
                            }}
                          >
                            {vault.isProtected ? '🛡️ Protected' : '🔨 Hack Vault'}
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                </Popup>
              </Marker>
              );
            })}

            {/* Drone-Detected Targets */}
            {/* Drone-detected Banks */}
            {Object.entries(droneDetectedBanks).map(([droneId, banks]) => 
              droneSniffersActive.has(droneId) && Array.isArray(banks) ? banks.filter(bank => mapFilters.showBanks).map((bank) => (
                <Marker
                  key={`drone-${droneId}-bank-${bank.bankId}`}
                  position={[bank.position.lat, bank.position.lng]}
                  icon={bankIcon}
                  eventHandlers={{
                    click: () => setSelectedBank(bank)
                  }}
                >
                  <Popup>
                    <div className="bank-popup">
                      <h4>🏦 {bank.name}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>👑 Owner: {bank.ownedBy === 'occult_bank' ? 'Occult Bank' : 'Private'}</p>
                      <p>Type: {bank.type}</p>
                      <p>Security: Level {bank.securityLevel}</p>
                      <p>💰 Reward: {bank.reward ? `${bank.reward.toLocaleString()} WR Coins` : '50,000-100,000 WR Coins'}</p>
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center' }}>
                        <p style={{ margin: '0', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                          👁️ Visual Reconnaissance Only - No Remote Access
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected Casinos */}
            {Object.entries(droneDetectedCasinos).map(([droneId, casinos]) => 
              droneSniffersActive.has(droneId) && Array.isArray(casinos) ? casinos.filter(casino => mapFilters.showCasinos).map((casino) => (
                <Marker
                  key={`drone-${droneId}-casino-${casino.casinoId}`}
                  position={[casino.position.lat, casino.position.lng]}
                  icon={casinoIcon}
                  eventHandlers={{
                    click: () => setSelectedCasino(casino)
                  }}
                >
                  <Popup>
                    <div className="casino-popup">
                      <h4>🎰 {casino.name}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>📍 Location: {casino.location}</p>
                      <p>👑 Owner: {casino.ownedBy === 'occult_bank' ? 'Occult Bank' : 'Private'}</p>
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center' }}>
                        <p style={{ margin: '0', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                          👁️ Visual Reconnaissance Only - No Remote Access
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected ATMs */}
            {Object.entries(droneDetectedATMs).map(([droneId, atms]) => 
              droneSniffersActive.has(droneId) && Array.isArray(atms) ? atms.filter(atm => mapFilters.showATMs).map((atm) => (
                <Marker
                  key={`drone-${droneId}-atm-${atm.atmId}`}
                  position={[atm.position.lat, atm.position.lng]}
                  icon={atmIcon}
                  eventHandlers={{
                    click: () => setSelectedATM(atm)
                  }}
                >
                  <Popup>
                    <div className="atm-popup">
                      <h4>🏧 ATM #{atm.atmId}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>📍 {atm.locationDescription}</p>
                      <p>💰 Reward: 500-2000 WR Coins</p>
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center' }}>
                        <p style={{ margin: '0', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                          👁️ Visual Reconnaissance Only - No Remote Access
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected Users */}
            {Object.entries(droneDetectedUsers).map(([droneId, users]) => 
              droneSniffersActive.has(droneId) && Array.isArray(users) ? users.filter(user => mapFilters.showUsers).map((detectedUser) => (
                <Marker
                  key={`drone-${droneId}-user-${detectedUser.userId}`}
                  position={[detectedUser.gameLocation.lat, detectedUser.gameLocation.lng]}
                  icon={userIcon}
                >
                  <Popup>
                    <div className="user-popup">
                      <h4>👤 {detectedUser.username}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>💰 WR Coins: {detectedUser.wrCoins?.toLocaleString() || 'Unknown'}</p>
                      <p>⭐ XP: {detectedUser.xp || 0}</p>
                      <p>📏 Distance: {detectedUser.distance}m</p>
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected Vaults */}
            {Object.entries(droneDetectedVaults).map(([droneId, vaults]) => 
              droneSniffersActive.has(droneId) && Array.isArray(vaults) ? vaults.filter(vault => mapFilters.showVaults).map((vault) => {
                const vaultEmoji = vault.isOnWater ? '⚓' : '🔐';
                const dynamicVaultIcon = createCustomIcon(vaultEmoji, [25, 25]);
                
                return (
                  <Marker
                    key={`drone-${droneId}-vault-${vault.vaultId}`}
                    position={[vault.position.lat, vault.position.lng]}
                    icon={dynamicVaultIcon}
                  >
                    <Popup>
                      <div className="vault-popup">
                        <h4>{vault.isOnWater ? '⚓ Maritime Vault' : '🔐 ATM Vault'}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>👑 Owner: {vault.ownerUsername}</p>
                      <p>💰 Balance: {vault.storedBalance?.toLocaleString() || 0} WR Coins</p>
                      <p>🔑 ATM Codes: {vault.atmCodeCount || 0}</p>
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center' }}>
                        <p style={{ margin: '0', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                          👁️ Visual Reconnaissance Only - No Remote Access
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
                );
              }) : []
            ).flat()}

            {/* Drone-detected Command Centers */}
            {Object.entries(droneDetectedCompounds).map(([droneId, compounds]) => 
              droneSniffersActive.has(droneId) && Array.isArray(compounds) ? compounds.filter(compound => 
                mapFilters.showCompounds !== false && 
                compound.owner?.username !== user?.username
              ).map((compound) => (
                <Marker
                  key={`drone-${droneId}-compound-${compound._id}`}
                  position={[compound.position.lat, compound.position.lng]}
                  icon={createCustomIcon(
                    compound.isOnWater ? '🛥️' : '🚐', 
                    [25, 25]
                  )}
                >
                  <Popup>
                    <div className="compound-popup">
                      <h4>{compound.isOnWater ? '🛥️ Naval Command Center' : '🚐 Command Center'}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>👑 Owner: {compound.owner?.username || 'Unknown'}</p>
                      <p>📏 Distance: {compound.distance}m</p>
                      <p>🗼 Defense Towers: {compound.activeTowers || 0}</p>
                      <p>🚨 Alert Level: {compound.alertLevel || 'Green'}</p>
                      
                      {/* Attack button when in attack mode with nuke drone */}
                      {attackMode && selectedAttackDrone && selectedAttackDrone.type === 'nuke' && selectedAttackDrone.type !== 'scout' && selectedAttackDrone.type !== 'maritime_scout' && (
                        <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(255, 255, 0, 0.1)', borderRadius: '4px', border: '1px solid #ffff00' }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#ffff00', fontSize: '12px' }}>
                            🎯 ATTACK TARGET: COMMAND CENTER
                          </p>
                          <p style={{ margin: '0 0 8px 0', color: '#ffcc00', fontSize: '11px' }}>
                            Nuke Drone: {selectedAttackDrone.droneId} | Range: 5000m | Duration: 20min
                          </p>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              // Convert compound to command center target format
                              const commandCenterTarget = {
                                type: 'command_center',
                                position: compound.position,
                                ownerId: compound.owner?._id || compound.ownerId,
                                ownerUsername: compound.owner?.username || 'Unknown',
                                compoundId: compound._id,
                                distance: compound.distance
                              };
                              attackTowerWithDrone(commandCenterTarget);
                            }}
                            style={{
                              background: '#ffd700',
                              color: '#000',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              width: '100%'
                            }}
                          >
                            🚀 NUKE COMMAND CENTER
                          </button>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected Computers */}
            {Object.entries(droneDetectedComputers).map(([droneId, computers]) => {
              return droneSniffersActive.has(droneId) && Array.isArray(computers) ? computers.filter(computer => mapFilters.showComputers !== false).map((computer) => (
                <Marker
                  key={`drone-${droneId}-computer-${computer.ownerId || computer._id}`}
                  position={[computer.position.lat, computer.position.lng]}
                  icon={createCustomIcon('💻', [25, 25])}
                >
                  <Popup>
                    <div className="computer-popup">
                      <h4>💻 Computer</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>👑 Owner: {computer.ownerUsername || 'Unknown'}</p>
                      <p>📏 Distance: {Math.round(computer.distance || 0)}m</p>
                      <p>🖥️ Status: Active</p>
                      <p>📡 Sniffer Range: Variable</p>
                    </div>
                  </Popup>
                </Marker>
              )) : [];
            }).flat()}

            {/* Drone-detected Defense Towers */}
            {Object.entries(droneDetectedTowers).map(([droneId, towers]) => 
              droneSniffersActive.has(droneId) && Array.isArray(towers) ? towers.filter(tower => mapFilters.showTowers !== false).map((tower) => (
                <Marker
                  key={`drone-${droneId}-tower-${tower._id}`}
                  position={[tower.position.lat, tower.position.lng]}
                  icon={createCustomIcon('🗼', [20, 20])}
                >
                  <Popup>
                    <div className="tower-popup">
                      <h4>🗼 Defense Towers</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>👑 Owner: {tower.ownerUsername || 'Unknown'}</p>
                      <p>📏 Distance: {tower.distance}m</p>
                      <p>❤️ Health: {tower.health || 'Unknown'}/{tower.maxHealth || 'Unknown'}</p>
                      <p>🗼 Tower ID: {tower.towerId || 'Unknown'}</p>
                      <p>⚡ Status: Active</p>
                      
                      {/* Attack button when in attack mode */}
                      {attackMode && selectedAttackDrone && selectedAttackDrone.type !== 'scout' && selectedAttackDrone.type !== 'maritime_scout' && (
                        <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(255, 68, 68, 0.1)', borderRadius: '4px' }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#ff4444', fontSize: '12px' }}>
                            🎯 TARGET AVAILABLE
                          </p>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              attackTowerWithDrone(tower);
                            }}
                            style={{
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              width: '100%'
                            }}
                          >
                            ⚔️ ATTACK WITH {selectedAttackDrone.name?.toUpperCase() || 'DRONE'}
                          </button>
                          <p style={{ fontSize: '10px', color: '#888', margin: '5px 0 0 0', textAlign: 'center' }}>
                            Attack Duration: 30 minutes
                          </p>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected Houses */}
            {Object.entries(droneDetectedHouses).map(([droneId, houses]) => 
              droneSniffersActive.has(droneId) && Array.isArray(houses) ? houses.filter(house => mapFilters.showHouses !== false).map((house) => (
                <Marker
                  key={`drone-${droneId}-house-${house._id}`}
                  position={[house.position?.lat || house.coordinates?.lat || house.location?.lat, house.position?.lng || house.coordinates?.lng || house.location?.lng]}
                  icon={createCustomIcon('🏠', [25, 25])}
                >
                  <Popup>
                    <div className="house-popup">
                      <h4>🏠 House</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>🏠 {house.name || 'Residential Property'}</p>
                      <p>🛏️ Bedrooms: {house.bedrooms || 'Unknown'}</p>
                      <p>💰 Price: {house.price ? `${house.price.toLocaleString()} WR Coins` : 'Unknown'}</p>
                      <p>📋 Status: {house.isForSale ? 'For Sale' : house.isForRent ? 'For Rent' : 'Private'}</p>
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Drone-detected Businesses */}
            {Object.entries(droneDetectedBusinesses).map(([droneId, businesses]) => 
              droneSniffersActive.has(droneId) && Array.isArray(businesses) ? businesses.filter(business => mapFilters.showBusinesses !== false).map((business) => {
                // Find the detecting drone to check if it's a scout drone
                const detectingDrone = deployedDrones.find(drone => drone.droneId === droneId);
                const isScoutDrone = detectingDrone && (detectingDrone.type === 'scout' || detectingDrone.type === 'maritime_scout');
                
                // Determine business icon based on type (use businessType field from backend)
                let businessEmoji = '🏢';
                const businessType = business.businessType || business.type;
                switch(businessType) {
                  case 'shop': businessEmoji = '🏪'; break;
                  case 'warehouse': businessEmoji = '🏭'; break;
                  case 'factory': businessEmoji = '🏗️'; break;
                  case 'farm': businessEmoji = '🚜'; break;
                  case 'fishing': {
                    // Always use fishing hook icon for fishing businesses
                    businessEmoji = '🎣';
                    break;
                  }
                  default: businessEmoji = '🏢';
                }
                
                const businessIcon = createCustomIcon(businessEmoji, [25, 25]);
                
                return (
                  <Marker
                    key={`drone-${droneId}-business-${business._id}`}
                    position={[business.position?.lat || business.location?.lat || business.coordinates?.lat, business.position?.lng || business.location?.lng || business.coordinates?.lng]}
                    icon={businessIcon}
                  >
                    <Popup>
                      <div className="business-popup" style={{ minWidth: '250px', maxWidth: '300px' }}>
                        
                        {/* Business Image */}
                        {business.businessImageUrl && (
                          <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                            <img 
                              src={business.businessImageUrl.startsWith('http') ? business.businessImageUrl : `${window.location.origin}${business.businessImageUrl}`}
                              alt={business.businessName || business.name || 'Business'}
                              style={{ 
                                width: '240px', 
                                height: '160px', 
                                objectFit: 'cover', 
                                borderRadius: '8px',
                                border: '2px solid #00ff41'
                              }}
                              onError={(e) => {
                                // Hide broken image without console spam
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        <h4 style={{ margin: '0 0 10px 0', color: '#00ff41' }}>
                          {businessEmoji} {business.businessName || business.name || 'Business'}
                        </h4>
                        
                        <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                          ✈️ Detected by {isScoutDrone ? 'Scout' : 'Drone'} Sniffer
                        </p>
                        
                        <div style={{ marginBottom: '10px' }}>
                          <p style={{ margin: '5px 0', fontSize: '12px' }}>
                            <strong>Owner:</strong> {business.ownerUsername || 'Unknown'}
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '12px' }}>
                            <strong>Type:</strong> {businessType === 'fishing' ? 'Fishing Farm' : businessType ? businessType.charAt(0).toUpperCase() + businessType.slice(1) : 'Unknown'}
                          </p>
                          {business.description && (
                            <p style={{ margin: '5px 0', fontSize: '12px', fontStyle: 'italic' }}>
                              "{business.description}"
                            </p>
                          )}
                        </div>
                        
                        <div style={{ marginBottom: '10px', padding: '8px', background: 'rgba(0, 255, 65, 0.1)', borderRadius: '5px' }}>
                          <p style={{ margin: '2px 0', fontSize: '11px' }}>
                            <strong>Produces:</strong> {
                              (() => {
                                const produces = business.supplyChain?.produces || business.produces;
                                switch(produces) {
                                  case 'consumer_goods': return 'Food & Supplies for Consumers';
                                  case 'supplies': return 'Processed Supplies';
                                  case 'processed_goods': return 'Manufactured Products';
                                  case 'raw_materials': return 'Raw Materials';
                                  case 'food': return 'Food Products';
                                  default: {
                                    // Fallback based on business type
                                    switch(businessType) {
                                      case 'shop': return 'Food & Supplies for Consumers';
                                      case 'warehouse': return 'Processed Supplies';
                                      case 'factory': return 'Manufactured Products';
                                      case 'farm': return 'Raw Materials';
                                      case 'fishing': return 'Fresh Seafood';
                                      default: return 'Unknown';
                                    }
                                  }
                                }
                              })()
                            }
                          </p>
                          <p style={{ margin: '2px 0', fontSize: '11px' }}>
                            <strong>Requires:</strong> {
                              (() => {
                                const requires = business.supplyChain?.requires || business.requires;
                                switch(requires) {
                                  case 'supplies': return 'Processed Supplies';
                                  case 'processed_goods': return 'Manufactured Products';
                                  case 'raw_materials': return 'Raw Materials';
                                  case 'none': return 'None (Self-sufficient)';
                                  default: {
                                    // Fallback based on business type
                                    switch(businessType) {
                                      case 'shop': return 'Processed Supplies';
                                      case 'warehouse': return 'Manufactured Products';
                                      case 'factory': return 'Raw Materials';
                                      case 'farm': return 'None (Self-sufficient)';
                                      case 'fishing': return 'None (Self-sufficient)';
                                      default: return 'Unknown';
                                    }
                                  }
                                }
                              })()
                            }
                          </p>
                          <p style={{ margin: '2px 0', fontSize: '11px' }}>
                            <strong>Status:</strong> {business.operationalStatus || 'operational'}
                          </p>
                          <p style={{ margin: '2px 0', fontSize: '11px' }}>
                            <strong>Supply Level:</strong> {business.supplyLevel || business.supplies || 0}/{business.maxCapacity || 1000}
                          </p>
                          {business.pricing?.supplyPrice && (
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Supply Price:</strong> {business.pricing.supplyPrice} WR Coins/unit
                            </p>
                          )}
                          {businessType === 'fishing' && (
                            <p style={{ margin: '8px 0 2px 0', fontSize: '10px', color: '#ffaa00', fontStyle: 'italic' }}>
                              🌊 Maritime Operation - Cannot link to supply chains
                            </p>
                          )}
                        </div>
                        
                      </div>
                    </Popup>
                  </Marker>
                );
              }) : []
            ).flat()}


            {/* Drone-detected Other Drones */}
            {Object.entries(droneDetectedDrones).map(([droneId, otherDrones]) => 
              droneSniffersActive.has(droneId) && Array.isArray(otherDrones) ? otherDrones.filter(otherDrone => mapFilters.showDrones !== false).map((otherDrone) => (
                <Marker
                  key={`drone-${droneId}-otherdrone-${otherDrone.droneId || otherDrone._id}`}
                  position={[otherDrone.position.lat, otherDrone.position.lng]}
                  icon={createCustomIcon('✈️', [25, 25])}
                >
                  <Popup>
                    <div className="drone-popup">
                      <h4>✈️ {otherDrone.name}</h4>
                      <p style={{ color: '#ff6b35', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                        ✈️ Detected by Drone Sniffer
                      </p>
                      <p>👑 Owner: {otherDrone.ownerUsername || 'Unknown'}</p>
                      <p>📏 Distance: {Math.round(otherDrone.distance || 0)}m</p>
                      <p>🎯 Type: {otherDrone.droneType || otherDrone.type}</p>
                      <p>⚡ Status: {otherDrone.status?.active ? 'Active' : 'Inactive'}</p>
                      <p>📡 Sniffer Range: {otherDrone.droneType === 'maritime_scout' || otherDrone.type === 'maritime_scout' ? '50km' : '3000m'}</p>
                    </div>
                  </Popup>
                </Marker>
              )) : []
            ).flat()}

            {/* Computer Locations */}
            {computerLocations.filter(computer => {
              // Apply filter for computers - but show user's own computer regardless of sniffer
              if (!mapFilters.showComputers) {
                return false;
              }
              // Basic filtering for valid coordinates
              if (!computer.coordinates || !computer.coordinates.lat || !computer.coordinates.lng) {
                return false;
              }
              
              // Trust backend's isHidden logic for all computers
              // Backend handles all hiding conditions: jail, sleeping, working, manually hidden, checked into hotels
              if (computer.isHidden) {
                return false;
              }
              
              return true;
            }).map((computer) => {
              // Determine computer emoji based on status
              let computerEmoji = '💻';
              
              // Check if computer is on water (boat mode)
              if (computer.isOnWater) {
                computerEmoji = '⛵'; // Boat icon for water-based computers
              } else if (computer.isFieldAgent) {
                // Show black suit only for active Field Agents during operations
                if (computer.userId === user?.id || computer.userId === user?._id) {
                  computerEmoji = fieldOperation ? '🤵' : '💻';
                } else {
                  computerEmoji = '🤵'; // Other field agents always show black suit when detected
                }
              }
              
              const dynamicComputerIcon = createCustomIcon(computerEmoji, [25, 25]);
              
              return (
                <Marker
                  key={computer.userId}
                  position={[computer.coordinates.lat, computer.coordinates.lng]}
                  icon={dynamicComputerIcon}
                  eventHandlers={{
                    click: async () => {
                      
                      if (computer.userId === user?.id || computer.userId === user?._id) {
                        // Activate sniffer when clicking own computer
                        if (!snifferActive) {
                          toggleSniffer();
                        } else {
                          setShowMyComputerModal(true);
                        }
                      } else {
                        
                        // If Field Agent with active operation, get user's active hacks
                        if (fieldOperation) {
                          console.log('Field Agent viewing target computer - loading active hacks');
                          const activeHacksData = await getUserActiveHacks(computer.userId);
                          setArrestData(activeHacksData);
                        }
                        
                        setSelectedComputer(computer);
                      }
                    }
                  }}
                >
                  <Popup>
                    <div className="computer-popup">
                      <h4>{computer.isFieldAgent ? '🤵' : '💻'} {(computer.userId === user?.id || computer.userId === user?._id) ? 'Your Computer' : `${computer.username}'s Computer`}</h4>
                      <p>Owner: {computer.username}{computer.isFieldAgent ? ' 🤵 Field Agent' : ''}</p>
                      <p>Status: {
                        computer.isSleeping ? '😴 Sleeping in house' :
                        computer.isActive ? '🟢 Active' : '🔴 Inactive'
                      }</p>
                      {(computer.userId === user?.id || computer.userId === user?._id) ? (
                        <>
                          <p>Moves: {computerStats.movesRemaining}/{computerStats.maxMoves}</p>
                          
                          {/* Compound Protection Status */}
                          {userCompound && userCompound.commandCenter && userCompound.defenseTowers && (() => {
                            const activeTowers = userCompound.defenseTowers.filter(t => t.active && t.health > 0);
                            
                            
                            // Try different ways to access computer coordinates
                            let computerPosition = null;
                            if (computer.lat && computer.lng) {
                              computerPosition = { lat: computer.lat, lng: computer.lng };
                            } else if (computer.position && computer.position.lat && computer.position.lng) {
                              computerPosition = { lat: computer.position.lat, lng: computer.position.lng };
                            } else if (computer.coordinates && computer.coordinates.lat && computer.coordinates.lng) {
                              computerPosition = { lat: computer.coordinates.lat, lng: computer.coordinates.lng };
                            } else {
                              console.error('Could not find computer coordinates in:', computer);
                              computerPosition = { lat: null, lng: null };
                            }
                            
                            const protectionStatus = isTargetProtectedByCompound(computerPosition, userCompound);
                            
                            return (
                              <div style={{ 
                                margin: '8px 0', 
                                padding: '8px', 
                                borderRadius: '6px',
                                backgroundColor: protectionStatus.protected ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 53, 0.2)',
                                border: `2px solid ${protectionStatus.protected ? '#00ff88' : '#ff6b35'}`
                              }}>
                                <p style={{ 
                                  margin: 0, 
                                  fontSize: '12px', 
                                  fontWeight: 'bold',
                                  color: protectionStatus.protected ? '#00ff88' : '#ff6b35'
                                }}>
                                  {protectionStatus.protected ? 
                                    '🛡️ FULLY PROTECTED - Immune to attacks' : 
                                    protectionStatus.reason.includes('Command center is destroyed') ?
                                      '⚠️ VULNERABLE - Command center destroyed' :
                                      `⚠️ OUT OF RANGE - ${protectionStatus.distance}m from command center`
                                  }
                                </p>
                                <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#666' }}>
                                  Defense Status: {activeTowers.length}/8 towers • {userCompound.commandCenter.health > 0 ? 'Command Center: ✅' : 'Command Center: ❌'}
                                </p>
                                {protectionStatus.distance !== null && (
                                  <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: protectionStatus.protected ? '#00ff88' : '#ff6b35' }}>
                                    📏 Distance: {protectionStatus.distance}m (Protection radius: {protectionStatus.protectionRadius}m)
                                  </p>
                                )}
                                {!protectionStatus.protected && (
                                  <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#ff6b35' }}>
                                    📋 Move computer within 1000m of command center for protection
                                  </p>
                                )}
                                {protectionStatus.protected && (
                                  <p style={{ margin: '4px 0 0 0', fontSize: '10px', color: '#00ff88' }}>
                                    📋 Attackers must destroy all towers + command center first
                                  </p>
                                )}
                              </div>
                            );
                          })()}
                          
                          <p style={{ fontSize: '12px', color: computer.isSleeping ? '#ffaa00' : '#00ff41' }}>
                            {computer.isSleeping ? '😴 Computer unavailable while sleeping' :
                             snifferActive ? `📡 Sniffer Active (${computer.isFieldAgent ? '2000m' : `${2000 + (user?.snifferInventory?.length || 0) * 600}m`} radius)${computer.isFieldAgent ? ' - Stealth Mode!' : ''}` : 
                             `📡 Click to activate sniffer${computer.isFieldAgent ? ' (Stealth 2000m range)' : ` (${2000 + (user?.snifferInventory?.length || 0) * 600}m range)`}`}
                          </p>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!computer.isSleeping) {
                                toggleSniffer();
                              }
                            }}
                            disabled={computer.isSleeping}
                            style={{
                              background: computer.isSleeping ? '#666' : 
                                         snifferActive ? '#ff8800' : '#00ff41',
                              color: computer.isSleeping ? '#999' : 
                                     snifferActive ? 'white' : 'black',
                              border: 'none',
                              padding: '5px 10px',
                              borderRadius: '3px',
                              cursor: computer.isSleeping ? 'not-allowed' : 'pointer',
                              width: '100%',
                              marginTop: '5px'
                            }}
                          >
                            {computer.isSleeping ? '😴 Sniffer Disabled' :
                             snifferActive ? '📡 Disable Sniffer' : '📡 Activate Sniffer'}
                          </button>
                          
                          {computerStats.movesRemaining > 0 && (
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!computer.isSleeping) {
                                  setIsMovingComputer(true);
                                }
                              }}
                              disabled={computer.isSleeping}
                              style={{
                                background: computer.isSleeping ? '#666' : '#0066cc',
                                color: computer.isSleeping ? '#999' : 'white',
                                border: 'none',
                                padding: '5px 10px',
                                borderRadius: '3px',
                                cursor: computer.isSleeping ? 'not-allowed' : 'pointer',
                                width: '100%',
                                marginTop: '5px'
                              }}
                            >
                              {computer.isSleeping ? '😴 Movement Disabled' : 
                               `🚚 Move Computer (${computerStats.movesRemaining}/10 moves)`}
                            </button>
                          )}
                        </>
                      ) : (
                        snifferActive && (
                          <>
                            {computer.isFieldAgent && (
                              <div style={{ 
                                backgroundColor: '#ff4444', 
                                color: 'white', 
                                padding: '5px', 
                                borderRadius: '3px', 
                                marginTop: '5px',
                                fontSize: '11px',
                                textAlign: 'center'
                              }}>
                                ⚠️ WARNING: Field Agent Security - High Risk!
                              </div>
                            )}
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (computer.isFieldAgent) {
                                  if (!window.confirm(`🚨 WARNING: ${computer.username} is a Field Agent with government-grade security systems. Attempting to hack this computer will result in immediate arrest and 8 hours in jail. Are you sure you want to proceed?`)) {
                                    return;
                                  }
                                }
                                hackComputer(computer);
                              }}
                              style={{
                                background: computer.isFieldAgent ? '#cc0000' : '#ff4444',
                                color: 'white',
                                border: 'none',
                                padding: '5px 10px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                width: '100%',
                                marginTop: '5px'
                              }}
                            >
                              {computer.isFieldAgent ? '🚨 Hack Agent (DANGEROUS!)' : '🔓 Hack Computer'}
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Users in Radius (when sniffer is active) */}
            {snifferActive && Array.isArray(usersInRadius) && usersInRadius.filter(userData => mapFilters.showUsers && userData.coordinates && userData.coordinates.lat && userData.coordinates.lng).map((userData) => {
              // Keep user icons consistent - no dynamic changes
              let userIconEmoji = '👤';
              
              const dynamicUserIcon = createCustomIcon(userIconEmoji, [25, 25]);
              
              return (
                <Marker
                  key={userData.userId}
                  position={[userData.coordinates.lat, userData.coordinates.lng]}
                  icon={dynamicUserIcon}
                >
                  <Popup>
                    <div className="user-popup" style={{ minWidth: '250px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: userData.role === 'admin' ? '#ffd700' : '#00ff41' }}>
                        {userIconEmoji} {userData.username}
                        {userData.role === 'admin' && <span style={{ fontSize: '12px', marginLeft: '5px' }}>(Admin)</span>}
                      </h4>
                      
                      <div style={{ fontSize: '13px', lineHeight: '1.4' }}>
                        <p style={{ margin: '3px 0' }}>
                          <strong>💰 WR Coins:</strong> {userData.wrCoins?.toFixed(3) || '0.000'}
                        </p>
                        <p style={{ margin: '3px 0' }}>
                          <strong>⭐ XP:</strong> {userData.xp || 0} (Level {userData.level})
                        </p>
                        <p style={{ margin: '3px 0' }}>
                          <strong>📍 Distance:</strong> {userData.distance ? (userData.distance / 1000).toFixed(2) : '0.00'} km
                        </p>
                        <p style={{ margin: '3px 0' }}>
                          <strong>🏆 Status:</strong> <span style={{ 
                            color: userData.status === 'Jailed' ? '#ff4444' : 
                                   userData.status === 'Operating' ? '#ff8800' : 
                                   userData.status === 'Admin' ? '#ffd700' : '#44ff44'
                          }}>{userData.status}</span>
                        </p>
                        {userData.totalParts > 0 && (
                          <p style={{ margin: '3px 0' }}>
                            <strong>🔧 Parts:</strong> {userData.totalParts}
                          </p>
                        )}
                        {userData.totalExpertTasks > 0 && (
                          <p style={{ margin: '3px 0' }}>
                            <strong>🎯 Expert Tasks:</strong> {userData.totalExpertTasks}
                          </p>
                        )}
                        <p style={{ margin: '3px 0' }}>
                          <strong>⚡ Mining:</strong> {userData.miningEfficiency.toFixed(1)}x efficiency
                        </p>
                        {userData.hasComputer && (
                          <p style={{ margin: '3px 0' }}>
                            <strong>💻 Computer:</strong> Deployed
                          </p>
                        )}
                        <p style={{ margin: '3px 0', fontSize: '11px', color: '#888' }}>
                          Account: {userData.accountAge} days old
                        </p>
                        
                        {/* Protection Status */}
                        {userData.protection && (
                          <div style={{ 
                            margin: '8px 0', 
                            padding: '8px', 
                            borderRadius: '6px',
                            backgroundColor: userData.protection.isProtected ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 53, 0.2)',
                            border: `2px solid ${userData.protection.isProtected ? '#00ff88' : '#ff6b35'}`
                          }}>
                            <p style={{ 
                              margin: 0, 
                              fontSize: '12px', 
                              fontWeight: 'bold',
                              color: userData.protection.isProtected ? '#00ff88' : '#ff6b35'
                            }}>
                              🛡️ {userData.protection.isProtected ? 'Protected' : 'Vulnerable'}
                            </p>
                            <p style={{ 
                              margin: '2px 0 0 0', 
                              fontSize: '11px', 
                              color: '#ccc' 
                            }}>
                              {userData.protection.hasCompound ? (
                                userData.protection.isProtected ? 
                                  `Within compound radius (${userData.protection.distanceToCommandCenter}m from center)` :
                                  `Outside compound radius (${userData.protection.distanceToCommandCenter}m from center)`
                              ) : (
                                'No compound defense system'
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}


            {/* User's Own Defense Towers (always visible) */}
            {userCompound && userCompound.defenseTowers && userCompound.defenseTowers.map((tower) => (
              <Marker
                key={`own-tower-${tower.id}`}
                position={[tower.position.lat, tower.position.lng]}
                icon={createCustomIcon('🗼', [20, 20])}
              >
                <Popup>
                  <div className="tower-popup">
                    <h4>🗼 Your Defense Tower</h4>
                    <p style={{ color: '#00ff41', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                      👑 Your Defense System
                    </p>
                    <p>❤️ Health: {tower.health.toLocaleString()}/{tower.maxHealth.toLocaleString()}</p>
                    <p>🔫 Status: {tower.active ? 'Active' : 'Offline'}</p>
                    <p>🎯 Range: {tower.range}m</p>
                    <p>⚡ Power: {tower.power}/{tower.maxPower}</p>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Sniffer-detected Command Centers (other users only) */}
            {snifferActive && nearbyCompounds.filter(compound => 
              mapFilters.showCompounds !== false && 
              compound.ownerUsername !== user?.username
            ).map((compound) => (
              <Marker
                key={`sniffer-compound-${compound.id}`}
                position={[compound.commandCenter.position.lat, compound.commandCenter.position.lng]}
                icon={createCustomIcon(
                  compound.commandCenter.isOnWater ? '🛥️' : '🚐', 
                  [25, 25]
                )}
              >
                <Popup>
                  <div className="compound-popup">
                    <h4>{compound.commandCenter.isOnWater ? '🛥️ Naval Command Center' : '🚐 Command Center'}</h4>
                    <p style={{ color: '#00ff41', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                      📡 Detected by Sniffer Tool
                    </p>
                    <p>👑 Owner: {compound.ownerUsername}</p>
                    <p>📏 Distance: {compound.distance}m</p>
                    <p>❤️ Health: {compound.commandCenter.health.toLocaleString()}/{compound.commandCenter.maxHealth.toLocaleString()}</p>
                    <p>🛡️ Shields: {compound.commandCenter.shieldsActive ? 'Active' : 'Down'}</p>
                    <p>🗼 Defense Towers: {compound.activeTowers}/{compound.totalTowers}</p>
                    <div style={{ marginTop: '10px', fontSize: '11px', color: '#ffaa44' }}>
                      <p>⚠️ This is a protected compound defense system</p>
                      <p>🎯 Requires assault and nuke drones to attack</p>
                    </div>
                    
                    {/* Attack button when in attack mode with nuke drone */}
                    {attackMode && selectedAttackDrone && selectedAttackDrone.type === 'nuke' && (
                      <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(255, 255, 0, 0.1)', borderRadius: '4px', border: '1px solid #ffff00' }}>
                        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#ffff00', fontSize: '12px' }}>
                          🎯 ATTACK TARGET: COMMAND CENTER
                        </p>
                        <p style={{ margin: '0 0 8px 0', color: '#ffcc00', fontSize: '11px' }}>
                          Nuke Drone: {selectedAttackDrone.droneId} | Range: 5000m | Duration: 20min
                        </p>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            // Convert compound to command center target format
                            const commandCenterTarget = {
                              type: 'command_center',
                              position: compound.commandCenter.position,
                              ownerId: compound.ownerId,
                              ownerUsername: compound.ownerUsername,
                              compoundId: compound.id,
                              distance: compound.distance
                            };
                            attackTowerWithDrone(commandCenterTarget);
                          }}
                          style={{
                            background: '#ffd700',
                            color: '#000',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            width: '100%'
                          }}
                        >
                          🚀 NUKE COMMAND CENTER
                        </button>
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Sniffer-detected Defense Towers */}
            {snifferActive && nearbyDefenseTowers.filter(tower => mapFilters.showDefenseTowers !== false).map((tower) => (
              <Marker
                key={`sniffer-tower-${tower.id}`}
                position={[tower.position.lat, tower.position.lng]}
                icon={createCustomIcon('🗼', [20, 20])}
              >
                <Popup>
                  <div className="tower-popup">
                    <h4>🗼 Defense Tower</h4>
                    <p style={{ color: '#00ff41', fontSize: '11px', fontStyle: 'italic', margin: '5px 0' }}>
                      📡 Detected by Sniffer Tool
                    </p>
                    <p>👑 Owner: {tower.ownerUsername}</p>
                    <p>📏 Distance: {tower.distance}m</p>
                    <p>❤️ Health: {tower.health.toLocaleString()}/{tower.maxHealth.toLocaleString()}</p>
                    <p>🛡️ Shield Radius: {tower.shieldRadius}m</p>
                    <div style={{ marginTop: '10px', fontSize: '11px', color: '#ffaa44' }}>
                      <p>⚠️ This tower provides compound protection</p>
                      <p>🎯 Can be attacked with assault drones</p>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Houses - User's own houses always visible, rented houses visible when inside or with sniffer, other houses visible while sleeping or within sniffer radius */}
            {houses.filter((house) => {
              // Apply filter - but always show user's own houses regardless of filter/sniffer
              if (house.ownerUsername === user?.username) {
                return true;
              }
              
              // For other houses, only show if filter is enabled
              if (!mapFilters.showHouses) {
                return false;
              }
              
              // Show houses that the user has actively rented, but only when inside them OR when sniffer is active
              const isRentedByUser = activeRentals && activeRentals.some(rental => rental.houseId === house._id);
              if (isRentedByUser) {
                // Show rented house if user is currently sleeping in it (inside)
                if (isSleeping && sleepingInHouse && house._id === sleepingInHouse._id) {
                  return true;
                }
                // Show rented house if sniffer is active and within range
                if (snifferActive && userComputer && userComputer.coordinates) {
                  const distance = calculateDistance(
                    userComputer.coordinates.lat,
                    userComputer.coordinates.lng,
                    house.location.lat,
                    house.location.lng
                  );
                  if (distance <= calculateSnifferRadius()) {
                    return true;
                  }
                }
                // Hide rented house if user is not inside and sniffer is not active
                return false;
              }
              
              // Show the house where the user is currently sleeping (only while actively sleeping)
              if (isSleeping && sleepingInHouse && house._id === sleepingInHouse._id) {
                return true;
              }
              
              // Show other users' houses only when sniffer is active and within 5km radius
              if (!snifferActive || !userComputer || !userComputer.coordinates) {
                return false;
              }
              
              const distance = calculateDistance(
                userComputer.coordinates.lat,
                userComputer.coordinates.lng,
                house.location.lat,
                house.location.lng
              );
              
              return distance <= calculateSnifferRadius(); // sniffer radius based on parts
            }).map((house) => (
              <Marker
                key={`house-${house._id}`}
                position={[house.location.lat, house.location.lng]}
                icon={houseIcon}
                eventHandlers={{
                  click: () => setSelectedHouse(house)
                }}
              >
                <Popup>
                  <div className="house-popup" style={{ minWidth: '250px', maxWidth: '300px' }}>
                    {/* House Image */}
                    <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                      {house.houseImageUrl ? (
                        <img 
                          src={house.houseImageUrl}
                          alt={`${house.ownerUsername}'s House`}
                          style={{ 
                            width: '240px', 
                            height: '160px', 
                            objectFit: 'cover', 
                            borderRadius: '8px',
                            border: '2px solid #00ff41'
                          }}
                          onError={(e) => {
                            // Hide image and show placeholder div when original fails to load
                            e.target.style.display = 'none';
                            const placeholderDiv = e.target.nextElementSibling;
                            if (placeholderDiv) {
                              placeholderDiv.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        style={{
                          width: '240px',
                          height: '160px',
                          backgroundColor: '#4a90e2',
                          display: house.houseImageUrl ? 'none' : 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          border: '2px solid #ff8800'
                        }}
                      >
                        {house.bedrooms}BR House
                      </div>
                    </div>
                    
                    {house.ownerUsername === user?.username ? (
                      <h4>🏠 Your House</h4>
                    ) : (
                      <h4>🏠 {house.ownerUsername}'s House</h4>
                    )}
                    <p>👑 Owner: {house.ownedBy === 'occult_bank' ? 'Occult Bank' : house.ownerUsername || 'Private'}</p>
                    <p>🛏️ Bedrooms: {house.bedrooms}</p>
                    {house.hasLounge && <p>🛋️ Has Lounge</p>}
                    {house.hasKitchen && <p>🍳 Has Kitchen</p>}
                    {house.hasDiningRoom && <p>🍽️ Has Dining Room</p>}
                    {house.hasGarden && <p>🌸 Has Garden</p>}
                    {house.ownerUsername === user?.username && (
                      <p>💰 Build Cost: {house.totalCost?.toLocaleString() || 'N/A'} WR</p>
                    )}

                    {/* House Management Controls for Owners */}
                    {house.ownerUsername === user?.username && (
                      <div style={{ 
                        margin: '10px 0', 
                        padding: '10px', 
                        background: 'rgba(255, 136, 0, 0.1)', 
                        borderRadius: '5px',
                        border: '1px solid rgba(255, 136, 0, 0.3)'
                      }}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'center' }}>
                          {/* Regenerate Image Button - Only show if image failed or is placeholder */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              regenerateHouseImage(house._id);
                            }}
                            style={{
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}
                          >
                            🖼️ New Image
                          </button>

                          {/* Stop Listing Button - Only show if house is for sale or rent */}
                          {(house.status === 'for_sale' || house.status === 'for_rent') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                stopHouseListing(house._id, house.status);
                              }}
                              style={{
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '4px 8px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '10px',
                                fontWeight: 'bold'
                              }}
                            >
                              🚫 Stop {house.status === 'for_sale' ? 'Sale' : 'Rental'}
                            </button>
                          )}

                          {/* Show current status */}
                          <span style={{
                            fontSize: '10px',
                            color: house.status === 'owned' ? '#00ff41' : 
                                  house.status === 'for_sale' ? '#ff8800' : 
                                  house.status === 'for_rent' ? '#17a2b8' : '#ccc',
                            fontWeight: 'bold',
                            padding: '4px 8px',
                            background: 'rgba(0,0,0,0.3)',
                            borderRadius: '3px'
                          }}>
                            📋 {house.status === 'owned' ? 'Private' : 
                                house.status === 'for_sale' ? 'For Sale' : 
                                house.status === 'for_rent' ? 'For Rent' : 'Unknown'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Distance and Sleep Status */}
                    {userComputer && userComputer.coordinates && (
                      <p style={{ fontSize: '12px', color: '#888', margin: '5px 0' }}>
                        📍 {Math.round(calculateDistance(
                          userComputer.coordinates.lat, 
                          userComputer.coordinates.lng, 
                          house.location.lat, 
                          house.location.lng
                        ))}m from computer
                      </p>
                    )}
                    

                    {/* Sleep Controls */}
                    {userComputer && userComputer.coordinates && (
                      <div style={{ margin: '10px 0', padding: '8px', background: 'rgba(0, 255, 65, 0.1)', borderRadius: '5px', border: '1px solid rgba(0, 255, 65, 0.3)' }}>

                        {!houseCountdown.active && isSleeping && sleepingInHouse?._id === house._id ? (
                          <div style={{ textAlign: 'center' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#00ff41', fontWeight: 'bold' }}>
                              😴 Sleeping in House
                            </p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                leaveHouse();
                              }}
                              style={{
                                background: '#ff6b35',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}
                            >
                              🌅 Leave House
                            </button>
                          </div>
                        ) : isWithinHouseRadius(house) ? (
                          <div style={{ textAlign: 'center' }}>
                            {(() => {
                              const distance = calculateDistance(
                                userComputer.coordinates.lat,
                                userComputer.coordinates.lng,
                                house.location.lat,
                                house.location.lng
                              );
                              const isOwnHouse = house.ownerUsername === user?.username;
                              const withinComputerRange = distance <= 500;
                              const withinSnifferRange = snifferActive && distance <= calculateSnifferRadius();
                              
                              let rangeText = '';
                              if (withinComputerRange) {
                                rangeText = '✅ Within computer range (≤500m)';
                              } else if (isOwnHouse && withinSnifferRange) {
                                rangeText = '📡 Within sniffer range (≤5km) for your house';
                              }
                              
                              return (
                                <p style={{ margin: '0 0 8px 0', color: '#00ff41', fontSize: '12px' }}>
                                  {rangeText}
                                </p>
                              );
                            })()}
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                              {(() => {
                                const hasActiveRental = activeRentals.some(rental => 
                                  rental.houseId === house._id
                                );

                                if (house.ownerUsername === user?.username) {
                                  // User's own house - show Enter House button only if not sleeping and no countdown
                                  return !isSleeping && !houseCountdown.active ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        sleepInHouse(house);
                                      }}
                                      style={{
                                        background: '#00ff41',
                                        color: 'black',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      🏠 Enter House
                                    </button>
                                  ) : null;
                                } else if (hasActiveRental && snifferActive) {
                                  // User has rented this house and sniffer is active - show Enter button
                                  return !isSleeping && !houseCountdown.active ? (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        enterRentedHouse(house);
                                      }}
                                      style={{
                                        background: '#ffc107',
                                        color: 'black',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      🔑 Enter Rented House
                                    </button>
                                  ) : null;
                                } else if (house.status === 'for_rent') {
                                  // Other user's house for rent - show Rent House button
                                  return (
                                    <button
                                      onClick={() => handleRentHouse(house)}
                                      disabled={isSleeping}
                                      style={{
                                        background: isSleeping ? '#666' : '#17a2b8',
                                        color: 'white',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        cursor: isSleeping ? 'not-allowed' : 'pointer',
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      🏠 Rent House ({house.rentPrice} WR/night)
                                    </button>
                                  );
                                } else {
                                  return null;
                                }
                              })()}
                            </div>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center' }}>
                            {(() => {
                              const distance = calculateDistance(
                                userComputer.coordinates.lat,
                                userComputer.coordinates.lng,
                                house.location.lat,
                                house.location.lng
                              );
                              const isOwnHouse = house.ownerUsername === user?.username;
                              
                              if (isOwnHouse) {
                                return (
                                  <div>
                                    <p style={{ margin: '0 0 4px 0', color: '#ff6b35', fontSize: '12px' }}>
                                      ❌ Too far to enter
                                    </p>
                                    <p style={{ margin: '0 0 8px 0', color: '#888', fontSize: '10px' }}>
                                      Need ≤500m OR ≤5km with sniffer active
                                    </p>
                                    {/* Only show Leave House button if currently sleeping in THIS house */}
                                    {isSleeping && sleepingInHouse?._id === house._id && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          leaveHouse();
                                        }}
                                        style={{
                                          background: '#ff6b35',
                                          color: 'white',
                                          border: 'none',
                                          padding: '6px 12px',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '12px',
                                          fontWeight: 'bold',
                                          marginTop: '5px'
                                        }}
                                      >
                                        🌅 Leave House
                                      </button>
                                    )}
                                  </div>
                                );
                              } else {
                                // Other user's house
                                if (house.status === 'for_rent') {
                                  return (
                                    <div style={{ textAlign: 'center' }}>
                                      <p style={{ margin: '0 0 4px 0', color: '#ff6b35', fontSize: '12px' }}>
                                        ❌ Too far to rent
                                      </p>
                                      <p style={{ margin: '0 0 8px 0', color: '#888', fontSize: '10px' }}>
                                        Need ≤500m to rent house
                                      </p>
                                      <p style={{ margin: '0', color: '#17a2b8', fontSize: '12px' }}>
                                        🏠 Available for rent: {house.rentPrice} WR/night
                                      </p>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <p style={{ margin: '0', color: '#888', fontSize: '12px', textAlign: 'center' }}>
                                      🏠 Private house - Not available for rent
                                    </p>
                                  );
                                }
                              }
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <p style={{ fontSize: '12px', color: '#888' }}>
                      📍 {house.location?.lat?.toFixed(4) || 'N/A'}, {house.location?.lng?.toFixed(4) || 'N/A'}
                    </p>
                    {house.status === 'for_sale' && (
                      <p style={{ color: '#28a745', fontWeight: 'bold' }}>
                        🏷️ For Sale: {house.salePrice?.toLocaleString()} WR
                      </p>
                    )}
                    {house.status === 'for_rent' && (
                      <p style={{ color: '#17a2b8', fontWeight: 'bold' }}>
                        🏠 For Rent: {house.rentPrice} WR/night
                      </p>
                    )}
                    {house.ownerUsername !== user?.username && snifferActive && (
                      <p style={{ fontSize: '11px', color: '#ff6b35', fontStyle: 'italic' }}>
                        📡 Discovered via Sniffer Tool
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}

            {/* Business Radius Circles - Show 10km radius for user's own placed business */}
            {mapFilters.showBusinessRadius && businesses.filter(business => 
              business.visibilityReason === 'own_business' && 
              business.location && 
              business.location.lat && 
              business.location.lng && 
              business.location.lat !== 0 && 
              business.location.lng !== 0
            ).map((business) => (
              <Circle
                key={`radius-${business._id}`}
                center={[business.location.lat, business.location.lng]}
                radius={10000} // 10km radius
                pathOptions={{
                  color: '#00ff41',
                  weight: 2,
                  opacity: 0.6,
                  fillColor: '#00ff41',
                  fillOpacity: 0.1,
                  dashArray: '10, 10'
                }}
              />
            ))}

            {/* Businesses - Show placed businesses + user's own business (even if unplaced) */}
            {businesses.filter(business => {
              // Check if business has valid location
              const hasValidLocation = business.location && 
                business.location.lat && 
                business.location.lng && 
                business.location.lat !== 0 && 
                business.location.lng !== 0;
              
              // For user's own businesses (especially fishing businesses)
              if (business.visibilityReason === 'own_business') {
                // Always show user's own businesses regardless of filter, only check valid location
                // This includes fishing businesses which should always be visible
                return hasValidLocation;
              }
              
              // Special case: Always show user's own fishing businesses even if not marked as own_business
              if (business.businessType === 'fishing' && business.ownerUsername === user?.username) {
                return hasValidLocation;
              }
              
              // For other businesses, only show if sniffer is active, filter is enabled, and they have valid coordinates
              return snifferActive && mapFilters.showBusinesses && hasValidLocation;
            }).map((business) => {
              // Determine business icon based on type
              let businessEmoji = '🏢';
              switch(business.businessType) {
                case 'shop': businessEmoji = '🏪'; break;
                case 'warehouse': businessEmoji = '🏭'; break;
                case 'factory': businessEmoji = '🏗️'; break;
                case 'farm': businessEmoji = '🚜'; break;
                case 'fishing': {
                  // Always use fishing hook icon for fishing businesses
                  businessEmoji = '🎣';
                  break;
                }
                default: businessEmoji = '🏢';
              }
              
              const businessIcon = createCustomIcon(businessEmoji, [25, 25]);
              
              // Handle unplaced businesses (user's own business without location)
              const isUnplaced = !business.location || !business.location.lat || !business.location.lng || (business.location.lat === 0 && business.location.lng === 0);
              
              // Skip rendering marker for unplaced businesses - they show in placement panel instead
              if (isUnplaced) {
                return null;
              }
              
              return (
                <Marker
                  key={`business-${business._id}`}
                  position={[business.location.lat, business.location.lng]}
                  icon={businessIcon}
                  eventHandlers={{
                    click: async () => {
                      await syncData(); // Refresh user data before showing popup
                      setSelectedBusiness(business);
                    }
                  }}
                >
                  <Popup>
                    <div className="business-popup" style={{ minWidth: '250px', maxWidth: '300px' }}>
                      
                      {/* Business Image */}
                      {business.businessImageUrl && (
                        <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                          <img 
                            src={business.businessImageUrl.startsWith('http') ? business.businessImageUrl : `${window.location.origin}${business.businessImageUrl}`}
                            alt={business.businessName}
                            style={{ 
                              width: '240px', 
                              height: '160px', 
                              objectFit: 'cover', 
                              borderRadius: '8px',
                              border: '2px solid #00ff41'
                            }}
                            onError={(e) => {
                              // Hide broken image without console spam
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      
                      <h4 style={{ margin: '0 0 10px 0', color: '#00ff41' }}>
                        {businessEmoji} {business.businessName}
                      </h4>
                      
                      <div style={{ marginBottom: '10px' }}>
                        <p style={{ margin: '5px 0', fontSize: '12px' }}>
                          <strong>Owner:</strong> {business.ownerUsername}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '12px' }}>
                          <strong>Type:</strong> {business.businessType === 'fishing' ? 'Fishing Farm' : business.businessType.charAt(0).toUpperCase() + business.businessType.slice(1)}
                        </p>
                        {business.description && (
                          <p style={{ margin: '5px 0', fontSize: '12px', fontStyle: 'italic' }}>
                            "{business.description}"
                          </p>
                        )}
                      </div>
                      
                      <div style={{ marginBottom: '10px', padding: '8px', background: 'rgba(0, 255, 65, 0.1)', borderRadius: '5px' }}>
                        <p style={{ margin: '2px 0', fontSize: '11px' }}>
                          <strong>Produces:</strong> {
                            (() => {
                              const produces = business.supplyChain?.produces;
                              switch(produces) {
                                case 'consumer_goods': return 'Food & Supplies for Consumers';
                                case 'supplies': return 'Processed Supplies';
                                case 'processed_goods': return 'Manufactured Products';
                                case 'raw_materials': return 'Raw Materials';
                                case 'food': return 'Food Products';
                                default: {
                                  // Fallback based on business type
                                  switch(business.businessType) {
                                    case 'shop': return 'Food & Supplies for Consumers';
                                    case 'warehouse': return 'Processed Supplies';
                                    case 'factory': return 'Manufactured Products';
                                    case 'farm': return 'Raw Materials';
                                    case 'fishing': return 'Fresh Seafood';
                                    default: return 'Unknown';
                                  }
                                }
                              }
                            })()
                          }
                        </p>
                        <p style={{ margin: '2px 0', fontSize: '11px' }}>
                          <strong>Requires:</strong> {
                            (() => {
                              const requires = business.supplyChain?.requires;
                              switch(requires) {
                                case 'supplies': return 'Processed Supplies';
                                case 'processed_goods': return 'Manufactured Products';
                                case 'raw_materials': return 'Raw Materials';
                                case 'none': return 'None (Self-sufficient)';
                                default: {
                                  // Fallback based on business type
                                  switch(business.businessType) {
                                    case 'shop': return 'Processed Supplies';
                                    case 'warehouse': return 'Manufactured Products';
                                    case 'factory': return 'Raw Materials';
                                    case 'farm': return 'None (Self-sufficient)';
                                    case 'fishing': return 'None (Self-sufficient)';
                                    default: return 'None';
                                  }
                                }
                              }
                            })()
                          }
                        </p>
                        <p style={{ margin: '2px 0', fontSize: '11px' }}>
                          <strong>Status:</strong> {business.operationalStatus || 'operational'}
                        </p>
                        <p style={{ margin: '2px 0', fontSize: '11px' }}>
                          <strong>Supply Level:</strong> {business.supplyLevel || 0}/{business.maxCapacity || 1000}
                        </p>
                        {business.businessType === 'fishing' && (
                          <p style={{ margin: '8px 0 2px 0', fontSize: '10px', color: '#ffaa00', fontStyle: 'italic' }}>
                            🌊 Maritime Operation - Cannot link to supply chains
                          </p>
                        )}
                      </div>
                      
                      {/* Visibility Reason Debug Info */}
                      {business.visibilityReason && (
                        <div style={{ marginBottom: '10px', padding: '6px', background: 'rgba(100, 100, 100, 0.3)', borderRadius: '4px' }}>
                          <p style={{ margin: '0', fontSize: '10px', color: '#888' }}>
                            <strong>Visible:</strong> {
                              business.visibilityReason === 'own_business' ? '👤 Your Business' :
                              business.visibilityReason === 'supply_chain_connected' ? '🔗 Supply Chain' :
                              business.visibilityReason === 'business_radius' ? `🏢 Business Radius (${business.distance}m)` :
                              business.visibilityReason === 'sniffer_detected' ? `📡 Sniffer (${business.distance}m)` :
                              business.visibilityReason
                            }
                          </p>
                        </div>
                      )}
                      
                      {/* Shop Purchase Interface - Only for shops */}
                      {business.businessType === 'shop' && (business.supplyLevel > 0 || business.ownerUsername === user?.username) && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(0, 255, 65, 0.15)', borderRadius: '6px', border: '1px solid rgba(0, 255, 65, 0.3)' }}>
                          <h5 style={{ margin: '0 0 8px 0', color: '#00ff41', fontSize: '13px' }}>
                            🛒 Buy Supplies from {business.ownerUsername === user?.username ? 'Your Shop' : 'Shop'}
                          </h5>
                          
                          <div style={{ marginBottom: '8px' }}>
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Price:</strong> {business.pricing?.supplyPrice || 50} WR coins per supply
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Shop supplies:</strong> {business.supplyLevel !== undefined ? business.supplyLevel : 250}
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Your supplies:</strong> {user?.foodDrink?.currentSupplies || 0}/10
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '10px', color: '#888' }}>
                              <em>Max 10 supplies total</em>
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '10px', color: '#888' }}>
                              📡 <em>Requires active sniffer tool within range</em>
                            </p>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                            <label style={{ fontSize: '11px', color: '#00ff41' }}>Amount:</label>
                            <input
                              type="number"
                              min="1"
                              max={Math.min(10 - (user?.foodDrink?.currentSupplies || 0), business.supplyLevel !== undefined ? business.supplyLevel : 250, 10)}
                              defaultValue="1"
                              id={`shop-supply-amount-${business._id}`}
                              style={{
                                width: '50px',
                                padding: '3px',
                                fontSize: '11px',
                                background: '#1a1a1a',
                                color: '#00ff41',
                                border: '1px solid #00ff41',
                                borderRadius: '3px'
                              }}
                            />
                            <span style={{ fontSize: '10px', color: '#888' }}>
                              (Max: {Math.min(10 - (computerStats?.foodDrinkSupplies ?? (user?.foodDrink?.currentSupplies || 0)), business.supplyLevel !== undefined ? business.supplyLevel : 250, 10)})
                            </span>
                          </div>
                          
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              await syncData(); // Refresh user data before purchase
                              const amountInput = document.getElementById(`shop-supply-amount-${business._id}`);
                              const amount = parseInt(amountInput.value) || 1;
                              handlePurchaseFromUserShop(business._id, amount, business.businessName);
                            }}
                            style={{
                              background: 'linear-gradient(135deg, #00ff41, #00cc33)',
                              color: '#000',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              width: '100%',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.background = 'linear-gradient(135deg, #00cc33, #00aa2a)'}
                            onMouseOut={(e) => e.target.style.background = 'linear-gradient(135deg, #00ff41, #00cc33)'}
                          >
                            💰 Buy Supplies
                          </button>
                        </div>
                      )}

                      {/* Fallback message for shops without stock */}
                      {business.businessType === 'shop' && business.supplyLevel <= 0 && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255, 165, 0, 0.15)', borderRadius: '6px', border: '1px solid rgba(255, 165, 0, 0.3)' }}>
                          <h5 style={{ margin: '0 0 8px 0', color: '#ffaa00', fontSize: '13px' }}>
                            📦 Shop Currently Empty
                          </h5>
                          <p style={{ margin: '2px 0', fontSize: '11px', color: '#ffaa00' }}>
                            ⚠️ This shop has no supplies in stock and cannot sell anything right now.
                          </p>
                          {business.ownerUsername === user?.username ? (
                            <div style={{ marginTop: '8px', padding: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px' }}>
                              <p style={{ margin: '2px 0', fontSize: '10px', color: '#ccc', fontStyle: 'italic' }}>
                                💡 <strong>Shop Owner Tip:</strong> To stock your shop, you need to:
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '10px', color: '#ccc' }}>
                                • Build a supply chain: Farm → Factory → Warehouse → Shop
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '10px', color: '#ccc' }}>
                                • Purchase supplies from warehouses/factories
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '10px', color: '#ccc' }}>
                                • Visit Shopping → Businesses → Marketplace to buy supplies
                              </p>
                            </div>
                          ) : (
                            <p style={{ margin: '8px 0 2px 0', fontSize: '10px', color: '#ccc', fontStyle: 'italic' }}>
                              💡 Check back later when the shop owner restocks their inventory.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Fishing Business Purchase Interface (Owner Only) */}
                      {business.businessType === 'fishing' && business.ownerUsername === user?.username && business.supplyLevel > 0 && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(0, 255, 255, 0.15)', borderRadius: '6px', border: '1px solid rgba(0, 255, 255, 0.3)' }}>
                          <h5 style={{ margin: '0 0 8px 0', color: '#00ffff', fontSize: '13px' }}>
                            🐠 Buy Supplies from Your Fishing Business
                          </h5>
                          
                          <div style={{ marginBottom: '8px' }}>
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Price:</strong> {business.pricing?.supplyPrice || 50} WR coins per supply
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Fishing supplies:</strong> {business.supplyLevel || 0}
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '11px' }}>
                              <strong>Your supplies:</strong> {user?.foodDrink?.currentSupplies || 0}/10
                            </p>
                            <p style={{ margin: '2px 0', fontSize: '10px', color: '#888' }}>
                              <em>Max 10 supplies per hour (owner only)</em>
                            </p>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                            <label style={{ fontSize: '11px', color: '#00ffff' }}>Amount:</label>
                            <input
                              type="number"
                              min="1"
                              max={Math.min(10 - (user?.foodDrink?.currentSupplies || 0), business.supplyLevel !== undefined ? business.supplyLevel : 250, 10)}
                              defaultValue="1"
                              id={`fishing-supply-amount-${business._id}`}
                              style={{
                                width: '50px',
                                padding: '2px 4px',
                                fontSize: '11px',
                                background: 'rgba(0, 255, 255, 0.1)',
                                border: '1px solid rgba(0, 255, 255, 0.3)',
                                borderRadius: '3px',
                                color: '#00ffff'
                              }}
                            />
                            <span style={{ fontSize: '11px', color: '#888' }}>supplies</span>
                          </div>
                          
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              await syncData(); // Refresh user data before purchase
                              const amount = document.getElementById(`fishing-supply-amount-${business._id}`).value;
                              if (!amount || amount < 1) {
                                addNotification({
                                  title: '❌ Invalid Amount',
                                  message: 'Please enter a valid amount',
                                  type: 'error'
                                });
                                return;
                              }
                              
                              // Check actual supplies for purchasing
                              const currentSupplies = user?.foodDrink?.currentSupplies || 0;
                              const maxSupplies = 10;
                              const quantity = parseInt(amount);
                              
                              if (currentSupplies >= maxSupplies) {
                                addNotification({
                                  title: '❌ Max Supplies Reached',
                                  message: `You already have the maximum ${maxSupplies} supplies. Use some supplies before purchasing more.`,
                                  type: 'error'
                                });
                                return;
                              }
                              
                              // Check if purchase would exceed maximum supplies
                              if (currentSupplies + quantity > maxSupplies) {
                                const availableSpace = maxSupplies - currentSupplies;
                                addNotification({
                                  title: '❌ Purchase Would Exceed Limit',
                                  message: `You can only purchase ${availableSpace} more supplies (currently have ${currentSupplies}/${maxSupplies} supplies).`,
                                  type: 'error'
                                });
                                return;
                              }
                              
                              try {
                                const response = await api.post('/business/purchase-from-fishing', {
                                  fishingId: business._id,
                                  suppliesAmount: parseInt(amount)
                                });
                                
                                const data = response.data;
                                if (response.status === 200) {
                                  addNotification({
                                    title: '✅ Purchase Successful',
                                    message: data.message,
                                    type: 'success'
                                  });
                                  
                                  // Update user data
                                  await updateUser({
                                    ...user,
                                    wrCoins: data.data.userBalance,
                                    foodDrink: {
                                      ...user.foodDrink,
                                      currentSupplies: data.data.currentSupplies,
                                      lastPurchase: new Date(),
                                      totalPurchased: (user.foodDrink?.totalPurchased || 0) + data.data.suppliesPurchased
                                    }
                                  });

                                  // Refresh businesses to update UI
                                  fetchBusinesses();
                                } else {
                                  addNotification({
                                    title: '❌ Purchase Failed',
                                    message: data.message,
                                    type: 'error'
                                  });
                                }
                              } catch (error) {
                                console.error('Error purchasing from fishing business:', error);
                                const errorMessage = error.response?.data?.message || 'Failed to purchase supplies. Please try again.';
                                addNotification({
                                  title: '❌ Network Error',
                                  message: errorMessage,
                                  type: 'error'
                                });
                              }
                            }}
                            style={{
                              background: 'linear-gradient(135deg, #00ffff, #0080ff)',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              width: '100%'
                            }}
                          >
                            🐠 Purchase Supplies
                          </button>
                        </div>
                      )}

                      {/* Fishing Business No Supplies Message */}
                      {business.businessType === 'fishing' && business.ownerUsername === user?.username && business.supplyLevel <= 0 && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255, 165, 0, 0.15)', borderRadius: '6px', border: '1px solid rgba(255, 165, 0, 0.3)' }}>
                          <p style={{ margin: '0', fontSize: '11px', color: '#ffaa00' }}>
                            ⚠️ Your fishing business is currently out of stock and cannot sell supplies.
                          </p>
                        </div>
                      )}

                      {/* Fishing Business Information for Non-Owners */}
                      {business.businessType === 'fishing' && business.ownerUsername !== user?.username && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(100, 100, 100, 0.15)', borderRadius: '6px', border: '1px solid rgba(100, 100, 100, 0.3)' }}>
                          <p style={{ margin: '0', fontSize: '11px', color: '#888' }}>
                            🌊 This is a fishing business. Only the owner can purchase supplies from it.
                          </p>
                        </div>
                      )}

                      {/* Shop Management Interface for Shop Owners (when out of range or no supplies) */}
                      {business.businessType === 'shop' && business.ownerUsername === user?.username && business.visibilityReason === 'own_business' && (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(0, 123, 255, 0.15)', borderRadius: '6px', border: '1px solid rgba(0, 123, 255, 0.3)' }}>
                          <h5 style={{ margin: '0 0 8px 0', color: '#007bff', fontSize: '13px' }}>
                            🏪 Your Shop Management
                          </h5>
                          <p style={{ margin: '0', fontSize: '11px', color: '#888' }}>
                            {business.supplyLevel <= 0 
                              ? 'Your shop is currently out of stock. No supplies available for purchase.'
                              : 'To purchase supplies from your own shop, you need to be within 5km with an active sniffer tool.'
                            }
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              window.location.href = '/system?tab=business';
                            }}
                            style={{
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              width: '100%',
                              marginTop: '8px'
                            }}
                          >
                            📊 Manage Shop
                          </button>
                        </div>
                      )}

                      {/* Farm Purchase Interface for All Users within 5000m */}
                      {business.businessType === 'farm' && business.supplyLevel > 0 && (() => {
                        // Check if user is within 5000m of farm
                        if (!userComputer || !userComputer.coordinates) return null;
                        
                        const farmLocation = business.location;
                        const computerLocation = userComputer.coordinates;
                        
                        // Calculate distance using Haversine formula
                        const R = 6371000; // Earth's radius in meters
                        const dLat = (farmLocation.lat - computerLocation.lat) * Math.PI / 180;
                        const dLng = (farmLocation.lng - computerLocation.lng) * Math.PI / 180;
                        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                                  Math.cos(computerLocation.lat * Math.PI / 180) * Math.cos(farmLocation.lat * Math.PI / 180) *
                                  Math.sin(dLng/2) * Math.sin(dLng/2);
                        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                        const distance = R * c;
                        
                        const isWithin5000m = distance <= 5000;
                        const isOwnFarm = business.ownerUsername === user?.username;
                        const hourlyLimit = isOwnFarm ? 10 : 3;
                        
                        if (!isWithin5000m) return null;
                        
                        return (
                          <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '6px', border: '1px solid rgba(34, 139, 34, 0.3)' }}>
                            <h5 style={{ margin: '0 0 8px 0', color: '#228B22', fontSize: '13px' }}>
                              🚜 Buy Supplies from {isOwnFarm ? 'Your' : ''} Farm
                            </h5>
                            
                            <div style={{ marginBottom: '8px' }}>
                              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                                <strong>Price:</strong> {business.pricing?.supplyPrice || 50} WR coins per supply
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                                <strong>Farm supplies:</strong> {business.supplyLevel || 0}
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                                <strong>Your supplies:</strong> {user?.foodDrink?.currentSupplies || 0}/10
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '10px', color: '#888' }}>
                                <em>Max {hourlyLimit} supplies per hour from {isOwnFarm ? 'your own' : 'other'} farms</em>
                              </p>
                              <p style={{ margin: '2px 0', fontSize: '10px', color: '#228B22' }}>
                                📍 Distance: {Math.round(distance)}m (within 1000m range)
                              </p>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '8px' }}>
                              <label style={{ fontSize: '11px', color: '#228B22' }}>Amount:</label>
                              <input
                                type="number"
                                min="1"
                                max={Math.min(10 - (user?.foodDrink?.currentSupplies || 0), business.supplyLevel || 0, hourlyLimit)}
                                defaultValue="1"
                                id={`farm-supply-amount-${business._id}`}
                                style={{
                                  width: '50px',
                                  padding: '3px',
                                  fontSize: '11px',
                                  background: '#1a1a1a',
                                  color: '#228B22',
                                  border: '1px solid #228B22',
                                  borderRadius: '3px'
                                }}
                              />
                              <span style={{ fontSize: '10px', color: '#888' }}>
                                (Max: {Math.min(10 - (computerStats?.foodDrinkSupplies ?? (user?.foodDrink?.currentSupplies || 0)), business.supplyLevel || 0, hourlyLimit)})
                              </span>
                            </div>
                            
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                await syncData(); // Refresh user data before purchase
                                const amountInput = document.getElementById(`farm-supply-amount-${business._id}`);
                                const amount = parseInt(amountInput.value) || 1;
                                handlePurchaseFromFarm(business._id, amount, business.businessName);
                              }}
                              style={{
                                background: 'linear-gradient(135deg, #228B22, #006400)',
                                color: '#fff',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                width: '100%',
                                transition: 'all 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = 'linear-gradient(135deg, #006400, #004d00)'}
                              onMouseOut={(e) => e.target.style.background = 'linear-gradient(135deg, #228B22, #006400)'}
                            >
                              🌾 Buy Farm Supplies
                            </button>
                          </div>
                        );
                      })()}

                      {/* Farm out of range message */}
                      {business.businessType === 'farm' && business.supplyLevel > 0 && (() => {
                        if (!userComputer || !userComputer.coordinates) return null;
                        
                        const farmLocation = business.location;
                        const computerLocation = userComputer.coordinates;
                        
                        // Calculate distance using Haversine formula
                        const R = 6371000; // Earth's radius in meters
                        const dLat = (farmLocation.lat - computerLocation.lat) * Math.PI / 180;
                        const dLng = (farmLocation.lng - computerLocation.lng) * Math.PI / 180;
                        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                                  Math.cos(computerLocation.lat * Math.PI / 180) * Math.cos(farmLocation.lat * Math.PI / 180) *
                                  Math.sin(dLng/2) * Math.sin(dLng/2);
                        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                        const distance = R * c;
                        
                        const isWithin5000m = distance <= 5000;
                        
                        if (isWithin5000m) return null;
                        
                        return (
                          <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255, 165, 0, 0.15)', borderRadius: '6px', border: '1px solid rgba(255, 165, 0, 0.3)' }}>
                            <p style={{ margin: '0', fontSize: '11px', color: '#ffaa00' }}>
                              📏 Too far to purchase supplies from this farm
                            </p>
                            <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: '#888' }}>
                              Distance: {Math.round(distance)}m (need to be within 5000m)
                            </p>
                          </div>
                        );
                      })()}

                      {/* Link Button for User's Own Businesses (exclude shops and fishing) */}
                      {business.ownerUsername === user?.username && business.businessType !== 'shop' && business.businessType !== 'fishing' && (
                        <div style={{ textAlign: 'center', marginTop: '15px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setBusinessLinkingModal({
                                show: true,
                                sourceBusiness: business,
                                targetBusinessType: getTargetBusinessType(business.businessType)
                              });
                            }}
                            style={{
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              width: '100%'
                            }}
                          >
                            🔗 Link Business
                          </button>
                        </div>
                      )}

                      {business.ownerUsername !== user?.username && business.businessType !== 'shop' && business.businessType !== 'farm' && (
                        <div style={{ textAlign: 'center', marginTop: '10px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBusinessLink(business._id, business.businessName);
                            }}
                            style={{
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: 'bold'
                            }}
                          >
                            🔗 Request Business Link
                          </button>
                        </div>
                      )}
                      
                      <p style={{ fontSize: '12px', color: '#888', margin: '10px 0 0 0' }}>
                        📍 {business.location?.lat?.toFixed(4) || 'N/A'}, {business.location?.lng?.toFixed(4) || 'N/A'}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Hotels - Always show checked-in hotel, show others only when sniffer is active - exclude casinos */}
            {(Array.isArray(hotels) ? hotels : []).filter(hotel => {
              // Must be a hotel (not casino) with valid coordinates
              if (hotel.isCasino || !mapFilters.showHotels || !hotel.coordinates || !hotel.coordinates.lat || !hotel.coordinates.lng) {
                return false;
              }
              
              // Check if there's a standalone casino at the same location
              const hasStandaloneCasinoAtSameLocation = nearbyCasinos.some(casino => 
                casino.coordinates && 
                casino.coordinates.lat === hotel.coordinates.lat && 
                casino.coordinates.lng === hotel.coordinates.lng
              );
              
              // Check if there's a casino hotel at the same location
              const hasCasinoHotelAtSameLocation = hotels.some(otherHotel => 
                otherHotel.isCasino && 
                otherHotel.coordinates && 
                otherHotel.coordinates.lat === hotel.coordinates.lat && 
                otherHotel.coordinates.lng === hotel.coordinates.lng
              );
              
              // Don't show regular hotels where there's a casino (standalone or hotel)
              if (hasStandaloneCasinoAtSameLocation || hasCasinoHotelAtSameLocation) {
                return false;
              }
              
              // Always show hotels the user is currently checked into
              const isCheckedIn = checkedInHotel && hotel.hotelId === checkedInHotel.hotelId;
              if (isCheckedIn) {
                return true;
              }
              
              // Show other hotels only when sniffer is active
              return snifferActive;
            }).map((hotel) => {
              // Check if user has an active booking for this hotel
              const activeBooking = userBookings.find(booking => 
                booking.hotelId === hotel.hotelId && 
                booking.status === 'booked' &&
                new Date() >= new Date(booking.checkInDate) &&
                new Date() <= new Date(booking.checkOutDate)
              );
              
              const checkedInBooking = userBookings.find(booking => 
                booking.hotelId === hotel.hotelId && 
                booking.status === 'checked_in'
              );
              
              // Keep hotel icons consistent - no dynamic changes
              let hotelEmoji = '🏨';
              
              const hotelIcon = createCustomIcon(hotelEmoji, [25, 25]);
              
              return (
                <Marker
                  key={hotel.hotelId}
                  position={[hotel.coordinates.lat, hotel.coordinates.lng]}
                  icon={hotelIcon}
                  eventHandlers={{
                    click: () => {
                      setSelectedHotel(hotel);
                    }
                  }}
                >
                  <Popup>
                    <div className="hotel-popup" style={{ minWidth: '220px' }}>
                      {/* Hotel Image */}
                      {hotel.imageUrl && (
                        <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                          <img 
                            src={hotel.imageUrl} 
                            alt={hotel.name}
                            style={{ 
                              width: '200px', 
                              height: '150px', 
                              objectFit: 'cover', 
                              borderRadius: '8px',
                              border: '2px solid #00ff41'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <h4 style={{ margin: '0 0 10px 0' }}>
                        {hotelEmoji} {hotel.name}
                        {hotel.isCasino && <span style={{ color: '#ffd700' }}> 🎰</span>}
                      </h4>
                      <p>📍 {hotel.country}{hotel.city ? `, ${hotel.city}` : ''}</p>
                      <p>👑 Owner: {hotel.ownedBy === 'occult_bank' ? 'Occult Bank' : 'Private'}</p>
                      <p>⭐ {'★'.repeat(hotel.rating)}</p>
                      <p>💰 {hotel.pricePerNight} WR/night</p>
                      <p>🏨 {hotel.totalRooms} rooms</p>
                      
                      {/* Show booking status */}
                      {checkedInBooking && (
                        <div style={{ margin: '8px 0', padding: '5px', background: '#28a745', borderRadius: '3px' }}>
                          <p style={{ fontSize: '12px', color: 'white', margin: '0' }}>
                            🏠 Currently staying here
                          </p>
                          <p style={{ fontSize: '11px', color: '#ccc', margin: '2px 0 0 0' }}>
                            Room: {checkedInBooking.roomNumber} | Check-out: {new Date(checkedInBooking.checkOutDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      {activeBooking && !checkedInBooking && (
                        <div style={{ margin: '8px 0', padding: '5px', background: '#007bff', borderRadius: '3px' }}>
                          <p style={{ fontSize: '12px', color: 'white', margin: '0' }}>
                            📅 Active booking
                          </p>
                          <p style={{ fontSize: '11px', color: '#ccc', margin: '2px 0 0 0' }}>
                            Room: {activeBooking.roomNumber} | {activeBooking.totalNights} nights
                          </p>
                        </div>
                      )}
                      
                      {hotel.amenities && hotel.amenities.length > 0 && (
                        <div style={{ margin: '8px 0' }}>
                          <p style={{ fontSize: '12px', margin: '2px 0' }}>Amenities:</p>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            {hotel.amenities.slice(0, 3).join(', ')}
                            {hotel.amenities.length > 3 && ` +${hotel.amenities.length - 3} more`}
                          </p>
                        </div>
                      )}
                      
                      <div style={{ marginTop: '10px' }}>
                        {/* Show appropriate buttons based on booking status */}
                        {checkedInBooking ? (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHotelCheckOut(checkedInBooking);
                            }}
                            style={{
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            🚪 Check Out
                          </button>
                        ) : activeBooking ? (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHotelCheckIn(hotel);
                            }}
                            style={{
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            ✅ Check In
                          </button>
                        ) : (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to booking page
                              window.location.href = '/lifestyle';
                            }}
                            style={{
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            🏨 Book Room
                          </button>
                        )}
                        
                        {/* Distance info */}
                        {userComputer && userComputer.coordinates && (
                          <p style={{ fontSize: '10px', color: '#888', margin: '5px 0 0 0', textAlign: 'center' }}>
                            📍 {Math.round(calculateDistance(
                              userComputer.coordinates.lat, 
                              userComputer.coordinates.lng, 
                              hotel.coordinates.lat, 
                              hotel.coordinates.lng
                            ))}m from computer
                          </p>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Casinos - Visible within 5km radius when sniffer is active */}
            {(snifferActive ? (Array.isArray(hotels) ? hotels : []).filter(hotel => hotel.isCasino && hotel.coordinates && hotel.coordinates.lat && hotel.coordinates.lng) : (Array.isArray(hotels) ? hotels : []).filter(hotel => hotel.isCasino && checkedInHotel && hotel.hotelId === checkedInHotel.hotelId && hotel.coordinates && hotel.coordinates.lat && hotel.coordinates.lng)).map((casino) => {
              // Check if user has an active booking for this casino hotel
              const activeBooking = userBookings.find(booking => 
                booking.hotelId === casino.hotelId && 
                booking.status === 'booked' &&
                new Date() >= new Date(booking.checkInDate) &&
                new Date() <= new Date(booking.checkOutDate)
              );
              
              const checkedInBooking = userBookings.find(booking => 
                booking.hotelId === casino.hotelId && 
                booking.status === 'checked_in'
              );
              
              // Always use casino icon
              const casinoIcon = createCustomIcon('🎰', [25, 25]);
              
              return (
                <Marker
                  key={casino.hotelId}
                  position={[casino.coordinates.lat, casino.coordinates.lng]}
                  icon={casinoIcon}
                  eventHandlers={{
                    click: () => {
                      setSelectedCasino({
                        ...casino,
                        casinoId: extractCasinoId(casino.hotelId) // Extract proper casino ID from hotel ID
                      });
                    }
                  }}
                >
                  <Popup>
                    <div className="casino-popup" style={{ minWidth: '220px' }}>
                      {/* Casino Hotel Image */}
                      {casino.imageUrl && (
                        <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                          <img 
                            src={casino.imageUrl} 
                            alt={casino.name}
                            style={{ 
                              width: '200px', 
                              height: '150px', 
                              objectFit: 'cover', 
                              borderRadius: '8px',
                              border: '2px solid #ffd700'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      
                      <h4 style={{ margin: '0 0 10px 0' }}>
                        🎰 {casino.name}
                      </h4>
                      <p>📍 {casino.country}{casino.city ? `, ${casino.city}` : ''}</p>
                      <p>⭐ {'★'.repeat(casino.rating)}</p>
                      <p>💰 {casino.pricePerNight} WR/night</p>
                      <p>🏨 {casino.totalRooms} rooms</p>
                      
                      {/* Show booking status */}
                      {checkedInBooking && (
                        <div style={{ margin: '8px 0', padding: '5px', background: '#28a745', borderRadius: '3px' }}>
                          <p style={{ fontSize: '12px', color: 'white', margin: '0' }}>
                            🏠 Currently staying here
                          </p>
                          <p style={{ fontSize: '11px', color: '#ccc', margin: '2px 0 0 0' }}>
                            Room: {checkedInBooking.roomNumber} | Check-out: {new Date(checkedInBooking.checkOutDate).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      {activeBooking && !checkedInBooking && (
                        <div style={{ margin: '8px 0', padding: '5px', background: '#007bff', borderRadius: '3px' }}>
                          <p style={{ fontSize: '12px', color: 'white', margin: '0' }}>
                            📅 Active booking
                          </p>
                          <p style={{ fontSize: '11px', color: '#ccc', margin: '2px 0 0 0' }}>
                            Room: {activeBooking.roomNumber} | {activeBooking.totalNights} nights
                          </p>
                        </div>
                      )}
                      
                      {casino.amenities && casino.amenities.length > 0 && (
                        <div style={{ margin: '8px 0' }}>
                          <p style={{ fontSize: '12px', margin: '2px 0' }}>Amenities:</p>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            {casino.amenities.slice(0, 3).join(', ')}
                            {casino.amenities.length > 3 && ` +${casino.amenities.length - 3} more`}
                          </p>
                        </div>
                      )}
                      
                      <div style={{ marginTop: '10px' }}>
                        {/* Casino-specific action buttons */}
                        <div style={{ marginBottom: '10px' }}>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCasino({
                                ...casino,
                                casinoId: extractCasinoId(casino.hotelId)
                              });
                            }}
                            style={{
                              background: '#ffc107',
                              color: 'black',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            🎰 Hack Slots
                          </button>
                          
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCasino({
                                ...casino,
                                casinoId: extractCasinoId(casino.hotelId)
                              });
                            }}
                            style={{
                              background: '#17a2b8',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            🏦 Hack Vault
                          </button>
                        </div>
                        
                        {/* Hotel booking options */}
                        {checkedInBooking ? (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHotelCheckOut(checkedInBooking);
                            }}
                            style={{
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            🚪 Check Out
                          </button>
                        ) : activeBooking ? (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleHotelCheckIn(casino);
                            }}
                            style={{
                              background: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            ✅ Check In
                          </button>
                        ) : (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to booking page
                              window.location.href = '/lifestyle';
                            }}
                            style={{
                              background: '#007bff',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              width: '100%',
                              marginBottom: '5px'
                            }}
                          >
                            🏨 Book Room
                          </button>
                        )}
                        
                        {/* Distance info */}
                        {userComputer && userComputer.coordinates && casino.coordinates && (
                          <p style={{ fontSize: '10px', color: '#888', margin: '5px 0 0 0', textAlign: 'center' }}>
                            📍 {Math.round(calculateDistance(
                              userComputer.coordinates.lat, 
                              userComputer.coordinates.lng, 
                              casino.coordinates.lat, 
                              casino.coordinates.lng
                            ))}m from computer
                          </p>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Job Office Markers - Visible when working OR within 5km radius when sniffer is active */}
            {((snifferActive && userComputer) || isActivelyWorking()) && careerData.offices && careerData.offices.filter(office => {
              // If actively working, only show the office where user works
              if (isActivelyWorking()) {
                return careerData.userJob && careerData.userJob.officeId === office._id;
              }
              
              // Otherwise, show offices within sniffer range
              if (snifferActive && userComputer) {
                const distance = calculateDistance(
                  userComputer.coordinates.lat,
                  userComputer.coordinates.lng,
                  office.location.lat,
                  office.location.lng
                );
                return distance <= calculateSnifferRadius(); // 5km sniffer range
              }
              
              return false;
            }).map((office) => {
              const isUserJob = careerData.userJob && careerData.userJob.officeId === office._id;
              const officeIcon = isUserJob ? 
                createCustomIcon('💼', [35, 35]) :  // Larger icon for user's job
                createCustomIcon('🏢', [25, 25]);   // Regular office icon
              
              return (
                <Marker
                  key={office._id}
                  position={[office.location.lat, office.location.lng]}
                  icon={officeIcon}
                  eventHandlers={{
                    click: () => {
                      // Only show popup if user has this job or is near the office
                      if (isUserJob && userComputer) {
                        const distance = calculateDistance(
                          userComputer.coordinates.lat,
                          userComputer.coordinates.lng,
                          office.location.lat,
                          office.location.lng
                        );
                        
                        // Show work controls if within 5km of office
                        if (distance <= calculateSnifferRadius()) {
                          // Trigger work popup
                          setSelectedOffice(office);
                        }
                      }
                    }
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: '250px', textAlign: 'center' }}>
                      <h3 style={{ margin: '0 0 10px 0', color: isUserJob ? '#4b0082' : '#333' }}>
                        {isUserJob ? '💼' : '🏢'} {office.officeName}
                      </h3>
                      <p style={{ margin: '5px 0', fontSize: '14px', fontWeight: 'bold' }}>
                        {office.company}
                      </p>
                      <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                        📍 {office.city}, {office.country}
                      </p>
                      
                      {isUserJob ? (
                        <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(75, 0, 130, 0.1)', borderRadius: '5px' }}>
                          <p style={{ margin: '5px 0', fontSize: '13px', color: '#4b0082', fontWeight: 'bold' }}>
                            🎯 Your Workplace
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '12px' }}>
                            Position: {careerData.userJob.jobTitle}
                          </p>
                          <p style={{ margin: '5px 0', fontSize: '12px' }}>
                            💰 {careerData.userJob.hourlyRate} WR/hour
                          </p>
                          
                          {/* Check if currently working at this office */}
                          {isActivelyWorking() && workSession.office._id === office._id ? (
                            <div style={{ marginTop: '15px' }}>
                              <div style={{ padding: '10px', background: 'rgba(40, 167, 69, 0.1)', borderRadius: '5px', border: '1px solid #28a745', marginBottom: '10px' }}>
                                <p style={{ fontSize: '12px', color: '#28a745', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                                  🚀 Currently Working
                                </p>
                                <p style={{ fontSize: '11px', color: '#666', margin: '0' }}>
                                  Your 8-hour shift is in progress
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  pauseWorkSession();
                                }}
                                style={{
                                  background: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 15px',
                                  borderRadius: '5px',
                                  cursor: 'pointer',
                                  width: '100%',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                🚪 Leave Work
                              </button>
                            </div>
                          ) : userComputer && (() => {
                            const distance = calculateDistance(
                              userComputer.coordinates.lat,
                              userComputer.coordinates.lng,
                              office.location.lat,
                              office.location.lng
                            );
                            
                            if (distance <= calculateSnifferRadius()) {
                              // Check if user has paused work session at this office
                              const isPausedAtThisOffice = workSession && workSession.paused && workSession.office._id === office._id;
                              
                              return (
                                <div style={{ marginTop: '15px' }}>
                                  <p style={{ fontSize: '12px', color: '#28a745', margin: '5px 0' }}>
                                    ✅ Within work range ({Math.round(distance)}m)
                                  </p>
                                  {isPausedAtThisOffice && (
                                    <div style={{ padding: '8px', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '5px', border: '1px solid #ffc107', marginBottom: '10px' }}>
                                      <p style={{ fontSize: '11px', color: '#856404', margin: '0', fontWeight: 'bold' }}>
                                        ⏸️ Work Session Paused
                                      </p>
                                    </div>
                                  )}
                                  {/* Regular work button for non-field agents */}
                                  {(!careerData.userJob || careerData.userJob.jobLevel !== 'fieldAgent') && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        
                                        // If work is paused at this office, resume it
                                        if (isPausedAtThisOffice) {
                                          await resumeWorkSession();
                                          return;
                                        }
                                        
                                        // Check work day before making API call
                                        if (!isWorkDay()) {
                                          addNotification({
                                            title: '❌ Work Day Restriction',
                                            message: 'You can only work Monday through Thursday.',
                                            type: 'error'
                                          });
                                          return;
                                        }
                                        
                                        // Otherwise start new work session
                                        try {
                                          const response = await api.post('/career/start-work', {
                                              lat: userComputer.coordinates.lat,
                                              lng: userComputer.coordinates.lng
                                            });
                                          
                                          const data = response.data;
                                          
                                          if (response.status === 200) {
                                            addNotification({
                                              title: '💼 Work Shift Started',
                                              message: `8-hour shift started! You'll earn ${careerData.userJob.hourlyRate * 8} WR Coins when complete.`,
                                              type: 'success'
                                            });
                                            
                                            // Start work session timer with deadline tracking
                                            const now = Date.now();
                                            setWorkSession({
                                              startTime: now,
                                              originalStartTime: now, // Track when shift originally began
                                              deadline: now + (24 * 60 * 60 * 1000), // 24 hours from start
                                              duration: 8 * 60 * 60 * 1000, // 8 hours in milliseconds
                                              remainingTime: 8 * 60 * 60 * 1000, // Time left to work
                                              totalWorkedTime: 0, // Total time actually worked
                                              hourlyRate: careerData.userJob.hourlyRate,
                                              office: office,
                                              active: true,
                                              paused: false
                                            });
                                            
                                            // Computer will be hidden by filtering logic during work session
                                            
                                            // Refresh career data
                                            fetchCareerData();
                                          } else {
                                            addNotification({
                                              title: '❌ Cannot Start Work',
                                              message: data.message || 'Unable to start work shift',
                                              type: 'error'
                                            });
                                          }
                                        } catch (error) {
                                          console.error('Error starting work:', error);
                                          
                                          // Check if it's a 400 error with a specific message from server
                                          const errorMessage = error.response?.data?.message || 'Failed to start work shift';
                                          
                                          addNotification({
                                            title: '❌ Work Error',
                                            message: errorMessage,
                                            type: 'error'
                                          });
                                        }
                                      }}
                                      style={{
                                        background: isPausedAtThisOffice ? '#ffc107' : '#28a745',
                                        color: isPausedAtThisOffice ? '#000' : 'white',
                                        border: 'none',
                                        padding: '10px 15px',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        width: '100%',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {isPausedAtThisOffice ? '▶️ Resume Work' : '🚀 Start 8-Hour Shift'}
                                    </button>
                                  )}
                                  
                                  {/* Field Agent Operations Button */}
                                  {careerData.userJob && careerData.userJob.jobLevel === 'fieldAgent' && (
                                    <div style={{ marginTop: '10px' }}>
                                      {fieldOperation ? (
                                        <div>
                                          <div style={{ padding: '8px', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '5px', border: '1px solid #ffd700', marginBottom: '8px' }}>
                                            <p style={{ fontSize: '11px', color: '#b8860b', margin: '0', fontWeight: 'bold' }}>
                                              🤵 Field Operations Active
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              completeFieldOperations();
                                            }}
                                            style={{
                                              background: '#dc3545',
                                              color: 'white',
                                              border: 'none',
                                              padding: '8px 12px',
                                              borderRadius: '5px',
                                              cursor: 'pointer',
                                              width: '100%',
                                              fontSize: '12px',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            🛑 End Field Operations
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            startFieldOperations();
                                          }}
                                          style={{
                                            background: '#ffd700',
                                            color: '#000',
                                            border: 'none',
                                            padding: '8px 12px',
                                            borderRadius: '5px',
                                            cursor: 'pointer',
                                            width: '100%',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          🤵 Start Field Operations
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            } else {
                              return (
                                <div style={{ marginTop: '15px' }}>
                                  <p style={{ fontSize: '12px', color: '#dc3545', margin: '5px 0' }}>
                                    ❌ Too far to work ({(distance/1000).toFixed(1)}km away)
                                  </p>
                                  <p style={{ fontSize: '11px', color: '#666', margin: '5px 0' }}>
                                    Move your computer within 5km to start work
                                  </p>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      ) : (
                        <div style={{ marginTop: '15px' }}>
                          <p style={{ fontSize: '12px', color: '#666', margin: '5px 0' }}>
                            🏢 Office Location
                          </p>
                          <p style={{ fontSize: '11px', color: '#888' }}>
                            Apply for jobs in Lifestyle → Career
                          </p>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Community Task Envelopes - Visible when sniffer is active */}
            {snifferActive && communityTasks.filter(task => mapFilters.showTasks && task.coordinates && task.coordinates.lat && task.coordinates.lng).map((task) => {
              const envelopeIcon = createCustomIcon('📮', [25, 25]);
              
              return (
                <Marker
                  key={task._id}
                  position={[task.coordinates.lat, task.coordinates.lng]}
                  icon={envelopeIcon}
                  eventHandlers={{
                    click: () => handleTaskInteraction(task)
                  }}
                >
                  <Popup>
                    <div className="community-task-popup">
                      <h4>📮 Community Task</h4>
                      <p><strong>Title:</strong> {task.title}</p>
                      <p><strong>Type:</strong> {task.type}</p>
                      <p><strong>Difficulty:</strong> {task.difficulty}</p>
                      <p><strong>Creator:</strong> {task.createdBy.username}</p>
                      <p><strong>Completions:</strong> {task.completions}</p>
                      <p><strong>Attempts:</strong> {task.attempts}</p>
                      <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <button 
                          onClick={() => handleTaskInteraction(task)}
                          style={{
                            backgroundColor: '#00ff41',
                            color: '#000',
                            border: 'none',
                            padding: '5px 10px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            width: '100%'
                          }}
                        >
                          🎯 View Task (100 WR Coins)
                        </button>
                        
                        <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskRating(task._id, 'up');
                            }}
                            style={{
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              flex: 1
                            }}
                          >
                            👍 Like
                          </button>
                          
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskRating(task._id, 'down');
                            }}
                            style={{
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              flex: 1
                            }}
                          >
                            👎 Dislike
                          </button>
                          
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskReport(task._id, task.title);
                            }}
                            style={{
                              backgroundColor: '#ff9800',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              flex: 1
                            }}
                          >
                            🚨 Report
                          </button>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Drone Sniffer Range Circles - 3000m radius for all drones */}
            {deployedDrones.filter(drone => mapFilters.showDrones !== false).map((drone) => (
              <Circle
                key={`drone-radius-${drone.droneId}`}
                center={[drone.position.lat, drone.position.lng]}
                radius={(drone.type === 'maritime_scout') ? 50000 : 3000} // 50km for maritime scout, 3km for others
                pathOptions={{
                  color: '#ff6b35', // Orange color to distinguish from computer sniffer
                  fillColor: '#ff6b35',
                  fillOpacity: 0.05, // Made more faint
                  weight: 1, // Thinner line
                  opacity: 0.3, // Made border more faint
                  dashArray: '5, 5' // Dashed line to distinguish from solid computer circle
                }}
              />
            ))}

            {/* Burst Fire Laser Projectiles - now handled by DOM manipulation instead of Polylines */}

            {/* Sniffer Range Circle */}
            {snifferActive && userComputer && (
              <Circle
                center={[userComputer.coordinates.lat, userComputer.coordinates.lng]}
                radius={calculateSnifferRadius()}
                pathOptions={{
                  color: '#00ff41',
                  fillColor: '#00ff41',
                  fillOpacity: 0.1,
                  weight: 2
                }}
              />
            )}
          </MapContainer>
          </div>
        </div>
      </div>


      {/* Computer Control Section - Second (Directly Under Map) */}
      {!isFullscreen && (
        <div className="computer-control-under-map">
            {/* Admin Coordinate Picker */}
            {user?.role === 'admin' && (
              <div className="control-panel" style={{ background: 'rgba(255, 136, 0, 0.1)', border: '2px solid #ff8800' }}>
                <h3>🎯 Admin Coordinate Picker</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                  <button
                    onClick={() => {
                      setCoordinatePickerMode(!coordinatePickerMode);
                      setPickedCoordinates(null);
                    }}
                    style={{
                      padding: '10px 15px',
                      backgroundColor: coordinatePickerMode ? '#dc3545' : '#ff8800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    {coordinatePickerMode ? '🚫 Stop Picking' : '🎯 Start Coordinate Picker'}
                  </button>
                  <span style={{ 
                    color: coordinatePickerMode ? '#ff8800' : '#888', 
                    fontSize: '12px',
                    fontWeight: coordinatePickerMode ? 'bold' : 'normal'
                  }}>
                    {coordinatePickerMode ? 'Click anywhere on the map to copy coordinates' : 'Click button to activate coordinate picker'}
                  </span>
                </div>
                {pickedCoordinates && (
                  <div style={{ 
                    padding: '10px', 
                    background: 'rgba(0, 255, 65, 0.1)', 
                    borderRadius: '5px',
                    border: '1px solid rgba(0, 255, 65, 0.3)'
                  }}>
                    <p style={{ margin: '0', color: '#00ff41', fontSize: '14px' }}>
                      📍 Last picked: {pickedCoordinates?.lat?.toFixed(6) || 'N/A'}, {pickedCoordinates?.lng?.toFixed(6) || 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Computer Controls */}
            <div className="control-panel">
              <h3>💻 Computer Control</h3>
              {userComputer ? (
                <div className="computer-status">
                  <p>Status: {
                    userComputer.isSleeping ? '😴 Sleeping in house' :
                    isActivelyWorking() ? '💼 Working' :
                    userComputer.isActive ? '🟢 Active' : '🔴 Inactive'
                  }</p>
                  <div className="survival-movement-info">
                    <p>
                      <span style={{ 
                        color: computerStats.mode === 'daily' ? '#00ff00' :
                               computerStats.mode === 'supplies' ? '#44ff44' : 
                               computerStats.mode === 'locked' ? '#ff4444' : '#ff8844' 
                      }}>
                        {computerStats.mode === 'daily' ? '📅 Daily Mode' :
                         computerStats.mode === 'supplies' ? '🍔 Normal Mode' : 
                         computerStats.mode === 'locked' ? '🔒 Computer Locked' : '🚨 Emergency Mode'}
                      </span>
                    </p>
                    <p>Moves: {computerStats.movesRemaining}/{computerStats.maxMoves}</p>
                    <p style={{ fontSize: '12px', color: '#ffaa00' }}>
                      Resets every {computerStats.resetPeriod}
                    </p>
                    {computerStats.mode === 'supplies' && (
                      <div style={{ fontSize: '11px', margin: '5px 0' }}>
                        <span style={{ color: '#44aa44' }}>🥤 Food/Drink: {computerStats.foodDrinkSupplies}/10 supplies</span>
                      </div>
                    )}
                    {computerStats.mode === 'emergency' && (
                      <div style={{ fontSize: '11px', color: '#ff4444' }}>
                        <p style={{ margin: '0 0 5px 0' }}>
                          ⚠️ Purchase Food/Drink supplies to restore normal movement
                        </p>
                        <button
                          onClick={async () => {
                            console.log('Manual data refresh triggered');
                            addNotification({
                              type: 'info',
                              title: '🔄 Refreshing Data',
                              message: 'Forcing complete data sync from database...'
                            });
                            await syncData();
                            window.location.reload();
                          }}
                          style={{
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            padding: '4px 8px',
                            borderRadius: '3px',
                            fontSize: '10px',
                            cursor: 'pointer',
                            width: '100%'
                          }}
                        >
                          🔄 Force Data Refresh
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="computer-actions" style={{
                    display: 'flex',
                    gap: '12px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    marginTop: '15px'
                  }}>
                    <button 
                      onClick={() => {
                        if (!userComputer.isSleeping) {
                          toggleSniffer();
                        }
                      }}
                      disabled={userComputer.isSleeping}
                      className={`sniffer-button ${snifferActive ? 'active' : ''}`}
                      style={{
                        background: snifferActive 
                          ? 'linear-gradient(135deg, #ff4444, #ff6666)' 
                          : 'linear-gradient(135deg, #00aa44, #00cc55)',
                        color: '#ffffff',
                        border: snifferActive 
                          ? '2px solid #ff6666' 
                          : '2px solid #00ff55',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        fontFamily: "'VT323', monospace",
                        cursor: userComputer.isSleeping ? 'not-allowed' : 'pointer',
                        opacity: userComputer.isSleeping ? 0.5 : 1,
                        transition: 'all 0.3s ease',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        minWidth: '140px',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        if (!userComputer.isSleeping) {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                      }}
                    >
                      {userComputer.isSleeping ? '😴 Sniffer Disabled' :
                       snifferActive ? '📡 Disable Sniffer' : '📡 Enable Sniffer (5km)'}
                    </button>
                    
                    {computerStats.movesRemaining > 0 && (
                      <button 
                        onClick={() => {
                          if (!userComputer.isSleeping) {
                            setIsMovingComputer(true);
                          }
                        }}
                        className="move-computer-button"
                        disabled={isMovingComputer || userComputer.isSleeping}
                        style={{
                          background: 'linear-gradient(135deg, #ff8800, #ffaa00)',
                          color: '#ffffff',
                          border: '2px solid #ffcc00',
                          borderRadius: '8px',
                          padding: '10px 16px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          fontFamily: "'VT323', monospace",
                          cursor: userComputer.isSleeping || isMovingComputer ? 'not-allowed' : 'pointer',
                          opacity: userComputer.isSleeping ? 0.5 : 1,
                          transition: 'all 0.3s ease',
                          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                          minWidth: '140px',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => {
                          if (!userComputer.isSleeping && !isMovingComputer) {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                        }}
                      >
                        {userComputer.isSleeping ? '😴 Movement Disabled' :
                         isMovingComputer ? '📍 Click map to move...' : '🚚 Move Computer (FREE)'}
                      </button>
                    )}
                    
                    <button 
                      onClick={() => setShowMyComputerModal(true)}
                      className="computer-info-button"
                      style={{
                        background: 'linear-gradient(135deg, #0066cc, #0088ff)',
                        color: '#ffffff',
                        border: '2px solid #00aaff',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        fontFamily: "'VT323', monospace",
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        minWidth: '140px',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 12px rgba(0,0,0,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                      }}
                    >
                      ℹ️ Computer Info
                    </button>
                  </div>

                  {isMovingComputer && (
                    <div className="placement-instructions">
                      <p>💡 Click anywhere on the map to move your computer</p>
                      <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0' }}>
                        ⚠️ Note: Computers can only be moved to land, not water/sea areas
                      </p>
                      <div className="coordinate-inputs" style={{ margin: '10px 0' }}>
                        <p style={{ fontSize: '14px', margin: '5px 0', color: '#00ff41' }}>Or enter coordinates manually:</p>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            type="number"
                            step="any"
                            placeholder="Latitude"
                            value={searchCoordinates.lat}
                            onChange={(e) => setSearchCoordinates({ ...searchCoordinates, lat: e.target.value })}
                            style={{
                              padding: '5px',
                              borderRadius: '3px',
                              border: '1px solid #00ff41',
                              background: '#000',
                              color: '#00ff41',
                              fontSize: '12px',
                              width: '90px'
                            }}
                          />
                          <input
                            type="number"
                            step="any"
                            placeholder="Longitude"
                            value={searchCoordinates.lng}
                            onChange={(e) => setSearchCoordinates({ ...searchCoordinates, lng: e.target.value })}
                            style={{
                              padding: '5px',
                              borderRadius: '3px',
                              border: '1px solid #00ff41',
                              background: '#000',
                              color: '#00ff41',
                              fontSize: '12px',
                              width: '90px'
                            }}
                          />
                          <button 
                            onClick={() => {
                              const lat = parseFloat(searchCoordinates.lat);
                              const lng = parseFloat(searchCoordinates.lng);
                              if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
                                moveComputer(lat, lng);
                              } else {
                                alert('Please enter valid coordinates (Lat: -90 to 90, Lng: -180 to 180)');
                              }
                            }}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '3px',
                              border: '1px solid #00ff41',
                              background: '#00ff41',
                              color: '#000',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            📍 Move
                          </button>
                          <button 
                            onClick={() => setIsMovingComputer(false)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '3px',
                              border: '1px solid #ff6b35',
                              background: '#ff6b35',
                              color: '#000',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-computer">
                  {/* Handle different states when computer is not visible */}
                  {isInJail() ? (
                    <>
                      <p>🔒 Computer hidden while in jail</p>
                      <p style={{ fontSize: '12px', color: '#ffaa00' }}>
                        Your computer is invisible to all players (including yourself) while incarcerated.
                        <br />Time remaining: {getJailTimeRemaining()} minutes
                      </p>
                      <p style={{ fontSize: '12px', color: '#ff6666', marginTop: '10px' }}>
                        🚫 Computer deployment is disabled while in jail.
                      </p>
                    </>
                  ) : (userComputer && userComputer.coordinates && isSleeping && sleepingInHouse) ? (
                    <>
                      <p>😴 Computer hidden while sleeping</p>
                      <p style={{ fontSize: '12px', color: '#ffaa00' }}>
                        Your computer is invisible to all players (including yourself) while you sleep in the house.
                        <br />Sleeping in: {sleepingInHouse.ownerUsername === user?.username ? 'your house' : `${sleepingInHouse.ownerUsername}'s house`}
                      </p>
                      <p style={{ fontSize: '12px', color: '#ff6666', marginTop: '10px' }}>
                        💡 Leave the house to make your computer visible again.
                      </p>
                    </>
                  ) : (userComputer && userComputer.coordinates && isActivelyWorking()) ? (
                    <>
                      <p>💼 Computer hidden while working</p>
                      <p style={{ fontSize: '12px', color: '#ffaa00' }}>
                        Your computer is invisible during your 8-hour work shift.
                        <br />Working at: {workSession?.office?.officeName}
                      </p>
                      <p style={{ fontSize: '12px', color: '#ff6666', marginTop: '10px' }}>
                        💡 Pause or complete your work shift to make your computer visible again.
                      </p>
                    </>
                  ) : isInJail() ? (
                    <>
                      <p>🔒 Computer deployment disabled</p>
                      <p style={{ fontSize: '12px', color: '#ff6666' }}>
                        You cannot deploy a computer while in jail.
                        <br />Complete your jail sentence to regain access.
                      </p>
                      <p style={{ fontSize: '12px', color: '#888', marginTop: '10px' }}>
                        💡 Jail time remaining: {getJailTimeRemaining()}
                      </p>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        background: 'rgba(255, 215, 0, 0.1)', 
                        border: '2px solid #ffd700', 
                        borderRadius: '8px', 
                        padding: '15px', 
                        marginBottom: '15px',
                        textAlign: 'center'
                      }}>
                        <h4 style={{ color: '#ffd700', margin: '0 0 10px 0' }}>
                          🎯 Set Your Game Location
                        </h4>
                        <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                          Deploy your computer to set your location for Compound Defense and other features!
                        </p>
                        <p style={{ margin: '0', fontSize: '12px', color: '#ffaa00' }}>
                          💡 Simply click anywhere on the map to place your computer
                        </p>
                      </div>
                      
                      <button 
                        onClick={() => setIsPlacingComputer(true)}
                        className="place-computer-button"
                        disabled={isPlacingComputer}
                        style={{
                          background: isPlacingComputer ? '#666' : 'linear-gradient(135deg, #ffd700, #ffaa00)',
                          color: isPlacingComputer ? '#fff' : '#000',
                          fontWeight: 'bold',
                          padding: '12px 20px',
                          fontSize: '16px'
                        }}
                      >
                        {isPlacingComputer ? '📍 Click map to place...' : '🖥️ Deploy Computer'}
                      </button>
                      
                      {isPlacingComputer && (
                        <div className="placement-instructions" style={{
                          background: 'rgba(0, 255, 65, 0.1)',
                          border: '1px solid #00ff41',
                          borderRadius: '5px',
                          padding: '10px',
                          marginTop: '10px'
                        }}>
                          <p style={{ margin: '0 0 5px 0' }}>💡 Click anywhere on the map to place your computer</p>
                          <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0' }}>
                            ⚠️ Note: Computers can only be placed on land, not in water/sea areas
                          </p>
                          <button 
                            onClick={() => setIsPlacingComputer(false)}
                            style={{
                              background: '#ff6b35',
                              color: 'white',
                              border: 'none',
                              padding: '8px 15px',
                              borderRadius: '5px',
                              cursor: 'pointer',
                              marginTop: '5px'
                            }}
                          >
                            ❌ Cancel
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

          {/* Sleep Status Panel */}
          {isSleeping && sleepingInHouse && (
            <div className="control-panel" style={{ background: 'rgba(0, 255, 65, 0.1)', border: '2px solid #00ff41' }}>
              <h3>😴 Sleeping Status</h3>
              <div style={{ textAlign: 'center', padding: '10px' }}>
                <p style={{ margin: '0 0 10px 0', color: '#00ff41', fontWeight: 'bold' }}>
                  Currently sleeping in {sleepingInHouse.ownerUsername === user?.username ? 'your house' : `${sleepingInHouse.ownerUsername}'s house`}
                </p>
                <p style={{ margin: '0 0 15px 0', fontSize: '12px', color: '#888' }}>
                  📍 Location: {sleepingInHouse.location?.lat?.toFixed(4) || 'N/A'}, {sleepingInHouse.location?.lng?.toFixed(4) || 'N/A'}
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    leaveHouse();
                  }}
                  style={{
                    background: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%'
                  }}
                >
                  🌅 Leave House
                </button>
              </div>
            </div>
          )}


          {/* House Placement Controls */}
          {unplacedHouses.length > 0 && (
            <div className="control-panel">
              <h3>🏠 House Placement</h3>
              <div className="house-placement-controls">
                <p>You have {unplacedHouses.length} unplaced house{unplacedHouses.length > 1 ? 's' : ''}</p>
                
                {unplacedHouses.map((house, index) => (
                  <div key={house._id} style={{ 
                    background: 'rgba(0, 255, 65, 0.1)', 
                    padding: '10px', 
                    borderRadius: '5px', 
                    margin: '5px 0',
                    border: '1px solid rgba(0, 255, 65, 0.3)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                          House #{index + 1}: {house.bedrooms} bedroom{house.bedrooms > 1 ? 's' : ''}
                        </p>
                        <div style={{ margin: '5px 0' }}>
                          {house.hasLounge && <span>🛋️ </span>}
                          {house.hasKitchen && <span>🍳 </span>}
                          {house.hasDiningRoom && <span>🍽️ </span>}
                          {house.hasGarden && <span>🌸 </span>}
                        </div>
                        <p style={{ 
                          margin: '5px 0 0 0', 
                          fontSize: '12px', 
                          color: '#ffaa00',
                          fontWeight: 'bold' 
                        }}>
                          💰 Cost: {house.totalCost} WR Coins
                        </p>
                      </div>
                      <button
                        onClick={() => deleteUnplacedHouse(house._id, house.totalCost)}
                        style={{
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          padding: '5px 8px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          marginLeft: '10px'
                        }}
                        title="Delete house and get full refund"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))}
                
                <button 
                  onClick={() => setIsPlacingHouse(!isPlacingHouse)}
                  className={`place-house-button ${isPlacingHouse ? 'active' : ''}`}
                  style={{
                    background: isPlacingHouse ? '#ff6b35' : '#00ff41',
                    color: isPlacingHouse ? 'white' : 'black',
                    border: 'none',
                    padding: '10px 15px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%',
                    marginTop: '10px'
                  }}
                >
                  {isPlacingHouse ? '📍 Click map to place house...' : '🏠 Place House on Map'}
                </button>
                
                {isPlacingHouse && (
                  <div className="placement-instructions" style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    padding: '10px',
                    borderRadius: '5px',
                    margin: '10px 0',
                    border: '1px solid rgba(255, 107, 53, 0.3)'
                  }}>
                    <p style={{ margin: '0 0 5px 0' }}>💡 Click anywhere on the map to place your house</p>
                    <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0' }}>
                      ⚠️ Houses must be at least 3.5km from banks, casinos, ATMs, hotels, and vaults
                    </p>
                    <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0' }}>
                      🏠 Houses must be at least 500m from other houses
                    </p>
                    <button 
                      onClick={() => setIsPlacingHouse(false)}
                      style={{
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vault Placement Controls */}
          {userVaults.length === 0 && !isInJail() && (
            <div className="control-panel">
              <h3>🔐 ATM Vault Placement</h3>
              <div className="vault-placement-controls">
                <p>Deploy your personal ATM vault to store codes and coins securely!</p>
                
                <button
                  onClick={() => setIsPlacingVault(!isPlacingVault)}
                  className={`place-vault-button ${isPlacingVault ? 'active' : ''}`}
                  style={{
                    background: isPlacingVault ? '#ff6b35' : '#00ff41',
                    color: isPlacingVault ? 'white' : 'black',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%',
                    marginTop: '10px'
                  }}
                >
                  {isPlacingVault ? '📍 Click map to place vault...' : '🔐 Place Vault on Map'}
                </button>
                
                {isPlacingVault && (
                  <div className="placement-instructions" style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid #ff6600',
                    borderRadius: '4px',
                    padding: '10px',
                    margin: '10px 0',
                    fontSize: '12px',
                    color: '#ffaa00'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>💡 Vault Placement Instructions:</p>
                    <ul style={{ margin: '0', paddingLeft: '20px' }}>
                      <li>Click anywhere on the map to place your vault</li>
                      <li>Vaults must be placed on land (not in water)</li>
                      <li>You can only have one vault at a time</li>
                      <li>Other players can discover and hack your vault</li>
                    </ul>
                    <button 
                      onClick={() => setIsPlacingVault(false)}
                      style={{
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginTop: '8px'
                      }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vault Movement Controls */}
          {userVaults.length > 0 && !isInJail() && (
            <div className="control-panel">
              <h3>🔄 Vault Movement</h3>
              <div className="vault-movement-controls">
                <p>Move your vault to a new location on the map (once per week, costs 100 WR Coins)</p>
                <button 
                  onClick={() => {
                    if (userVaults.length > 0) {
                      setVaultToMove(userVaults[0]);
                      setIsMovingVault(!isMovingVault);
                    }
                  }}
                  className={`place-vault-button ${isMovingVault ? 'active' : ''}`}
                  style={{
                    background: isMovingVault ? '#ff6b35' : '#00ff41',
                    color: isMovingVault ? 'white' : 'black',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%',
                    marginBottom: '10px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
                  }}
                  disabled={!userVaults[0] || (userVaults[0].weeklyMovementUsed && userVaults[0].lastWeeklyMoveAt && new Date() < new Date(new Date(userVaults[0].lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000)))}
                >
                  {isMovingVault ? '📍 Click map to move vault...' : 
                   (!userVaults[0] || (userVaults[0].weeklyMovementUsed && userVaults[0].lastWeeklyMoveAt && new Date() < new Date(new Date(userVaults[0].lastWeeklyMoveAt).getTime() + (7 * 24 * 60 * 60 * 1000)))) 
                     ? '⏰ Weekly move used' 
                     : '🔄 Move Vault on Map'}
                </button>
                
                {isMovingVault && (
                  <div className="movement-instructions" style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    border: '1px solid #ff6600',
                    borderRadius: '4px',
                    padding: '10px',
                    margin: '10px 0',
                    fontSize: '12px',
                    color: '#ffaa00'
                  }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>💡 Vault Movement Instructions:</p>
                    <ul style={{ margin: '0', paddingLeft: '20px' }}>
                      <li>Click anywhere on the map to move your vault</li>
                      <li>Costs 100 WR Coins to move</li>
                      <li>Can only move once per week</li>
                      <li>Vaults must be moved to land (not in water)</li>
                    </ul>
                    <button 
                      onClick={() => {
                        setIsMovingVault(false);
                        setVaultToMove(null);
                      }}
                      style={{
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginTop: '8px'
                      }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Business Placement Controls */}
          {(() => {
            // Find all unplaced businesses
            const unplacedBusinesses = userBusinesses.filter(business => 
              !business.location || !business.location.lat || !business.location.lng || 
              (business.location.lat === 0 && business.location.lng === 0)
            );
            
            if (unplacedBusinesses.length > 0) {
              return true;
            }
            return false;
          })() && (
            <div className="control-panel">
              <h3>🏢 Business Placement</h3>
              <div className="business-placement-controls">
                {(() => {
                  const unplacedBusinesses = userBusinesses.filter(business => 
                    !business.location || !business.location.lat || !business.location.lng || 
                    (business.location.lat === 0 && business.location.lng === 0)
                  );
                  
                  return (
                    <>
                      <p>
                        {unplacedBusinesses.length === 1 
                          ? 'Your business needs to be placed on the map!' 
                          : `You have ${unplacedBusinesses.length} businesses that need to be placed on the map!`
                        }
                      </p>
                      
                      {/* Business Selection */}
                      <div style={{ marginBottom: '15px' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#00ff41' }}>Select Business to Place:</h5>
                        {unplacedBusinesses.map((business, index) => (
                          <div key={business._id || index} style={{ 
                            background: 'rgba(0, 255, 65, 0.1)', 
                            padding: '10px', 
                            borderRadius: '5px', 
                            margin: '5px 0',
                            border: selectedBusinessForPlacement?._id === business._id ? '2px solid #00ff41' : '1px solid rgba(0, 255, 65, 0.3)',
                            cursor: 'pointer'
                          }}
                          onClick={() => setSelectedBusinessForPlacement(business)}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                                  🏗️ {business.businessName}
                                </p>
                                <p style={{ margin: '0', fontSize: '12px', color: '#888' }}>
                                  Type: {business.businessType.charAt(0).toUpperCase() + business.businessType.slice(1)}
                                </p>
                              </div>
                              <div style={{ 
                                width: '20px', 
                                height: '20px', 
                                borderRadius: '50%', 
                                border: '2px solid #00ff41',
                                background: selectedBusinessForPlacement?._id === business._id ? '#00ff41' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {selectedBusinessForPlacement?._id === business._id && (
                                  <span style={{ color: 'black', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
                
                {/* Place Business Button */}
                <button 
                  onClick={() => {
                    if (!selectedBusinessForPlacement) {
                      addNotification({
                        title: '❌ No Business Selected',
                        message: 'Please select a business to place first.',
                        type: 'error'
                      });
                      return;
                    }
                    setIsPlacingBusiness(!isPlacingBusiness);
                  }}
                  className={`place-business-button ${isPlacingBusiness ? 'active' : ''}`}
                  style={{
                    background: isPlacingBusiness ? '#ff6b35' : (selectedBusinessForPlacement ? '#00ff41' : '#666'),
                    color: isPlacingBusiness ? 'white' : (selectedBusinessForPlacement ? 'black' : '#ccc'),
                    border: 'none',
                    padding: '10px 15px',
                    borderRadius: '5px',
                    cursor: selectedBusinessForPlacement ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%',
                    marginTop: '10px'
                  }}
                  disabled={!selectedBusinessForPlacement}
                >
                  {isPlacingBusiness ? '📍 Click map to place business...' : '🏢 Place Business on Map'}
                </button>
                
                {isPlacingBusiness && selectedBusinessForPlacement && (
                  <div className="placement-instructions" style={{
                    background: 'rgba(255, 107, 53, 0.1)',
                    padding: '10px',
                    borderRadius: '5px',
                    margin: '10px 0',
                    border: '1px solid rgba(255, 107, 53, 0.3)'
                  }}>
                    <p style={{ margin: '0 0 5px 0' }}>💡 Click anywhere on the map to place <strong>{selectedBusinessForPlacement.businessName}</strong></p>
                    <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0' }}>
                      🏢 Other users will be able to see and interact with your business
                    </p>
                    <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0' }}>
                      🔗 Users can request to link their business to yours for trade
                    </p>
                    <button 
                      onClick={() => {
                        setIsPlacingBusiness(false);
                        setSelectedBusinessForPlacement(null);
                      }}
                      style={{
                        background: '#ff4444',
                        color: 'white',
                        border: 'none',
                        padding: '5px 10px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      ❌ Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Defense Tower Placement Control */}
          {isPlacingTower && (
            <div className="control-panel active" style={{
              background: 'rgba(40, 40, 40, 0.95)',
              border: '2px solid var(--hacker-green)',
              borderRadius: '10px',
              padding: '15px',
              margin: '10px 0'
            }}>
              <h3 style={{ 
                color: 'var(--hacker-green)', 
                marginBottom: '10px',
                fontSize: '1.2rem',
                textAlign: 'center',
                borderBottom: '1px solid rgba(0, 255, 65, 0.3)',
                paddingBottom: '10px'
              }}>
                🗼 Tower Placement Mode
              </h3>
              
              <div className="placement-instructions" style={{
                background: 'rgba(0, 255, 65, 0.1)',
                padding: '10px',
                borderRadius: '5px',
                margin: '10px 0',
                border: '1px solid rgba(0, 255, 65, 0.3)'
              }}>
                <p style={{ margin: '0 0 5px 0', textAlign: 'center' }}>
                  {towerDeploymentLoading ? (
                    <span style={{ color: '#ffaa00' }}>
                      🔄 Deploying tower, please wait...
                    </span>
                  ) : (
                    '💡 Click anywhere on the map to deploy a defense tower'
                  )}
                </p>
                <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0', textAlign: 'center' }}>
                  🗼 Cost: 400 WR Coins | Must be within 2km of command center | 500m from other towers
                </p>
                <p style={{ fontSize: '11px', color: '#888', margin: '5px 0', textAlign: 'center' }}>
                  🛡️ Each tower provides 1km shield radius protection
                </p>
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button 
                    onClick={() => setIsPlacingTower(false)}
                    disabled={towerDeploymentLoading}
                    style={{
                      background: towerDeploymentLoading ? '#666' : '#ff4444',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '3px',
                      cursor: towerDeploymentLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      flex: 1,
                      opacity: towerDeploymentLoading ? 0.6 : 1
                    }}
                  >
                    ❌ Cancel Placement
                  </button>
                  <button 
                    onClick={() => window.location.href = '/system?tab=compound'}
                    disabled={towerDeploymentLoading}
                    style={{
                      background: towerDeploymentLoading ? '#666' : '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '3px',
                      cursor: towerDeploymentLoading ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      flex: 1,
                      opacity: towerDeploymentLoading ? 0.6 : 1
                    }}
                  >
                    🏰 Back to Compound
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Drone Deployment Control */}
          {isDeployingDrone && (
            <div className="control-panel active" style={{
              background: 'rgba(40, 40, 40, 0.95)',
              border: '2px solid var(--hacker-green)',
              borderRadius: '10px',
              padding: '15px',
              margin: '10px 0'
            }}>
              <h3 style={{ 
                color: 'var(--hacker-green)', 
                marginBottom: '10px',
                fontSize: '1.2rem',
                textAlign: 'center',
                borderBottom: '1px solid rgba(0, 255, 65, 0.3)',
                paddingBottom: '10px'
              }}>
                ✈️ Drone Deployment Mode
              </h3>
              
              <div className="placement-instructions" style={{
                background: 'rgba(0, 255, 65, 0.1)',
                padding: '10px',
                borderRadius: '5px',
                margin: '10px 0',
                border: '1px solid rgba(0, 255, 65, 0.3)'
              }}>
                <p style={{ margin: '0 0 5px 0', textAlign: 'center' }}>
                  💡 Click anywhere on the map to deploy your drone
                </p>
                <p style={{ fontSize: '11px', color: '#ffaa00', margin: '5px 0', textAlign: 'center' }}>
                  ✈️ Single-use attack drone | 500m attack range | 1-hour duration
                </p>
                <p style={{ fontSize: '11px', color: '#888', margin: '5px 0', textAlign: 'center' }}>
                  🎯 Can destroy one defense tower within range
                </p>
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button 
                    onClick={() => setIsDeployingDrone(false)}
                    style={{
                      background: '#ff4444',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      flex: 1
                    }}
                  >
                    ❌ Cancel Deployment
                  </button>
                  <button 
                    onClick={() => window.location.href = '/computer/parts?tab=offense_drones'}
                    style={{
                      background: '#007bff',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      flex: 1
                    }}
                  >
                    🛒 Buy More Drones
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Drone Management Panel */}
          {mapFilters.showOffense && user?.droneInventory?.length > 0 && !isDeployingDrone && (
            <div className="control-panel" style={{
              background: 'rgba(40, 40, 40, 0.95)',
              border: '2px solid #ff6b35',
              borderRadius: '10px',
              padding: '15px',
              margin: '10px 0'
            }}>
              <h3 style={{ 
                color: '#ff6b35', 
                marginBottom: '10px',
                fontSize: '1.2rem',
                textAlign: 'center',
                borderBottom: '1px solid rgba(255, 107, 53, 0.3)',
                paddingBottom: '10px'
              }}>
                ✈️ Drone Operations
              </h3>
              
              <div className="drone-inventory" style={{
                background: 'rgba(255, 107, 53, 0.1)',
                padding: '10px',
                borderRadius: '5px',
                margin: '10px 0',
                border: '1px solid rgba(255, 107, 53, 0.3)'
              }}>
                <p style={{ margin: '0 0 10px 0', textAlign: 'center', fontSize: '14px' }}>
                  📦 Available Drones: {user?.droneInventory?.length || 0}
                </p>
                
                {user?.droneInventory?.slice(0, 3).map((drone, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    margin: '5px 0',
                    padding: '5px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '3px'
                  }}>
                    <span style={{ fontSize: '12px' }}>
                      {drone.icon || '✈️'} {drone.name || 'Attack Drone'}
                    </span>
                    <button 
                      onClick={() => {
                        setSelectedDroneForDeployment(drone);
                        setIsDeployingDrone(true);
                      }}
                      style={{
                        background: '#ff6b35',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '10px'
                      }}
                    >
                      🚀 Deploy
                    </button>
                  </div>
                ))}
                
                {user?.droneInventory?.length > 3 && (
                  <p style={{ textAlign: 'center', fontSize: '11px', color: '#888', margin: '5px 0' }}>
                    ...and {user.droneInventory.length - 3} more
                  </p>
                )}
              </div>
            </div>
          )}

      {/* All Other Controls Section - Third (Below Computer Controls) */}
      {!isFullscreen && (
        <div className="controls-section">
          {/* XP Chest Management */}
          <div className="control-panel">
            <h3>⭐ XP Chest Manager</h3>
            <div className="xp-chest-controls">
              <p style={{ fontSize: '14px', margin: '10px 0', color: '#ffaa00' }}>
                💡 Having trouble moving your computer? Clear your personal XP chests to enable movement.
              </p>
              <button 
                onClick={clearPersonalChests}
                className="clear-chests-button"
                style={{
                  padding: '10px 15px',
                  borderRadius: '5px',
                  border: '2px solid #ff6b35',
                  background: 'linear-gradient(45deg, #ff4444, #ff6b35)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  margin: '5px 0',
                  width: '100%',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'linear-gradient(45deg, #ff6b35, #ff8800)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'linear-gradient(45deg, #ff4444, #ff6b35)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                🗑️ Clear All Personal XP Chests
              </button>
              <p style={{ fontSize: '12px', margin: '5px 0', color: '#888' }}>
                This will remove all your personal XP chests and allow computer movement.
              </p>
            </div>
          </div>

          {/* Career Job Location Panel */}
          {careerData.userJob && userComputer && (
            <div className="control-panel" style={{ background: 'rgba(75, 0, 130, 0.1)', border: '2px solid #4b0082' }}>
              <h3>💼 Career Center</h3>
              <div className="career-controls">
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ margin: '5px 0', color: '#9370db', fontWeight: 'bold' }}>
                    Current Job: {careerData.userJob.jobLevel === 'junior' ? '🔰' : '⭐'} {careerData.userJob.jobTitle}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    📍 {careerData.userJob.officeName}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '13px', color: '#888' }}>
                    {careerData.userJob.company}
                  </p>
                </div>
                
                <button
                  onClick={moveComputerToJob}
                  disabled={isMovingComputer || !hasAvailableMoves()}
                  style={{
                    padding: '12px 15px',
                    borderRadius: '5px',
                    border: '2px solid #4b0082',
                    background: isMovingComputer || !hasAvailableMoves() 
                      ? 'rgba(75, 0, 130, 0.3)' 
                      : 'linear-gradient(45deg, #4b0082, #9370db)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isMovingComputer || !hasAvailableMoves() ? 'not-allowed' : 'pointer',
                    width: '100%',
                    margin: '5px 0'
                  }}
                  title={
                    !hasAvailableMoves() 
                      ? 'No computer moves remaining. Wait for reset or use supplies.' 
                      : 'Move your computer to your job location to start work'
                  }
                >
                  {isMovingComputer ? '📍 Moving...' : '🏢 Move to Job Location'}
                </button>
                
                <p style={{ fontSize: '12px', margin: '5px 0', color: '#888' }}>
                  Move your computer to your workplace to start work shifts. Uses 1 computer move.
                </p>
                
                {!hasAvailableMoves() && (
                  <p style={{ fontSize: '11px', margin: '5px 0', color: '#ff6b35' }}>
                    ⚠️ No moves available ({computerStats.dailyMoves || 0}/10 used)
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Work Session Timer Panel */}
          {workSession && workSession.active && (
            <div className="control-panel" style={{ background: 'rgba(40, 167, 69, 0.1)', border: '2px solid #28a745' }}>
              <h3>⏰ Work Shift in Progress</h3>
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: '15px' }}>
                  <p style={{ margin: '5px 0', color: '#28a745', fontWeight: 'bold' }}>
                    🏢 {workSession.office.officeName}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '14px' }}>
                    💰 Earning {workSession.hourlyRate} WR/hour
                  </p>
                </div>
                
                <div style={{ 
                  padding: '15px', 
                  background: 'rgba(40, 167, 69, 0.2)', 
                  borderRadius: '8px',
                  border: '1px solid #28a745'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: workSession.paused ? '#ff8800' : '#28a745', margin: '10px 0' }}>
                    {(() => {
                      const remaining = Math.max(0, workSession.remainingTime || 0);
                      
                      const hours = Math.floor(remaining / (1000 * 60 * 60));
                      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
                      
                      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    })()}
                  </div>
                  <p style={{ margin: '5px 0', fontSize: '12px', color: '#666' }}>
                    {workSession.paused ? '⏸️ PAUSED' : 'Time Remaining'}
                  </p>
                </div>
                
                <div style={{ marginTop: '15px' }}>
                  <div style={{ 
                    background: 'rgba(40, 167, 69, 0.3)', 
                    borderRadius: '10px', 
                    height: '10px', 
                    margin: '10px 0',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: workSession.paused 
                        ? 'linear-gradient(90deg, #ff8800, #ffaa44)' 
                        : 'linear-gradient(90deg, #28a745, #20c997)',
                      height: '100%',
                      borderRadius: '10px',
                      width: `${(() => {
                        const totalWorkTime = 8 * 60 * 60 * 1000; // 8 hours
                        const workedTime = totalWorkTime - (workSession.remainingTime || 0);
                        const progress = (workedTime / totalWorkTime) * 100;
                        return Math.min(100, Math.max(0, progress));
                      })()}%`,
                      transition: 'width 1s ease'
                    }} />
                  </div>
                  <p style={{ margin: '5px 0', fontSize: '11px', color: '#666' }}>
                    {(() => {
                      const totalWorkTime = 8 * 60 * 60 * 1000; // 8 hours
                      const workedTime = totalWorkTime - (workSession.remainingTime || 0);
                      const progress = (workedTime / totalWorkTime) * 100;
                      return `${Math.min(100, Math.max(0, progress)).toFixed(1)}% Complete`;
                    })()}
                  </p>
                </div>
                
                {/* Pause/Resume Controls */}
                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                  {workSession.active && !workSession.paused ? (
                    <button
                      onClick={pauseWorkSession}
                      style={{
                        background: '#ff8800',
                        color: 'white',
                        border: 'none',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        flex: '1',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      ⏸️ Leave Work
                    </button>
                  ) : workSession.paused ? (
                    <button
                      onClick={resumeWorkSession}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        flex: '1',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      ▶️ Resume Work
                    </button>
                  ) : null}
                </div>
                
                {/* Deadline Warning */}
                <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(220, 53, 69, 0.1)', borderRadius: '5px' }}>
                  <p style={{ margin: '5px 0', fontSize: '12px', color: '#dc3545' }}>
                    ⏰ Deadline: {(() => {
                      const timeLeft = workSession.deadline - Date.now();
                      const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
                      const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
                      return `${hoursLeft}h ${minutesLeft}m remaining`;
                    })()}
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '11px', color: '#666' }}>
                    Complete within 24 hours or lose payment
                  </p>
                </div>
                
                <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255, 193, 7, 0.1)', borderRadius: '5px' }}>
                  <p style={{ margin: '5px 0', fontSize: '12px', color: '#856404' }}>
                    💵 Expected Pay: {workSession.hourlyRate * 8} WR Coins
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '11px', color: '#666' }}>
                    Payment will be automatically processed when shift completes
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ATM Codes Panel */}
          {userATMCodes && userATMCodes.length > 0 && (
            <div className="control-panel">
              <h3>🏧 Your ATM Codes ({userATMCodes.length})</h3>
              <div className="atm-codes-content" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {userATMCodes.map((code, index) => (
                  <div key={index} style={{ 
                    margin: '5px 0', 
                    padding: '8px', 
                    backgroundColor: '#2a2a2a', 
                    borderRadius: '4px',
                    fontSize: '12px',
                    position: 'relative'
                  }}>
                    <p style={{ margin: '2px 0' }}>
                      <strong>🔑 Code:</strong> {code.code}
                    </p>
                    <p style={{ margin: '2px 0' }}>
                      <strong>📍 Location:</strong> {code.coordinates?.lat?.toFixed(4) || 'N/A'}, {code.coordinates?.lng?.toFixed(4) || 'N/A'}
                    </p>
                    {code.locationDescription && (
                      <p style={{ margin: '2px 0', color: '#888' }}>
                        {code.locationDescription}
                      </p>
                    )}
                    <p style={{ margin: '2px 0', fontSize: '10px', color: '#666' }}>
                      Found: {new Date(code.foundAt).toLocaleDateString()}
                    </p>
                    <p style={{ margin: '2px 0', fontSize: '10px' }}>
                      {formatCountdown(code.hoursRemaining, code.minutesRemaining, code.isExpired)}
                    </p>
                    <div style={{ position: 'absolute', top: '5px', right: '5px', display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => moveComputerNearATM(code.atmId, code)}
                        disabled={!hasAvailableMoves() || !userComputer}
                        style={{
                          background: hasAvailableMoves() && userComputer ? '#00ff41' : '#666',
                          color: hasAvailableMoves() && userComputer ? '#000' : '#999',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          cursor: hasAvailableMoves() && userComputer ? 'pointer' : 'not-allowed'
                        }}
                        title={
                          !hasAvailableMoves() 
                            ? `No moves available (${computerStats.dailyMoves}/10 used)` 
                            : !userComputer 
                              ? 'Deploy computer first' 
                              : `Move computer near ATM location (Uses 1 supply)`
                        }
                      >
                        📍
                      </button>
                      <button
                        onClick={() => deleteATMCode(code.atmId)}
                        style={{
                          background: '#ff4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '2px 6px',
                          fontSize: '10px',
                          cursor: 'pointer'
                        }}
                        title="Delete ATM code and remove machine from map"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Network Attack Status */}
          {networkAttackStatus && (networkAttackStatus.activeAttacks?.length > 0 || networkAttackStatus.incomingAttacks?.length > 0) && (
            <div className="control-panel">
              <h3>⚡ Network Operations</h3>
              <div className="network-status-content">
                {networkAttackStatus.activeAttacks?.length > 0 && (
                  <div style={{ margin: '10px 0' }}>
                    <h4>🔨 Active Attacks ({networkAttackStatus.activeAttacks.length})</h4>
                    {networkAttackStatus.activeAttacks.slice(0, 3).map((attack, index) => (
                      <div key={index} style={{ 
                        margin: '5px 0', 
                        padding: '5px', 
                        backgroundColor: '#ff8800', 
                        borderRadius: '3px',
                        fontSize: '12px'
                      }}>
                        <p>🎯 Target: {attack.targetUsername}</p>
                        <p>⏱️ Time left: {attack.timeRemaining || 'Calculating...'}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {networkAttackStatus.incomingAttacks?.length > 0 && (
                  <div style={{ margin: '10px 0' }}>
                    <h4>🚨 Incoming Attacks ({networkAttackStatus.incomingAttacks.length})</h4>
                    {networkAttackStatus.incomingAttacks.slice(0, 3).map((attack, index) => (
                      <div key={index} style={{ 
                        margin: '5px 0', 
                        padding: '5px', 
                        backgroundColor: '#dc3545', 
                        borderRadius: '3px',
                        fontSize: '12px'
                      }}>
                        <p>👤 From: {attack.attackerUsername}</p>
                        <p>⏱️ Time left: {attack.timeRemaining || 'Calculating...'}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                <div style={{ margin: '10px 0', fontSize: '12px' }}>
                  <p>🔧 Network Tools: {networkAttackStatus.networkInjectorTools || 0}</p>
                  <p>🛡️ Firewall Parts: {networkAttackStatus.firewallParts || 0}</p>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="control-panel">
            <h3>📋 Getting Started</h3>
            <div className="instructions-content">
              <div style={{ 
                background: 'rgba(255, 215, 0, 0.1)', 
                border: '1px solid #ffd700', 
                borderRadius: '5px', 
                padding: '10px', 
                marginBottom: '10px' 
              }}>
                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#ffd700' }}>
                  🎯 <strong>NEW USER?</strong> Click anywhere on the map to deploy your computer!
                </p>
                <p style={{ margin: '0', fontSize: '12px' }}>
                  This sets your game location for Compound Defense, business management, and all other features.
                </p>
              </div>
              <p>💻 <strong>Deploy Computer:</strong> Click anywhere on the map to set your location</p>
              <p>🖱️ <strong>Activate Sniffer:</strong> Click your computer to reveal nearby targets (5km radius)</p>
              <p>🎯 <strong>Find Treasure:</strong> Discover chests and enter combinations for rewards</p>
              <p>🏧 <strong>Hack Systems:</strong> Banks, casinos, ATMs require your computer to be within range</p>
              <p>✈️ <strong>Scout with Drones:</strong> Deploy drones for reconnaissance (cannot hack financial targets)</p>
              <p>🚨 <strong>Warning:</strong> SWAT may respond to illegal activities!</p>
              <br />
              <p style={{ textAlign: 'center', color: '#00ff87' }}>
                <strong>📖 <a href="/how-to-play" style={{ color: '#00ff87', textDecoration: 'underline' }}>View Complete Game Guide</a></strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Combination Modal */}
      {selectedChest && (
        <div className="modal-overlay" onClick={() => setSelectedChest(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>🔓 Enter Combination</h3>
            <p>Chest at: {selectedChest.coordinates.lat?.toFixed(4) || '0.0000'}, {selectedChest.coordinates.lng?.toFixed(4) || '0.0000'}</p>
            
            {/* Check for active brute force on treasure chest */}
            {(() => {
              const activeBruteForce = activeBruteForces.find(bf => bf.targetId === selectedChest.chestId && bf.targetType === 'chest');
              
              if (activeBruteForce) {
                // Helper functions for progress calculation (same as BruteForceTools)
                const formatTimeRemaining = (estimatedCompletion) => {
                  const now = new Date();
                  const completion = new Date(estimatedCompletion);
                  const timeRemaining = completion.getTime() - now.getTime();
                  
                  if (timeRemaining <= 0) {
                    return 'Completed! Check Brute Force Tools to collect.';
                  }
                  
                  const days = Math.floor(timeRemaining / (24 * 60 * 60 * 1000));
                  const hours = Math.floor((timeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
                  const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
                  
                  if (days > 0) {
                    return `${days}d ${hours}h ${minutes}m`;
                  } else if (hours > 0) {
                    return `${hours}h ${minutes}m`;
                  } else {
                    return `${minutes}m`;
                  }
                };

                const getProgressPercentage = (startTime, estimatedCompletion) => {
                  const start = new Date(startTime).getTime();
                  const end = new Date(estimatedCompletion).getTime();
                  const now = new Date().getTime();
                  
                  const totalTime = end - start;
                  const elapsed = now - start;
                  
                  return Math.min(100, Math.max(0, (elapsed / totalTime) * 100));
                };

                const isCompleted = activeBruteForce.completed || new Date() >= new Date(activeBruteForce.estimatedCompletion);
                const progress = getProgressPercentage(activeBruteForce.startTime, activeBruteForce.estimatedCompletion);
                
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: isCompleted ? '#28a745' : activeBruteForce.connectionLost ? '#dc3545' : '#ff8800', borderRadius: '5px' }}>
                    <h4>🔨 Active Brute Force Attack</h4>
                    <div style={{ margin: '10px 0' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span><strong>Status:</strong></span>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          backgroundColor: isCompleted ? '#4caf50' : activeBruteForce.connectionLost ? '#f44336' : '#ff9800',
                          color: 'white'
                        }}>
                          {isCompleted ? '✅ Ready' : activeBruteForce.connectionLost ? '📡 Connection Lost' : '⚡ Running'}
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div style={{ margin: '10px 0' }}>
                        <div style={{ 
                          width: '100%', 
                          height: '8px', 
                          backgroundColor: '#333', 
                          borderRadius: '4px', 
                          overflow: 'hidden' 
                        }}>
                          <div style={{ 
                            width: activeBruteForce.connectionLost ? '0%' : `${progress}%`, 
                            height: '100%', 
                            backgroundColor: isCompleted ? '#4caf50' : '#ff8800',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '12px', marginTop: '4px', color: '#ccc' }}>
                          {activeBruteForce.connectionLost ? 'No Signal' : `${Math.round(progress)}% Complete`}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Started:</strong> {new Date(activeBruteForce.startTime).toLocaleDateString()} {new Date(activeBruteForce.startTime).toLocaleTimeString()}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Time Remaining:</strong> {activeBruteForce.connectionLost ? 'Connection Lost' : formatTimeRemaining(activeBruteForce.estimatedCompletion)}
                      </p>
                      {activeBruteForce.connectionLost && (
                        <p style={{ margin: '5px 0', color: '#ffcccc' }}>
                          <strong>⚠️ Status:</strong> Move computer within {calculateSnifferRadius()}m to resume
                        </p>
                      )}
                      <p style={{ margin: '5px 0' }}>
                        <strong>Success Rate:</strong> 70%
                      </p>
                    </div>
                    
                    {isCompleted && (
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: 'rgba(76, 175, 80, 0.2)', borderRadius: '4px' }}>
                        <p style={{ margin: '0', color: '#4caf50', fontWeight: 'bold' }}>
                          🎯 Attack Complete! Visit System Tools → Brute Force to collect your results.
                        </p>
                      </div>
                    )}
                  </div>
                );
              }
              
              return null; // No active brute force for this chest
            })()}
            
            <input
              type="text"
              value={combinationInput}
              onChange={(e) => setCombinationInput(e.target.value)}
              placeholder="Enter combination..."
              className="combination-input"
            />
            
            {/* Option to start brute force if no active attack */}
            {!activeBruteForces.find(bf => bf.targetId === selectedChest.chestId && bf.targetType === 'chest') && (
              <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                <h5>🔨 Brute Force Attack</h5>
                <p>• Duration: 16-30 days (random, reduced by brute force parts)</p>
                <p>• Success rate: 70%</p>
                <p>• Requires computer within range</p>
                <button 
                  onClick={() => {
                    handleBruteForce(selectedChest, 'chest');
                    setSelectedChest(null);
                  }}
                  style={{
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    width: '100%',
                    marginTop: '5px'
                  }}
                >
                  🔨 Start Brute Force
                </button>
              </div>
            )}
            
            <div className="modal-buttons">
              <button onClick={handleCombinationSubmit} className="submit-button">
                🔓 Open Chest
              </button>
              <button onClick={() => setSelectedChest(null)} className="cancel-button">
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* XP Chest Countdown Modal */}
      {showXPCountdown && xpCountdownData && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            border: '2px solid #00ff41',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              {xpCountdownData.chestType === 'movement_xp' ? '🚀' : '⭐'}
            </div>
            
            <h3 style={{ 
              color: '#00ff41', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(0, 255, 65, 0.5)'
            }}>
              {xpCountdownData.chestType === 'movement_xp' ? 'Movement XP Crate Opened!' : 'XP Chest Opened!'}
            </h3>
            
            <div style={{ 
              fontSize: '24px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              +{xpCountdownData.xpGained} XP
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: xpCountdownTimer <= 2 ? '#ff4444' : '#00ff41',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {xpCountdownTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: 0
            }}>
              Closing automatically...
            </p>
          </div>
        </div>
      )}

      {/* Computer Movement Countdown Modal */}
      {showMoveCountdown && moveCountdownData && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            border: '2px solid #ff6b35',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(255, 107, 53, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              🚗
            </div>
            
            <h3 style={{ 
              color: '#ff6b35', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(255, 107, 53, 0.5)'
            }}>
              Moving Computer...
            </h3>
            
            <div style={{ 
              fontSize: '18px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              Target: {moveCountdownData?.lat?.toFixed(4) || 'N/A'}, {moveCountdownData?.lng?.toFixed(4) || 'N/A'}
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: moveCountdownTimer <= 3 ? '#ff4444' : '#ff6b35',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {moveCountdownTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: '0'
            }}>
              Computer is moving...
            </p>
          </div>
        </div>
      )}

      {/* Computer Deployment Countdown Modal */}
      {showDeployCountdown && deployCountdownData && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            border: '2px solid #00ff41',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              🖥️
            </div>
            
            <h3 style={{ 
              color: '#00ff41', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(0, 255, 65, 0.5)'
            }}>
              Deploying Computer...
            </h3>
            
            <div style={{ 
              fontSize: '18px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              Target: {deployCountdownData?.lat?.toFixed(4) || 'N/A'}, {deployCountdownData?.lng?.toFixed(4) || 'N/A'}
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: deployCountdownTimer <= 3 ? '#ff4444' : '#00ff41',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {deployCountdownTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: '0'
            }}>
              Computer is being deployed...
            </p>
          </div>
        </div>
      )}

      {/* House Distance Warning Modal */}
      {showHouseDistanceWarning && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '450px',
            padding: '30px',
            background: 'linear-gradient(135deg, #2d1a1a 0%, #4d2d2d 100%)',
            border: '2px solid #ff6b35',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(255, 107, 53, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              ⚠️
            </div>
            
            <h3 style={{ 
              color: '#ff6b35', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(255, 107, 53, 0.5)'
            }}>
              Too Close to Place House!
            </h3>
            
            <div style={{ 
              fontSize: '16px', 
              color: '#fff', 
              marginBottom: '20px',
              lineHeight: '1.5',
              padding: '15px',
              background: 'rgba(255, 107, 53, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(255, 107, 53, 0.3)'
            }}>
              {houseDistanceMessage}
            </div>
            
            <div style={{ 
              fontSize: '14px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold'
            }}>
              📍 Please move away and try a different location
            </div>
            
            <button 
              onClick={() => setShowHouseDistanceWarning(false)}
              style={{
                background: '#ff6b35',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(255, 107, 53, 0.3)'
              }}
            >
              🔄 Try Another Location
            </button>
          </div>
        </div>
      )}

      {/* House Construction Modal */}
      {showHouseConstruction && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #1a2d1a 0%, #2d4d2d 100%)',
            border: '2px solid #00ff41',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              🏗️
            </div>
            
            <h3 style={{ 
              color: '#00ff41', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(0, 255, 65, 0.5)'
            }}>
              Constructing Your House!
            </h3>
            
            <div style={{ 
              fontSize: '18px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              🏠 Building in progress...
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: houseConstructionTimer <= 2 ? '#ffd700' : '#00ff41',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {houseConstructionTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: 0
            }}>
              🔨 Construction crew working...
            </p>
          </div>
        </div>
      )}

      {/* Arrest Pending Countdown Modal */}
      {arrestPending && showArrestModal && (
        <div className="modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{ 
            backgroundColor: '#2a0a0a', 
            border: '3px solid #ff0000',
            boxShadow: '0 0 30px rgba(255, 0, 0, 0.8)',
            animation: 'pulse 1s infinite',
            textAlign: 'center'
          }}>
            <h2 style={{ color: '#ff0000', marginBottom: '20px' }}>
              🚨 ARREST WARRANT ISSUED! 🚨
            </h2>
            <div style={{ 
              backgroundColor: '#ff0000', 
              color: '#fff', 
              padding: '15px', 
              borderRadius: '10px', 
              fontSize: '24px', 
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              ⏰ {Math.ceil(escapeTimeRemaining / 1000)} seconds to escape!
            </div>
            <p style={{ color: '#ffaa00', fontSize: '18px', marginBottom: '15px' }}>
              Field Agent <strong>{arrestPending.arrestedBy}</strong> has issued an arrest warrant!
            </p>
            <p style={{ color: '#fff', fontSize: '16px', marginBottom: '20px' }}>
              <strong>🏃 ESCAPE NOW:</strong> Move your computer to evade arrest!<br/>
              <strong>⚰️ If caught:</strong> You will be jailed for {arrestPending.jailDuration} hours!
            </p>
            <div style={{ 
              backgroundColor: '#333', 
              padding: '10px', 
              borderRadius: '5px',
              fontSize: '14px',
              color: '#ccc',
              marginBottom: '20px'
            }}>
              💡 <strong>How to escape:</strong> Click the button below, then click anywhere on the map to move your computer!
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                style={{
                  backgroundColor: isEscapeMode ? '#666' : '#ff6600',
                  color: '#fff',
                  border: 'none',
                  padding: '15px 25px',
                  borderRadius: '5px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: isEscapeMode ? 'default' : 'pointer',
                  boxShadow: '0 0 15px rgba(255, 102, 0, 0.6)'
                }}
                onClick={() => {
                  if (!isEscapeMode) {
                    setIsEscapeMode(true);
                    // Close the arrest modal to allow map interaction
                    setShowArrestModal(false);
                    addNotification({
                      title: '🎯 ESCAPE MODE ACTIVATED',
                      message: 'Click anywhere on the map to move your computer!',
                      type: 'info'
                    });
                  }
                }}
                disabled={isEscapeMode}
              >
                {isEscapeMode ? '🎯 Click Map to Escape!' : '🏃 MOVE COMPUTER TO ESCAPE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Computer Info Modal */}
      {showMyComputerModal && userComputer && (
        <div className="modal-overlay" onClick={() => setShowMyComputerModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>💻 Your Computer Information</h3>
            <div style={{ textAlign: 'left' }}>
              <p><strong>📍 Location:</strong> {userComputer.coordinates?.lat?.toFixed(4) || 'N/A'}, {userComputer.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
              <p><strong>🔋 Status:</strong> {userComputer.isActive ? '🟢 Active' : '🔴 Inactive'}</p>
              <p><strong>🚚 Moves Today:</strong> {computerStats.dailyMoves}/10</p>
              <p><strong>🍔 Movement:</strong> Uses 1 supply or 1 emergency move</p>
              <p><strong>📅 Placed:</strong> {new Date(userComputer.placedAt).toLocaleDateString()}</p>
              
              {computerStats.dailyMoves >= 10 && (
                <p style={{ color: '#ff4444' }}>
                  <strong>🔒 Locked:</strong> Computer is locked for 24 hours after 10 moves
                </p>
              )}
              
              <div style={{ marginTop: '15px' }}>
                <h4>Computer Status Legend:</h4>
                <p>🟢 Fresh (0 moves)</p>
                <p>🟠 Move 1 (1 move)</p>
                <p>🔴 Move 2 (2 moves)</p>
                <p>🔒 Locked (3 moves - 24h cooldown)</p>
              </div>
            </div>
            <div className="modal-buttons">
              <button onClick={() => setShowMyComputerModal(false)} className="cancel-button">
                ✅ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Computer Hacking Modal */}
      {selectedComputer && (
        <div className="modal-overlay" onClick={() => setSelectedComputer(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {fieldOperation ? (
              // Field Agent interface
              <>
                <h3>🤵 Field Agent Investigation</h3>
                <p>Target: <strong>{selectedComputer.username}</strong></p>
                <p>Location: {selectedComputer.coordinates?.lat?.toFixed(4) || 'N/A'}, {selectedComputer.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
                <p>Status: {selectedComputer.isActive ? '🟢 Active' : '🔴 Inactive'}</p>
                
                {arrestData ? (
                  <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#2a2a3a', borderRadius: '5px', border: '1px solid #00ff41' }}>
                    <h4>🔍 Active Criminal Activities</h4>
                    <p>Username: {arrestData.username}</p>
                    <p>Total Active Attacks: <strong style={{ color: arrestData.totalAttacks > 0 ? '#ff4444' : '#00ff41' }}>{arrestData.totalAttacks}</strong></p>
                    
                    {arrestData.activeHacks.networkAttacks.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <strong>🔨 Network Injection Attacks ({arrestData.activeHacks.networkAttacks.length}):</strong>
                        {arrestData.activeHacks.networkAttacks.map((attack, index) => (
                          <p key={index} style={{ fontSize: '12px', color: '#ffaa44', marginLeft: '10px' }}>
                            • Target: {attack.targetUsername || 'Unknown'}
                          </p>
                        ))}
                      </div>
                    )}
                    
                    {arrestData.activeHacks.bruteForces.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <strong>🔓 Brute Force Attacks ({arrestData.activeHacks.bruteForces.length}):</strong>
                        {arrestData.activeHacks.bruteForces.map((attack, index) => (
                          <p key={index} style={{ fontSize: '12px', color: '#ffaa44', marginLeft: '10px' }}>
                            • Type: {attack.attackType || attack.type || 'Unknown'}
                          </p>
                        ))}
                      </div>
                    )}
                    
                    {arrestData.activeHacks.vaultHacks.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <strong>🏰 Vault Hacks ({arrestData.activeHacks.vaultHacks.length}):</strong>
                        {arrestData.activeHacks.vaultHacks.map((attack, index) => (
                          <p key={index} style={{ fontSize: '12px', color: '#ffaa44', marginLeft: '10px' }}>
                            • Stage: {attack.stage || 'Unknown'}
                          </p>
                        ))}
                      </div>
                    )}
                    
                    {arrestData.activeHacks.malwareAttacks.length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <strong>🦠 Malware Attacks ({arrestData.activeHacks.malwareAttacks.length}):</strong>
                        {arrestData.activeHacks.malwareAttacks.map((attack, index) => (
                          <p key={index} style={{ fontSize: '12px', color: '#ffaa44', marginLeft: '10px' }}>
                            • Target: {attack.targetEmail || 'Unknown'}
                          </p>
                        ))}
                      </div>
                    )}
                    
                    {arrestData.totalAttacks === 0 && (
                      <p style={{ color: '#00ff41', fontStyle: 'italic' }}>✅ No active criminal activities detected</p>
                    )}
                  </div>
                ) : (
                  <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#444', borderRadius: '5px' }}>
                    <p>🔄 Loading criminal activity data...</p>
                  </div>
                )}
                
                <div className="modal-buttons">
                  {arrestData && arrestData.hasActiveAttacks && (
                    <button 
                      onClick={() => {
                        arrestUser({ userId: selectedComputer.userId, username: selectedComputer.username });
                        setSelectedComputer(null);
                        setArrestData(null);
                      }}
                      className="submit-button"
                      style={{ backgroundColor: '#ff4444' }}
                    >
                      🚨 Arrest for Criminal Activity
                    </button>
                  )}
                  <button onClick={() => {
                    setSelectedComputer(null);
                    setArrestData(null);
                  }} className="cancel-button">
                    ❌ Close Investigation
                  </button>
                </div>
              </>
            ) : (
              // Regular hacker interface
              <>
                <h3>🔓 Hack Computer</h3>
                <p>Target: <strong>{selectedComputer.username}</strong></p>
                <p>Location: {selectedComputer.coordinates?.lat?.toFixed(4) || 'N/A'}, {selectedComputer.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
                <p>Status: {selectedComputer.isActive ? '🟢 Active' : '🔴 Inactive'}</p>
                
                <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#333', borderRadius: '5px' }}>
                  <h4>🔨 Network Injection Attack</h4>
                  <p>• Base duration: 6 hours</p>
                  <p>• Tools owned: {networkAttackStatus.networkInjectorTools}</p>
                  <p>• Reduced time: {Math.max(1, 6 - networkAttackStatus.networkInjectorTools)} hours</p>
                  <p>• Success rate: 70%</p>
                </div>
                
                <div className="modal-buttons">
                  <button 
                    onClick={() => {
                      hackComputer(selectedComputer);
                      setSelectedComputer(null);
                    }}
                    className="submit-button"
                  >
                    🚀 Start Attack
                  </button>
                  <button onClick={() => setSelectedComputer(null)} className="cancel-button">
                    ❌ Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Casino Modal */}
      {selectedCasino && (
        <div className="modal-overlay" onClick={() => setSelectedCasino(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {/* Casino Hotel Image */}
            {selectedCasino.imageUrl && (
              <div style={{ marginBottom: '15px', textAlign: 'center' }}>
                <img 
                  src={selectedCasino.imageUrl} 
                  alt={selectedCasino.name}
                  style={{ 
                    width: '100%', 
                    maxWidth: '400px',
                    height: '200px', 
                    objectFit: 'cover', 
                    borderRadius: '8px',
                    border: '2px solid #ffd700'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
            
            <h3>🎰 {selectedCasino.name}</h3>
            <p>📍 Location: {selectedCasino.location}</p>
            
            {/* Check for active vault hack */}
            {(() => {
              const activeHack = activeVaultHacks.find(hack => hack.casinoId === selectedCasino.casinoId);
              
              if (activeHack) {
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#333', borderRadius: '5px' }}>
                    <h4>🏦 Active Vault Hack</h4>
                    <p><strong>Stage:</strong> {activeHack.stageDescription || activeHack.stage}</p>
                    <p><strong>Progress:</strong> {activeHack.progressPercent ? `${activeHack.progressPercent}% - ${activeHack.timeDisplay}` : 'Starting...'}</p>
                    
                    {/* Progress Bar */}
                    {activeHack.progressPercent !== undefined && (
                      <div style={{
                        width: '100%',
                        backgroundColor: '#222',
                        borderRadius: '4px',
                        height: '8px',
                        margin: '5px 0',
                        border: '1px solid #555'
                      }}>
                        <div style={{
                          width: `${Math.max(0, Math.min(100, activeHack.progressPercent))}%`,
                          backgroundColor: activeHack.progressPercent === 100 ? '#00ff41' : '#ff6b35',
                          height: '100%',
                          borderRadius: '3px',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    )}
                    
                    {(activeHack.stage === 'security' || activeHack.stage === 'network_injection') && (
                      <p>🔒 Stage 1: Network injection in progress...</p>
                    )}
                    
                    {activeHack.stage === 'awaiting_malware' && (
                      <div>
                        <div style={{ background: 'rgba(255, 165, 0, 0.1)', padding: '10px', borderRadius: '4px', border: '1px solid #ffa500', marginBottom: '10px' }}>
                          <p style={{ margin: '0 0 10px 0', color: '#ffa500', fontWeight: 'bold' }}>✅ Stage 1 Complete!</p>
                          <p style={{ margin: '0 0 5px 0', color: '#fff' }}>Network injection successful. Employee credentials discovered.</p>
                          {activeHack.casinoEmployeeEmail && (
                            <div style={{ background: 'rgba(0, 255, 65, 0.1)', padding: '8px', borderRadius: '4px', marginTop: '8px' }}>
                              <p style={{ margin: '0', fontSize: '0.9em', color: '#00ff41', fontFamily: 'monospace' }}>
                                📧 <strong>Discovered:</strong> {activeHack.casinoEmployeeEmail}
                              </p>
                              <p style={{ margin: '5px 0 0 0', fontSize: '0.8em', color: '#aaa' }}>
                                💡 Use this email in System → Tools → Malware Tool for Stage 2
                              </p>
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => {
                            handleMalwareStage(selectedCasino.casinoId);
                            setSelectedCasino(null);
                          }}
                          style={{
                            background: '#ff6b35',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            margin: '5px 0'
                          }}
                        >
                          🦠 Go to Malware Tool (Stage 2)
                        </button>
                      </div>
                    )}
                    
                    {activeHack.stage === 'malware' && (
                      <p>🦠 Stage 2: Malware attack in progress...</p>
                    )}
                    
                    {activeHack.stage === 'malware_complete' && (
                      <div>
                        <div style={{ background: 'rgba(0, 255, 65, 0.1)', padding: '10px', borderRadius: '4px', border: '1px solid #00ff41', marginBottom: '10px' }}>
                          <p style={{ margin: '0 0 5px 0', color: '#00ff41', fontWeight: 'bold' }}>✅ Stage 2 Complete!</p>
                          <p style={{ margin: '0', color: '#fff' }}>Malware successfully deployed. Casino security compromised.</p>
                        </div>
                        <button 
                          onClick={() => {
                            handleTimerLooting(selectedCasino.casinoId);
                            setSelectedCasino(null);
                          }}
                          style={{
                            background: '#28a745',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            margin: '5px 0'
                          }}
                        >
                          💰 Start Looting (Stage 3)
                        </button>
                      </div>
                    )}
                    
                    {activeHack.stage === 'looting' && (
                      <div style={{ background: 'linear-gradient(145deg, #1a4a1a, #0a3a0a)', padding: '15px', borderRadius: '8px', border: '2px solid #00ff41' }}>
                        <h5 style={{ color: '#00ff41', margin: '0 0 10px 0', textShadow: '0 0 5px #00ff41' }}>💰 Stage 3: Active Vault Looting</h5>
                        
                        {/* Real-time earnings display */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                          <div style={{ background: 'rgba(0, 255, 65, 0.1)', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#00ff41' }}>
                              {(() => {
                                // Calculate real-time earnings if looting started
                                if (activeHack.lootingStartTime) {
                                  const now = new Date();
                                  const lootingStart = new Date(activeHack.lootingStartTime);
                                  const secondsLooted = Math.floor((now - lootingStart) / 1000);
                                  const coinsEarned = Math.max(0, secondsLooted * 3); // 3 coins per second
                                  return coinsEarned.toLocaleString();
                                }
                                return (activeHack.totalLooted || 0).toFixed(1);
                              })()} 
                            </div>
                            <div style={{ fontSize: '0.8em', color: '#aaa' }}>💎 Total Looted</div>
                          </div>
                          <div style={{ background: 'rgba(255, 107, 53, 0.1)', padding: '8px', borderRadius: '4px', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#ff6b35' }}>3.0</div>
                            <div style={{ fontSize: '0.8em', color: '#aaa' }}>⚡ WR Coins/sec</div>
                          </div>
                        </div>
                        
                        {/* SWAT notification system */}
                        {(() => {
                          const now = new Date();
                          
                          // Check if SWAT notification has been triggered
                          if (activeHack.swatNotificationTriggered) {
                            // SWAT has been called - show 30 minute countdown
                            if (activeHack.swatArrivalTime) {
                              const swatTime = new Date(activeHack.swatArrivalTime);
                              const timeUntilSwat = swatTime.getTime() - now.getTime();
                              
                              if (timeUntilSwat > 0) {
                                const minutes = Math.floor(timeUntilSwat / (1000 * 60));
                                const seconds = Math.floor((timeUntilSwat % (1000 * 60)) / 1000);
                                
                                const isDanger = timeUntilSwat <= 600000; // 10 minute danger
                                const isCritical = timeUntilSwat <= 300000; // 5 minute critical
                                
                                return (
                                  <div style={{ 
                                    background: isCritical ? 'rgba(255, 68, 68, 0.3)' : isDanger ? 'rgba(255, 68, 68, 0.2)' : 'rgba(255, 165, 0, 0.2)', 
                                    border: `2px solid ${isCritical ? '#ff4444' : isDanger ? '#ff6666' : '#ffa500'}`,
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    marginBottom: '10px',
                                    textAlign: 'center'
                                  }}>
                                    <div style={{ fontSize: '1.1em', fontWeight: 'bold', color: isCritical ? '#ff4444' : isDanger ? '#ff6666' : '#ffa500' }}>
                                      🚨 SWAT HAS BEEN CALLED!
                                    </div>
                                    <div style={{ fontSize: '1.0em', fontWeight: 'bold', color: isCritical ? '#ff4444' : isDanger ? '#ff6666' : '#ffa500', marginTop: '5px' }}>
                                      ⏰ {minutes}m {seconds}s to escape!
                                    </div>
                                    <div style={{ fontSize: '0.9em', color: isCritical ? '#ff6666' : isDanger ? '#ff8888' : '#ffaa66', marginTop: '5px' }}>
                                      {isCritical ? '🚨 CRITICAL: Move computer NOW!' : '⚠️ Move computer >500 miles to escape!'}
                                    </div>
                                  </div>
                                );
                              } else {
                                return (
                                  <div style={{ 
                                    background: 'rgba(255, 68, 68, 0.3)', 
                                    border: '2px solid #ff4444',
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    marginBottom: '10px',
                                    textAlign: 'center'
                                  }}>
                                    <div style={{ fontSize: '1.2em', fontWeight: 'bold', color: '#ff4444' }}>
                                      🚨 SWAT HAS ARRIVED!
                                    </div>
                                    <div style={{ fontSize: '0.9em', color: '#ff6666', marginTop: '5px' }}>
                                      Move your computer immediately or face arrest!
                                    </div>
                                  </div>
                                );
                              }
                            }
                          } else {
                            // No notification yet - don't show exact timing
                            return (
                              <div style={{ 
                                background: 'rgba(255, 255, 0, 0.1)', 
                                border: '2px solid #ffff00',
                                padding: '10px', 
                                borderRadius: '4px', 
                                marginBottom: '10px',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '1.1em', fontWeight: 'bold', color: '#ffff00' }}>
                                  🚨 SWAT Status: Unknown
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#ffdd66', marginTop: '5px' }}>
                                  Continue looting - SWAT could arrive at any time!
                                </div>
                                <div style={{ fontSize: '0.8em', color: '#ffcc44', marginTop: '3px' }}>
                                  You'll receive a 30-minute warning when they're called
                                </div>
                              </div>
                            );
                          }
                        })()}
                        
                        {/* Looting status and duration */}
                        <div style={{ fontSize: '0.9em', color: '#ccc', textAlign: 'center' }}>
                          {(() => {
                            if (activeHack.lootingStartTime) {
                              const now = new Date();
                              const lootingStart = new Date(activeHack.lootingStartTime);
                              const secondsElapsed = Math.floor((now - lootingStart) / 1000);
                              const hours = Math.floor(secondsElapsed / 3600);
                              const minutes = Math.floor((secondsElapsed % 3600) / 60);
                              const seconds = secondsElapsed % 60;
                              
                              const timeDisplay = hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
                              
                              return (
                                <div>
                                  <div>💰 Looting for: {timeDisplay}</div>
                                  <div style={{ marginTop: '5px' }}>⚡ Earning 3 WR Coins every second...</div>
                                </div>
                              );
                            }
                            return '💰 Vault looting in progress...';
                          })()} 
                        </div>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => {
                        handleStopAttack(selectedCasino.casinoId);
                        setSelectedCasino(null);
                      }}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginTop: '10px'
                      }}
                    >
                      🛑 Stop Attack
                    </button>
                  </div>
                );
              }
              
              // No active hack - show casino options
              // Check if user has already hacked this casino this month
              const currentMonth = new Date().getMonth() + 1;
              const currentYear = new Date().getFullYear();
              const alreadyHackedThisMonth = user?.hackedCasinos?.some(hack => 
                hack.casinoId === selectedCasino.casinoId && 
                hack.monthHacked === currentMonth && 
                hack.yearHacked === currentYear
              );
              
              // Check if user has another active casino hack
              const hasActiveHack = user?.casinoHack?.isActive;
              
              if (alreadyHackedThisMonth) {
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#3a2a2a', borderRadius: '5px', border: '2px solid #666' }}>
                    <h4>🏦 Casino Already Hacked</h4>
                    <div style={{ background: 'rgba(255, 165, 0, 0.1)', padding: '10px', borderRadius: '4px', border: '1px solid #ffa500', marginBottom: '10px' }}>
                      <p style={{ margin: '0', color: '#ffa500', fontWeight: 'bold' }}>
                        ✅ You have already successfully hacked this casino this month
                      </p>
                      <p style={{ margin: '5px 0 0 0', color: '#ccc', fontSize: '0.9em' }}>
                        🗓️ Try again next month to hack this casino vault again
                      </p>
                    </div>
                    <div style={{ margin: '10px 0', padding: '8px', backgroundColor: '#2a2a2a', borderRadius: '4px' }}>
                      <p style={{ margin: '0', fontSize: '0.9em', color: '#aaa' }}>
                        💡 <strong>Tip:</strong> Find other casinos on the map to hack, or try slot machine hacking which has no monthly limit.
                      </p>
                    </div>
                  </div>
                );
              }
              
              if (hasActiveHack) {
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#3a2a2a', borderRadius: '5px', border: '2px solid #ff6b35' }}>
                    <h4>🚨 Casino Hack In Progress</h4>
                    <div style={{ background: 'rgba(255, 107, 53, 0.1)', padding: '10px', borderRadius: '4px', border: '1px solid #ff6b35', marginBottom: '10px' }}>
                      <p style={{ margin: '0', color: '#ff6b35', fontWeight: 'bold' }}>
                        ⚠️ You can only hack one casino at a time
                      </p>
                      <p style={{ margin: '5px 0 0 0', color: '#ccc', fontSize: '0.9em' }}>
                        Complete or stop your current casino hack before starting a new one
                      </p>
                    </div>
                    <div style={{ margin: '10px 0', padding: '8px', backgroundColor: '#2a2a2a', borderRadius: '4px' }}>
                      <p style={{ margin: '0', fontSize: '0.9em', color: '#aaa' }}>
                        💡 <strong>Tip:</strong> Find the casino you're currently hacking on the map to check progress, or stop the attack to start a new one.
                      </p>
                    </div>
                  </div>
                );
              }
              
              // Check if this casino was detected by a drone (not computer)
              const isDroneDetected = Object.values(droneDetectedCasinos).some(casinos => 
                Array.isArray(casinos) && casinos.some(casino => casino.casinoId === selectedCasino.casinoId)
              );
              
              // If detected by drone, show reconnaissance-only message
              if (isDroneDetected) {
                return (
                  <div style={{ margin: '15px 0' }}>
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      textAlign: 'center',
                      border: '2px solid #ff6b35'
                    }}>
                      <h4 style={{ color: '#ff6b35', margin: '0 0 10px 0' }}>✈️ Drone Reconnaissance</h4>
                      <p style={{ margin: '5px 0', color: '#6c757d', fontSize: '14px' }}>
                        This casino was detected by your drone sniffer system.
                      </p>
                      <p style={{ margin: '5px 0', color: '#dc3545', fontSize: '14px', fontWeight: 'bold' }}>
                        🚫 Drones cannot perform hacking operations
                      </p>
                      <p style={{ margin: '5px 0', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                        Deploy a computer within range to access this casino.
                      </p>
                    </div>
                  </div>
                );
              }
              
              return (
                <div style={{ margin: '15px 0' }}>
                  <h4>🎯 Casino Operations</h4>
                  
                  <div style={{ margin: '10px 0', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                    <h5>🎰 Slot Machine Hack</h5>
                    <p>• Quick hack with 20% SWAT chance</p>
                    <p>• Earn 500-2000 WR Coins</p>
                    <p>• Daily limit: 5 attempts</p>
                    <button 
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (slotHackInProgress) return; // Prevent double clicks
                        
                        try {
                          await handleSlotHack(selectedCasino);
                          // Refresh the map to show updated data
                          await loadTreasureHuntData();
                          // Keep popup open briefly to show result, then close
                          setTimeout(() => {
                            setSelectedCasino(null);
                          }, 2000);
                        } catch (error) {
                          console.error('Slot hack error:', error);
                        }
                      }}
                      disabled={slotHackInProgress}
                      style={{
                        background: slotHackInProgress ? '#666' : '#ffc107',
                        color: slotHackInProgress ? '#ccc' : 'black',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: slotHackInProgress ? 'not-allowed' : 'pointer',
                        width: '100%',
                        opacity: slotHackInProgress ? 0.6 : 1
                      }}
                    >
                      {slotHackInProgress ? '🎰 Hacking...' : '🎰 Hack Slot Machines'}
                    </button>
                  </div>
                  
                  <div style={{ margin: '10px 0', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                    <h5>🏦 Vault Hack</h5>
                    <p>• Multi-stage operation</p>
                    <p>• Higher rewards but more complex</p>
                    <p>• Requires malware tools</p>
                    <button 
                      onClick={() => {
                        handleVaultHackStart(selectedCasino);
                        setSelectedCasino(null);
                      }}
                      style={{
                        background: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                    >
                      🏦 Start Vault Hack
                    </button>
                  </div>
                </div>
              );
            })()}
            
            {/* Hotel booking section - only show if user is not currently hacking */}
            {(() => {
              const activeHack = activeVaultHacks.find(hack => hack.casinoId === selectedCasino.casinoId);
              const isHacking = activeHack || user?.casinoHack?.isActive;
              
              // Check if user has a booking for this casino
              const activeBooking = userBookings.find(booking => 
                booking.hotelId === selectedCasino.casinoId && 
                booking.status === 'booked' &&
                new Date() >= new Date(booking.checkInDate) &&
                new Date() <= new Date(booking.checkOutDate)
              );
              
              const checkedInBooking = userBookings.find(booking => 
                booking.hotelId === selectedCasino.casinoId && 
                booking.status === 'checked_in'
              );
              
              if (isHacking) {
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#3a2a2a', borderRadius: '5px', border: '2px solid #ff6b35' }}>
                    <h4>🚫 Hotel Services Unavailable</h4>
                    <div style={{ background: 'rgba(255, 107, 53, 0.1)', padding: '10px', borderRadius: '4px', border: '1px solid #ff6b35' }}>
                      <p style={{ margin: '0', color: '#ff6b35', fontWeight: 'bold' }}>
                        ⚠️ Cannot book rooms while hacking this casino
                      </p>
                      <p style={{ margin: '5px 0 0 0', color: '#ccc', fontSize: '0.9em' }}>
                        Complete or stop your casino hack to access hotel services
                      </p>
                    </div>
                  </div>
                );
              }
              
              return (
                <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                  <h4>🏨 Hotel Services</h4>
                  <p>⭐ {'★'.repeat(selectedCasino.rating || 3)} | 💰 {selectedCasino.pricePerNight || 'N/A'} WR/night</p>
                  
                  {/* Show booking status */}
                  {checkedInBooking && (
                    <div style={{ margin: '8px 0', padding: '8px', background: '#28a745', borderRadius: '3px' }}>
                      <p style={{ fontSize: '12px', color: 'white', margin: '0', fontWeight: 'bold' }}>
                        🏠 Currently staying here
                      </p>
                      <p style={{ fontSize: '11px', color: '#ccc', margin: '2px 0 0 0' }}>
                        Room: {checkedInBooking.roomNumber} | Check-out: {new Date(checkedInBooking.checkOutDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  
                  {activeBooking && !checkedInBooking && (
                    <div style={{ margin: '8px 0', padding: '8px', background: '#007bff', borderRadius: '3px' }}>
                      <p style={{ fontSize: '12px', color: 'white', margin: '0', fontWeight: 'bold' }}>
                        📅 Active booking
                      </p>
                      <p style={{ fontSize: '11px', color: '#ccc', margin: '2px 0 0 0' }}>
                        Room: {activeBooking.roomNumber} | {activeBooking.totalNights} nights
                      </p>
                    </div>
                  )}
                  
                  <div style={{ marginTop: '10px' }}>
                    {/* Show appropriate buttons based on booking status */}
                    {checkedInBooking ? (
                      <button 
                        onClick={() => {
                          handleHotelCheckOut(checkedInBooking);
                          setSelectedCasino(null);
                        }}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        🚪 Check Out
                      </button>
                    ) : activeBooking ? (
                      <button 
                        onClick={() => {
                          handleHotelCheckIn({...selectedCasino, hotelId: selectedCasino.casinoId});
                          setSelectedCasino(null);
                        }}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        ✅ Check In
                      </button>
                    ) : (
                      <button 
                        onClick={() => {
                          // Navigate to booking page
                          window.location.href = '/shopping';
                        }}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        🏨 Book Room
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            
            <div className="modal-buttons">
              <button onClick={() => setSelectedCasino(null)} className="cancel-button">
                ❌ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Access Modal */}
      {selectedBank && (
        <div className="modal-overlay" onClick={() => setSelectedBank(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>🏦 {selectedBank.name}</h3>
            <p>📍 Type: {selectedBank.type}</p>
            <p>🔒 Security Level: {selectedBank.securityLevel}</p>
            <p>💰 Reward: {selectedBank.reward ? `${selectedBank.reward.toLocaleString()} WR Coins` : '50,000-100,000 WR Coins'}</p>
            
            {/* Check for active brute force */}
            {(() => {
              const activeBruteForce = activeBruteForces.find(bf => bf.targetId === selectedBank.bankId && bf.targetType === 'bank');
              
              if (activeBruteForce) {
                // Helper functions for progress calculation (same as BruteForceTools)
                const formatTimeRemaining = (estimatedCompletion) => {
                  const now = new Date();
                  const completion = new Date(estimatedCompletion);
                  const timeRemaining = completion.getTime() - now.getTime();
                  
                  if (timeRemaining <= 0) {
                    return 'Completed! Check Brute Force Tools to collect.';
                  }
                  
                  const days = Math.floor(timeRemaining / (24 * 60 * 60 * 1000));
                  const hours = Math.floor((timeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
                  const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
                  
                  if (days > 0) {
                    return `${days}d ${hours}h ${minutes}m`;
                  } else if (hours > 0) {
                    return `${hours}h ${minutes}m`;
                  } else {
                    return `${minutes}m`;
                  }
                };

                const getProgressPercentage = (startTime, estimatedCompletion) => {
                  const start = new Date(startTime).getTime();
                  const end = new Date(estimatedCompletion).getTime();
                  const now = new Date().getTime();
                  
                  const totalTime = end - start;
                  const elapsed = now - start;
                  
                  return Math.min(100, Math.max(0, (elapsed / totalTime) * 100));
                };

                const isCompleted = activeBruteForce.completed || new Date() >= new Date(activeBruteForce.estimatedCompletion);
                const progress = getProgressPercentage(activeBruteForce.startTime, activeBruteForce.estimatedCompletion);
                
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: isCompleted ? '#28a745' : activeBruteForce.connectionLost ? '#dc3545' : '#ff8800', borderRadius: '5px' }}>
                    <h4>🔨 Active Brute Force Attack</h4>
                    <div style={{ margin: '10px 0' }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '8px'
                      }}>
                        <span><strong>Status:</strong></span>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '12px', 
                          fontSize: '12px',
                          backgroundColor: isCompleted ? '#4caf50' : activeBruteForce.connectionLost ? '#f44336' : '#ff9800',
                          color: 'white'
                        }}>
                          {isCompleted ? '✅ Ready' : activeBruteForce.connectionLost ? '📡 Connection Lost' : '⚡ Running'}
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div style={{ margin: '10px 0' }}>
                        <div style={{ 
                          width: '100%', 
                          height: '8px', 
                          backgroundColor: '#333', 
                          borderRadius: '4px', 
                          overflow: 'hidden' 
                        }}>
                          <div style={{ 
                            width: activeBruteForce.connectionLost ? '0%' : `${progress}%`, 
                            height: '100%', 
                            backgroundColor: isCompleted ? '#4caf50' : '#ff8800',
                            transition: 'width 0.3s ease'
                          }}></div>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '12px', marginTop: '4px', color: '#ccc' }}>
                          {activeBruteForce.connectionLost ? 'No Signal' : `${Math.round(progress)}% Complete`}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Started:</strong> {new Date(activeBruteForce.startTime).toLocaleDateString()} {new Date(activeBruteForce.startTime).toLocaleTimeString()}
                      </p>
                      <p style={{ margin: '5px 0' }}>
                        <strong>Time Remaining:</strong> {activeBruteForce.connectionLost ? 'Connection Lost' : formatTimeRemaining(activeBruteForce.estimatedCompletion)}
                      </p>
                      {activeBruteForce.connectionLost && (
                        <p style={{ margin: '5px 0', color: '#ffcccc' }}>
                          <strong>⚠️ Status:</strong> Move computer within {calculateSnifferRadius()}m to resume
                        </p>
                      )}
                      <p style={{ margin: '5px 0' }}>
                        <strong>Success Rate:</strong> 70%
                      </p>
                    </div>
                    
                    {isCompleted && (
                      <div style={{ marginTop: '10px', padding: '8px', backgroundColor: 'rgba(76, 175, 80, 0.2)', borderRadius: '4px' }}>
                        <p style={{ margin: '0', color: '#4caf50', fontWeight: 'bold' }}>
                          🎯 Attack Complete! Visit System Tools → Brute Force to collect your results.
                        </p>
                      </div>
                    )}
                  </div>
                );
              }
              
              // No active brute force - show access options
              
              // Check if this bank was detected by a drone (not computer)
              const isDroneDetected = Object.values(droneDetectedBanks).some(banks => 
                Array.isArray(banks) && banks.some(bank => bank.bankId === selectedBank.bankId)
              );
              
              // If detected by drone, show reconnaissance-only message
              if (isDroneDetected) {
                return (
                  <div style={{ margin: '15px 0' }}>
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      textAlign: 'center',
                      border: '2px solid #ff6b35'
                    }}>
                      <h4 style={{ color: '#ff6b35', margin: '0 0 10px 0' }}>✈️ Drone Reconnaissance</h4>
                      <p style={{ margin: '5px 0', color: '#6c757d', fontSize: '14px' }}>
                        This bank was detected by your drone sniffer system.
                      </p>
                      <p style={{ margin: '5px 0', color: '#dc3545', fontSize: '14px', fontWeight: 'bold' }}>
                        🚫 Drones cannot perform hacking operations
                      </p>
                      <p style={{ margin: '5px 0', color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                        Deploy a computer within range to access this bank.
                      </p>
                    </div>
                  </div>
                );
              }
              
              return (
                <div style={{ margin: '15px 0' }}>
                  <h4>🎯 Bank Access Options</h4>
                  
                  <div style={{ margin: '10px 0', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                    <h5>🔐 Direct Access</h5>
                    <p>Enter the 6-digit access code if you have it</p>
                    <input
                      type="text"
                      value={bankAccessCode}
                      onChange={(e) => setBankAccessCode(e.target.value)}
                      placeholder="Enter 6-digit code..."
                      maxLength={6}
                      style={{
                        width: '100%',
                        padding: '8px',
                        margin: '5px 0',
                        backgroundColor: '#444',
                        color: 'white',
                        border: '1px solid #666',
                        borderRadius: '4px'
                      }}
                    />
                    <button 
                      onClick={() => handleBankAccess(selectedBank, bankAccessCode)}
                      disabled={bankAccessCode.length !== 6}
                      style={{
                        background: bankAccessCode.length === 6 ? '#28a745' : '#666',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: bankAccessCode.length === 6 ? 'pointer' : 'not-allowed',
                        width: '100%'
                      }}
                    >
                      🔓 Access Bank
                    </button>
                  </div>
                  
                  <div style={{ margin: '10px 0', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                    <h5>🔨 Brute Force Attack</h5>
                    <p>• Duration: 16-30 days (random, reduced by brute force parts)</p>
                    <p>• Success rate: 40%</p>
                    <p>• Requires computer within range</p>
                    <button 
                      onClick={() => {
                        handleBruteForce(selectedBank, 'bank');
                        setSelectedBank(null);
                      }}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                    >
                      🔨 Start Brute Force
                    </button>
                  </div>
                </div>
              );
            })()}
            
            <div className="modal-buttons">
              <button onClick={() => setSelectedBank(null)} className="cancel-button">
                ❌ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ATM Access Modal */}
      {selectedATM && (
        <div className="modal-overlay" onClick={() => setSelectedATM(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>🏧 ATM Access</h3>
            <p>🏦 Bank: {selectedATM.bankName}</p>
            <p>📍 Location: {selectedATM.locationDescription}</p>
            <p>💰 Contains: 500 WR Coins</p>
            
            {(() => {
              // Check if this is a user-owned ATM or if user has the code for this ATM
              const isUserOwned = selectedATM.isUserOwned || selectedATM.ownedBy === 'user';
              const hasCode = userATMCodes.find(code => code.atmId === selectedATM.atmId);
              
              if (isUserOwned && selectedATM.accessCode) {
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#28a745', borderRadius: '5px' }}>
                    <h4>🔑 Your ATM Machine!</h4>
                    <p><strong>Your Code:</strong> {selectedATM.accessCode}</p>
                    <p>📍 <strong>Located at:</strong> {selectedATM.coordinates?.lat?.toFixed(4) || 'N/A'}, {selectedATM.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
                    {selectedATM.locationDescription && (
                      <p>📝 <strong>Description:</strong> {selectedATM.locationDescription}</p>
                    )}
                    {selectedATM.foundAt && (
                      <p>📅 <strong>Found:</strong> {new Date(selectedATM.foundAt).toLocaleDateString()}</p>
                    )}
                    <button 
                      onClick={() => handleATMAccess(selectedATM, selectedATM.accessCode)}
                      style={{
                        background: '#00ff41',
                        color: '#000',
                        border: 'none',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        width: '100%',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        marginTop: '10px'
                      }}
                    >
                      💰 Access Your ATM (Auto-Access)
                    </button>
                  </div>
                );
              } else if (hasCode) {
                return (
                  <div style={{ margin: '15px 0', padding: '15px', backgroundColor: '#28a745', borderRadius: '5px' }}>
                    <h4>🔑 You have the access code!</h4>
                    <p><strong>Code:</strong> {hasCode.code}</p>
                    <p>📍 <strong>Found at:</strong> {hasCode.coordinates?.lat?.toFixed(4) || 'N/A'}, {hasCode.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
                    {hasCode.locationDescription && (
                      <p>📝 <strong>Description:</strong> {hasCode.locationDescription}</p>
                    )}
                    <button 
                      onClick={() => handleATMAccess(selectedATM, hasCode.code)}
                      style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        width: '100%',
                        marginTop: '10px'
                      }}
                    >
                      💰 Use Code to Access ATM
                    </button>
                  </div>
                );
              }
              
              // No code found - manual entry
              return (
                <div style={{ margin: '15px 0' }}>
                  <h4>🔐 Manual Access</h4>
                  <p>Enter the 4-digit access code if you have it</p>
                  <input
                    type="text"
                    value={atmAccessCode}
                    onChange={(e) => setAtmAccessCode(e.target.value)}
                    placeholder="Enter 4-digit code..."
                    maxLength={4}
                    style={{
                      width: '100%',
                      padding: '8px',
                      margin: '5px 0',
                      backgroundColor: '#444',
                      color: 'white',
                      border: '1px solid #666',
                      borderRadius: '4px',
                      textAlign: 'center',
                      fontSize: '18px'
                    }}
                  />
                  <button 
                    onClick={() => handleATMAccess(selectedATM, atmAccessCode)}
                    disabled={atmAccessCode.length !== 4}
                    style={{
                      background: atmAccessCode.length === 4 ? '#007bff' : '#666',
                      color: 'white',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      cursor: atmAccessCode.length === 4 ? 'pointer' : 'not-allowed',
                      width: '100%'
                    }}
                  >
                    🏧 Access ATM
                  </button>
                  
                  <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#333', borderRadius: '5px' }}>
                    <h5>💡 How to find ATM codes:</h5>
                    <p>• Open treasure chests to find ATM codes</p>
                    <p>• Some chests contain coordinates and codes</p>
                    <p>• Check your ATM codes in the control panel</p>
                  </div>
                </div>
              );
            })()}
            
            <div className="modal-buttons">
              <button onClick={() => setSelectedATM(null)} className="cancel-button">
                ❌ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vault Hack Modal */}
      {selectedVault && (
        <div className="modal-overlay" onClick={() => setSelectedVault(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>🔐 Hack Vault</h3>
            <p>👤 Owner: {selectedVault.ownerUsername}</p>
            <p>📍 Location: {selectedVault.coordinates?.lat?.toFixed(4) || 'N/A'}, {selectedVault.coordinates?.lng?.toFixed(4) || 'N/A'}</p>
            <p>💰 Stored Balance: {selectedVault.storedBalance} WR Coins</p>
            <p>🔑 ATM Codes: {selectedVault.atmCodes ? selectedVault.atmCodes.length : (selectedVault.atmCodeCount || 0)}/4</p>
            <p>📦 Movement Count: {selectedVault.movementCount}/3</p>
            
            {/* Show protection status prominently */}
            {selectedVault.isProtected && (
              <div style={{ 
                margin: '15px 0', 
                padding: '10px', 
                backgroundColor: '#28a745', 
                borderRadius: '5px',
                border: '2px solid #1e7e34'
              }}>
                <p style={{ margin: '0', fontWeight: 'bold', fontSize: '16px' }}>
                  🛡️ VAULT PROTECTED BY COMMAND CENTER
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                  This vault cannot be hacked while protected by an active command center
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '13px', fontStyle: 'italic' }}>
                  To hack this vault: Destroy all defense towers → Destroy command center → Then hack vault
                </p>
              </div>
            )}
            
            {!selectedVault.isProtected && selectedVault.commandCenterActive === false && (
              <div style={{ 
                margin: '15px 0', 
                padding: '10px', 
                backgroundColor: '#dc3545', 
                borderRadius: '5px',
                border: '2px solid #bd2130'
              }}>
                <p style={{ margin: '0', fontWeight: 'bold', fontSize: '16px' }}>
                  ⚠️ VAULT VULNERABLE - NO PROTECTION
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                  Command center destroyed - vault can be hacked!
                </p>
              </div>
            )}
            
            {selectedVault.isLocked && selectedVault.lockedUntil && new Date() < new Date(selectedVault.lockedUntil) && (
              <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#dc3545', borderRadius: '5px' }}>
                <p><strong>🚨 Vault is currently locked due to SWAT activity</strong></p>
                <p>Unlocks: {new Date(selectedVault.lockedUntil).toLocaleString()}</p>
              </div>
            )}
            
            {/* Only show hacking section for other users' vaults */}
            {(() => {
              // Multiple checks to ensure we don't show hack button for own vaults
              const isOwnVault = selectedVault && user && (
                selectedVault.ownerUsername === user.username ||
                selectedVault.vaultId?.includes(user.username) ||
                selectedVault.vaultId?.includes(`_${user.username}_`)
              );
              
              return !isOwnVault;
            })() && (
              <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                <h4>🔨 Vault Hacking</h4>
                <p>• Base success rate: 60%</p>
                <p>• +5% per ATM code stored</p>
                <p>• +up to 20% for high balance</p>
                <p>• 70% chance of SWAT response</p>
                <p>• Success steals ALL coins and codes</p>
                <p>• Earn 50 XP for successful hack</p>
                
                {(() => {
                  // Check if this vault was detected by a drone
                  const isDroneDetected = Object.values(droneDetectedVaults).some(vaults => 
                    Array.isArray(vaults) && vaults.some(vault => vault.vaultId === selectedVault.vaultId)
                  );
                  
                  if (isDroneDetected) {
                    return (
                      <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px', 
                        border: '2px solid #ff6b35',
                        marginTop: '10px',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: '0 0 5px 0', color: '#ff6b35', fontSize: '14px', fontWeight: 'bold' }}>
                          ✈️ Drone Reconnaissance
                        </p>
                        <p style={{ margin: '0', color: '#dc3545', fontSize: '12px' }}>
                          🚫 Drones cannot perform vault attacks
                        </p>
                      </div>
                    );
                  }
                  
                  return (
                    <button 
                      onClick={() => handleVaultHack(selectedVault)}
                      disabled={selectedVault.isProtected || (selectedVault.isLocked && selectedVault.lockedUntil && new Date() < new Date(selectedVault.lockedUntil))}
                      style={{
                        background: (selectedVault.isProtected || (selectedVault.isLocked && selectedVault.lockedUntil && new Date() < new Date(selectedVault.lockedUntil))) ? '#666' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: (selectedVault.isProtected || (selectedVault.isLocked && selectedVault.lockedUntil && new Date() < new Date(selectedVault.lockedUntil))) ? 'not-allowed' : 'pointer',
                        width: '100%',
                        marginTop: '10px',
                        opacity: selectedVault.isProtected ? 0.6 : 1
                      }}
                      title={selectedVault.isProtected ? 'Vault is protected by command center' : ''}
                    >
                      {selectedVault.isProtected ? '🛡️ Protected - Cannot Hack' : '🔨 Hack This Vault'}
                    </button>
                  );
                })()}
              </div>
            )}
            
            {/* Show vault management info for own vaults */}
            {(() => {
              // Multiple checks to ensure we show vault info for own vaults
              const isOwnVault = selectedVault && user && (
                selectedVault.ownerUsername === user.username ||
                selectedVault.vaultId?.includes(user.username) ||
                selectedVault.vaultId?.includes(`_${user.username}_`)
              );
              
              return isOwnVault;
            })() && (
              <div style={{ margin: '15px 0', padding: '10px', backgroundColor: '#1a4a3a', borderRadius: '5px' }}>
                <h4>🔐 Your Vault</h4>
                <p>• This is your personal vault</p>
                <p>• Store ATM codes and WR Coins safely</p>
                <p>• Move your vault up to 3 times</p>
                <p>• Others can attempt to hack your vault</p>
                <p>• 70% chance they trigger SWAT when attacking</p>
                
                {/* Withdraw button for own vault */}
                {selectedVault.storedBalance > 0 && (
                  <div style={{ marginTop: '15px' }}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWithdrawClick(selectedVault);
                      }}
                      disabled={!isVaultInRange(selectedVault)}
                      style={{
                        background: isVaultInRange(selectedVault) ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        cursor: isVaultInRange(selectedVault) ? 'pointer' : 'not-allowed',
                        width: '100%',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        opacity: isVaultInRange(selectedVault) ? 1 : 0.6
                      }}
                      title={!isVaultInRange(selectedVault) ? 'Must be within 1000m of vault to withdraw' : 'Withdraw WR Coins from vault'}
                    >
                      {isVaultInRange(selectedVault) ? '💰 Withdraw Coins' : '📏 Out of Range (Need 1000m)'}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <div className="modal-buttons">
              <button onClick={() => setSelectedVault(null)} className="cancel-button">
                ❌ Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Business Linking Modal */}
      {businessLinkingModal.show && (
        <div className="modal-overlay" onClick={() => setBusinessLinkingModal({ show: false, sourceBusiness: null, targetBusinessType: null })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>🔗 Link Business</h3>
            <p>Link your <strong>{businessLinkingModal.sourceBusiness?.businessName}</strong> ({businessLinkingModal.sourceBusiness?.businessType}) to a {businessLinkingModal.targetBusinessType}</p>
            
            <div style={{ margin: '15px 0' }}>
              <h4>Select {businessLinkingModal.targetBusinessType} to link with:</h4>
              {businesses
                .filter(business => 
                  business.businessType === businessLinkingModal.targetBusinessType &&
                  business.location && 
                  business.location.lat && 
                  business.location.lng &&
                  business.location.lat !== 0 &&
                  business.location.lng !== 0
                )
                .map(business => (
                  <div key={business._id} style={{
                    margin: '10px 0',
                    padding: '15px',
                    background: 'rgba(40, 167, 69, 0.1)',
                    border: '2px solid rgba(40, 167, 69, 0.3)',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleBusinessLinking(businessLinkingModal.sourceBusiness, business)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h5 style={{ margin: '0', color: '#28a745' }}>
                          {business.businessType === 'shop' ? '🏪' : 
                           business.businessType === 'warehouse' ? '🏭' : 
                           business.businessType === 'factory' ? '🏗️' : 
                           business.businessType === 'farm' ? '🚜' : '🏢'} {business.businessName}
                        </h5>
                        <p style={{ margin: '5px 0', fontSize: '12px', color: '#888' }}>
                          Owner: {business.ownerUsername}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '12px' }}>
                          Supply Level: {business.supplyLevel || 0}/{business.maxCapacity || 1000}
                        </p>
                      </div>
                      <button style={{
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}>
                        🔗 Link
                      </button>
                    </div>
                  </div>
                ))}
              
              {businesses.filter(business => 
                business.businessType === businessLinkingModal.targetBusinessType &&
                business.location && 
                business.location.lat && 
                business.location.lng &&
                business.location.lat !== 0 &&
                business.location.lng !== 0
              ).length === 0 && (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#888',
                  fontStyle: 'italic'
                }}>
                  No {businessLinkingModal.targetBusinessType} businesses available for linking.
                  <br />
                  You need to find placed {businessLinkingModal.targetBusinessType} businesses on the map first.
                </div>
              )}
            </div>
            
            <div className="modal-buttons">
              <button 
                onClick={() => setBusinessLinkingModal({ show: false, sourceBusiness: null, targetBusinessType: null })} 
                className="cancel-button"
              >
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hotel Check-in Countdown Modal */}
      {showHotelCheckinCountdown && hotelCheckinCountdownData && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #1a3a4a 0%, #2d4d5d 100%)',
            border: '2px solid #4a90e2',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(74, 144, 226, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              🏨
            </div>
            
            <h3 style={{ 
              color: '#4a90e2', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(74, 144, 226, 0.5)'
            }}>
              {hotelCheckinCountdownData.action}...
            </h3>
            
            <div style={{ 
              fontSize: '18px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              {hotelCheckinCountdownData.hotel?.name || 'Hotel'}
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: hotelCheckinCountdownTimer <= 2 ? '#ff4444' : '#4a90e2',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {hotelCheckinCountdownTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: '0'
            }}>
              Preparing your room...
            </p>
          </div>
        </div>
      )}

      {/* Rental House Entry Countdown Modal */}
      {showRentalHouseCountdown && rentalHouseCountdownData && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #2a1a4a 0%, #4d2d5d 100%)',
            border: '2px solid #9d4edd',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(157, 78, 221, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              🏠
            </div>
            
            <h3 style={{ 
              color: '#9d4edd', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(157, 78, 221, 0.5)'
            }}>
              Entering Rental House...
            </h3>
            
            <div style={{ 
              fontSize: '18px', 
              color: '#ffd700', 
              marginBottom: '20px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              {rentalHouseCountdownData.houseName}
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: rentalHouseCountdownTimer <= 2 ? '#ff4444' : '#9d4edd',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {rentalHouseCountdownTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: '0'
            }}>
              Accessing rented property...
            </p>
          </div>
        </div>
      )}

      {/* ATM Travel Countdown Modal */}
      {showATMTravelCountdown && atmTravelCountdownData && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            textAlign: 'center', 
            maxWidth: '400px',
            padding: '30px',
            background: 'linear-gradient(135deg, #1a4a1a 0%, #2d5d2d 100%)',
            border: '2px solid #00ff41',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)'
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>
              🏧
            </div>
            
            <h3 style={{ 
              color: '#00ff41', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(0, 255, 65, 0.5)'
            }}>
              Moving to ATM...
            </h3>
            
            <div style={{ 
              fontSize: '18px', 
              color: '#ffd700', 
              marginBottom: '15px',
              fontWeight: 'bold',
              textShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
            }}>
              Cost: {atmTravelCountdownData.costText}
            </div>
            
            <div style={{ 
              fontSize: '48px', 
              color: atmTravelCountdownTimer <= 3 ? '#ff4444' : '#00ff41',
              fontWeight: 'bold',
              marginBottom: '15px',
              textShadow: '0 0 15px currentColor'
            }}>
              {atmTravelCountdownTimer}
            </div>
            
            <p style={{ 
              color: '#888', 
              fontSize: '14px',
              margin: '0'
            }}>
              Traveling to ATM location...
            </p>
          </div>
        </div>
      )}

      {/* Vault Withdrawal Modal */}
      {showWithdrawModal && withdrawVault && (
        <div className="modal-overlay" onClick={() => setShowWithdrawModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            maxWidth: '400px',
            padding: '20px',
            background: 'linear-gradient(135deg, #1a4a1a 0%, #2d5d2d 100%)',
            border: '2px solid #00ff41',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)'
          }}>
            <h3 style={{ 
              color: '#00ff41', 
              marginBottom: '15px',
              textShadow: '0 0 10px rgba(0, 255, 65, 0.5)',
              textAlign: 'center'
            }}>
              💰 Withdraw from Vault
            </h3>
            
            <div style={{ marginBottom: '15px', color: '#fff' }}>
              <p><strong>Vault Balance:</strong> {withdrawVault.storedBalance} WR Coins</p>
              <p><strong>Your Balance:</strong> {user?.wrCoins || 0} WR Coins</p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '5px', 
                color: '#00ff41',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Amount to Withdraw:
              </label>
              <input
                type="number"
                min="1"
                max={withdrawVault.storedBalance}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Enter amount..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '2px solid #00ff41',
                  borderRadius: '5px',
                  background: '#1a1a1a',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
            </div>

            <div className="modal-buttons" style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleWithdrawSubmit}
                disabled={!withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > withdrawVault.storedBalance}
                style={{
                  background: (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > withdrawVault.storedBalance) ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > withdrawVault.storedBalance) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                💰 Withdraw
              </button>
              <button
                onClick={() => setShowWithdrawModal(false)}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Community Task Modal */}
      {showTaskModal && selectedCommunityTask && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{
            maxWidth: '600px',
            padding: '25px',
            background: 'linear-gradient(135deg, #1a1a4a 0%, #2d2d5d 100%)',
            border: '2px solid #00ff41',
            borderRadius: '15px',
            boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)'
          }}>
            <h3 style={{ 
              color: '#00ff41', 
              marginBottom: '20px',
              textShadow: '0 0 10px rgba(0, 255, 65, 0.5)',
              textAlign: 'center',
              fontSize: '24px'
            }}>
              📮 Community Task
            </h3>
            
            <div style={{ color: '#fff', marginBottom: '20px' }}>
              <h4 style={{ color: '#00ff41', marginBottom: '10px' }}>{selectedCommunityTask.title}</h4>
              <p style={{ marginBottom: '8px' }}><strong>Type:</strong> {selectedCommunityTask.type}</p>
              <p style={{ marginBottom: '8px' }}><strong>Difficulty:</strong> {selectedCommunityTask.difficulty}</p>
              <p style={{ marginBottom: '8px' }}><strong>Creator:</strong> {selectedCommunityTask.createdBy.username}</p>
              <p style={{ marginBottom: '15px' }}><strong>Description:</strong> {selectedCommunityTask.description}</p>
              
              <div style={{ 
                background: 'rgba(0, 255, 65, 0.1)', 
                padding: '15px', 
                borderRadius: '8px',
                border: '1px solid rgba(0, 255, 65, 0.3)',
                marginBottom: '15px'
              }}>
                <h5 style={{ color: '#00ff41', marginBottom: '10px' }}>Task Content:</h5>
                <p style={{ lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{selectedCommunityTask.content}</p>
              </div>
              
              {selectedCommunityTask.hint && (
                <div style={{ 
                  background: 'rgba(255, 193, 7, 0.1)', 
                  padding: '10px', 
                  borderRadius: '5px',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  marginBottom: '15px'
                }}>
                  <p style={{ color: '#ffc107' }}><strong>💡 Hint:</strong> {selectedCommunityTask.hint}</p>
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#ccc' }}>
                <span>Attempts: {selectedCommunityTask.attempts}</span>
                <span>Completions: {selectedCommunityTask.completions}</span>
                <span>Rating: 👍{selectedCommunityTask.upvotes} 👎{selectedCommunityTask.downvotes}</span>
              </div>
              
              {/* Rating Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '15px', 
                paddingTop: '15px', 
                borderTop: '1px solid #444'
              }}>
                <button
                  onClick={() => rateTask(selectedCommunityTask._id, 'upvote')}
                  style={{
                    background: selectedCommunityTask.userRating === 'upvote' ? '#28a745' : 'rgba(40, 167, 69, 0.2)',
                    color: selectedCommunityTask.userRating === 'upvote' ? 'white' : '#28a745',
                    border: '1px solid #28a745',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  👍 Like
                </button>
                <button
                  onClick={() => rateTask(selectedCommunityTask._id, 'downvote')}
                  style={{
                    background: selectedCommunityTask.userRating === 'downvote' ? '#dc3545' : 'rgba(220, 53, 69, 0.2)',
                    color: selectedCommunityTask.userRating === 'downvote' ? 'white' : '#dc3545',
                    border: '1px solid #dc3545',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  👎 Dislike
                </button>
              </div>
            </div>
            
            {!selectedCommunityTask.hasCompleted ? (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: '#00ff41',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}>
                    Your Solution:
                  </label>
                  <textarea
                    value={taskSolution}
                    onChange={(e) => setTaskSolution(e.target.value)}
                    placeholder="Enter your solution here..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px solid #00ff41',
                      borderRadius: '5px',
                      background: '#1a1a1a',
                      color: '#fff',
                      fontSize: '14px',
                      resize: 'vertical',
                      minHeight: '80px'
                    }}
                  />
                </div>
                
                <div className="modal-buttons" style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={submitTaskSolution}
                    disabled={!taskSolution.trim()}
                    style={{
                      background: !taskSolution.trim() ? '#6c757d' : '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '5px',
                      cursor: !taskSolution.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    🎯 Submit Solution {!selectedCommunityTask.hasPaid ? '(100 WR Coins)' : ''}
                  </button>
                  <button
                    onClick={() => reportCommunityTask('inappropriate', 'User reported as inappropriate')}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    🚨 Report Task
                  </button>
                  
                  {/* Field Agent Removal Option */}
                  {isFieldAgent && (
                    <div style={{ 
                      width: '100%', 
                      marginTop: '15px',
                      padding: '15px',
                      background: 'rgba(220, 53, 69, 0.1)',
                      border: '2px solid #dc3545',
                      borderRadius: '8px'
                    }}>
                      <h4 style={{ color: '#dc3545', marginBottom: '10px' }}>🕵️ Field Agent Controls</h4>
                      <p style={{ color: '#fff', marginBottom: '10px', fontSize: '13px' }}>
                        As an active field agent, you can permanently remove this task from the map.
                      </p>
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '5px', 
                          color: '#dc3545',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}>
                          Removal Reason:
                        </label>
                        <select
                          value={fieldAgentRemovalReason}
                          onChange={(e) => setFieldAgentRemovalReason(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '2px solid #dc3545',
                            borderRadius: '5px',
                            background: '#1a1a1a',
                            color: '#fff',
                            fontSize: '14px'
                          }}
                        >
                          <option value="">Select reason...</option>
                          <option value="inappropriate_content">Inappropriate Content</option>
                          <option value="spam">Spam</option>
                          <option value="offensive">Offensive Material</option>
                          <option value="copyright">Copyright Violation</option>
                          <option value="illegal">Illegal Content</option>
                          <option value="location_violation">Improper Location</option>
                          <option value="other">Other Policy Violation</option>
                        </select>
                      </div>
                      <button
                        onClick={fieldAgentRemoveTask}
                        disabled={!fieldAgentRemovalReason}
                        style={{
                          background: !fieldAgentRemovalReason ? '#6c757d' : '#dc3545',
                          color: 'white',
                          border: 'none',
                          padding: '10px 15px',
                          borderRadius: '5px',
                          cursor: !fieldAgentRemovalReason ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        🗑️ Remove Task (PERMANENT)
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => setShowTaskModal(false)}
                    style={{
                      background: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ❌ Close
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  background: 'rgba(40, 167, 69, 0.2)',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '2px solid #28a745',
                  marginBottom: '15px'
                }}>
                  <h4 style={{ color: '#28a745', margin: 0 }}>✅ Task Completed!</h4>
                  <p style={{ color: '#fff', margin: '5px 0 0 0' }}>You have already completed this task and earned XP.</p>
                </div>
                <button
                  onClick={() => setShowTaskModal(false)}
                  style={{
                    background: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ❌ Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unplaced My Tasks Section - Bottom of Page */}
      {(unplacedTasks.length > 0 || true) && (
        <div style={{
          width: '100%',
          padding: '20px',
          background: 'rgba(0, 20, 0, 0.95)',
          border: '2px solid var(--hacker-green)',
          borderRadius: '8px',
          margin: '20px 0',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 0 20px rgba(0, 255, 65, 0.3)'
        }}>
          <h3 style={{
            margin: '0 0 15px 0',
            color: 'var(--hacker-green)',
            fontSize: '1.2rem',
            textAlign: 'center',
            borderBottom: '1px solid rgba(0, 255, 65, 0.3)',
            paddingBottom: '10px'
          }}>
            📋 My Tasks
          </h3>
          <div style={{ 
            fontSize: '0.9rem', 
            marginBottom: '15px', 
            color: '#888',
            textAlign: 'center' 
          }}>
            Click any task below to place it on the map:
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '15px',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {unplacedTasks.map((task) => (
              <div
                key={task._id}
                onClick={() => handlePlaceUnplacedTask(task)}
                style={{
                  background: 'rgba(0, 40, 0, 0.6)',
                  border: '1px solid rgba(0, 255, 65, 0.4)',
                  borderRadius: '8px',
                  padding: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '0.9rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(0, 60, 0, 0.8)';
                  e.target.style.borderColor = 'var(--hacker-green)';
                  e.target.style.transform = 'translateY(-3px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(0, 40, 0, 0.6)';
                  e.target.style.borderColor = 'rgba(0, 255, 65, 0.4)';
                  e.target.style.transform = 'translateY(0px)';
                }}
              >
                <div style={{
                  fontWeight: 'bold',
                  color: 'var(--hacker-green)',
                  marginBottom: '8px',
                  fontSize: '1.1rem'
                }}>
                  📍 {task.title}
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#ccc',
                  marginBottom: '10px',
                  display: 'flex',
                  gap: '20px'
                }}>
                  <span>🎯 {task.type}</span>
                  <span>⭐ {task.difficulty || 'easy'}</span>
                  <span>👤 {task.completions || 0} solved</span>
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: '#aaa',
                  lineHeight: '1.4',
                  marginBottom: '12px',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {task.description}
                </div>
                <div style={{
                  fontSize: '0.85rem',
                  color: 'var(--hacker-green)',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  background: 'rgba(0, 255, 65, 0.1)',
                  padding: '8px',
                  borderRadius: '4px'
                }}>
                  🖱️ Click to place on map
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default LeafletTreasureHunt;